<template>
  <div
    class="home-wage-page"
    :class="{
      'home-wage-page--member': !showTooltips,
      'home-wage-page--magam': isWageReadOnly,
      'home-wage-page--director-store': true,
    }"
  >
    <YunsuTestWageSlipPrompt
      :member="memberData"
      :member-code="memberCode"
      :biz-gubun="selectedBizGubun"
      :year-month="yearMonth"
    />
    <div class="home-wage-layout">
      <aside class="home-wage-sidebar member-remote-sidebar">
        <div class="sidebar-scroll">
          <div class="sidebar-top">
            <div class="sidebar-ym-selector">
              <button type="button" class="ym-nav-btn" :disabled="loading || detailLoading || importingPrevMonth" @click="changeMonth(-1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
              <div class="ym-display">
                <span class="ym-value">{{ selectedYear }}.{{ String(selectedMonth).padStart(2, '0') }}</span>
                <input
                  type="month"
                  :value="`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`"
                  class="hidden-month-picker"
                  :aria-label="`${selectedYear}년 ${selectedMonth}월 선택`"
                  :disabled="loading || detailLoading || importingPrevMonth"
                  @change="onMonthPickerChange"
                />
              </div>
              <div class="ym-magam-slot">
                <LoadingOverlay :show="wageMagamLoading" variant="light" />
                <template v-if="!wageMagamLoading">
                  <button
                    v-if="isWageMagamClosed"
                    type="button"
                    class="ym-magam-btn ym-magam-btn--done ym-magam-btn--clickable"
                    :disabled="wageMagamReleasing"
                    :title="movedMagamButtonTitle"
                    @click="releaseWageMagam"
                  >
                    <v-icon v-if="wageMagamReleasing" size="12" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>마감완료</template>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="ym-magam-btn ym-magam-btn--ready"
                    :disabled="wageMagamClosing || loading"
                    :title="magamReadyButtonTitle"
                    @click="onCloseWageMagamClick"
                  >
                    <v-icon v-if="wageMagamClosing" size="12" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>마감하기</template>
                  </button>
                </template>
              </div>
              <button type="button" class="ym-nav-btn" :disabled="loading || detailLoading || importingPrevMonth" @click="changeMonth(1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>

            <div
              v-if="!isWageMagamClosed && !wageMagamLoading"
              class="wage-magam-guide wage-magam-guide--tip"
            >
              <p class="wage-magam-guide__text">
                이번 달 임금 작성이 끝나면 「마감하기」를 눌러 주세요.
              </p>
            </div>
            <div
              v-if="isMovedWageMonth"
              class="wage-magam-guide wage-magam-guide--moved"
            >
              <p class="wage-magam-guide__title">이전 시스템에서 옮긴 임금</p>
              <p class="wage-magam-guide__text">
                이 달 임금은 다른 시스템에서 옮겨 마감해 두었습니다.
              </p>
            </div>

            <!-- 지급일·산재요율: 당월 마감 완료 시에만 잠금 (전월 미마감 잠금과 분리) -->
            <div class="sidebar-settings">
              <div class="setting-item">
                <span class="setting-label">지급일</span>
                <YmdDateInput
                  v-model="paymentDate"
                  class="setting-input"
                  :disabled="loading || isWageMagamClosed || savingMonthSettings"
                />
              </div>
              <div class="setting-item">
                <span class="setting-label">산재요율</span>
                <div class="setting-rate-input">
                  <input
                    v-model="sanRateDisplay"
                    type="text"
                    class="setting-input setting-rate-input__field"
                    placeholder="0.68"
                    inputmode="decimal"
                    :disabled="loading || isWageMagamClosed || savingMonthSettings"
                  />
                  <span class="setting-rate-input__suffix">%</span>
                </div>
              </div>
              <v-btn
                v-if="!isWageMagamClosed"
                variant="flat"
                class="btn-save sidebar-setting-save-btn"
                size="small"
                block
                :disabled="loading || savingMonthSettings"
                @click="handleSaveMonthSettings"
              >
                <v-icon v-if="savingMonthSettings" size="14" class="mdi-spin">mdi-loading</v-icon>
                <template v-else>저장</template>
              </v-btn>
            </div>

            <div class="home-wage-editable" :class="{ 'home-wage-editable--locked': isWageReadOnly }">

            <button
              type="button"
              class="sidebar-savings-btn"
              :disabled="!memberCode || loadingSidebarMonthSaving"
              @click="openNontaxSavingsModal"
            >
              <v-icon v-if="loadingSidebarMonthSaving" size="16" class="mdi-spin">mdi-loading</v-icon>
              <v-icon v-else size="16">mdi-piggy-bank-outline</v-icon>
              <span class="sidebar-savings-btn__text">
                <span class="sidebar-savings-btn__label">인건비 절감</span>
                <span v-if="sidebarMonthSaving != null" class="sidebar-savings-btn__value">
                  {{ formatWageNumber(sidebarMonthSaving) }}원
                </span>
                <span v-else class="sidebar-savings-btn__sub">당월 절감액</span>
              </span>
            </button>
            </div>

            <button
              v-if="mainView !== 'list'"
              type="button"
              class="btn-show-all-list"
              @click="showAllList"
            >
              <v-icon size="14">mdi-format-list-bulleted</v-icon>
              사원전체목록보기
            </button>
          </div>

          <!-- 리모컨 사원 목록 패널 -->
          <div class="sidebar-employee-panel">
            <div class="sidebar-list-header">
              <span class="list-header-title">
                <span class="list-header-dot"></span>
                사원 목록
              </span>
              <span class="list-header-count">{{ sortedEmployees.length }}명</span>
              <span v-if="isSuperAdmin && viewMismatchEmpKeySet.size > 0" class="list-header-mismatch">
                재저장 {{ viewMismatchEmpKeySet.size }}명
              </span>
            </div>
            <div class="sidebar-search-wrap">
              <button
                type="button"
                class="sidebar-search-toggle"
                :class="{ 'sidebar-search-toggle--expanded': searchFiltersExpanded }"
                @click="searchFiltersExpanded = !searchFiltersExpanded"
              >
                <span class="sidebar-search-toggle__label">조건검색</span>
                <v-icon size="14">{{ searchFiltersExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
              <v-expand-transition>
                <div v-show="searchFiltersExpanded" class="sidebar-search">
                  <div class="search-item">
                    <span class="search-label">사원 검색</span>
                    <div class="search-input-wrap">
                      <input v-model="searchName" type="text" placeholder="이름..." class="search-input" />
                      <button v-if="searchName" type="button" class="search-clear" @click="searchName = ''">
                        <v-icon size="10">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                  <div class="search-item">
                    <span class="search-label">직종 필터</span>
                    <select v-model="filterJobType" class="search-select">
                      <option value="">전체</option>
                      <option v-for="job in jobTypeOptions" :key="job" :value="job">{{ job }}</option>
                    </select>
                  </div>
                  <div v-if="showClaimBizFilter" class="search-item">
                    <span class="search-label">사업구분</span>
                    <select v-model="filterClaimBiz" class="search-select">
                      <option value="">전체</option>
                      <option value="방문요양">방문요양</option>
                      <option value="방문목욕">방문목욕</option>
                    </select>
                  </div>
                </div>
              </v-expand-transition>
            </div>
            <p v-if="employeeListMeta && !employeeListMeta.hasClaimDataForMonth" class="sidebar-list-notice">
              해당 연월 청구내역이 없습니다. 요양보호사는 표시되지 않습니다.
            </p>
            <LtcsMissingRequestCta
              v-if="employeeListMeta && !employeeListMeta.hasClaimDataForMonth"
              :member-code="memberCode"
              :biz-gubun="selectedBizGubun"
              :year-month="yearMonth"
              :types="WAGE_CLAIM_LTCS_REQUEST_TYPES"
              @updated="onLtcsWageRequestReady"
            />
            <div v-if="!loading && sortedEmployees.length > 0" class="sidebar-list-columns">
              <span>이름</span>
              <span>직종</span>
            </div>
            <div class="sidebar-employee-list">
              <div v-if="loading" class="sidebar-list-loading">
                <v-progress-circular indeterminate color="#1e3a5f" size="20" width="2" />
              </div>
              <template v-else-if="sortedEmployees.length === 0">
                <p class="sidebar-list-empty">
                  <template v-if="employeeListMeta && !employeeListMeta.hasClaimDataForMonth">
                    해당 연월 청구내역이 없어 요양보호사가 없습니다.
                  </template>
                  <template v-else>등록된 사원이 없습니다.</template>
                </p>
              </template>
              <button
                v-for="emp in sortedEmployees"
                :key="getEmpKey(emp)"
                type="button"
                class="sidebar-emp-item"
                :class="{ active: isSelectedEmployee(emp) }"
                :disabled="loading || detailLoading || importingPrevMonth || savingAllRecipients"
                @click="selectEmployee(emp)"
              >
                <div class="emp-item-main">
                  <span
                    class="emp-item-name"
                    :class="{ 'emp-name-view-mismatch': isViewMismatch(emp) }"
                    :title="isViewMismatch(emp) ? '센터장·관리자 시점의 지급금액이 일치하지 않습니다. 확인 후 재저장이 필요합니다.' : undefined"
                  >{{ getEmpName(emp) }}</span>
                  <span v-if="isRepresentativeEmployee(emp)" class="emp-representative-chip">대표</span>
                  <span v-if="getEmpClaimBizChips(emp).length" class="claim-biz-chips">
                    <span
                      v-for="chip in getEmpClaimBizChips(emp)"
                      :key="chip"
                      class="claim-biz-chip"
                      :class="getClaimBizChipClass(chip)"
                    >{{ chip }}</span>
                  </span>
                </div>
                <span class="emp-item-job">{{ getEmpJob(emp) }}</span>
              </button>
            </div>
          </div>

          <div v-if="canEditTaxRate" class="sidebar-migration-block">
            <p class="sidebar-migration-block__note">관리자 전용 · 엑셀 마이그레이션</p>
            <v-btn
              variant="flat"
              class="btn-refresh btn-migration"
              size="small"
              block
              :disabled="loading || detailLoading || isWageReadOnly"
              @click="showMigrationModal = true"
            >
              <v-icon size="14">mdi-file-excel</v-icon>
              임금 엑셀 업로드
            </v-btn>
            <v-btn
              variant="flat"
              class="btn-refresh btn-migration"
              size="small"
              block
              :disabled="loading || detailLoading"
              @click="showLedgerConvertModal = true"
            >
              <v-icon size="14">mdi-swap-horizontal</v-icon>
              임금대장 치환(테스트)
            </v-btn>
          </div>

          <div class="sidebar-footer-block">
            <button
              type="button"
              class="sidebar-labor-cost-btn sidebar-labor-cost-btn--above-tax"
              :disabled="loadingLaborCostModal"
              @click="openLaborCostModal"
            >
              <v-icon v-if="loadingLaborCostModal" size="14" class="mdi-spin">mdi-loading</v-icon>
              <v-icon v-else size="14">mdi-chart-pie</v-icon>
              인건비지출내역
            </button>

            <div v-if="taxRateInfo" class="sidebar-bottom">
            <div class="sidebar-tax-info">
              <div class="tax-info-title-row">
                <button
                  type="button"
                  class="tax-info-toggle"
                  :class="{ 'tax-info-toggle--expanded': taxRatePanelExpanded }"
                  @click="taxRatePanelExpanded = !taxRatePanelExpanded"
                >
                  <span class="tax-info-title">4대보험·세율 ({{ taxRateInfo.year }}년)</span>
                  <v-icon size="14">{{ taxRatePanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
              </div>
              <div v-show="taxRatePanelExpanded" class="tax-info-body">
                <div v-if="taxRateFallback" class="tax-info-note">※ 최근 연도 세율 적용</div>
                <div v-for="row in taxRateDisplayRows" :key="row.label" class="tax-info-row">
                  <span>{{ row.label }}</span>
                  <span>{{ row.value }}</span>
                </div>
                <div v-if="canEditTaxRate" class="sidebar-wage-delete">
                  <p class="sidebar-wage-delete__admin-note">관리자 전용 · 관리자만 표시됩니다</p>
                  <p class="sidebar-wage-delete__hint">
                    <template v-if="isWageReadOnly">마감된 연월은 삭제할 수 없습니다.</template>
                    <template v-else-if="!memberCode">
                      시설 정보가 없습니다.
                    </template>
                    <template v-else>
                      {{ formatYearMonthLabel(yearMonth) }} · 해당 연월 임금 데이터 전체 삭제<br />
                      (급여대장 + 수급자별 설정)
                    </template>
                  </p>
                  <v-btn
                    variant="flat"
                    class="btn-delete sidebar-wage-delete__btn"
                    size="small"
                    block
                    :disabled="!canDeleteMonthWageData || deletingWageData"
                    @click="deleteMonthWageData"
                  >
                    <v-icon v-if="deletingWageData" size="14" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>임금 데이터 삭제</template>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
          </div>

          <LtcsRequestSidebarButton
            :member-code="memberCode"
            :biz-gubun="selectedBizGubun"
            :year-month="yearMonth"
            variant="wage"
            @ready="onLtcsWageRequestReady"
          />

        </div>
      </aside>

      <div class="home-wage-main home-wage-editable" :class="{ 'home-wage-editable--locked': isWageReadOnly }">
        <!-- 전체 목록 (기본 화면) -->
        <template v-if="mainView === 'list'">
          <div class="home-wage-content-shell home-wage-content-shell--list">
          <LoadingOverlay :show="loading" text="불러오는 중..." variant="light" />
          <LoadingOverlay :show="importingPrevMonth" text="데이터 불러오는 중..." />
          <div class="home-wage-toolbar">
            <div class="toolbar-left">
              <div class="toolbar-title">
                <v-icon size="20" color="#1e3a5f">mdi-format-list-bulleted</v-icon>
                <span>사원별 급여 현황</span>
              </div>
              <span class="toolbar-count">{{ sortedEmployees.length }}명</span>
              <div class="toolbar-report-actions">
                <CommonWageReportMenu
                  :saved-wage-count="savedWageCount"
                  :employee-count="sortedEmployees.length"
                  :can-view-accounting-report="canViewAccountingReport"
                  :is-admin="canEditTaxRate"
                  :is-super-admin="isSuperAdmin"
                  :slip-disabled="savedWageCount === 0"
                  :show-nontax-section="false"
                  slip-label="명세서PDF"
                  @accounting-report="openAccountingWageReport"
                  @client-report="openClientWageReport"
                  @wage-slip="showWageSlipOptionsDialog = true"
                  @retirement-compare="openRetirementCompareReport"
                />
                <WageSlipBroadcastButton
                  :is-closed="isWageMagamClosed"
                  :member-code="memberCode"
                  :year-month="yearMonth"
                  :biz-gubun="selectedBizGubun"
                />
              </div>
            </div>
            <div class="toolbar-right">
              <v-btn
                variant="flat"
                class="btn-reset"
                size="small"
                :disabled="loadingExpandedData || importingPrevMonth || savingAllRecipients"
                @click="toggleExpandedView"
              >
                <v-icon v-if="loadingExpandedData" size="14" class="mdi-spin">mdi-loading</v-icon>
                <v-icon v-else size="14">{{ isExpandedView ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ isExpandedView ? '접기' : '수급자별 시급 펼치기' }}
              </v-btn>
              <v-btn
                v-if="isExpandedView && !isWageReadOnly"
                variant="flat"
                class="btn-edit"
                size="small"
                @click="showGlobalBulkHourlyModal = true"
              >
                <v-icon size="14">mdi-cash-sync</v-icon>
                전체 시급 일괄 설정
              </v-btn>
              <v-btn
                v-if="!isWageReadOnly"
                variant="flat"
                class="btn-refresh toolbar-import-btn"
                size="small"
                :disabled="!canImportPrevMonthDataList || importingPrevMonth || savingAllRecipients || isAdminPreviewView"
                @click="importPrevMonthDataForAll"
              >
                <v-icon v-if="importingPrevMonth" size="14" class="mdi-spin">mdi-loading</v-icon>
                <template v-else>지난달 데이터 가져오기</template>
              </v-btn>
              <v-btn
                v-if="!isWageReadOnly"
                variant="flat"
                class="btn-save"
                size="small"
                :disabled="savingAllRecipients || importingPrevMonth || isAdminPreviewView"
                @click="saveAllData"
              >
                <v-icon v-if="savingAllRecipients" size="14" class="mdi-spin">mdi-loading</v-icon>
                <template v-else>모두저장</template>
              </v-btn>
            </div>
          </div>

          <div class="home-wage-table-wrap">
            <!-- 펼침 뷰 로딩 오버레이 -->
            <LoadingOverlay :show="loadingExpandedData" text="청구내역 불러오는 중..." />

            <!-- 모두저장 로딩 오버레이 -->
            <LoadingOverlay :show="savingAllRecipients" text="저장 중..." variant="save" />

            <table class="home-wage-table" :class="{ 'home-wage-table--loading': loading }">
              <thead>
                <tr>
                  <th>No</th>
                  <th class="sortable" @click="toggleSort('name')">
                    사원명 <v-icon size="12">{{ getSortIcon('name') }}</v-icon>
                  </th>
                  <th class="sortable" @click="toggleSort('job')">
                    직종 <v-icon size="12">{{ getSortIcon('job') }}</v-icon>
                  </th>
                  <th class="sortable col-wage" @click="toggleSort('monthlyPay')">
                    월급여 <v-icon size="12">{{ getSortIcon('monthlyPay') }}</v-icon>
                  </th>
                  <th class="sortable col-wage" @click="toggleSort('corpInsurance')">
                    기관부담보험 <v-icon size="12">{{ getSortIcon('corpInsurance') }}</v-icon>
                  </th>
                  <th class="sortable col-wage" @click="toggleSort('retirement')">
                    퇴직금 <v-icon size="12">{{ getSortIcon('retirement') }}</v-icon>
                  </th>
                  <th class="col-recipient-status">수가시급</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="sortedEmployees.length === 0">
                  <td colspan="7" class="empty-row">등록된 사원이 없습니다.</td>
                </tr>
                <template v-for="(emp, idx) in sortedEmployees" :key="getEmpKey(emp)">
                  <!-- 사원 기본 행 -->
                  <tr
                    class="row-clickable"
                    :class="{
                      'row-selected': isSelectedEmployee(emp),
                      'row-expanded': isExpandedView && expandedEmpKeys.has(getEmpKey(emp))
                    }"
                    @click="isExpandedView && hasCaregiverClaims(emp) ? toggleEmpExpand(getEmpKey(emp)) : selectEmployee(emp)"
                  >
                    <td class="col-no">
                      <span v-if="isExpandedView && hasCaregiverClaims(emp)" class="expand-icon">
                        <v-icon size="14">{{ expandedEmpKeys.has(getEmpKey(emp)) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      </span>
                      {{ idx + 1 }}
                    </td>
                    <td class="col-name">
                      <div class="name-cell">
                        <div class="avatar">
                          {{ getInitial(getEmpName(emp)) }}
                        </div>
                        <div class="name-cell-text">
                          <span
                            class="name-cell-label"
                            :class="{ 'emp-name-view-mismatch': isViewMismatch(emp) }"
                            :title="isViewMismatch(emp) ? '센터장·관리자 시점의 지급금액이 일치하지 않습니다. 확인 후 재저장이 필요합니다.' : undefined"
                          >{{ getEmpName(emp) }}</span>
                          <span v-if="getEmpClaimBizChips(emp).length" class="claim-biz-chips">
                            <span
                              v-for="chip in getEmpClaimBizChips(emp)"
                              :key="chip"
                              class="claim-biz-chip"
                              :class="getClaimBizChipClass(chip)"
                            >{{ chip }}</span>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{{ getEmpJob(emp) }}</td>
                    <td class="col-wage">{{ formatListWageAmount(emp, 'monthlyPay') }}</td>
                    <td class="col-wage">{{ formatListWageAmount(emp, 'corpInsurance') }}</td>
                    <td class="col-wage">{{ formatListWageAmount(emp, 'retirement') }}</td>
                    <td class="col-recipient-status">
                      <span :class="getRecipientHourlyCompleteStatusClass(emp)">
                        {{ formatRecipientHourlyCompleteLabel(emp) }}
                      </span>
                    </td>
                  </tr>

                  <!-- 펼침 뷰: 수급자별 시급/수가시간 -->
                  <tr
                    v-if="isExpandedView && expandedEmpKeys.has(getEmpKey(emp))"
                    class="row-expanded-content"
                  >
                    <td colspan="7">
                      <div class="expanded-recipient-panel">
                        <div class="expanded-emp-toolbar">
                          <div class="expanded-emp-toolbar__title">
                            <span class="expanded-emp-toolbar__label">수급자별 시급</span>
                            <span class="expanded-emp-toolbar__count">
                              {{ allEmpSummaryRows.get(getEmpKey(emp))?.length || 0 }}건
                            </span>
                          </div>
                          <div class="expanded-emp-toolbar__actions">
                            <span
                              v-if="getEmpWorkMeta(emp).showWorkDays || getEmpWorkMeta(emp).showWorkHours"
                              class="wage-detail-work-meta"
                            >
                              <span v-if="getEmpWorkMeta(emp).showWorkDays">
                                근로 {{ getEmpWorkMeta(emp).workDays }}일
                              </span>
                              <span v-if="getEmpWorkMeta(emp).showWorkHours">
                                {{ getEmpWorkMeta(emp).workHoursLabel }}
                              </span>
                            </span>
                            <button
                              v-if="!isWageReadOnly"
                              type="button"
                              class="emp-bulk-btn"
                              @click.stop="openEmpBulkHourlyModal(getEmpKey(emp), getEmpName(emp))"
                            >
                              <v-icon size="12">mdi-cash-multiple</v-icon>
                              일괄시급
                            </button>
                          </div>
                        </div>
                        <div class="expanded-recipient-table-wrap">
                          <table class="expanded-recipient-table">
                          <thead>
                            <tr>
                              <th class="col-no">No</th>
                              <th>수급자</th>
                              <th>수가명</th>
                              <th>1회수가</th>
                              <th>서비스횟수</th>
                              <th>수가시간</th>
                              <th>시급</th>
                              <th>합계금액</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-if="!getEmpSummaryRowsForTable(getEmpKey(emp)).length">
                              <td colspan="8" class="empty-row">청구내역 없음</td>
                            </tr>
                            <tr
                              v-for="(row, rowIdx) in getEmpSummaryRowsForTable(getEmpKey(emp))"
                              :key="row.groupKey"
                              :class="{ 'row-holiday': row.isHolidayFeeSurcharge }"
                            >
                              <td class="col-no">{{ rowIdx + 1 }}</td>
                              <td>{{ row.recipientName || '-' }}</td>
                              <td class="col-fee">
                                <span>{{ row.feeName || row.feeCode || '-' }}</span>
                                <span v-if="row.isHolidayFeeSurcharge" class="claim-holiday-chip">휴일</span>
                              </td>
                              <td class="col-fee-amount">{{ formatFee(row.feeAmountPerVisit) }}</td>
                              <td class="col-count">{{ row.serviceCount }}회</td>
                              <td class="col-hours">{{ formatSugaHours(row.sugaHoursPerVisit) }}</td>
                              <td class="col-hourly">
                                <input
                                  type="text"
                                  class="expanded-input"
                                  :class="{ 'wage-input--empty': isRecipientHourlyEmpty(row, getEmpKey(emp)) }"
                                  :value="getExpandedHourlyDisplay(getEmpKey(emp), row.groupKey)"
                                  placeholder="시급"
                                  @input="onExpandedHourlyInput(getEmpKey(emp), row.groupKey, $event)"
                                  @blur="onExpandedHourlyBlur(getEmpKey(emp), row.groupKey)"
                                  @click.stop
                                />
                              </td>
                              <td class="col-total">{{ formatFee(row.totalAmount) }}</td>
                            </tr>
                          </tbody>
                          <tfoot v-if="getEmpSummaryRowsForTable(getEmpKey(emp)).length">
                            <tr>
                              <td colspan="7" class="expanded-total-label">합계</td>
                              <td class="expanded-total-value">{{ formatFee(getExpandedEmpTotal(getEmpKey(emp))) }}</td>
                            </tr>
                          </tfoot>
                        </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          </div>
        </template>

        <!-- 개별 사원 급여 -->
        <template v-else-if="mainView === 'detail' && selectedEmployee">
          <div class="home-wage-content-shell home-wage-content-shell--detail">
          <LoadingOverlay :show="isDetailPageLoading" text="불러오는 중..." variant="light" />
          <div class="home-wage-toolbar home-wage-toolbar--detail">
            <div class="toolbar-row toolbar-row--main">
              <div class="toolbar-start">
                <button type="button" class="toolbar-back-btn" @click="showAllList">
                  <v-icon size="16">mdi-arrow-left</v-icon>
                  목록
                </button>
                <div class="toolbar-title">
                  <div class="toolbar-name-nav">
                    <button
                      type="button"
                      class="toolbar-emp-nav-btn"
                      :disabled="!canNavigatePrevEmployee || isEmployeeNavDisabled"
                      aria-label="이전 사원"
                      @click="navigatePrevEmployee"
                    >
                      <v-icon size="18">mdi-chevron-left</v-icon>
                    </button>
                    <span class="toolbar-emp-name">{{ getEmpName(selectedEmployee) }}</span>
                    <button
                      type="button"
                      class="toolbar-emp-nav-btn"
                      :disabled="!canNavigateNextEmployee || isEmployeeNavDisabled"
                      aria-label="다음 사원"
                      @click="navigateNextEmployee"
                    >
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </button>
                  </div>
                  <div v-if="selectedEmployee" class="toolbar-emp-meta">
                    <span v-if="getEmpClaimBizChips(selectedEmployee).length" class="claim-biz-chips">
                      <span
                        v-for="chip in getEmpClaimBizChips(selectedEmployee)"
                        :key="chip"
                        class="claim-biz-chip"
                        :class="getClaimBizChipClass(chip)"
                      >{{ chip }}</span>
                    </span>
                    <span
                      v-if="getEmpJob(selectedEmployee)"
                      class="claim-biz-chip claim-biz-chip--job"
                    >{{ getEmpJob(selectedEmployee) }}</span>
                    <span
                      v-if="getEmpJoinDate(selectedEmployee)"
                      class="claim-biz-chip claim-biz-chip--tenure"
                    >{{ formatEmpTenureChip(selectedEmployee) }}</span>
                    <span
                      v-if="formatEmpAgeChip(selectedEmployee)"
                      class="claim-biz-chip claim-biz-chip--age"
                    >{{ formatEmpAgeChip(selectedEmployee) }}</span>
                  </div>
                </div>
              </div>
              <div class="toolbar-end">
                <v-btn
                  v-if="canRecalcDirectorBasicPay && !isWageReadOnly && selectedEmployee"
                  variant="flat"
                  class="btn-start toolbar-recalc-btn"
                  size="small"
                  :disabled="isDetailPageLoading || payrollRecalculating || isAdminPreviewView || importingPrevMonth"
                  @click="runDirectorBasicPayRecalculate"
                >
                  <v-icon v-if="payrollRecalculating" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>계산</template>
                </v-btn>
                <v-btn
                  variant="flat"
                  class="btn-refresh toolbar-slip-btn"
                  size="small"
                  @click="previewIndividualWageSlipPdf"
                >
                  <v-icon size="14">mdi-file-pdf-box</v-icon>
                  명세서
                </v-btn>
                <v-btn
                  v-if="!isWageReadOnly"
                  variant="flat"
                  class="btn-refresh toolbar-import-btn"
                  size="small"
                  :disabled="!canImportPrevMonthData || importingPrevMonth || isAdminPreviewView"
                  @click="importPrevMonthData"
                >
                  <v-icon v-if="importingPrevMonth" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>지난달 데이터 가져오기</template>
                </v-btn>
                <v-btn
                  v-if="!isWageReadOnly"
                  variant="flat"
                  class="btn-save toolbar-save-btn"
                  size="small"
                  :disabled="savingAllRecipients || importingPrevMonth"
                  @click="saveCurrentEmployeeData"
                >
                  <v-icon v-if="savingAllRecipients" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>저장</template>
                </v-btn>
              </div>
            </div>
            <div v-if="canToggleDirectorPreview" class="toolbar-row toolbar-row--admin">
              <div class="toolbar-admin-zone">
                <HomeWagePayrollViewToggle
                  :director-active="directorPreviewActive"
                  :disabled="!selectedEmployee"
                  @select="selectPayrollViewMode"
                />
                <HomeWageAdminToolbarMenu
                  :has-selected-employee="!!selectedEmployee"
                  @open-employee-condition="openEmployeeConditionModal"
                />
              </div>
            </div>
          </div>

          <div
            class="detail-wage-board"
            :class="{ 'detail-wage-board--admin-view': isAdminPreviewView }"
          >
            <!-- 상세 데이터 로딩 (사원 선택·연월 변경·청구내역) -->
            <LoadingOverlay
              :show="isDetailPageLoading"
              text="불러오는 중..."
              variant="light"
            />

            <LoadingOverlay :show="savingAllRecipients" text="저장 중..." variant="save" />
            <LoadingOverlay :show="importingPrevMonth" text="데이터 불러오는 중..." />
            <LoadingOverlay :show="deletingWageData" text="임금 데이터 삭제 중..." variant="delete" />

            <template v-if="selectedEmployee">
              <div
                class="detail-wage-board__layout"
                :class="{
                  'detail-wage-board__layout--loading': isDetailPageLoading,
                }"
              >
                <div
                  class="detail-wage-board__row"
                  :class="{ 'detail-wage-board__row--director-allowances': showDirectorMergedAllowanceCard }"
                >
                  <section
                    v-if="isCaregiverEmployee(selectedEmployee)"
                    class="wage-detail-card wage-detail-card--pay-hourly"
                  >
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">기본급</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content">
                      <div class="wage-payroll-grid wage-payroll-grid--hourly-stepper">
                        <HomeWageTooltip location="top">
                          <template #activator="{ props: tooltipProps }">
                            <label
                              v-bind="tooltipProps"
                              class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                              for="pay-calc-hourly-wage"
                            >시급</label>
                          </template>
                          <span style="white-space: pre-line">지급계산시급 (수급자별 서비스 제공 시 적용)
현재: {{ formatWageNumber(caregiverHourlyWage) }}원</span>
                        </HomeWageTooltip>
                        <div class="wage-pay-stepper">
                          <div class="wage-pay-stepper__actions">
                            <button
                              type="button"
                              class="wage-pay-stepper__btn"
                              aria-label="1원 증가"
                              @pointerdown.prevent="onPayCalcStepperDown(1)"
                              @pointerup="onPayCalcStepperUp"
                              @pointerleave="onPayCalcStepperUp"
                              @pointercancel="onPayCalcStepperUp"
                            >
                              <v-icon size="14">mdi-chevron-up</v-icon>
                            </button>
                            <button
                              type="button"
                              class="wage-pay-stepper__btn"
                              aria-label="1원 감소"
                              @pointerdown.prevent="onPayCalcStepperDown(-1)"
                              @pointerup="onPayCalcStepperUp"
                              @pointerleave="onPayCalcStepperUp"
                              @pointercancel="onPayCalcStepperUp"
                            >
                              <v-icon size="14">mdi-chevron-down</v-icon>
                            </button>
                          </div>
                          <input
                            id="pay-calc-hourly-wage"
                            type="text"
                            class="wage-pay-hourly-input"
                            :value="caregiverHourlyWageDisplay"
                            placeholder="0"
                            inputmode="numeric"
                            @input="onCaregiverHourlyWageInput"
                            @blur="onCaregiverHourlyWageBlur"
                            @keydown="onPayCalcHourlyKeydown"
                          />
                        </div>
                      </div>
                      <HomeWagePayrollFieldRows
                        grid-class="wage-payroll-grid--hourly-linked"
                        :fields="basePayFields"
                        :format-money="formatPayrollMoneyField"
                        :format-premium-hours="formatPremiumHoursField"
                        :get-tooltip="getFieldTooltip"
                        :text-value="payrollTextValue"
                        @money-input="onPayrollMoneyInput"
                        @money-blur="onPayrollMoneyBlur"
                      />
                      </div>
                      <div class="wage-detail-card__footer">
                        <div class="wage-card-total">
                          <label class="wage-card-total__label">기본급 합계</label>
                          <span class="wage-card-total__value">{{ formatWageNumber(basePayTotal) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section v-else class="wage-detail-card wage-detail-card--pay-hourly">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">근무정보</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content">
                        <div class="wage-payroll-grid wage-payroll-grid--work">
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label
                                v-bind="tooltipProps"
                                class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                                for="staff-hourly-wage"
                              >시급</label>
                            </template>
                            <span style="white-space: pre-line">시간당 임금
현재: {{ formatWageNumber(staffForm.hourlyWage) }}원</span>
                          </HomeWageTooltip>
                          <input
                            id="staff-hourly-wage"
                            type="text"
                            class="wage-payroll-grid__input"
                            :value="staffHourlyWageDisplay"
                            placeholder="0"
                            inputmode="numeric"
                            @input="onStaffHourlyWageInput"
                            @blur="onStaffHourlyWageBlur"
                          />
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label
                                v-bind="tooltipProps"
                                class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                                for="staff-work-hours"
                              >근무시간</label>
                            </template>
                            <span style="white-space: pre-line">월 총 근무 시간 (기본: {{ HOME_WAGE_STAFF_DEFAULT_WORK_HOURS }}시간)
현재: {{ formatHoursDisplay(staffForm.workHours) || staffForm.workHours }}시간</span>
                          </HomeWageTooltip>
                          <input
                            id="staff-work-hours"
                            type="text"
                            class="wage-payroll-grid__input"
                            :value="staffWorkHoursDisplay"
                            inputmode="decimal"
                            @input="onStaffWorkHoursInput"
                            @blur="onStaffWorkHoursBlur"
                          />
                        </div>
                        <HomeWagePayrollFieldRows
                          grid-class="wage-payroll-grid--hourly-linked"
                          :fields="staffWorkInfoFields"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :get-tooltip="getFieldTooltip"
                          :text-value="payrollTextValue"
                          :premium-hours-readonly="directorMaskPremiumDisplay"
                          @money-input="onPayrollMoneyInput"
                          @money-blur="onPayrollMoneyBlur"
                          @premium-hours-input="onPremiumHoursInput"
                          @premium-hours-blur="onPremiumHoursBlur"
                        />
                        <div v-if="staffCalcResult?.validation?.warnings?.length" class="wage-staff-warnings">
                          <div v-for="(warn, idx) in staffCalcResult.validation.warnings" :key="idx" class="wage-staff-warning">
                            <v-icon size="14" color="#c62828">mdi-alert-circle</v-icon>
                            <span>{{ warn }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="wage-detail-card__footer">
                        <div class="wage-card-total">
                          <label class="wage-card-total__label">근무정보 합계</label>
                          <span class="wage-card-total__value">{{ formatWageNumber(staffWorkInfoDisplayTotal) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- 센터장 시점: 과세·비과세 통합 수당 (2열 그리드) -->
                  <section
                    v-if="showDirectorMergedAllowanceCard"
                    class="wage-detail-card wage-detail-card--allowances-merged"
                  >
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">수당</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content wage-payroll-block wage-payroll-block--director-allowances">
                        <HomeWagePayrollFieldRows
                          grid-class="wage-payroll-grid--director-allowances"
                          :fields="directorMergedAllowanceFields"
                          :format-money="formatDirectorAllowanceMoney"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                          :premium-hours-readonly="directorMaskPremiumDisplay"
                          @money-input="onPayrollMoneyInput"
                          @money-blur="onPayrollMoneyBlur"
                          @premium-hours-input="onPremiumHoursInput"
                          @premium-hours-blur="onPremiumHoursBlur"
                        />
                        <div
                          v-if="showDirectorPremiumPaySwitches"
                          class="wage-director-premium-pay__section"
                        >
                          <p class="wage-director-premium-pay__hint">
                            지급·미지급 변경 후 저장해야 명세서·임금대장에 반영됩니다.
                          </p>
                          <HomeWageDirectorPremiumPaySwitches
                            :items="directorPremiumPaySwitchItems"
                            :disabled="isWageReadOnly || payrollRecalculating"
                            :format-amount="formatDirectorPremiumPayAmount"
                            @update:enabled="onDirectorPremiumPaySwitchChange"
                          />
                        </div>
                      </div>
                      <div class="wage-detail-card__footer">
                        <div class="wage-card-total">
                          <label class="wage-card-total__label">수당 합계</label>
                          <span class="wage-card-total__value">{{ formatWageNumber(directorMergedAllowanceTotal) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <template v-else>
                  <section class="wage-detail-card wage-detail-card--nonTax">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">{{ HOME_WAGE_NON_TAX_CARD.title }}</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content wage-payroll-block">
                        <!-- 1. 노동절수당 (5월·요양보호사, 비과세 자동 산출) -->
                        <HomeWagePayrollFieldRows
                          v-if="showLaborDayNontaxField && HOME_WAGE_LABOR_DAY_NONTAX_FIELD"
                          :fields="[HOME_WAGE_LABOR_DAY_NONTAX_FIELD]"
                          :align-with-fix-checkbox="showCaregiverNontaxPremiumSection"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                        />
                        <!-- 2. 식대 (관리자만) -->
                        <HomeWagePayrollFieldRows
                          v-if="!isDirectorPayrollView"
                          :fields="nonTaxCashInputFields.filter((f) => f.key === 'lunch')"
                          :align-with-fix-checkbox="showCaregiverNontaxPremiumSection"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                          @money-input="onPayrollMoneyInput"
                          @money-blur="onPayrollMoneyBlur"
                        />
                        <HomeWagePayrollFieldRows
                          v-if="!isDirectorPayrollView"
                          :fields="nonTaxCashInputFields.filter((f) => f.key === 'lunchDirector')"
                          :align-with-fix-checkbox="showCaregiverNontaxPremiumSection"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                        />
                        <div
                          v-if="!isDirectorPayrollView && combinedLunchDisplay > 0"
                          class="wage-nontax-combined-lunch"
                        >
                          <span class="wage-nontax-combined-lunch__label">{{ HOME_WAGE_NONTAX_TERM.lunchCombined }}</span>
                          <span class="wage-nontax-combined-lunch__value">{{ formatWageNumber(combinedLunchDisplay) }}</span>
                        </div>
                        <!-- 3. 식대 실비변상 (관리자만) -->
                        <div v-if="!isDirectorPayrollView" class="wage-nontax-options">
                          <HomeWageTooltip location="top" :text="`${HOME_WAGE_NONTAX_TERM.lunch}를 실비변상으로 처리 시 퇴직금 계산에서 제외`">
                            <template #activator="{ props: tooltipProps }">
                              <label v-bind="tooltipProps" class="wage-nontax-options__item wage-nontax-options__item--tooltip">
                                <input
                                  v-model="nontaxSettings.lunchExpense"
                                  type="checkbox"
                                  class="wage-nontax-options__checkbox"
                                  @change="onNontaxSettingChange"
                                />
                                <span class="wage-nontax-options__text">{{ HOME_WAGE_NONTAX_TERM.lunchExpense }}</span>
                                <span class="wage-nontax-options__hint">(퇴직금제외)</span>
                              </label>
                            </template>
                          </HomeWageTooltip>
                        </div>
                        <!-- 5. 차량보조비 · 6. 자녀교육비 -->
                        <HomeWagePayrollFieldRows
                          :fields="nonTaxCashInputFields.filter((f) => f.key === 'car' || f.key === 'child')"
                          :align-with-fix-checkbox="showCaregiverNontaxPremiumSection"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                          @money-input="onPayrollMoneyInput"
                          @money-blur="onPayrollMoneyBlur"
                        />
                        <!-- 5. 연야휴 월 20만 비과세 (관리자 자동 · 가족요양 포함) -->
                        <div v-if="!isDirectorPayrollView" class="wage-nontax-options">
                          <HomeWageTooltip
                            location="top"
                            text="연야휴가 반영된 경우(60시간 이상·override 지급·가족요양 관리자 분리 포함) 생산직 체크 없이 월 20만원까지 비과세(연장→야간→휴일). 초과분은 과세."
                          >
                            <template #activator="{ props: tooltipProps }">
                              <div
                                v-bind="tooltipProps"
                                class="wage-nontax-options__item wage-nontax-options__item--tooltip wage-nontax-options__item--static"
                              >
                                <span class="wage-nontax-options__text">연야휴 비과세 자동 적용</span>
                                <span class="wage-nontax-options__hint">(월20만 · 가족요양 포함)</span>
                              </div>
                            </template>
                          </HomeWageTooltip>
                        </div>
                        <!-- 6. 연장야간휴일 비과세분 -->
                        <HomeWagePremiumCollapse
                          v-if="showCaregiverNontaxPremiumSection"
                          v-model:expanded="nontaxPremiumExpanded"
                          :sum="nontaxPremiumTotal"
                          :format-sum="formatWageNumber"
                          :show-manual-toggle="true"
                          :manual-adjust-enabled="premiumManualAdjustEnabled"
                          :manual-toggle-disabled="isWageReadOnly"
                          @update:manual-adjust-enabled="onPremiumManualAdjustToggle"
                        >
                          <HomeWagePayrollFieldRows
                            grid-class="wage-payroll-grid--premium-nested"
                            :align-with-fix-checkbox="true"
                            :fields="activeCaregiverPremiumFields"
                            :format-money="formatNontaxPremiumMoney"
                            :format-premium-hours="formatPremiumHoursField"
                            hide-premium-hours
                            :text-value="payrollTextValue"
                            :get-tooltip="getFieldTooltip"
                          />
                        </HomeWagePremiumCollapse>
                      </div>
                      <div v-if="nonTaxTotalFields.length" class="wage-detail-card__footer">
                        <div
                          v-for="field in nonTaxTotalFields"
                          :key="field.key"
                          class="wage-card-total"
                        >
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label v-bind="tooltipProps" class="wage-card-total__label wage-card-total__label--tooltip">{{ field.label }}</label>
                            </template>
                            <span style="white-space: pre-line">{{ getFieldTooltip(field.key) }}</span>
                          </HomeWageTooltip>
                          <span class="wage-card-total__value">{{ formatPayrollMoneyField(field.key) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section class="wage-detail-card wage-detail-card--taxable-other">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">과세 수당</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content wage-payroll-block">
                        <HomeWagePayrollFieldRows
                          :fields="activeTaxableOtherFields"
                          :align-with-fix-checkbox="showCaregiverTaxablePremiumSection"
                          :format-money="formatPayrollMoneyField"
                          :format-premium-hours="formatPremiumHoursField"
                          :text-value="payrollTextValue"
                          :get-tooltip="getFieldTooltip"
                          @money-input="onPayrollMoneyInput"
                          @money-blur="onPayrollMoneyBlur"
                          @premium-hours-input="onPremiumHoursInput"
                          @premium-hours-blur="onPremiumHoursBlur"
                        />
                        <HomeWagePremiumCollapse
                          v-if="showCaregiverTaxablePremiumSection"
                          v-model:expanded="taxablePremiumExpanded"
                          :sum="taxablePremiumDisplaySum"
                          :format-sum="formatWageNumber"
                          :show-manual-toggle="true"
                          :manual-adjust-enabled="premiumManualAdjustEnabled"
                          :manual-toggle-disabled="isWageReadOnly"
                          @update:manual-adjust-enabled="onPremiumManualAdjustToggle"
                        >
                          <HomeWagePayrollFieldRows
                            grid-class="wage-payroll-grid--premium-nested"
                            :align-with-fix-checkbox="true"
                            :fields="activeCaregiverPremiumFields"
                            :format-money="formatTaxablePremiumMoney"
                            :format-premium-hours="formatPremiumHoursField"
                            :hide-premium-hours="productionNontaxActive && !isDirectorPayrollView"
                            :text-value="payrollTextValue"
                            :get-tooltip="getFieldTooltip"
                            @premium-hours-input="onPremiumHoursInput"
                            @premium-hours-blur="onPremiumHoursBlur"
                          />
                        </HomeWagePremiumCollapse>
                      </div>
                      <div class="wage-detail-card__footer">
                        <div class="wage-card-total">
                          <label class="wage-card-total__label">과세 수당 합계</label>
                          <span class="wage-card-total__value">{{ formatWageNumber(activeTaxableOtherTotal) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>
                  </template>
                </div>

                <div class="detail-wage-board__row detail-wage-board__row--deduct">
                  <section class="wage-detail-card wage-detail-card--socialInsurance">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">{{ HOME_WAGE_SOCIAL_INSURANCE_CARD.title }}</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content">
                        <div class="wage-insurance-single">
                          <div
                            v-for="pair in insuranceSharedPairs"
                            :key="`ins-${pair.key}`"
                            class="wage-insurance-single__row"
                          >
                            <HomeWageTooltip location="top">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="wage-insurance-single__name wage-insurance-single__name--tooltip wage-insurance-single__name-with-chip">
                                  <span>{{ pair.label }}</span>
                                  <span class="wage-insurance-single__role-chip wage-insurance-single__role-chip--placeholder" aria-hidden="true">근로</span>
                                </span>
                              </template>
                              <span style="white-space: pre-line">{{ getInsuranceTooltip(pair) }}</span>
                            </HomeWageTooltip>
                            <HomeWageTooltip location="top" member-visible>
                              <template #activator="{ props: cbTip }">
                                <input
                                  v-bind="cbTip"
                                  type="checkbox"
                                  class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                                  :checked="getInsuranceFixedByPairKey(pair.key)"
                                  @change="toggleInsuranceFixed(pair.key, ($event.target as HTMLInputElement).checked)"
                                />
                              </template>
                              <span style="white-space: pre-line">{{ HOME_WAGE_INSURANCE_FIX_CHECKBOX_TOOLTIP }}</span>
                            </HomeWageTooltip>
                            <input
                              :id="`payroll-ins-${pair.key}`"
                              type="text"
                              class="wage-payroll-grid__input"
                              :class="{ 'wage-payroll-grid__input--readonly': getInsuranceFixedByPairKey(pair.key) }"
                              :value="formatSharedInsuranceField(pair)"
                              :readonly="isWageReadOnly || getInsuranceFixedByPairKey(pair.key)"
                              inputmode="numeric"
                              @click="onInsuranceSharedInputClick(pair)"
                              @focus="onInsuranceSharedInputFocus(pair, $event)"
                              @input="onSharedInsuranceInput(pair, $event)"
                              @blur="onSharedInsuranceBlur(pair)"
                            />
                          </div>
                          <div
                            v-for="side in employmentInsuranceSides"
                            :key="`ins-go-${side.key}`"
                            class="wage-insurance-single__row"
                          >
                            <HomeWageTooltip location="top">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="wage-insurance-single__name wage-insurance-single__name--tooltip wage-insurance-single__name-with-chip">
                                  <span>{{ employmentInsurancePair.label }}</span>
                                  <span
                                    class="wage-insurance-single__role-chip"
                                    :class="side.key === 'labor' ? 'wage-insurance-single__role-chip--labor' : 'wage-insurance-single__role-chip--corp'"
                                  >{{ side.chipLabel }}</span>
                                </span>
                              </template>
                              <span style="white-space: pre-line">{{ getEmploymentInsuranceTooltip(side.key) }}</span>
                            </HomeWageTooltip>
                            <HomeWageTooltip location="top" member-visible>
                              <template #activator="{ props: cbTip }">
                                <input
                                  v-bind="cbTip"
                                  type="checkbox"
                                  class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                                  :checked="getEmploymentInsuranceFixed(side.key)"
                                  @change="toggleEmploymentInsuranceFixed(side.key, ($event.target as HTMLInputElement).checked)"
                                />
                              </template>
                              <span style="white-space: pre-line">{{ HOME_WAGE_INSURANCE_FIX_CHECKBOX_TOOLTIP }}</span>
                            </HomeWageTooltip>
                            <input
                              :id="`payroll-ins-go-${side.key}`"
                              type="text"
                              class="wage-payroll-grid__input"
                              :class="{ 'wage-payroll-grid__input--readonly': getEmploymentInsuranceFixed(side.key) }"
                              :value="formatEmploymentInsuranceField(side.key)"
                              :readonly="isWageReadOnly || getEmploymentInsuranceFixed(side.key)"
                              inputmode="numeric"
                              @click="onEmploymentInsuranceInputClick(side.key)"
                              @focus="onEmploymentInsuranceInputFocus(side.key, $event)"
                              @input="onEmploymentInsuranceInput(side.key, $event)"
                              @blur="onEmploymentInsuranceBlur(side.key)"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="wage-detail-card__footer">
                        <div class="wage-detail-card__footer-spacer" aria-hidden="true" />
                      </div>
                    </div>
                  </section>

                  <section class="wage-detail-card wage-detail-card--laborDeduct">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">{{ HOME_WAGE_LABOR_DEDUCT_CARD.title }}</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content">
                        <div class="wage-insurance-single wage-insurance-single--labor-deduct">
                          <HomeWageDeductionFixRow
                            v-for="field in HOME_WAGE_LABOR_FIX_DEDUCT_FIELDS"
                            :key="field.key"
                            :label="field.label"
                            :tooltip="getFieldTooltip(field.key)"
                            :fixed="isDeductionFieldFixed(field.key as HomeWageDeductionFixFieldKey)"
                            :display-value="formatPayrollMoneyField(field.key)"
                            :input-id="`payroll-${field.key}`"
                            :disabled="isWageReadOnly"
                            :fix-checkbox-tooltip="getDeductionFixCheckboxTooltip(field.key as HomeWageDeductionFixFieldKey)"
                            @toggle-fixed="toggleDeductionFixed(field.key as HomeWageDeductionFixFieldKey, $event)"
                            @input-click="onDeductionFixInputClick(field.key as HomeWageDeductionFixFieldKey)"
                            @input-focus="onDeductionFixInputFocus(field.key as HomeWageDeductionFixFieldKey, $event)"
                            @input="onPayrollMoneyInput(field.key, $event)"
                            @blur="onPayrollMoneyBlur(field.key)"
                          />
                          <div
                            v-for="field in HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELDS"
                            :key="field.key"
                            class="wage-insurance-single__row wage-insurance-single__row--manual-deduct"
                          >
                            <HomeWageTooltip v-if="getFieldTooltip(field.key)" location="top">
                              <template #activator="{ props: tooltipProps }">
                                <span
                                  v-bind="tooltipProps"
                                  class="wage-insurance-single__name wage-insurance-single__name--tooltip"
                                >{{ field.label }}</span>
                              </template>
                              <span style="white-space: pre-line">{{ getFieldTooltip(field.key) }}</span>
                            </HomeWageTooltip>
                            <span v-else class="wage-insurance-single__name">{{ field.label }}</span>
                            <span class="wage-insurance-single__fix-cb-spacer" aria-hidden="true"></span>
                            <input
                              :id="`payroll-${field.key}`"
                              type="text"
                              class="wage-payroll-grid__input"
                              :value="formatPayrollMoneyField(field.key)"
                              :readonly="isWageReadOnly"
                              inputmode="numeric"
                              @input="onPayrollMoneyInput(field.key, $event)"
                              @blur="onPayrollMoneyBlur(field.key)"
                              @keydown.enter="($event.target as HTMLInputElement).blur()"
                            />
                          </div>
                        </div>
                      </div>
                      <div v-if="HOME_WAGE_LABOR_TOTAL_FIELD" class="wage-detail-card__footer">
                        <div class="wage-card-total wage-card-total--grand">
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label
                                v-bind="tooltipProps"
                                class="wage-card-total__label wage-card-total__label--tooltip"
                              >{{ HOME_WAGE_LABOR_TOTAL_FIELD.label }}</label>
                            </template>
                            <span style="white-space: pre-line">{{ getFieldTooltip(HOME_WAGE_LABOR_TOTAL_FIELD.key) }}</span>
                          </HomeWageTooltip>
                          <span class="wage-card-total__value">{{ formatPayrollMoneyField(HOME_WAGE_LABOR_TOTAL_FIELD.key) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section class="wage-detail-card wage-detail-card--corpDeduct">
                    <header class="wage-detail-card__head">
                      <div class="wage-detail-card__head-text">
                        <h3 class="wage-detail-card__title">{{ HOME_WAGE_CORP_DEDUCT_CARD.title }}</h3>
                      </div>
                    </header>
                    <div class="wage-detail-card__body wage-detail-card__body--stretch">
                      <div class="wage-detail-card__content">
                        <div class="wage-insurance-single wage-insurance-single--corp-deduct">
                          <HomeWageDeductionFixRow
                            v-for="field in corpOtherFieldsWithoutRetirement"
                            :key="field.key"
                            :label="field.label"
                            :tooltip="getFieldTooltip(field.key)"
                            :fixed="isDeductionFieldFixed(field.key as HomeWageDeductionFixFieldKey)"
                            :display-value="formatPayrollMoneyField(field.key)"
                            :input-id="`payroll-${field.key}`"
                            :disabled="isWageReadOnly"
                            :fix-checkbox-tooltip="getDeductionFixCheckboxTooltip(field.key as HomeWageDeductionFixFieldKey)"
                            @toggle-fixed="toggleDeductionFixed(field.key as HomeWageDeductionFixFieldKey, $event)"
                            @input-click="onDeductionFixInputClick(field.key as HomeWageDeductionFixFieldKey)"
                            @input-focus="onDeductionFixInputFocus(field.key as HomeWageDeductionFixFieldKey, $event)"
                            @input="onPayrollMoneyInput(field.key, $event)"
                            @blur="onPayrollMoneyBlur(field.key)"
                          />
                          <div v-if="HOME_WAGE_CORP_RETIREMENT_FIELD" class="wage-insurance-single__row">
                            <HomeWageTooltip location="top">
                              <template #activator="{ props: tooltipProps }">
                                <span
                                  v-bind="tooltipProps"
                                  class="wage-insurance-single__name wage-insurance-single__name--tooltip"
                                >{{ HOME_WAGE_CORP_RETIREMENT_FIELD.label }}</span>
                              </template>
                              <span style="white-space: pre-line">{{ getFieldTooltip('c_out') }}</span>
                            </HomeWageTooltip>
                            <HomeWageTooltip location="top" member-visible>
                              <template #activator="{ props: cbTip }">
                                <input
                                  v-bind="cbTip"
                                  type="checkbox"
                                  class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                                  :checked="applyRetirement"
                                  :disabled="isWageReadOnly"
                                  @change="toggleApplyRetirement(($event.target as HTMLInputElement).checked)"
                                />
                              </template>
                              <span style="white-space: pre-line">{{ retirementApplyCheckboxTooltip }}</span>
                            </HomeWageTooltip>
                            <input
                              id="payroll-c-out"
                              type="text"
                              class="wage-payroll-grid__input"
                              :class="{
                                'wage-payroll-grid__input--retirement-blocked': showRetirementBlockedInInput,
                                'wage-payroll-grid__input--readonly': isRetirementCaregiverFixedInput,
                              }"
                              :value="formatRetirementInputDisplay"
                              :readonly="showRetirementBlockedInInput || isWageReadOnly || isRetirementCaregiverFixedInput"
                              inputmode="numeric"
                              @click="onRetirementInputClick"
                              @focus="onRetirementInputFocus"
                              @input="onRetirementMoneyInput"
                              @blur="onPayrollMoneyBlur('c_out')"
                            />
                          </div>
                        </div>
                        <div v-if="!isWageReadOnly" class="wage-representative-row">
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label v-bind="tooltipProps" class="wage-representative-row__label">
                                <input
                                  type="checkbox"
                                  class="wage-representative-row__cb"
                                  :checked="isRepresentative"
                                  @change="toggleRepresentative(($event.target as HTMLInputElement).checked)"
                                />
                                <span>대표자</span>
                              </label>
                            </template>
                            <span style="white-space: pre-line">대표자: 고용보험·산재보험·퇴직금·실업급여 미적용</span>
                          </HomeWageTooltip>
                        </div>
                        <div v-else-if="isRepresentative" class="wage-representative-row wage-representative-row--readonly">
                          <span class="wage-representative-row__badge">대표자</span>
                        </div>
                      </div>
                      <div v-if="HOME_WAGE_CORP_TOTAL_FIELD" class="wage-detail-card__footer">
                        <div class="wage-card-total wage-card-total--grand">
                          <HomeWageTooltip location="top">
                            <template #activator="{ props: tooltipProps }">
                              <label
                                v-bind="tooltipProps"
                                class="wage-card-total__label wage-card-total__label--tooltip"
                              >{{ HOME_WAGE_CORP_TOTAL_FIELD.label }}</label>
                            </template>
                            <span style="white-space: pre-line">{{ getFieldTooltip(HOME_WAGE_CORP_TOTAL_FIELD.key) }}</span>
                          </HomeWageTooltip>
                          <span class="wage-card-total__value">{{ formatPayrollMoneyField(HOME_WAGE_CORP_TOTAL_FIELD.key) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              <!-- 결과 요약 바 -->
              <div class="wage-reference-bar-wrap">
                <p v-if="isAdminPreviewView" class="wage-reference-bar-hint">체크 시 수동입력 · 해제 시 자동계산</p>
                <section class="wage-reference-bar">
                  <div class="wage-reference-bar__items">
                  <div
                    v-for="field in referenceBarFields"
                    :key="field.key"
                    class="wage-reference-bar__item"
                  >
                    <HomeWageTooltip location="top">
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps" class="wage-reference-bar__label wage-reference-bar__label--tooltip">{{ field.label }}</span>
                      </template>
                      <span style="white-space: pre-line">{{ getFieldTooltip(field.key) }}</span>
                    </HomeWageTooltip>
                    <span class="wage-reference-bar__value">{{ formatReferenceMoneyField(field.key) }}원</span>
                  </div>
                </div>
              </section>
              </div>

              <!-- 수급자별 시급·수가시간 (요양보호사만, 맨 아래) -->
              <section
                v-if="isCaregiverEmployee(selectedEmployee)"
                class="wage-detail-card wage-detail-card--claim"
              >
                <header class="wage-detail-card__head wage-detail-card__head--claim">
                  <div class="wage-detail-card__head-leading">
                    <h3 class="wage-detail-card__title">수급자별 시급 및 수가시간</h3>
                    <v-btn
                      variant="flat"
                      class="btn-refresh wage-detail-claim-btn"
                      size="small"
                      :disabled="!hasAnyClaimModalData"
                      @click="openClaimDetailModal"
                    >청구내역</v-btn>
                  </div>
                  <div class="wage-detail-card__head-actions">
                    <!-- 가족요양 칩 -->
                    <HomeWageTooltip v-if="hasFamilyCareInClaim" location="top" max-width="360">
                      <template #activator="{ props: tooltipProps }">
                        <span
                          v-bind="tooltipProps"
                          class="wage-family-care-chip"
                          :class="{ 'wage-family-care-chip--all': isAllFamilyCareInClaim }"
                        >
                          <v-icon size="14" :color="FAMILY_CARE_COLOR">{{ FAMILY_CARE_ICON }}</v-icon>
                          <span class="wage-family-care-chip__text">
                            {{ isAllFamilyCareInClaim ? '가족요양 전용' : `가족 ${familyCarePatientCount}건` }}
                          </span>
                        </span>
                      </template>
                      <span style="white-space: pre-line">{{ familyCareTooltip }}</span>
                    </HomeWageTooltip>
                    <span
                      v-if="selectedEmpWorkMeta.showWorkDays || selectedEmpWorkMeta.showWorkHours"
                      class="wage-detail-work-meta"
                    >
                      <span v-if="selectedEmpWorkMeta.showWorkDays">
                        근로 {{ selectedEmpWorkMeta.workDays }}일
                      </span>
                      <span v-if="selectedEmpWorkMeta.showWorkHours">
                        {{ selectedEmpWorkMeta.workHoursLabel }}
                      </span>
                    </span>
                    <v-btn
                      v-if="displayClaimSummaryRows.length > 0 && !isWageReadOnly"
                      variant="flat"
                      class="btn-edit wage-detail-claim-btn"
                      size="small"
                      @click="showBulkHourlyModal = true"
                    >일괄시급</v-btn>
                  </div>
                </header>
                <div class="wage-detail-card__body">
                  <div class="claim-summary-panel">
                    <div
                      v-if="displayClaimSummaryRows.length > 0"
                      class="claim-summary-totals-bar"
                    >
                      <div class="claim-summary-totals-bar__item">
                        <span class="claim-summary-totals-bar__label">근로일</span>
                        <span class="claim-summary-totals-bar__value">{{ workCalendarSummary.workDayCount }}일</span>
                      </div>
                      <div class="claim-summary-totals-bar__item">
                        <span class="claim-summary-totals-bar__label">근무시간</span>
                        <span class="claim-summary-totals-bar__value">
                          {{ formatWorkCalendarDuration(totalSugaHours) }}
                        </span>
                      </div>
                      <div class="claim-summary-totals-bar__item">
                        <span class="claim-summary-totals-bar__label">총 서비스횟수</span>
                        <span class="claim-summary-totals-bar__value">{{ displayClaimTotalServiceCount }}회</span>
                      </div>
                      <div
                        v-if="displaySevereServiceCount > 0"
                        class="claim-summary-totals-bar__item claim-summary-totals-bar__item--severe"
                      >
                        <span class="claim-summary-totals-bar__label">중증수급자가산</span>
                        <span class="claim-summary-totals-bar__value">{{ displaySevereServiceCount }}회</span>
                      </div>
                      <div class="claim-summary-totals-bar__item">
                        <span class="claim-summary-totals-bar__label">총 수가시간</span>
                        <span class="claim-summary-totals-bar__value claim-summary-totals-bar__value--emphasis">
                          {{ formatSugaHours(totalSugaHours) || '0분' }}
                        </span>
                      </div>
                    </div>
                    <table class="claim-summary-table">
                      <colgroup>
                        <col class="claim-col-no" />
                        <col class="claim-col-recipient" />
                        <col class="claim-col-fee" />
                        <col class="claim-col-fee-amount" />
                        <col class="claim-col-count" />
                        <col class="claim-col-hours" />
                        <col class="claim-col-wage" />
                        <col class="claim-col-total" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th class="claim-col-no">No</th>
                          <th>
                            <HomeWageTooltip location="top" text="해당 수급자">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">수급자</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="청구 수가코드 명칭">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">수가명</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="1회 서비스 수가 금액">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">1회수가</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="월 서비스 제공 횟수">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">서비스횟수</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="실제 서비스시간 평균 (자동 계산)">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">수가시간</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="해당 수급자에게 적용되는 시급">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">시급</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                          <th>
                            <HomeWageTooltip location="top" text="서비스횟수 × 수가시간 × 시급">
                              <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps" class="claim-th--tooltip">합계금액</span>
                              </template>
                            </HomeWageTooltip>
                          </th>
                        </tr>
                      </thead>
                    <tbody>
                      <tr v-if="displayClaimSummaryRowsForTable.length === 0">
                        <td colspan="8" class="empty-row">청구내역이 없습니다.</td>
                      </tr>
                      <tr
                        v-for="(row, rowIdx) in displayClaimSummaryRowsForTable"
                        :key="row.groupKey"
                        :class="{ 'row-holiday': row.isHolidayFeeSurcharge }"
                      >
                        <td class="claim-cell-num claim-cell-no">{{ rowIdx + 1 }}</td>
                        <td class="claim-cell-name">{{ row.recipientName || '-' }}</td>
                        <td class="claim-cell-fee" :title="row.feeName || row.feeCode || ''">
                          <span class="claim-cell-fee__text">{{ row.feeName || row.feeCode || '-' }}</span>
                          <span v-if="row.isHolidayFeeSurcharge" class="claim-holiday-chip">휴일</span>
                        </td>
                        <td class="claim-cell-num claim-cell-num--money">{{ formatFee(row.feeAmountPerVisit) }}</td>
                        <td class="claim-cell-num">{{ row.serviceCount }}회</td>
                        <td class="claim-cell-num">{{ formatSugaHours(row.sugaHoursPerVisit) }}</td>
                        <td class="claim-cell-input">
                          <input
                            type="text"
                            class="claim-table-input"
                            :class="{ 'wage-input--empty': isRecipientHourlyEmpty(row) }"
                            :value="getRecipientHourlyDisplay(row)"
                            placeholder="시급"
                            @input="onHourlyWageInput(row.groupKey, $event)"
                            @blur="onHourlyWageBlur(row.groupKey)"
                            @keydown.enter="($event.target as HTMLInputElement).blur()"
                          />
                        </td>
                        <td class="claim-cell-total">
                          <span class="claim-cell-total__amount">{{ formatFee(row.totalAmount) }}</span>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot v-if="displayClaimSummaryRowsForTable.length > 0">
                      <tr class="claim-summary-total">
                        <td colspan="7">합계</td>
                        <td class="claim-cell-total">
                          <span class="claim-cell-total__amount">{{ formatFee(displayClaimGrandTotalForTable) }}</span>
                        </td>
                      </tr>
                    </tfoot>
                    </table>
                    <div
                      v-if="severeSummaryRowsForTable.length > 0"
                      class="claim-severe-summary-panel"
                    >
                      <div class="claim-severe-summary-panel__head">
                        <span class="claim-severe-summary-panel__title">중증수급자가산 (별도 확인)</span>
                        <span class="claim-severe-summary-panel__count">{{ displaySevereServiceCount }}회</span>
                      </div>
                      <table class="claim-summary-table claim-summary-table--severe">
                        <thead>
                          <tr>
                            <th>수급자</th>
                            <th>수가명</th>
                            <th>서비스횟수</th>
                            <th>수가(1회)</th>
                            <th>합계금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="row in severeSummaryRowsForTable"
                            :key="row.groupKey"
                            class="row-severe"
                          >
                            <td class="claim-cell-name">{{ row.recipientName || '-' }}</td>
                            <td class="claim-cell-fee">{{ row.feeName || row.feeCode || '-' }}</td>
                            <td class="claim-cell-num">{{ row.serviceCount }}회</td>
                            <td class="claim-cell-input">
                              <input
                                type="text"
                                class="claim-table-input"
                                :value="severeFeeDisplay[row.groupKey] ?? (row.feeAmountPerVisit > 0 ? formatWageNumber(row.feeAmountPerVisit) : '')"
                                placeholder="0"
                                @input="onSevereFeeInput(row.groupKey, $event)"
                                @blur="onSevereFeeBlur(row.groupKey, row.feeAmountPerVisit)"
                                @keydown.enter="($event.target as HTMLInputElement).blur()"
                              />
                            </td>
                            <td class="claim-cell-total">
                              <span class="claim-cell-total__amount">{{ formatFee(row.totalAmount) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 슈퍼바이저(최고관리자) 전용 — 목록 빨간 표시(금액 불일치) 사유 -->
              <section
                v-if="isSuperAdmin && selectedEmployeePayMismatchReport?.mismatch"
                class="home-wage-pay-mismatch-panel"
                aria-label="재저장 필요 사유"
              >
                <header class="home-wage-pay-mismatch-panel__head">
                  <v-icon size="18" color="#ff1744">mdi-alert-circle-outline</v-icon>
                  <h3 class="home-wage-pay-mismatch-panel__title">재저장 필요 — 금액 불일치</h3>
                  <span class="home-wage-pay-mismatch-panel__badge">슈퍼바이저 전용</span>
                </header>
                <p class="home-wage-pay-mismatch-panel__desc">
                  센터장·관리자 시점의 <strong>기본급 합계</strong>, <strong>수당 합계</strong>, <strong>지급금액</strong>이 모두 같아야 합니다. ±100원 이내는 일치로 봅니다.
                </p>
                <div class="home-wage-pay-mismatch-panel__amounts">
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">센터장 기본급 합계</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.director.basePayTotal) }}원
                    </span>
                    <span class="home-wage-pay-mismatch-panel__amount-hint">DB · slip 표시</span>
                  </div>
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">관리자 기본급 합계</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.admin?.basePayTotal ?? 0) }}원
                    </span>
                    <span class="home-wage-pay-mismatch-panel__amount-hint">DB · 관리자 표시</span>
                  </div>
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">센터장 수당 합계</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.director.allowanceTotal) }}원
                    </span>
                  </div>
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">관리자 수당 합계</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.admin?.allowanceTotal ?? 0) }}원
                    </span>
                  </div>
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">센터장 지급금액</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.director.grossPay) }}원
                    </span>
                  </div>
                  <div class="home-wage-pay-mismatch-panel__amount-item">
                    <span class="home-wage-pay-mismatch-panel__amount-label">관리자 지급금액</span>
                    <span class="home-wage-pay-mismatch-panel__amount-value">
                      {{ formatWageNumber(selectedEmployeePayMismatchReport.admin?.grossPay ?? 0) }}원
                    </span>
                  </div>
                </div>
                <ul class="home-wage-pay-mismatch-panel__diff-list">
                  <li
                    v-for="(pair, idx) in selectedEmployeePayMismatchReport.pairs.filter((p) => !p.matched)"
                    :key="`mismatch-pair-${idx}`"
                    class="home-wage-pay-mismatch-panel__diff-item"
                  >
                    <strong>{{ pair.labelA }}</strong>
                    <span class="home-wage-pay-mismatch-panel__diff-arrow">↔</span>
                    <strong>{{ pair.labelB }}</strong>
                    <span class="home-wage-pay-mismatch-panel__diff-gap">
                      차이 {{ formatWageNumber(pair.diff) }}원
                    </span>
                  </li>
                </ul>
                <div class="home-wage-pay-mismatch-panel__actions">
                  <p class="home-wage-pay-mismatch-panel__action">
                    같은 DB 저장값인데 센터장·관리자 표시 지급금액이 다릅니다.
                    표시 규칙을 확인한 뒤, 필요하면 청구 기준으로 재산출 저장하세요.
                  </p>
                  <v-btn
                    variant="flat"
                    class="btn-save home-wage-pay-mismatch-panel__resave-btn"
                    size="small"
                    :disabled="isWageReadOnly || resavingPayMismatch || savingAllRecipients || importingPrevMonth"
                    @click="resavePayMismatchFromAdmin"
                  >
                    <v-icon v-if="resavingPayMismatch" size="14" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>청구 기준으로 재저장</template>
                  </v-btn>
                </div>
              </section>
            </template>
          </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 청구내역 상세 모달 (v-dialog → body teleport, 스타일은 _home-wage-page.scss 전역) -->
    <v-dialog
      v-model="showClaimDetailModal"
      max-width="1100"
      scrollable
      content-class="home-claim-detail-dialog-shell"
    >
      <div class="home-claim-detail-modal" role="dialog" aria-labelledby="home-claim-detail-modal-title">
        <header class="home-claim-detail-modal__header">
          <div class="home-claim-detail-modal__head-main">
            <div class="home-claim-detail-modal__icon-wrap">
              <v-icon size="22" color="#90caf9">mdi-calendar-month</v-icon>
            </div>
            <div class="home-claim-detail-modal__titles">
              <h3 id="home-claim-detail-modal-title" class="home-claim-detail-modal__title">청구내역</h3>
              <p v-if="selectedEmployee" class="home-claim-detail-modal__subtitle">
                <span class="home-claim-detail-modal__emp-name">{{ getEmpName(selectedEmployee) }}</span>
                <span class="home-claim-detail-modal__ym">{{ workCalendarMonthLabel }}</span>
                <span
                  v-if="caregiverClaimRowsSevere.length > 0"
                  class="home-claim-detail-modal__count home-claim-detail-modal__count--severe"
                >
                  중증가산 {{ caregiverClaimRowsSevere.length }}건
                </span>
                <span
                  v-if="canViewUnjoinedClaimTab && claimUnjoinedTotalCount > 0"
                  class="home-claim-detail-modal__count home-claim-detail-modal__count--warn"
                >
                  미조인 {{ claimUnjoinedTotalCount }}건
                </span>
              </p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            class="home-claim-detail-modal__close"
            aria-label="닫기"
            @click="showClaimDetailModal = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </header>

        <div class="home-claim-detail-modal__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="home-claim-detail-modal__tab"
            :class="{ 'home-claim-detail-modal__tab--active': claimModalTab === 'list' }"
            :aria-selected="claimModalTab === 'list'"
            @click="claimModalTab = 'list'"
          >청구내역</button>
          <button
            type="button"
            role="tab"
            class="home-claim-detail-modal__tab"
            :class="{ 'home-claim-detail-modal__tab--active': claimModalTab === 'calendar' }"
            :aria-selected="claimModalTab === 'calendar'"
            @click="claimModalTab = 'calendar'"
          >근무달력</button>
          <button
            v-if="caregiverClaimRowsSevere.length > 0"
            type="button"
            role="tab"
            class="home-claim-detail-modal__tab home-claim-detail-modal__tab--severe"
            :class="{ 'home-claim-detail-modal__tab--active': claimModalTab === 'severe' }"
            :aria-selected="claimModalTab === 'severe'"
            @click="claimModalTab = 'severe'"
          >
            중증수급자가산
            <span class="home-claim-detail-modal__tab-badge">{{ caregiverClaimRowsSevere.length }}</span>
          </button>
          <button
            v-if="canViewUnjoinedClaimTab && claimUnjoinedTotalCount > 0"
            type="button"
            role="tab"
            class="home-claim-detail-modal__tab home-claim-detail-modal__tab--unjoined"
            :class="{ 'home-claim-detail-modal__tab--active': claimModalTab === 'unjoined' }"
            :aria-selected="claimModalTab === 'unjoined'"
            @click="claimModalTab = 'unjoined'"
          >
            미조인
            <span class="home-claim-detail-modal__tab-badge">{{ claimUnjoinedTotalCount }}</span>
          </button>
        </div>

        <div class="home-claim-detail-modal__body">
          <LoadingOverlay :show="claimLoading" text="청구내역 불러오는 중..." />

          <!-- 탭: 청구내역 목록 -->
          <div v-if="!claimLoading && claimModalTab === 'list'" class="home-claim-detail-modal__table-wrap">
            <!-- 청구내역 요약 정보 -->
            <div class="claim-modal-summary-bar">
              <div class="claim-modal-summary-bar__item">
                <span class="claim-modal-summary-bar__label">근무일수</span>
                <span class="claim-modal-summary-bar__value">{{ workCalendarSummary.workDayCount }}일</span>
              </div>
              <div class="claim-modal-summary-bar__item">
                <span class="claim-modal-summary-bar__label">서비스횟수</span>
                <span class="claim-modal-summary-bar__value">{{ workCalendarSummary.serviceCount }}건</span>
              </div>
              <div
                v-if="caregiverClaimRowsSevere.length > 0"
                class="claim-modal-summary-bar__item claim-modal-summary-bar__item--severe"
              >
                <span class="claim-modal-summary-bar__label">중증가산</span>
                <span class="claim-modal-summary-bar__value">{{ caregiverClaimRowsSevere.length }}건</span>
              </div>
              <div class="claim-modal-summary-bar__item">
                <span class="claim-modal-summary-bar__label">총수가금액</span>
                <span class="claim-modal-summary-bar__value claim-modal-summary-bar__value--bold">{{ formatWageNumber(displayClaimGrandTotal) }}원</span>
              </div>
              <div
                v-if="workCalendarPremiumSummary.overtimeHours > 0"
                class="claim-modal-summary-bar__item claim-modal-summary-bar__item--premium claim-modal-summary-bar__item--overtime"
              >
                <span class="claim-modal-summary-bar__label">연장</span>
                <span class="claim-modal-summary-bar__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.overtimeHours) }}h</span>
              </div>
              <div
                v-if="workCalendarPremiumSummary.nightHours > 0"
                class="claim-modal-summary-bar__item claim-modal-summary-bar__item--premium claim-modal-summary-bar__item--night"
              >
                <span class="claim-modal-summary-bar__label">야간</span>
                <span class="claim-modal-summary-bar__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.nightHours) }}h</span>
              </div>
              <div
                v-if="workCalendarPremiumSummary.holidayHours > 0"
                class="claim-modal-summary-bar__item claim-modal-summary-bar__item--premium claim-modal-summary-bar__item--holiday"
              >
                <span class="claim-modal-summary-bar__label">휴일</span>
                <span class="claim-modal-summary-bar__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.holidayHours) }}h</span>
              </div>
              <div
                v-if="!isDirectorPayrollView"
                class="claim-modal-summary-bar__item claim-modal-summary-bar__item--lunch"
              >
                <span class="claim-modal-summary-bar__label">{{ HOME_WAGE_NONTAX_TERM.lunchEligible }}</span>
                <span class="claim-modal-summary-bar__value">{{ workCalendarSummary.lunchEligibleCount }}건</span>
              </div>
            </div>
            <p class="claim-modal-premium-note">
              연·야·휴는 B1/B2 plan 기준 · 연장=일 {{ HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS }}h 초과(동시 케어 겹침 병합) · 야간=22~06 · 휴일=수가명 가산 또는 5/1 실근무
              · 근무달력 탭에서 날짜별 확인
            </p>
            <p class="claim-modal-status-note">
              같은 방문의 청구의뢰·지급은 포털 처리상태입니다. 자동가져오기가 두 번 된 것이 아니며, 임금·수가 합계에는 지급만 반영합니다.
            </p>
            <table class="claim-detail-modal-table">
              <thead>
                <tr>
                  <th class="col-date sortable" @click="toggleClaimModalSort('date')">
                    서비스일자 <v-icon size="12">{{ getClaimModalSortIcon('date') }}</v-icon>
                  </th>
                  <th class="col-recipient sortable" @click="toggleClaimModalSort('recipient')">
                    수급자 <v-icon size="12">{{ getClaimModalSortIcon('recipient') }}</v-icon>
                  </th>
                  <th class="col-time-range sortable" @click="toggleClaimModalSort('planStart')">
                    시간간격 <v-icon size="12">{{ getClaimModalSortIcon('planStart') }}</v-icon>
                  </th>
                  <th class="col-duration sortable" @click="toggleClaimModalSort('duration')">
                    서비스시간 <v-icon size="12">{{ getClaimModalSortIcon('duration') }}</v-icon>
                  </th>
                  <th class="col-fee-name sortable" @click="toggleClaimModalSort('feeName')">
                    수가명 <v-icon size="12">{{ getClaimModalSortIcon('feeName') }}</v-icon>
                  </th>
                  <th class="col-amount sortable" @click="toggleClaimModalSort('feeAmount')">
                    수가금액 <v-icon size="12">{{ getClaimModalSortIcon('feeAmount') }}</v-icon>
                  </th>
                  <th class="col-premium sortable" @click="toggleClaimModalSort('premium')">
                    가산 <v-icon size="12">{{ getClaimModalSortIcon('premium') }}</v-icon>
                  </th>
                  <th v-if="!isDirectorPayrollView" class="col-lunch sortable" @click="toggleClaimModalSort('lunch')">
                    중식 <v-icon size="12">{{ getClaimModalSortIcon('lunch') }}</v-icon>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="sortedClaimModalListRows.length === 0">
                  <td :colspan="isDirectorPayrollView ? 7 : 8" class="empty-row">청구내역이 없습니다.</td>
                </tr>
                <tr
                  v-for="(row, idx) in sortedClaimModalListRows"
                  :key="row.clcd_idx ? `modal-d-${row.clcd_idx}` : `modal-c-${row.clc_idx}-${idx}`"
                >
                  <td class="col-date">{{ formatClaimDate(row.date) }}</td>
                  <td class="col-recipient">{{ formatRecipientWithCert(row.recipient_name, row.cert_number) }}</td>
                  <td class="col-time-range">{{ formatPlanTimeRange(row.plan_start_time, row.plan_end_time) }}</td>
                  <td class="col-duration">{{ formatServiceDuration(row.plan_start_time, row.plan_end_time) }}</td>
                  <td class="col-fee-name">{{ formatFeeNameOnly(row.fee_name) }}</td>
                  <td class="col-amount">{{ formatFee(row.fee_amount) }}</td>
                  <td class="col-premium">
                    <template v-if="getRowPremiumInfo(row).isVisitFee">
                      <span
                        v-if="getRowPremiumInfo(row).isOvertimeRow"
                        class="claim-premium-chip claim-premium-chip--overtime"
                        title="일 8시간 초과분 plan 시간대(마지막 구간) → 연장수당 대상"
                      >연장</span>
                      <span
                        v-if="getRowPremiumInfo(row).nightHours > 0"
                        class="claim-premium-chip claim-premium-chip--night"
                        :title="`야간 ${formatClaimPremiumHours(getRowPremiumInfo(row).nightHours)}`"
                      >야간 {{ formatClaimPremiumHours(getRowPremiumInfo(row).nightHours) }}</span>
                      <span
                        v-if="getRowPremiumInfo(row).isHolidayWork"
                        class="claim-premium-chip claim-premium-chip--holiday"
                        title="수가명 가산 또는 5/1 실근무 → 휴일수당 대상"
                      >휴일</span>
                    </template>
                    <span v-else class="claim-premium-chip claim-premium-chip--none">—</span>
                  </td>
                  <td v-if="!isDirectorPayrollView" class="col-lunch">
                    <span v-if="isLunchIncludedRow(row)" class="claim-lunch-chip">중식</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 탭: 근무달력 -->
          <div v-else-if="!claimLoading && claimModalTab === 'calendar'" class="home-claim-detail-modal__calendar">
            <template v-if="caregiverClaimRowsNonSevere.length === 0">
              <p class="home-claim-detail-modal__calendar-empty">청구내역이 없어 근무달력을 표시할 수 없습니다.</p>
            </template>
            <template v-else>
              <p class="claim-modal-premium-note">
                연·야·휴는 B1/B2 plan 기준 · 연장=일 {{ HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS }}h 초과(동시 케어 겹침 병합) · 야간=22~06 · 휴일=수가명 가산 또는 5/1 실근무
              </p>
              <div class="work-calendar-summary">
                <div class="work-calendar-summary__item">
                  <span class="work-calendar-summary__label">근무일수</span>
                  <span class="work-calendar-summary__value">{{ workCalendarSummary.workDayCount }}일</span>
                </div>
                <div class="work-calendar-summary__item">
                  <span class="work-calendar-summary__label">총 근무시간</span>
                  <span class="work-calendar-summary__value">{{ formatWorkCalendarDuration(workCalendarSummary.totalHours) }}</span>
                </div>
                <div class="work-calendar-summary__item">
                  <span class="work-calendar-summary__label">총 수가시간</span>
                  <span class="work-calendar-summary__value">{{ formatSugaHours(totalSugaHours) || '0분' }}</span>
                </div>
                <div class="work-calendar-summary__item">
                  <span class="work-calendar-summary__label">서비스 횟수</span>
                  <span class="work-calendar-summary__value">{{ workCalendarSummary.serviceCount }}회</span>
                </div>
                <div
                  v-if="workCalendarPremiumSummary.overtimeHours > 0"
                  class="work-calendar-summary__item work-calendar-summary__item--premium work-calendar-summary__item--overtime"
                >
                  <span class="work-calendar-summary__label">연장</span>
                  <span class="work-calendar-summary__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.overtimeHours) }}h</span>
                </div>
                <div
                  v-if="workCalendarPremiumSummary.nightHours > 0"
                  class="work-calendar-summary__item work-calendar-summary__item--premium work-calendar-summary__item--night"
                >
                  <span class="work-calendar-summary__label">야간</span>
                  <span class="work-calendar-summary__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.nightHours) }}h</span>
                </div>
                <div
                  v-if="workCalendarPremiumSummary.holidayHours > 0"
                  class="work-calendar-summary__item work-calendar-summary__item--premium work-calendar-summary__item--holiday"
                >
                  <span class="work-calendar-summary__label">휴일</span>
                  <span class="work-calendar-summary__value">{{ formatHomeWagePremiumHoursDisplay(workCalendarPremiumSummary.holidayHours) }}h</span>
                </div>
                <div
                  v-if="!isDirectorPayrollView"
                  class="work-calendar-summary__item work-calendar-summary__item--lunch"
                >
                  <span class="work-calendar-summary__label">{{ HOME_WAGE_NONTAX_TERM.lunchEligible }}</span>
                  <span class="work-calendar-summary__value">{{ workCalendarSummary.lunchEligibleCount }}건</span>
                </div>
              </div>

              <div class="work-calendar-chip-legend">
                <span class="work-calendar-stock-chip work-calendar-stock-chip--overtime">연장</span>
                <span class="work-calendar-stock-chip work-calendar-stock-chip--night">야간</span>
                <span class="work-calendar-stock-chip work-calendar-stock-chip--holiday">휴일</span>
                <span
                  v-if="!isDirectorPayrollView"
                  class="work-calendar-stock-chip work-calendar-stock-chip--lunch"
                >중식</span>
              </div>

              <div class="work-calendar-grid-wrap">
                <div class="work-calendar-weekdays">
                  <span
                    v-for="(label, wIdx) in workCalendarWeekdayLabels"
                    :key="label"
                    class="work-calendar-weekday"
                    :class="{
                      'work-calendar-weekday--sun': wIdx === 0,
                      'work-calendar-weekday--sat': wIdx === 6,
                    }"
                  >{{ label }}</span>
                </div>
                <div class="work-calendar-weeks">
                  <div
                    v-for="(week, wIdx) in workCalendarWeeks"
                    :key="`week-${wIdx}`"
                    class="work-calendar-week"
                  >
                    <div
                      v-for="(cell, cIdx) in week"
                      :key="`${cell.dateKey || 'x'}-${wIdx}-${cIdx}`"
                      class="work-calendar-day"
                      :class="{
                        'work-calendar-day--outside': !cell.inMonth && !cell.isHoliday,
                        'work-calendar-day--holiday': cell.isHoliday,
                        'work-calendar-day--sun':
                          cell.inMonth && cell.isSunday && !cell.isHoliday,
                        'work-calendar-day--work':
                          cell.inMonth &&
                          !cell.isSunday &&
                          !cell.isHoliday &&
                          cell.events.length > 0,
                      }"
                    >
                      <div
                        class="work-calendar-day__head"
                        :class="{
                          'work-calendar-day__head--red': cell.isSunday || cell.isHoliday,
                        }"
                      >
                        <span class="work-calendar-day__num">{{ cell.dateLabel }}</span>
                        <em v-if="cell.holidayName" class="work-calendar-day__holiday-name">
                          {{ cell.holidayName }}
                        </em>
                      </div>
                      <div class="work-calendar-day__events">
                        <div
                          v-for="(ev, eIdx) in cell.events"
                          :key="`${cell.dateKey}-${eIdx}-${ev.sortKey}`"
                          class="work-calendar-event"
                          :title="`${ev.startTime} ${ev.recipientName}`"
                        >
                          <span class="work-calendar-event__time">{{ ev.startTime }}</span>
                          <span class="work-calendar-event__name">{{ ev.recipientName }}</span>
                        </div>
                      </div>
                      <div
                        v-if="cell.inMonth && (cell.overtimeHours > 0 || cell.nightHours > 0 || cell.holidayHours > 0 || (!isDirectorPayrollView && cell.isLunchDay))"
                        class="work-calendar-day__chip-bar"
                      >
                        <span
                          v-if="cell.overtimeHours > 0"
                          class="work-calendar-stock-chip work-calendar-stock-chip--overtime"
                          :title="`연장 ${cell.overtimeHours}h (일 8h 초과분)`"
                        >연장 {{ cell.overtimeHours }}</span>
                        <span
                          v-if="cell.nightHours > 0"
                          class="work-calendar-stock-chip work-calendar-stock-chip--night"
                          :title="`야간 ${cell.nightHours}h`"
                        >야간 {{ cell.nightHours }}</span>
                        <span
                          v-if="cell.holidayHours > 0"
                          class="work-calendar-stock-chip work-calendar-stock-chip--holiday"
                          :title="`휴일 ${cell.holidayHours}h`"
                        >휴일 {{ cell.holidayHours }}</span>
                        <span
                          v-if="!isDirectorPayrollView && cell.isLunchDay"
                          class="work-calendar-stock-chip work-calendar-stock-chip--lunch"
                          :title="HOME_WAGE_NONTAX_TERM.lunchEligible"
                        >중식</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="work-calendar-recipients">
                <h4 class="work-calendar-recipients__title">수급자별 서비스</h4>
                <div class="work-calendar-recipients__list">
                  <div
                    v-for="item in workCalendarRecipientSummaries"
                    :key="item.recipientName"
                    class="work-calendar-recipient-card"
                  >
                    <div class="work-calendar-recipient-card__top">
                      <span class="work-calendar-recipient-card__name">{{ item.recipientName }}</span>
                      <span v-if="item.feeName" class="work-calendar-recipient-card__fee-name">{{ item.feeName }}</span>
                    </div>
                    <div class="work-calendar-recipient-card__stats">
                      <span class="work-calendar-recipient-card__stat">{{ item.serviceCount }}회</span>
                      <span class="work-calendar-recipient-card__stat">{{ formatWorkCalendarDuration(item.totalHours) }}</span>
                      <span v-if="item.totalFeeAmount" class="work-calendar-recipient-card__amount">{{ item.totalFeeAmount.toLocaleString() }}원</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- 탭: 중증수급자가산 (청구내역 분리) -->
          <div v-else-if="!claimLoading && claimModalTab === 'severe'" class="home-claim-detail-modal__table-wrap">
            <p class="claim-modal-severe-note">
              중증수급자가산은 일반 청구내역·서비스횟수·{{ HOME_WAGE_NONTAX_TERM.lunch }} 집계에 포함되지 않습니다. 수가 및 합계는 「수급자별 수가 및 수가시간」 하단에서 확인·수정할 수 있습니다.
            </p>
            <table class="claim-detail-modal-table claim-detail-modal-table--severe">
              <thead>
                <tr>
                  <th class="col-date">서비스일자</th>
                  <th class="col-recipient">수급자</th>
                  <th class="col-time-range">시간간격</th>
                  <th class="col-fee-name">수가명</th>
                  <th class="col-amount">수가금액</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="caregiverClaimRowsSevere.length === 0">
                  <td colspan="5" class="empty-row">중증수급자가산 청구내역이 없습니다.</td>
                </tr>
                <tr
                  v-for="(row, idx) in caregiverClaimRowsSevere"
                  :key="row.clcd_idx ? `severe-d-${row.clcd_idx}` : `severe-c-${row.clc_idx}-${idx}`"
                >
                  <td class="col-date">{{ formatClaimDate(row.date) }}</td>
                  <td class="col-recipient">{{ formatRecipientWithCert(row.recipient_name, row.cert_number) }}</td>
                  <td class="col-time-range">{{ formatPlanTimeRange(row.plan_start_time, row.plan_end_time) }}</td>
                  <td class="col-fee-name">{{ formatFeeNameOnly(row.fee_name) }}</td>
                  <td class="col-amount">{{ formatFee(row.fee_amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 탭: 미조인 (청구내역 ↔ 상세 미매칭) — 관리자(60+) 전용, 중증수급자가산 제외 -->
          <div
            v-else-if="!claimLoading && canViewUnjoinedClaimTab && claimModalTab === 'unjoined'"
            class="home-claim-detail-modal__unjoined"
          >
            <p class="claim-modal-unjoined-note">
              청구내역과 청구내역상세가 서로 매칭되지 않은 데이터입니다. 수급자별 시급·수가시간 집계에는 포함되지 않습니다.
            </p>

            <section v-if="caregiverUnjoinedClaims.length > 0" class="claim-unjoined-section">
              <h4 class="claim-unjoined-section__title">
                <span class="claim-unjoined-section__dot claim-unjoined-section__dot--claim"></span>
                청구내역만 (상세 미연결)
                <span class="claim-unjoined-section__count">{{ caregiverUnjoinedClaims.length }}건</span>
              </h4>
              <div class="home-claim-detail-modal__table-wrap">
                <table class="claim-detail-modal-table claim-detail-modal-table--unjoined">
                  <thead>
                    <tr>
                      <th class="col-date">서비스일자</th>
                      <th class="col-recipient">수급자</th>
                      <th class="col-time-range">계획시간</th>
                      <th class="col-duration">서비스시간</th>
                      <th class="col-fee-code">수가코드</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in caregiverUnjoinedClaims"
                      :key="`unjoined-claim-${row.clc_idx}-${idx}`"
                    >
                      <td class="col-date">{{ formatClaimDate(row.date) }}</td>
                      <td class="col-recipient">{{ formatRecipientWithCert(row.recipient_name, row.cert_number) }}</td>
                      <td class="col-time-range">{{ formatPlanTimeRange(row.plan_start_time, row.plan_end_time) }}</td>
                      <td class="col-duration">{{ formatServiceDuration(row.plan_start_time, row.plan_end_time) }}</td>
                      <td class="col-fee-code">{{ row.fee_code || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section v-if="caregiverUnjoinedDetails.length > 0" class="claim-unjoined-section">
              <h4 class="claim-unjoined-section__title">
                <span class="claim-unjoined-section__dot claim-unjoined-section__dot--detail"></span>
                청구내역상세만 (내역 미연결)
                <span class="claim-unjoined-section__count">{{ caregiverUnjoinedDetails.length }}건</span>
              </h4>
              <div class="home-claim-detail-modal__table-wrap">
                <table class="claim-detail-modal-table claim-detail-modal-table--unjoined">
                  <thead>
                    <tr>
                      <th class="col-date">서비스일자</th>
                      <th class="col-recipient">수급자</th>
                      <th class="col-time-range">실제시간</th>
                      <th class="col-duration">서비스시간</th>
                      <th class="col-fee-name">수가명</th>
                      <th class="col-fee-code">수가코드</th>
                      <th class="col-amount">수가금액</th>
                      <th class="col-status">처리상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in caregiverUnjoinedDetails"
                      :key="`unjoined-detail-${row.clcd_idx}-${idx}`"
                    >
                      <td class="col-date">{{ formatClaimDate(row.date) }}</td>
                      <td class="col-recipient">{{ formatRecipientWithCert(row.recipient_name, row.cert_number) }}</td>
                      <td class="col-time-range">{{ formatPlanTimeRange(row.service_start_time, row.service_end_time) }}</td>
                      <td class="col-duration">{{ formatServiceDuration(row.service_start_time, row.service_end_time) }}</td>
                      <td class="col-fee-name">{{ formatFeeNameOnly(row.fee_name) }}</td>
                      <td class="col-fee-code">{{ row.fee_code || '-' }}</td>
                      <td class="col-amount">{{ formatFee(row.fee_amount) }}</td>
                      <td class="col-status">{{ row.detail_status || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <p v-if="claimUnjoinedTotalCount === 0" class="home-claim-detail-modal__calendar-empty">
              미조인 데이터가 없습니다.
            </p>
          </div>
        </div>

        <footer class="home-claim-detail-modal__footer">
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="showClaimDetailModal = false">닫기</v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- 일괄시급 입력 모달 -->
    <v-dialog
      v-model="showBulkHourlyModal"
      max-width="320"
      persistent
    >
      <div class="bulk-hourly-modal">
        <header class="bulk-hourly-modal__head">
          <h3 class="bulk-hourly-modal__title">일괄 시급 적용</h3>
          <v-btn
            icon
            size="small"
            variant="text"
            class="bulk-hourly-modal__close"
            aria-label="닫기"
            @click="closeBulkHourlyModal"
          >
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="bulk-hourly-modal__body">
          <p class="bulk-hourly-modal__desc">
            모든 수급자({{ displayClaimSummaryRows.filter(r => !r.isSevereRecipientSurcharge).length }}건)에 동일한 시급을 적용합니다.
          </p>
          <div class="bulk-hourly-modal__field">
            <label class="bulk-hourly-modal__label" for="bulk-hourly-input">시급</label>
            <input
              id="bulk-hourly-input"
              type="text"
              class="bulk-hourly-modal__input"
              v-model="bulkHourlyDisplay"
              placeholder="0"
              inputmode="numeric"
              @input="onBulkHourlyInput"
              @keydown.enter="applyBulkHourlyWage"
            />
            <span class="bulk-hourly-modal__unit">원</span>
          </div>
        </div>
        <footer class="bulk-hourly-modal__footer">
          <v-btn variant="text" class="btn-cancel" @click="closeBulkHourlyModal">취소</v-btn>
          <v-btn variant="flat" class="btn-confirm" :disabled="bulkHourlyValue <= 0" @click="applyBulkHourlyWage">적용</v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- 펼침 뷰 개인별 일괄시급 모달 -->
    <v-dialog
      v-model="showEmpBulkHourlyModal"
      max-width="320"
      persistent
    >
      <div class="bulk-hourly-modal">
        <header class="bulk-hourly-modal__head">
          <h3 class="bulk-hourly-modal__title">{{ empBulkHourlyName }} 일괄 시급</h3>
          <v-btn
            icon
            size="small"
            variant="text"
            class="bulk-hourly-modal__close"
            aria-label="닫기"
            @click="closeEmpBulkHourlyModal"
          >
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="bulk-hourly-modal__body">
          <p class="bulk-hourly-modal__desc">
            {{ empBulkHourlyName }}의 모든 수급자({{ (allEmpSummaryRows.get(empBulkHourlyKey) || []).filter((r: any) => !r.isSevereRecipientSurcharge).length }}건)에 동일한 시급을 적용합니다.
          </p>
          <div class="bulk-hourly-modal__field">
            <label class="bulk-hourly-modal__label" for="emp-bulk-hourly-input">시급</label>
            <input
              id="emp-bulk-hourly-input"
              type="text"
              class="bulk-hourly-modal__input"
              v-model="empBulkHourlyDisplay"
              placeholder="0"
              inputmode="numeric"
              @input="onEmpBulkHourlyInput"
              @keydown.enter="applyEmpBulkHourlyWage"
            />
            <span class="bulk-hourly-modal__unit">원</span>
          </div>
        </div>
        <footer class="bulk-hourly-modal__footer">
          <v-btn variant="text" class="btn-cancel" @click="closeEmpBulkHourlyModal">취소</v-btn>
          <v-btn variant="flat" class="btn-confirm" :disabled="empBulkHourlyValue <= 0" @click="applyEmpBulkHourlyWage">적용</v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- 퇴직금 산정 — 일반/가족 방문요양 지급금 선택 -->
    <HomeWageEmployeeConditionModal
      v-model="showEmployeeConditionModal"
      :employee-name="getEmpName(selectedEmployee)"
      :year-month-label="employeeConditionYearMonthLabel"
      :sections="employeeConditionSections"
    />

    <v-dialog
      v-model="showRetirementSourceModal"
      max-width="420"
      persistent
    >
      <div class="retirement-source-modal">
        <header class="retirement-source-modal__head">
          <h3 class="retirement-source-modal__title">퇴직금 산정 기준</h3>
          <v-btn
            icon
            size="small"
            variant="text"
            class="retirement-source-modal__close"
            aria-label="닫기"
            @click="closeRetirementSourceModal"
          >
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="retirement-source-modal__body">
          <p class="retirement-source-modal__desc">
            퇴직금을 산정할 지급금액을 선택하세요. ({{ HOME_WAGE_NONTAX_TERM.lunchExpense }} 시 {{ HOME_WAGE_NONTAX_TERM.lunch }} 제외 후 ÷12)
          </p>
          <label v-if="retirementModalVisitCarePay > 0" class="retirement-source-modal__option">
            <input
              v-model="retirementModalUseVisitCare"
              type="checkbox"
              class="retirement-source-modal__cb"
            />
            <span class="retirement-source-modal__option-text">
              <span class="retirement-source-modal__option-label">일반 방문요양 지급금액</span>
              <span class="retirement-source-modal__option-amount">{{ formatWageNumber(retirementModalVisitCarePay) }}원</span>
            </span>
          </label>
          <label v-if="retirementModalVisitBathPay > 0" class="retirement-source-modal__option">
            <input
              v-model="retirementModalUseVisitBath"
              type="checkbox"
              class="retirement-source-modal__cb"
            />
            <span class="retirement-source-modal__option-text">
              <span class="retirement-source-modal__option-label">방문목욕 지급금액</span>
              <span class="retirement-source-modal__option-amount">{{ formatWageNumber(retirementModalVisitBathPay) }}원</span>
            </span>
          </label>
          <label v-if="retirementModalFamilyPay > 0" class="retirement-source-modal__option">
            <input
              v-model="retirementModalUseFamily"
              type="checkbox"
              class="retirement-source-modal__cb"
            />
            <span class="retirement-source-modal__option-text">
              <span class="retirement-source-modal__option-label">가족 방문요양 지급금액</span>
              <span class="retirement-source-modal__option-amount">{{ formatWageNumber(retirementModalFamilyPay) }}원</span>
            </span>
          </label>
          <div
            v-if="!retirementModalHasSourceSelection"
            class="retirement-source-modal__manual"
          >
            <label class="retirement-source-modal__manual-label" for="retirement-manual-input">퇴직금 직접 입력</label>
            <div class="retirement-source-modal__manual-row">
              <input
                id="retirement-manual-input"
                v-model="retirementModalManualDisplay"
                type="text"
                class="retirement-source-modal__manual-input"
                inputmode="numeric"
                placeholder="0"
                @input="onRetirementModalManualInput"
              />
              <span class="retirement-source-modal__manual-unit">원</span>
            </div>
          </div>
          <div v-else class="retirement-source-modal__preview">
            <span class="retirement-source-modal__preview-label">산출 퇴직금</span>
            <span class="retirement-source-modal__preview-value">{{ formatWageNumber(retirementModalPreviewAmount) }}원</span>
          </div>
        </div>
        <footer class="retirement-source-modal__footer">
          <v-btn variant="text" class="btn-cancel" @click="closeRetirementSourceModal">취소</v-btn>
          <v-btn variant="flat" class="btn-confirm" @click="applyRetirementSourceModal">적용</v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- 공제 고정 금액 설정 모달 -->
    <HomeWageDeductionFixModal
      v-model="showDeductionFixModal"
      :title="deductionFixModalTitle"
      :has-auto-preview="deductionFixModalPreview.hasAuto"
      :auto-amount="deductionFixModalPreview.amount"
      :formula="deductionFixModalPreview.formula"
      :notes="deductionFixModalPreview.notes"
      :current-amount="deductionFixModalCurrentAmount"
      manual-label="직접 입력"
      :manual-display="deductionFixModalManualDisplay"
      input-id="deduction-fix-manual-input"
      :format-money="formatWageNumber"
      @cancel="closeDeductionFixModal"
      @apply-auto="applyDeductionFixModalAuto"
      @apply-manual="applyDeductionFixModalManual"
      @manual-input="onDeductionFixModalManualInput"
    />

    <!-- 지원공제 항목 선택 모달 -->
    <HomeWageSupportDeductionModal
      v-model="showSupportDeductionModal"
      :items="supportDeductionModalItems"
      :flags="supportDeductionDraftFlags"
      :base-total="supportDeductionModalPreview.base"
      :preview-total="supportDeductionModalPreview.total"
      :formula="supportDeductionModalPreview.formula"
      :format-money="formatWageNumber"
      @cancel="closeSupportDeductionModal(true)"
      @apply="applySupportDeductionModal"
      @update:flags="onSupportDeductionDraftFlagsUpdate"
    />

    <HomeWagePremiumAdjustModal
      v-model="showPremiumAdjustModal"
      :is-family-care="isAllFamilyCareInClaim"
      :auto-allocated-total="premiumManualBaseline.autoAllocatedTotal"
      :rows="premiumAdjustModalRows"
      :initial-manual="premiumAdjustInitialManual"
      @cancel="closePremiumAdjustModal"
      @apply="applyPremiumManualAdjust"
      @show-holiday-detail="showHolidayPremiumDetailModal = true"
    />

    <HomeWageHolidayPremiumDetailModal
      v-model="showHolidayPremiumDetailModal"
      :rows="holidayPremiumDetailRows"
      @close="showHolidayPremiumDetailModal = false"
    />

    <HomeWageNontaxSavingsModal
      ref="nontaxSavingsModalRef"
      :m-code="memberCode"
      :biz-gubun="selectedBizGubun || ''"
      :focus-year="selectedYear"
      :focus-month="selectedMonth"
      :employees="sortedEmployees"
      :is-admin="canEditTaxRate"
      @update:month-saving="sidebarMonthSaving = $event"
      @update:loading-month-saving="loadingSidebarMonthSaving = $event"
    />

    <!-- 전체 시급 일괄 설정 모달 -->
    <v-dialog
      v-model="showGlobalBulkHourlyModal"
      max-width="400"
      persistent
    >
      <div class="global-bulk-hourly-modal">
        <header class="global-bulk-hourly-modal__head">
          <h3 class="global-bulk-hourly-modal__title">전체 시급 일괄 설정</h3>
          <v-btn
            icon
            size="small"
            variant="text"
            class="global-bulk-hourly-modal__close"
            aria-label="닫기"
            @click="closeGlobalBulkHourlyModal"
          >
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="global-bulk-hourly-modal__body">
          <p class="global-bulk-hourly-modal__desc">
            모든 요양보호사의 수급자에게 일괄 시급을 적용합니다.
          </p>

          <!-- 일반요양 -->
          <div class="global-bulk-hourly-modal__section">
            <div class="section-header">
              <span class="section-label">일반요양</span>
              <span class="section-count">{{ globalBulkGeneralCount }}건</span>
            </div>
            <div class="global-bulk-hourly-modal__field">
              <input
                type="text"
                class="global-bulk-hourly-modal__input"
                v-model="globalBulkGeneralDisplay"
                placeholder="0"
                inputmode="numeric"
                @input="onGlobalBulkGeneralInput"
              />
              <span class="global-bulk-hourly-modal__unit">원</span>
            </div>
          </div>

          <!-- 가족요양 -->
          <div class="global-bulk-hourly-modal__section">
            <div class="section-header">
              <span class="section-label section-label--family">
                <v-icon size="14" color="#8b5cf6">mdi-home-heart</v-icon>
                가족요양
              </span>
              <span class="section-count">{{ globalBulkFamilyCount }}건</span>
            </div>
            <div class="global-bulk-hourly-modal__field">
              <input
                type="text"
                class="global-bulk-hourly-modal__input"
                v-model="globalBulkFamilyDisplay"
                placeholder="0"
                inputmode="numeric"
                @input="onGlobalBulkFamilyInput"
              />
              <span class="global-bulk-hourly-modal__unit">원</span>
            </div>
          </div>

        </div>
        <footer class="global-bulk-hourly-modal__footer">
          <v-btn variant="text" class="btn-cancel" @click="closeGlobalBulkHourlyModal">취소</v-btn>
          <v-btn
            variant="flat"
            class="btn-confirm"
            :disabled="globalBulkGeneralValue <= 0 && globalBulkFamilyValue <= 0"
            @click="applyGlobalBulkHourlyWage"
          >
            적용
          </v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- 임금대장 미리보기 모달 -->
    <HomeWageReportExcel
      ref="wageReportRef"
      :year="selectedYear"
      :month="selectedMonth"
      :wage-data="wageReportData"
      :member-name="memberData?.m_name"
      @error="handleWageReportError"
    />

    <HomeWageRetirementCompareModal ref="retirementCompareModalRef" />

    <!-- 인건비지출내역 모달 -->
    <HomeLaborCostModal
      ref="laborCostModalRef"
      :member-code="memberCode"
      :year="selectedYear"
      :month="selectedMonth"
      :biz-gubun="selectedBizGubun"
    />

    <WageExcelMigrationModal
      v-model="showMigrationModal"
      wage-type="home"
      :m-code="memberCode"
      :year-month="yearMonth"
      :biz-gubun="selectedBizGubun"
      :pay-date="paymentDate || null"
      @imported="onMigrationImported"
    />

    <WageLedgerConvertModal
      v-model="showLedgerConvertModal"
      wage-type="home"
    />

    <WageSsnMissingModal
      v-model="ssnMissingModalOpen"
      :member-code="memberCode"
      :year-month="yearMonth"
      @saved="onSsnMissingSaved"
      @continue-magam="onSsnMissingContinueMagam"
    />

    <!-- 전체 명세서 PDF 옵션 -->
    <v-dialog v-model="showWageSlipOptionsDialog" max-width="420">
      <div class="home-wage-slip-options-dialog">
        <div class="home-wage-slip-options-dialog__header">
          <span>명세서 PDF</span>
          <button
            type="button"
            class="home-wage-slip-options-dialog__close"
            aria-label="닫기"
            @click="showWageSlipOptionsDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="home-wage-slip-options-dialog__body">
          <button
            type="button"
            class="home-wage-slip-options-dialog__option"
            @click="previewAllWageSlipPdf"
          >
            <v-icon size="18">mdi-file-eye-outline</v-icon>
            <span class="option-title">전체 명세서 미리보기</span>
            <span class="option-desc">새 창에서 전체 사원 명세서를 확인하고 저장합니다.</span>
          </button>
          <button
            type="button"
            class="home-wage-slip-options-dialog__option"
            @click="downloadWageSlipZip"
          >
            <v-icon size="18">mdi-folder-zip-outline</v-icon>
            <span class="option-title">개별 명세서 ZIP 다운로드</span>
            <span class="option-desc">사원별 PDF를 폴더로 묶어 ZIP 파일로 받습니다.</span>
          </button>
        </div>
        <div class="home-wage-slip-options-dialog__footer">
          <v-btn variant="text" class="btn-close" @click="showWageSlipOptionsDialog = false">닫기</v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- 미등록 사원 경고 모달 -->
    <v-dialog v-model="showUnregisteredModal" max-width="520" persistent>
      <div class="home-wage-unregistered-dialog">
        <LoadingOverlay :show="registeringEmployees" text="등록 중..." variant="save" />
        <div class="home-wage-unregistered-dialog__header">
          <v-icon size="20" color="#c62828">mdi-alert-circle</v-icon>
          <span class="header-title">미등록 요양보호사 발견</span>
          <button
            type="button"
            class="home-wage-unregistered-dialog__close"
            :disabled="registeringEmployees"
            aria-label="닫기"
            @click="showUnregisteredModal = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="home-wage-unregistered-dialog__body">
          <p class="unregistered-desc">
            청구내역에는 있지만 <strong>종사자 목록에 등록되지 않은</strong> 요양보호사가 있습니다.<br>
            임금설정을 위해 해당 종사자를 등록해 주세요.
          </p>
          <div class="unregistered-list">
            <div 
              v-for="emp in unregisteredEmployees" 
              :key="`unreg-${emp.clci_name}-${emp.clci_birth_date}`"
              class="unregistered-item"
            >
              <div class="unregistered-item__info">
                <span class="unregistered-item__name">{{ emp.clci_name }}</span>
                <span class="unregistered-item__birth">{{ formatBirthDisplay(emp.clci_birth_date) }}</span>
                <span class="unregistered-item__job">{{ emp.clci_job_type }}</span>
              </div>
              <span class="unregistered-item__biz">{{ emp.claim_biz_label }}</span>
            </div>
          </div>
        </div>
        <div class="home-wage-unregistered-dialog__footer">
          <v-btn variant="text" class="btn-cancel" :disabled="registeringEmployees" @click="showUnregisteredModal = false">
            나중에
          </v-btn>
          <v-btn variant="flat" class="btn-register" :disabled="registeringEmployees" @click="registerUnregisteredEmployees">
            <v-icon v-if="registeringEmployees" size="14" class="mdi-spin">mdi-loading</v-icon>
            {{ unregisteredEmployees.length === 1 ? '등록하기' : `${unregisteredEmployees.length}명 일괄 등록` }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, inject, provide, nextTick, type Ref } from 'vue';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import { useRoute, useRouter } from 'vue-router';
import { homeWageApi, commonWageApi, homeScheduleApi, employeeApi, tokenManager } from '@/services/api';
import {
  useWageMemberYearMonth,
  useHomeWageClaimSummary,
  useHomeWageWorkCalendar,
  useWageMagam,
  getPrevYearMonth,
  formatYearMonthLabel,
  buildMagamBlockButtonTitle,
  formatWageNumber,
  parseWageNumber,
} from '@/composables';
import { useHomeWagePayrollViewMode } from '@/composables/wage/useHomeWagePayrollViewMode';
import {
  isProductionWorkerPosition,
  validateProductionWorkerNontax,
} from '@/composables/wage/useWageCalculator';
import {
  calcTotalSugaHoursFromSummary,
  calcTotalServiceCountFromSummary,
  calcSevereServiceCountFromSummary,
  filterNonSevereSummaryRows,
  filterNonSevereClaimRows,
  filterSevereClaimRows,
  calcWorkDaysFromClaimRows,
  calcHomeWageLunchWorkDayCountFromClaims,
  isSevereRecipientSurcharge,
  calcHomeWageHourlyLinkedPay,
  calcHomeWagePayBreakdown,
  calcDirectorCaregiverLinkedPay,
  type HomeWagePayrollViewMode,
  calcHomeWageTaxableIncome,
  calcHomeWageLaborDayPay,
  buildHomeWageLaborDayTooltip,
  hasWorkedOnMayFirstFromClaims,
  calcMayFirstPlanHoursFromClaims,
  isHomeWageLaborDayMonth,
  isMayFirstSunday,
  calcPremiumHoursFromClaims,
  getClaimRowPremiumInfo,
  sortClaimRowsByDateTime,
  calcPlanDurationHours,
  HOME_WAGE_DAILY_OVERTIME_THRESHOLD_HOURS,
  allocateHomeWagePremiumPays,
  resolveHomeWagePremiumPayMode,
  resolvePremiumHoursForAllocation,
  resolveDirectorPremiumPayEnabled,
  parseHomeWageDirectorPremiumPayFlag,
  formatHomeWageDirectorPremiumPayFlag,
  getHomeWageDirectorPremiumBlockedAlert,
  parseHomeWagePremiumHours,
  formatHomeWagePremiumHoursDisplay,
  calcHomeWageCaregiverPremiumPays,
  buildHomeWagePremiumAdminTooltip,
  calcOptimalHourlyWage,
  getAnnualLeaveDays,
  parseHomeWageTaxRateValue,
  parseJoinDate,
  calcTenureMonths,
  createEmptyPayrollForm,
  calcHomeWageTotalNontax,
  calcHomeWageCombinedLunch,
  resolveHomeWageSlipAutoLunch,
  resolveHomeWageAdminAnnualForSlip,
  applyHomeWageExtractLunchFromAnnual,
  calcHomeWageAutoLunch,
  clampHomeWageLunchDirectorInput,
  resolveHomeWageAdjustedAutoLunch,
  applyHomeWageLunchDirectorAdjustment,
  HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX,
  type HomeWageNontaxViewMode,
  calcHomeWagePremiumTaxableAmount,
  calcHomeWagePremiumNonTaxAllowance,
  splitHomeWagePremiumForStorage,
  normalizeHomeWagePremiumFromDb,
  applyHomeWagePremiumSplitToForm,
  getHomeWagePremiumFullAmounts,
  calcHomeWageInstitutionRetirement,
  calcHomeWageInstitutionRetirementFromSource,
  calcHomeWageRetirementPaySplit,
  calcHomeWageRetirementBase,
  calcHomeWageCenterTaxSavings,
  calcHomeWageCenterTaxSavingsBreakdown,
  calcHomeWageCenterSavingsNontaxBase,
  type HomeWageNontaxSavingsInput,
  calcHomeWageCenterProfit,
  formatHomeWageRatePercent,
  parseHomeWageRatePercentInput,
  applyHomeWageRepresentativeCorpDeduction,
  parseHomeWageRepresentativeFlag,
  formatHomeWageRepresentativeFlag,
  parseHomeWageApplyRetirementFlag,
  formatHomeWageApplyRetirementFlag,
  resolveHomeWageRetirementApplicable,
  getHomeWageRetirementApplyCheckboxTooltip,
  calcClaimFeeAmountTotal,
  preferPaidClaimDetailRows,
  calcHomeWageNonSevereRecipientTotalFromSummaryRows,
  dbWageAmount,
  getHomeWageCaregiverSlipPayDisplay,
  hasHomeWagePayAmountTripleMismatch,
  buildHomeWagePayAmountTripleMismatchReport,
  buildHomeWageAdminForwardPayrollForm,
  calcHomeWageAdminViewPayTotalsFromContext,
  type HomeWagePayAmountMismatchReport,
  encodeHomeWageDirectorResavedMemo,
  calcHomeWageDirectorCaregiverBasePayTotal,
  calcHomeWageDirectorCaregiverAllowanceTotal,
  calcHomeWageDirectorStaffWorkInfoTotal,
  calcHomeWageDirectorStaffAllowanceTotal,
  splitHomeWageStaffPoolByDirectorLunch,
  resolveHomeWageStaffAutoLunchFill,
  calcHomeWageStaffBasePay,
  getHomeWageAccountingDisplayBasic,
  getHomeWageAdminSettingDisplayParts,
  getHomeWageAdminSettingGrossPay,
  isHomeWageAutoLunchAbsorbedInAnnual,
  HOME_WAGE_LUNCH_MONTHLY_LIMIT,
  resolveHomeWageFundedAutoLunch,
  getHomeWageStaffLedgerGrossPay,
  resolveHomeWageDirectorAnnualFromRecipientCap,
  calcHomeWageAdminDisplayGrossPay,
  calcHomeWageClientReportGrossPay,
  buildHomeWageClientReportRowFromDb,
  buildHomeWageDirectorStoreFromLegacyRow,
  mapHomeWageRowToDirectorStore,
  getHomeWageAdminPartsFromDirectorStore,
  packHomeWageDirectorCtAdPayload,
  calcHomeWageDirectorAdminAutoLunch,
  deriveHomeWageAdminAutoLunchFromCtAd,
  hasHomeWageStoredAdParts,
  hasHomeWageStoredCtParts,
  readHomeWageStoredCtParts,
  sumHomeWageStoredCtPayParts,
  readHomeWageStoredAdParts,
  readHomeWageStoredAdminAutoLunch,
  isHomeWageDirectorCanonicalRow,
  type HomeWageAdminSettingDisplayParts,
  matchHomeWageRowByNameBirth,
  calcHomeWageOvertimeNontax,
  calcHomeWageCaregiverUnifiedDisplayGrossPay,
  buildHomeWageCaregiverUnifiedGrossReportRow,
  calcHomeWageStoredUnifiedGrossPay,
  shouldExtractAutoLunchFromAnnualField,
  isHomeWageCaregiverJob,
  type HomeWageClientPayDisplayRow,
  type HomeWageClientReportRow,
  getHomeWageLedgerGrossPay,
  getHomeWageLedgerNetPay,
  getHomeWageLedgerEmployerInsurance,
  getHomeWageLedgerRetirement,
  distributeHomeWagePay,
  calcHomeWageStaffTotal,
  HOME_WAGE_STAFF_DEFAULT_WORK_HOURS,
  HOME_WAGE_INSURANCE_MIN_HOURS,
  resolveHomeWageFamilyUnder60HealthInsuranceExempt,
  applyHomeWageFamilyUnder60HealthInsuranceFixedRelease,
  HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE,
  isHomeWageRowAllFamilyCareFromSvc,
  resolveHomeWageAgeInsurance,
  aggregateRecipientFeeSummary,
  buildRecipientFeeGroupKey,
  serializeHomeWageRecipientSummaryToSvc,
  parseHomeWageRecipientSummaryFromSvc,
  mergeLiveClaimSummaryWithDbSnapshot,
  recipientSummaryMatchKey,
  DEFAULT_HOME_WAGE_NONTAX_SETTINGS,
  HOME_WAGE_PAYROLL_CARDS,
  HOME_WAGE_BASE_PAY_FIELDS,
  HOME_WAGE_BASE_PAY_KEYS,
  HOME_WAGE_STAFF_WORK_INFO_FIELDS,
  HOME_WAGE_STAFF_WORK_INFO_KEYS,
  HOME_WAGE_STAFF_TAXABLE_OTHER_FIELDS,
  HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS,
  HOME_WAGE_TAXABLE_COMPONENT_KEYS,
  HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS,
  HOME_WAGE_TAXABLE_OTHER_FIELDS,
  HOME_WAGE_TAXABLE_OTHER_KEYS,
  HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS,
  HOME_WAGE_PREV_MONTH_FIXED_COPY_NONTAX_KEYS,
  HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_FIELDS,
  HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS,
  HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_FIELDS,
  HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_KEYS,
  HOME_WAGE_TAXABLE_TOTAL_FIELD,
  HOME_WAGE_NON_TAX_CARD,
  HOME_WAGE_NONTAX_TERM,
  HOME_WAGE_NON_TAX_CASH_FIELDS,
  HOME_WAGE_LABOR_DAY_NONTAX_FIELD,
  HOME_WAGE_CAREGIVER_PREMIUM_FIELDS,
  HOME_WAGE_SOCIAL_INSURANCE_CARD,
  HOME_WAGE_LABOR_DEDUCT_CARD,
  HOME_WAGE_CORP_DEDUCT_CARD,
  HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS,
  HOME_WAGE_LABOR_FIX_DEDUCT_FIELDS,
  HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELDS,
  HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS,
  HOME_WAGE_LABOR_TOTAL_FIELD,
  HOME_WAGE_CORP_TOTAL_FIELD,
  HOME_WAGE_REFERENCE_CARD,
  HOME_WAGE_INSURANCE_PAIRS,
  HOME_WAGE_INSURANCE_SHARED_PAIRS,
  HOME_WAGE_EMPLOYMENT_INSURANCE_PAIR,
  HOME_WAGE_INSURANCE_FIX_CHECKBOX_TOOLTIP,
  HOME_WAGE_DEDUCTION_FIX_CHECKBOX_TOOLTIP,
  createEmptyHomeWageDeductionFixedFlags,
  parseHomeWageDeductionFixedFlag,
  formatHomeWageDeductionFixedFlag,
  HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS,
  type HomeWageDeductionFixFieldKey,
  type HomeWageDeductionFixedFlags,
  type HomeWageDeductionAutoPreview,
  createAllIncludedSupportDeductionFlags,
  loadSupportDeductionItemFlagsFromRow,
  mapSupportDeductionItemFlagsToDb,
  buildSupportDeductionAmounts,
  listSupportDeductionItemRows,
  resolveAutoSupportDeductionFlags,
  calcHomeWageSupportDeductionWithItems,
  formatSupportDeductionTargetSummaryLine,
  formatSupportDeductionTargetDetailLine,
  calcHomeWageAutoPremiumSnapshot,
  applyHomeWageManualPremiumAdjustment,
  revertHomeWageManualPremiumAdjustment,
  buildHomeWageHolidayPremiumClaimRows,
  buildHomeWageBasicPayAdminTooltip,
  isHomeWagePremiumProductionTaxNontaxSplit,
  type HomeWageAutoPremiumSnapshot,
  HOME_WAGE_SUPPORT_DEDUCTION_SELECTIVE_TOOLTIP,
  HOME_WAGE_SUPPORT_DEDUCTION_UI,
  type HomeWageSupportDeductionItemFlags,
  type HomeWageSupportDeductionAmounts,
  type HomeWageSupportDeductionResult,
  isHomeWagePayrollTotalKey,
  // 가족요양 관련
  isFamilyCare,
  isAllFamilyCaregiver,
  hasFamilyCarePatient,
  FAMILY_CARE_ICON,
  FAMILY_CARE_COLOR,
  FAMILY_CARE_CHIP_STYLE,
  FAMILY_CARE_WARNINGS,
  type HomeWagePayrollFieldDef,
  type HomeWageInsurancePairDef,
  type HomeWagePayrollFieldKey,
  type HomeWagePremiumAmountKey,
  type HomeWagePremiumHoursKey,
  type HomeWagePayrollFormData,
  type HomeWageClaimRow,
  type HomeWageNontaxSettings,
  type HomeWageStaffInput,
  type HomeWageRecipientFeeSummary,
  type HomeWagePremiumPayAmounts,
} from '@/policies';
import { compareJobThenName, getJobTypeSortRank } from '@/policies/jobTypeSort';
import {
  parseHours,
  sanitizeHoursInput,
  formatHoursDisplay,
  formatDurationHoursMinutes,
  calcEmployeeDeductions,
  calcEmployerDeductions,
  clampNontaxCashFieldInput,
  type DeductionRates,
} from '@/composables/wage/useWageCalculator';
import { calculateIncomeTax, type IncomeTaxRow } from '@/composables/wage/useCommonWageCalc';
import type { CommonWageTaxRate } from '@/types/commonWage';
import { buildEmpMatchKey, normalizeEmpBirth, normalizeEmpName, rowEmpKey } from '@/utils/empMatchKey';
import HomeWageAdminToolbarMenu from '@/components/wage/home/HomeWageAdminToolbarMenu.vue';
import HomeWagePayrollViewToggle from '@/components/wage/home/HomeWagePayrollViewToggle.vue';
import HomeWageEmployeeConditionModal, {
  type HomeWageEmployeeConditionSection,
  type HomeWageEmployeeConditionFieldItem,
  type HomeWageEmployeeConditionSettingItem,
} from '@/components/wage/home/HomeWageEmployeeConditionModal.vue';
import HomeWagePayrollFieldRows from '@/components/wage/home/HomeWagePayrollFieldRows.vue';
import HomeWageDirectorPremiumPaySwitches from '@/components/wage/home/HomeWageDirectorPremiumPaySwitches.vue';
import HomeWageDeductionFixRow from '@/components/wage/home/HomeWageDeductionFixRow.vue';
import HomeWageDeductionFixModal from '@/components/wage/home/HomeWageDeductionFixModal.vue';
import HomeWageSupportDeductionModal from '@/components/wage/home/HomeWageSupportDeductionModal.vue';
import HomeWagePremiumAdjustModal from '@/components/wage/home/HomeWagePremiumAdjustModal.vue';
import type { PremiumAdjustKey } from '@/components/wage/home/HomeWagePremiumAdjustModal.vue';
import HomeWageHolidayPremiumDetailModal from '@/components/wage/home/HomeWageHolidayPremiumDetailModal.vue';
import HomeWageNontaxSavingsModal from '@/components/wage/home/HomeWageNontaxSavingsModal.vue';
import HomeWagePremiumCollapse from '@/components/wage/home/HomeWagePremiumCollapse.vue';
import HomeWageTooltip from '@/components/wage/home/HomeWageTooltip.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import HomeWageReportExcel from '@/components/wage/home/HomeWageReportExcel.vue';
import HomeWageRetirementCompareModal from '@/components/wage/home/HomeWageRetirementCompareModal.vue';
import CommonWageReportMenu from '@/components/wage/common/CommonWageReportMenu.vue';
import WageSlipBroadcastButton from '@/components/wage/common/WageSlipBroadcastButton.vue';
import YunsuTestWageSlipPrompt from '@/components/wage/common/YunsuTestWageSlipPrompt.vue';
import HomeLaborCostModal from '@/components/wage/home/HomeLaborCostModal.vue';
import WageExcelMigrationModal from '@/components/wage/WageExcelMigrationModal.vue';
import WageLedgerConvertModal from '@/components/wage/WageLedgerConvertModal.vue';
import WageSsnMissingModal from '@/components/wage/WageSsnMissingModal.vue';
import LtcsRequestSidebarButton from '@/components/member/request/LtcsRequestSidebarButton.vue';
import LtcsMissingRequestCta from '@/components/member/request/LtcsMissingRequestCta.vue';
import { WAGE_CLAIM_LTCS_REQUEST_TYPES } from '@/composables/useLtcsMacroRequest';
import store from '@/store';

type MainView = 'list' | 'detail';
type ClaimModalTab = 'list' | 'calendar' | 'unjoined' | 'severe';

const route = useRoute();
const router = useRouter();
/** 방문요양 임금설정 — 센터장 정본 테이블만 조회/저장 */
const isHomeWageDirectorStorePage = computed(() => true);
const memberData = inject<any>('memberData');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');
const selectedBizGubun = computed(() => selectedBizGubunRef?.value || '');

const isHomeCareBiz = (biz: string) => biz === '방문요양' || biz === '방문목욕';

const memberCode = computed(
  () => (route.params.memberCode as string) || memberData?.value?.m_code || ''
);

/** 직원(40+)·관리자만 툴팁 표시 — 기관회원(고객) 로그인 시 숨김 */
const showTooltips = computed(() => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 40);
const roleLevel = computed(() => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0));
/** 회계처리용 임금대장 — 매니저(50+) 이상, 기관회원은 고객용(임금대장)만 */
const canViewAccountingReport = computed(() => store.getters.canViewAccountingWageReport);
/** 관리자(60+)만 청구내역 모달 — 미조인 탭 */
const canViewUnjoinedClaimTab = computed(() => roleLevel.value >= 60);
/** 관리자(60+)만 임금 데이터 삭제·마이그레이션 */
const canEditTaxRate = computed(() => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 60);
/** 관리자(90+) — 센터장 시점 기본급·주휴·연차 일괄 재계산 버튼 */
const canRecalcDirectorBasicPay = computed(() => roleLevel.value >= 90);
/** 최고관리자(SUPER_ADMIN, role_level 100) 전용 기능 표시 */
const isSuperAdmin = computed(() => roleLevel.value >= 100);

const {
  directorPreviewActive,
  isDirectorPayrollView,
  canToggleDirectorPreview,
  payrollViewMode,
  loadDirectorPreview,
  persistDirectorPreview,
} = useHomeWagePayrollViewMode(canEditTaxRate);

watch(memberCode, (code) => loadDirectorPreview(code), { immediate: true });

/** 시점 전환 중 watcher 연쇄 방지 */
const payrollViewSwitching = ref(false);

/** 센터장 ↔ 관리자 시점 토글 */
const selectPayrollViewMode = (mode: 'director' | 'admin') => {
  if (!canToggleDirectorPreview.value) return;
  const wantDirector = mode === 'director';
  if (directorPreviewActive.value === wantDirector) return;
  directorPreviewActive.value = wantDirector;
  if (memberCode.value) {
    persistDirectorPreview(memberCode.value);
  }
};

/**
 * 관리자 시점(미리보기) — 관리자(60+)가 센터장 시점 토글을 끈 상태.
 * 연·야·휴·비과세 항목만 DB에 병합 저장 가능 (기본급·공제 등 센터장 캐논 유지).
 */
const isAdminPreviewView = computed(
  () => canToggleDirectorPreview.value && !directorPreviewActive.value
);

/** 자동계산 — 항상 ON (watch 기반 자동 계산) */
const autoCalcMode = ref(true);
const showMigrationModal = ref(false);
const showLedgerConvertModal = ref(false);

const onMigrationImported = async () => {
  await loadEmployees();
};
const taxRatePanelExpanded = ref(false);
provide('showWageTooltips', showTooltips);

// ═══════════════════════════════════════════════════════════════════════
// ★★★ 저장하지 않은 변경사항 추적 (Unsaved Changes Guard) ★★★
// ═══════════════════════════════════════════════════════════════════════
/** 현재 상세 화면에서 저장하지 않은 변경사항이 있는지 (참고용 플래그) */
const hasUnsavedChanges = ref(false);

/**
 * 편집 대상 상태를 문자열로 직렬화 — 실제 저장값 변경 여부 판정용.
 * blur/focus만으로는 값이 바뀌지 않으므로, 이 스냅샷 비교로 오탐(변경 없는데 알림)을 방지한다.
 */
const computeDirtySignature = (): string | null => {
  if (mainView.value !== 'detail' || !selectedEmployee.value) return null;
  try {
    return JSON.stringify({
      payroll: payrollForm.value,
      staff: staffForm.value,
      nontax: nontaxSettings.value,
      deductionFixed: deductionFixed.value,
      support: supportDeductionItemFlags.value,
      applyRetirement: applyRetirement.value,
      isRepresentative: isRepresentative.value,
      retirementSource: retirementSource.value,
      premiumManual: premiumManualAdjustEnabled.value,
      directorPremiumPayOverride: directorPremiumPayOverride.value,
      caregiverHourly: caregiverHourlyWage.value,
      maps: getMapsForSave(),
    });
  } catch {
    return null;
  }
};

/** 로드·저장 직후의 기준 상태 스냅샷 (이후 비교로 실제 변경 여부 판정) */
const dirtyBaseline = ref<string | null>(null);

/** 저장 완료 또는 초기화 시 호출 — 기준 스냅샷 갱신·dirty 플래그 해제 */
const clearDirty = () => {
  hasUnsavedChanges.value = false;
  dirtyBaseline.value = computeDirtySignature();
};

/** 실제 저장값이 기준 스냅샷과 다른지 — 단순 포커스·blur는 변경으로 보지 않음 */
const isReallyDirty = (): boolean => {
  const current = computeDirtySignature();
  if (current === null || dirtyBaseline.value === null) return false;
  return current !== dirtyBaseline.value;
};

/** 저장하지 않은 변경사항 확인 — true면 진행, false면 취소 */
const confirmUnsavedChanges = (): boolean => {
  if (!isReallyDirty()) return true;
  return window.confirm(
    '저장하지 않은 변경사항이 있습니다.\n\n저장하지 않고 계속 진행하시겠습니까?'
  );
};

const isPopupMode = computed(() => !!route.params.memberCode);

const facilityRouteName = computed(() =>
  isPopupMode.value ? 'memberDetailWageMonthlySettingFacility' : 'memberWageMonthlySettingFacility'
);

const {
  selectedYear,
  selectedMonth,
  yearMonth,
  changeMonth: rawChangeMonth,
  onMonthPickerChange: rawMonthPickerChange,
} = useWageMemberYearMonth(memberCode);

/** ★ 월 변경 래핑 — 저장하지 않은 변경사항 확인 */
const changeMonth = (delta: number) => {
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  rawChangeMonth(delta);
};

/** ★ 월 선택기 래핑 — 저장하지 않은 변경사항 확인 */
const onMonthPickerChange = (event: Event) => {
  if (!confirmUnsavedChanges()) {
    // 취소 시 입력값을 원래대로 복원
    const input = event.target as HTMLInputElement;
    input.value = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}`;
    return;
  }
  clearDirty();
  rawMonthPickerChange(event);
};

const {
  isClosed: isWageMagamClosed,
  closedAt: wageMagamClosedAt,
  memo: wageMagamMemo,
  loading: wageMagamLoading,
  closing: wageMagamClosing,
  releasing: wageMagamReleasing,
  isReadOnly: isWageReadOnly,
  ssnMissingCount,
  joinMissingCount,
  ssnMissingBlocksClose,
  closeMagam: closeWageMagam,
  releaseMagam: releaseWageMagam,
  loadStatus: loadWageMagamStatus,
} = useWageMagam({
  memberCode,
  yearMonth,
  bizGubun: selectedBizGubun,
  hasPrevWageData: async () => {
    const prevYm = getPrevYearMonth(yearMonth.value);
    if (!memberCode.value || !prevYm) return false;
    try {
      const res = await homeScheduleApi.getDirectorWageData(
        memberCode.value,
        prevYm,
        selectedBizGubun.value || ''
      );
      const rows = res.data?.data;
      return Array.isArray(rows) && rows.length > 0;
    } catch {
      return false;
    }
  },
});

const ssnMissingModalOpen = ref(false);

const onCloseWageMagamClick = async () => {
  if (ssnMissingBlocksClose.value) {
    ssnMissingModalOpen.value = true;
    return;
  }
  await closeWageMagam();
};

const onSsnMissingSaved = async () => {
  await loadWageMagamStatus();
};

const onSsnMissingContinueMagam = async () => {
  ssnMissingModalOpen.value = false;
  await loadWageMagamStatus();
  if (ssnMissingBlocksClose.value) {
    ssnMissingModalOpen.value = true;
    return;
  }
  await closeWageMagam();
};

const MOVED_WAGE_MEMO = '이전 시스템에서 옮긴 임금';

const isMovedWageMonth = computed(
  () => String(wageMagamMemo.value || '') === MOVED_WAGE_MEMO
);

const magamReadyButtonTitle = computed(() =>
  buildMagamBlockButtonTitle(ssnMissingCount.value, joinMissingCount.value)
);

const formatMagamClosedAt = (value: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${hh}:${mm} 마감`;
};

const movedMagamButtonTitle = computed(() => {
  const closed = wageMagamClosedAt.value
    ? `${formatMagamClosedAt(wageMagamClosedAt.value)} — 클릭 시 마감 해제`
    : '임금 마감 완료 — 클릭 시 마감 해제';
  if (!isMovedWageMonth.value) return closed;
  return `${closed}\n이전 시스템에서 옮긴 임금입니다.`;
});

const paymentDate = ref('');
const savingMonthSettings = ref(false);
const sanRate = ref(0.0068);
const sanRateDisplay = ref('0.68');

const formatSanRateDisplay = (value: number | string): string =>
  formatHomeWageRatePercent(value, { withSuffix: false });

const loading = ref(false);
/** 사원 상세 선택·연월 변경(상세 화면) 데이터 로드 */
const detailLoading = ref(false);
/** 상세 급여대장 초기 로드 중 — 조기 자동계산·watch 연쇄 반응 차단 (입력값 깜빡임 방지) */
const payrollHydrating = ref(false);
const saving = ref(false);
const savingSanRate = ref(false);

const employees = ref<any[]>([]);
const mainView = ref<MainView>('list');
const selectedEmployee = ref<any | null>(null);

/** 변경사항 발생 시 호출 — dirty 플래그 설정 (상세 화면에서만) */
const markDirty = () => {
  // 상세 화면이고, 사원이 선택되어 있고, 마감 상태가 아닐 때만 dirty 설정
  if (mainView.value === 'detail' && selectedEmployee.value && !isWageReadOnly.value) {
    hasUnsavedChanges.value = true;
  }
};

// ─────────────────────────────────────────────
// 펼침 뷰: 사원별 수급자 시급/수가시간 일괄 편집
// ─────────────────────────────────────────────
const isExpandedView = ref(false);
const expandedEmpKeys = ref<Set<string>>(new Set());
const allEmpClaimData = ref<Map<string, HomeWageClaimRow[]>>(new Map());
const allEmpSummaryRows = ref<Map<string, any[]>>(new Map());
const loadingExpandedData = ref(false);
const savingAllRecipients = ref(false);

/** 모두저장 전까지 메모리에 보관하는 사원별 급여·수급자 설정 초안 */
interface EmployeeWageDraft {
  wagePayload: Record<string, unknown>;
  directorPayload: Record<string, unknown>;
  recipient?: {
    hourlyByGroup?: Record<string, number>;
    severeFeeByGroup?: Record<string, number>;
    sugaHoursByGroup?: Record<string, number>;
  };
}
const employeeWageDrafts = ref<Map<string, EmployeeWageDraft>>(new Map());

const clearEmployeeWageDrafts = () => {
  employeeWageDrafts.value = new Map();
};

/** 연월 전환 시 이전 요청 무효화 — 빠른 월 이동 시 stale 응답이 화면을 덮어쓰지 않도록 */
const monthDataLoadSeq = ref(0);
const isMonthDataLoadStale = (seq: number) => seq !== monthDataLoadSeq.value;

/** 임금대장/명세서 리포트 상태 */
const wageReportData = ref<any[]>([]);
const loadingWageReportData = ref(false);
const showWageSlipOptionsDialog = ref(false);
const wageReportRef = ref<InstanceType<typeof HomeWageReportExcel> | null>(null);
const retirementCompareModalRef = ref<InstanceType<typeof HomeWageRetirementCompareModal> | null>(null);
const laborCostModalRef = ref<InstanceType<typeof HomeLaborCostModal> | null>(null);
const loadingLaborCostModal = ref(false);
const nontaxSavingsModalRef = ref<InstanceType<typeof HomeWageNontaxSavingsModal> | null>(null);
const sidebarMonthSaving = ref<number | null>(null);
const loadingSidebarMonthSaving = ref(false);

/** 미등록 사원 경고 모달 */
const showUnregisteredModal = ref(false);
/** 같은 연월에서 등록 실패 후 loadEmployees가 모달을 다시 여는 루프 방지 */
const skipUnregisteredAutoPrompt = ref(false);
const unregisteredPromptYm = ref('');
const unregisteredEmployees = computed(() => 
  employees.value.filter((emp) => emp.is_unregistered)
);
const registeringEmployees = ref(false);

/** 펼침 뷰용 시급/수가 입력값 (empKey → groupKey → value) */
const expandedHourlyWage = ref<Map<string, Map<string, number>>>(new Map());
const expandedHourlyDisplay = ref<Map<string, Map<string, string>>>(new Map());
const expandedSevereFee = ref<Map<string, Map<string, number>>>(new Map());
const expandedSevereDisplay = ref<Map<string, Map<string, string>>>(new Map());

const clearExpandedRecipientMaps = () => {
  expandedHourlyWage.value = new Map();
  expandedHourlyDisplay.value = new Map();
  expandedSevereFee.value = new Map();
  expandedSevereDisplay.value = new Map();
  expandedEmpKeys.value = new Set();
};

const HOME_CARE_BIZ_GUBUNS = '방문요양,방문목욕,방문간호';
const claimLoading = ref(false);

/** 상세 화면 전체 로딩 (청구내역 + 급여대장 + 수급자별 시급까지 포함) */
const isDetailPageLoading = computed(
  () =>
    detailLoading.value
    || payrollHydrating.value
    || loading.value
    || (mainView.value === 'detail'
      && !!selectedEmployee.value
      && (claimLoading.value || bulkEmployeeProcessing.value))
);

const importingPrevMonth = ref(false);
/** 목록 일괄 처리 중 — 상세 화면 전환·사이드바 선택 하이라이트 방지 */
const bulkEmployeeProcessing = ref(false);
const showClaimDetailModal = ref(false);
const claimModalTab = ref<ClaimModalTab>('list');
type ClaimModalSortKey = 'date' | 'recipient' | 'planStart' | 'duration' | 'feeName' | 'feeAmount' | 'premium' | 'lunch';
const claimModalSortKey = ref<ClaimModalSortKey>('date');
const claimModalSortOrder = ref<'asc' | 'desc'>('asc');
const showBulkHourlyModal = ref(false);
const bulkHourlyDisplay = ref('');
const bulkHourlyValue = ref(0);

// 펼침 뷰 개인별 일괄시급 모달
const showEmpBulkHourlyModal = ref(false);
const empBulkHourlyKey = ref('');
const empBulkHourlyName = ref('');
const empBulkHourlyDisplay = ref('');
const empBulkHourlyValue = ref(0);

// 펼침 뷰 전체 일괄시급 모달
const showGlobalBulkHourlyModal = ref(false);
const globalBulkGeneralDisplay = ref('');
const globalBulkGeneralValue = ref(0);
const globalBulkFamilyDisplay = ref('');
const globalBulkFamilyValue = ref(0);

const savingRecipientSettings = ref(false);
const caregiverClaimRows = ref<HomeWageClaimRow[]>([]);
/** 청구내역 ↔ 상세 미조인 — 청구내역만 */
const caregiverUnjoinedClaims = ref<HomeWageClaimRow[]>([]);
/** 청구내역 ↔ 상세 미조인 — 청구내역상세만 (중증수급자가산 제외) */
const caregiverUnjoinedDetails = ref<HomeWageClaimRow[]>([]);
/** 미조인 중증수급자가산 — 조인 불가 특수 케이스, 중증 탭 전용 */
const caregiverUnjoinedSevereRows = ref<HomeWageClaimRow[]>([]);

/** 청구 집계용 — 조인 청구 + 미조인 중증수급자가산 (수급자별 표·계산 버튼) */
const caregiverClaimRowsForAgg = computed(() => [
  ...caregiverClaimRows.value,
  ...caregiverUnjoinedSevereRows.value,
]);

const claimUnjoinedTotalCount = computed(
  () => caregiverUnjoinedClaims.value.length + caregiverUnjoinedDetails.value.length
);
const hasAnyClaimModalData = computed(
  () =>
    caregiverClaimRows.value.length > 0
    || claimUnjoinedTotalCount.value > 0
    || caregiverUnjoinedSevereRows.value.length > 0
);

const clearCaregiverClaimData = () => {
  caregiverClaimRows.value = [];
  caregiverUnjoinedClaims.value = [];
  caregiverUnjoinedDetails.value = [];
  caregiverUnjoinedSevereRows.value = [];
};

/** YYYY-MM-DD → 공휴일명 (caion_home_holiday) */
const holidayMap = ref<Record<string, string>>({});
/** 공휴일 API 캐시 (연도별 1회 조회) */
const holidaysLoadedYear = ref<number | null>(null);
let holidaysLoadingPromise: Promise<void> | null = null;
const holidayLoading = ref(false);
const employeeListMeta = ref<{
  hasClaimDataForMonth: boolean;
  hasClaimDetailForMonth: boolean;
  claimCaregiverCount: number;
  claimDetailCount: number;
} | null>(null);
const minHourlyWage = ref(0);
const taxRateInfo = ref<CommonWageTaxRate | null>(null);
const taxRateFallback = ref(false);

const taxRateDisplayRows = computed(() => {
  const t = taxRateInfo.value;
  if (!t) return [];
  const san = sanRate.value > 0 ? sanRate.value : parseHomeWageTaxRateValue(t.rateSan, 0.007);
  return [
    { label: '국민연금', value: formatHomeWageRatePercent(t.rateYeon) },
    { label: '건강보험', value: formatHomeWageRatePercent(t.rateGun) },
    { label: '고용보험', value: formatHomeWageRatePercent(t.rateGo) },
    { label: '장기요양', value: formatHomeWageRatePercent(t.rateJang) },
    { label: '실업급여', value: formatHomeWageRatePercent(t.rateSil) },
    { label: '산재보험', value: formatHomeWageRatePercent(san) },
    { label: '인건비율', value: formatHomeWageRatePercent(t.laborCostRate, { scaleFromDecimal: false }) },
  ];
});
const incomeTaxTable = ref<IncomeTaxRow[]>([]);
const incomeTaxLoadedYear = ref(0);
let incomeTaxInflight: { year: number; promise: Promise<void> } | null = null;
const payrollForm = ref<HomeWagePayrollFormData>(createEmptyPayrollForm());
/** 대표자 여부 — cwhd_is_representative (고용·산재·실업·퇴직금 미적용) */
const isRepresentative = ref(false);
/** 퇴직금 적용(고정) 체크 — true면 c_out 수동·산정원 선택, false면 자동 산출 */
const applyRetirement = ref(true);

/** 퇴직금 산정 — 일반 방문요양(B1) / 방문목욕(B2) / 가족방문요양 선택 */
const retirementSource = ref({
  useVisitCare: false,
  useVisitBath: false,
  useFamily: false,
  visitCarePay: 0,
  visitBathPay: 0,
  familyPay: 0,
});
const showRetirementSourceModal = ref(false);
const showEmployeeConditionModal = ref(false);
const showDeductionFixModal = ref(false);
const deductionFixModalTarget = ref<HomeWageDeductionFixFieldKey | null>(null);
const showSupportDeductionModal = ref(false);
const supportDeductionItemFlags = ref<HomeWageSupportDeductionItemFlags>(
  createAllIncludedSupportDeductionFlags()
);
const supportDeductionDraftFlags = ref<HomeWageSupportDeductionItemFlags>(
  createAllIncludedSupportDeductionFlags()
);
const deductionFixModalManualDisplay = ref('');
const retirementModalUseVisitCare = ref(false);
const retirementModalUseVisitBath = ref(false);
const retirementModalUseFamily = ref(false);
const retirementModalManualDisplay = ref('');
/** DB 저장 급여대장 로드 후 — 자동 재계산이 저장값을 덮어쓰지 않도록 */
const payrollDbLock = ref(false);
/** DB cwhd_svc 파싱 — 수급자별 표시용 (청구 재집계 대신) */
const dbSavedSummaryRows = ref<HomeWageRecipientFeeSummary[]>([]);
const currentWageDataIdx = ref<number | null>(null);
const hasSavedRecipientSettings = ref(false);
const deletingWageData = ref(false);

const nontaxSettings = ref<HomeWageNontaxSettings>({ ...DEFAULT_HOME_WAGE_NONTAX_SETTINGS });

/** 센터장 시점 — 4대보험·공제 산출용 관리자(비과세 혜택 전체) 급여 스냅샷 */
const accountingPayrollShadow = ref<HomeWagePayrollFormData | null>(null);
/** 청구·관리자 산출 자동 중식대 원값 (센터장 입력 전) — 저장 시 lunch 조정 기준 */
const autoBaseLunchRef = ref(0);
/** DB/관리자 폼에 저장된 자동중식 — 센터장 연차에 더할 값 (재산출·센터장중식 금지) */
const dbSavedAutoLunchRef = ref(0);
/** 센터장 정본 테이블(또는 이관값)을 폼에 올린 상태 — 연차에 자동중식 포함 */
const directorCanonicalActive = ref(false);
const loadedDirectorRow = ref<Record<string, unknown> | null>(null);

const shouldSkipAdminLiveCalc = () =>
  isHomeWageDirectorStorePage.value
  && !isDirectorPayrollView.value
  && hasHomeWageStoredAdParts(loadedDirectorRow.value);

const resolveNontaxViewMode = (): HomeWageNontaxViewMode =>
  isDirectorPayrollView.value ? 'director' : 'admin';

const resolveCaregiverPayrollViewMode = (): HomeWagePayrollViewMode =>
  isDirectorPayrollView.value ? 'director' : 'admin';

/** 관리자 칸(과세/비과세 분리)을 공제 산정용 폼에 올린다 — 센터장 칸 덮어쓰기 */
const applyAdminSettingPartsToFormData = (
  form: HomeWagePayrollFormData,
  p: HomeWageAdminSettingDisplayParts
): HomeWagePayrollFormData => ({
  ...form,
  basic: p.basic,
  weekend: p.weekend,
  annual: p.annual,
  laborDay: p.laborDay,
  overtime: p.overtime,
  overtimeNontax: p.overtimeNontax,
  night: p.night,
  nightNontax: p.nightNontax,
  holiday: p.holiday,
  holidayNontax: p.holidayNontax,
  senior: p.senior,
  sunim: p.sunim,
  bosu: p.bosu,
  severe: p.severe,
  bonus: p.bonus,
  lunch: p.lunchAuto,
  lunchDirector: p.lunchDirector,
  car: p.car,
  child: p.child,
  welfare: p.welfare,
  other: p.other,
});

/** 저장된 정본 행의 관리자 칸 — 명세서·대장과 동일 원천. 센터장 폼 혼합 금지 */
const getLoadedAdminInsuranceParts = (): HomeWageAdminSettingDisplayParts | null => {
  const row = loadedDirectorRow.value;
  if (!row) return null;
  return getHomeWageAdminSettingDisplayParts(row, yearMonth.value);
};

/**
 * 실시간 관리자 shadow가 식대를 못 빼면 센터장 과세와 같아진다.
 * 저장된 관리자 식대가 있는데 lunch=0이면 신뢰하지 않는다.
 */
const isLiveAdminInsuranceShadowReliable = (shadow: HomeWagePayrollFormData): boolean => {
  if (!nontaxSettings.value.lunchExpense) return true;
  if ((Number(shadow.lunch) || 0) > 0) return true;
  const row = loadedDirectorRow.value;
  if (!row) return true;
  const adminLunch = hasHomeWageStoredAdParts(row)
    ? readHomeWageStoredAdminAutoLunch(row)
    : deriveHomeWageAdminAutoLunchFromCtAd(row);
  return adminLunch <= 0;
};

/**
 * 4대보험·소득세 — 관리자 시점 과세액만 사용
 * 센터장 화면의 payrollForm(연차에 식대 포함)은 쓰지 않는다.
 */
const getAdminInsurancePayrollForm = (): HomeWagePayrollFormData => {
  if (!isDirectorPayrollView.value) {
    return payrollForm.value;
  }

  const loadedParts = getLoadedAdminInsuranceParts();
  const loadedForm = loadedParts
    ? applyAdminSettingPartsToFormData(payrollForm.value, loadedParts)
    : null;

  if (payrollDbLock.value && loadedForm) {
    return loadedForm;
  }

  const live = accountingPayrollShadow.value;
  if (live && isLiveAdminInsuranceShadowReliable(live)) {
    return live;
  }

  if (loadedForm) return loadedForm;

  return applyAdminSettingPartsToFormData(
    payrollForm.value,
    adminSettingDisplayParts.value
  );
};

/** 4대보험·소득세 산정용 과세임금 — 관리자 시점 과세액 */
const getInsuranceTaxableBase = () => {
  const caregiver = isCaregiverEmployee(selectedEmployee.value);
  return calcHomeWageTaxableIncome(getAdminInsurancePayrollForm(), {
    productionNontax: productionNontaxActive.value,
    staffEmployee: !caregiver,
    yearMonth: yearMonth.value,
  });
};

/** 공제 항목 고정값 플래그 (체크 시 계산·가져오기 시 해당 금액 자동계산 제외) */
const deductionFixed = ref<HomeWageDeductionFixedFlags>(createEmptyHomeWageDeductionFixedFlags());

/** 이전 달 급여 데이터 (전달 가져오기·4대보험 계산 참조) */
interface PrevMonthTransferData {
  prevYearMonth?: string;
  payCalcHourly?: number;
  /** 근무시간 (사복/센터장 필수) */
  hour?: string | number | null;
  basic?: number;
  weekend?: number;
  annual?: number;
  lunch?: number;
  lunchDirector?: number;
  car?: number;
  child?: number;
  productionNontax?: string;
  lunchExpense?: string;
  overtime?: number;
  night?: number;
  holiday?: number;
  overtimeNontax?: number;
  nightNontax?: number;
  holidayNontax?: number;
  overtimeHours?: number;
  nightHours?: number;
  holidayHours?: number;
  payOvertime?: string | null;
  payHoliday?: string | null;
  payNight?: string | null;
  senior?: number;
  sunim?: number;
  bosu?: number;
  bonus?: number;
  welfare?: number;
  other?: number;
  l_yeon: number; l_yeon_fixed: string;
  l_gun: number; l_gun_fixed: string;
  l_jang: number; l_jang_fixed: string;
  l_go: number; l_go_fixed: string;
  c_yeon: number; c_yeon_fixed: string;
  c_gun: number; c_gun_fixed: string;
  c_jang: number; c_jang_fixed: string;
  c_go: number; c_go_fixed: string;
  l_so?: number; l_so_fixed?: string;
  l_ji?: number; l_ji_fixed?: string;
  l_jiwon?: number; l_jiwon_fixed?: string;
  l_bonin?: number;
  l_etc?: number;
  c_sil?: number; c_sil_fixed?: string;
  c_san?: number; c_san_fixed?: string;
  c_out?: number;
  applyRetirement?: string;
  retirementVisitCarePay?: number;
  retirementVisitBathPay?: number;
  retirementFamilyPay?: number;
  retirementUseVisitCare?: string;
  retirementUseVisitBath?: string;
  retirementUseFamily?: string;
  /** @deprecated 레거시 alias */
  retirementGeneralPay?: number;
  retirementUseGeneral?: string;
  isRepresentative?: string;
}
const prevMonthInsurance = ref<PrevMonthTransferData | null>(null);

/** DB/전월 row → 공제 고정 플래그 로드 */
const loadDeductionFixedFromRow = (row: Record<string, unknown>) => {
  deductionFixed.value = {
    l_yeon: parseHomeWageDeductionFixedFlag(row.l_yeon_fixed),
    l_gun: parseHomeWageDeductionFixedFlag(row.l_gun_fixed),
    l_jang: parseHomeWageDeductionFixedFlag(row.l_jang_fixed),
    l_go: parseHomeWageDeductionFixedFlag(row.l_go_fixed),
    l_so: parseHomeWageDeductionFixedFlag(row.l_so_fixed),
    l_ji: parseHomeWageDeductionFixedFlag(row.l_ji_fixed),
    l_jiwon: parseHomeWageDeductionFixedFlag(row.l_jiwon_fixed),
    c_yeon: parseHomeWageDeductionFixedFlag(row.c_yeon_fixed),
    c_gun: parseHomeWageDeductionFixedFlag(row.c_gun_fixed),
    c_jang: parseHomeWageDeductionFixedFlag(row.c_jang_fixed),
    c_go: parseHomeWageDeductionFixedFlag(row.c_go_fixed),
    c_sil: parseHomeWageDeductionFixedFlag(row.c_sil_fixed),
    c_san: parseHomeWageDeductionFixedFlag(row.c_san_fixed),
  };
  syncFamilyUnder60HealthInsuranceFixedRelease();
  loadSupportDeductionFromRow(row);
};

/** DB row → 지원공제 항목 포함 플래그 */
const loadSupportDeductionFromRow = (row: Record<string, unknown>) => {
  supportDeductionItemFlags.value = loadSupportDeductionItemFlagsFromRow(row);
};

/** 저장 payload — 지원공제 항목 플래그 */
const mapSupportDeductionFieldsFromFlags = () =>
  mapSupportDeductionItemFlagsToDb(supportDeductionItemFlags.value);

/**
 * 가족요양 60h 미만 — 건강·장기요양은 체크(고정) 없이 정책 자동계산(0원).
 * DB/전월에 고정(Y)으로 저장된 레거시는 면제 조건 시 체크 해제.
 */
const syncFamilyUnder60HealthInsuranceFixedRelease = (): boolean => {
  const { changed, fixed } = applyHomeWageFamilyUnder60HealthInsuranceFixedRelease(
    isFamilyUnder60HealthExempt.value,
    {
      l_gun: deductionFixed.value.l_gun,
      l_jang: deductionFixed.value.l_jang,
      c_gun: deductionFixed.value.c_gun,
      c_jang: deductionFixed.value.c_jang,
    }
  );
  if (!changed) return false;
  deductionFixed.value.l_gun = fixed.l_gun;
  deductionFixed.value.l_jang = fixed.l_jang;
  deductionFixed.value.c_gun = fixed.c_gun;
  deductionFixed.value.c_jang = fixed.c_jang;
  return true;
};

/** DB row → 저장 API *_fixed Y/N (13개 공제 고정 플래그) */
const mapDeductionFixedFieldsFromRow = (row: Record<string, unknown>) => {
  const yn = (val: unknown) => formatHomeWageDeductionFixedFlag(parseHomeWageDeductionFixedFlag(val));
  return {
    l_yeon_fixed: yn(row.l_yeon_fixed),
    l_gun_fixed: yn(row.l_gun_fixed),
    l_jang_fixed: yn(row.l_jang_fixed),
    l_go_fixed: yn(row.l_go_fixed),
    l_so_fixed: yn(row.l_so_fixed),
    l_ji_fixed: yn(row.l_ji_fixed),
    l_jiwon_fixed: yn(row.l_jiwon_fixed),
    c_yeon_fixed: yn(row.c_yeon_fixed),
    c_gun_fixed: yn(row.c_gun_fixed),
    c_jang_fixed: yn(row.c_jang_fixed),
    c_go_fixed: yn(row.c_go_fixed),
    c_sil_fixed: yn(row.c_sil_fixed),
    c_san_fixed: yn(row.c_san_fixed),
  };
};

/** 전월 고정 항목 금액 반영 (4대보험 공유쌍은 근로·기관 동시 반영) */
const applyPrevMonthFixedDeductionAmounts = (data: PrevMonthTransferData) => {
  const applyIfFixed = (fixed?: string, amount?: number, key?: HomeWageDeductionFixFieldKey) => {
    if (fixed !== 'Y' || !key || amount == null) return;
    setPayrollMoneyField(key, Number(amount) || 0);
  };
  const applySharedPairIfFixed = (
    pairKey: 'yeon' | 'gun' | 'jang',
    lFixed?: string,
    lAmount?: number,
    cFixed?: string,
    cAmount?: number
  ) => {
    if (lFixed !== 'Y' && cFixed !== 'Y') return;
    const pair = HOME_WAGE_INSURANCE_SHARED_PAIRS.find((p) => p.key === pairKey);
    if (!pair) return;
    let amount = 0;
    if (lFixed === 'Y' && lAmount != null) amount = Math.max(amount, Number(lAmount) || 0);
    if (cFixed === 'Y' && cAmount != null) amount = Math.max(amount, Number(cAmount) || 0);
    setPayrollMoneyField(pair.laborKey, amount);
    setPayrollMoneyField(pair.corpKey, amount);
  };
  applySharedPairIfFixed('yeon', data.l_yeon_fixed, data.l_yeon, data.c_yeon_fixed, data.c_yeon);
  applySharedPairIfFixed('gun', data.l_gun_fixed, data.l_gun, data.c_gun_fixed, data.c_gun);
  applySharedPairIfFixed('jang', data.l_jang_fixed, data.l_jang, data.c_jang_fixed, data.c_jang);
  applyIfFixed(data.l_go_fixed, data.l_go, 'l_go');
  applyIfFixed(data.c_go_fixed, data.c_go, 'c_go');
  applyIfFixed(data.l_so_fixed, data.l_so, 'l_so');
  applyIfFixed(data.l_ji_fixed, data.l_ji, 'l_ji');
  applyIfFixed(data.c_sil_fixed, data.c_sil, 'c_sil');
  applyIfFixed(data.c_san_fixed, data.c_san, 'c_san');
};

const referenceCard = HOME_WAGE_REFERENCE_CARD;

const formatYearMonthLabel = (ym: string) => {
  const s = String(ym).replace(/-/g, '').substring(0, 6);
  if (s.length < 6) return ym;
  return `${s.substring(0, 4)}.${s.substring(4, 6)}`;
};

/** 저장된 급여대장·수급자별 설정 존재 여부 */
const hasSavedWageData = computed(
  () => currentWageDataIdx.value != null || hasSavedRecipientSettings.value
);

/** 관리자 + 미마감 + 시설코드 있을 때 해당 연월 전체 삭제 가능 */
const canDeleteMonthWageData = computed(() => {
  if (!canEditTaxRate.value) return false;
  if (isWageReadOnly.value) return false;
  if (!memberCode.value) return false;
  return true;
});

/** 지난달 데이터 가져오기 버튼 활성 조건 (상세 화면) */
const canImportPrevMonthData = computed(() => {
  if (isWageReadOnly.value) return false;
  if (!selectedEmployee.value || mainView.value !== 'detail') return false;
  if (employees.value.length === 0) return false;
  if (isCaregiverEmployee(selectedEmployee.value)) {
    if (!employeeListMeta.value?.hasClaimDataForMonth) return false;
    if (!employeeListMeta.value?.hasClaimDetailForMonth) return false;
  }
  return true;
});

/** 지난달 데이터 가져오기 버튼 활성 조건 (목록 화면 — 전체 사원) */
const canImportPrevMonthDataList = computed(() => {
  if (isWageReadOnly.value) return false;
  if (mainView.value !== 'list') return false;
  if (employees.value.length === 0) return false;
  // 당월 청구가 있으면 전원 허용
  if (employeeListMeta.value?.hasClaimDataForMonth) return true;
  // 청구 없음 → 요보는 목록에 없음. 사복/센터장만 있어도 전월 급여 가져오기 허용
  return sortedEmployees.value.some((emp) => !isCaregiverEmployee(emp));
});

/** 참고 바 — 인건비절감액(c_save) 미표시, 기관수익은 요양보호사(청구 수가 연동)만 표시 */
const referenceBarFields = computed(() => {
  const fields = (referenceCard.fields ?? []).filter((f) => f.key !== 'c_save');
  if (isCaregiverEmployee(selectedEmployee.value)) return fields;
  return fields.filter((f) => f.key !== 'profit');
});
const basePayFields = HOME_WAGE_BASE_PAY_FIELDS;
const staffWorkInfoFields = computed(() => {
  if (!directorMaskPremiumDisplay.value) return HOME_WAGE_STAFF_WORK_INFO_FIELDS;
  return HOME_WAGE_STAFF_WORK_INFO_FIELDS.filter((field) => !field.premiumHours);
});

/** 기본급 카드 합계 (기본급 + 주휴 + 연차 + 중증수급자가산) */
const basePayTotal = computed(() => {
  if (
    isDirectorPayrollView.value
    && isCaregiverEmployee(selectedEmployee.value)
  ) {
    return calcHomeWageDirectorCaregiverBasePayTotal(
      payrollForm.value,
      clientPayDisplayRow.value
    );
  }
  return HOME_WAGE_BASE_PAY_KEYS.reduce(
    (total, key) => total + (Number(payrollForm.value[key]) || 0),
    0
  );
});

/** 비요양보호사 — 근무정보 카드 합계 (기본급 + 연장·야간·휴일) */
const staffWorkInfoTotal = computed(() =>
  HOME_WAGE_STAFF_WORK_INFO_KEYS.reduce((sum, key) => sum + (Number(payrollForm.value[key]) || 0), 0)
);

/** 과세 수당 카드 필드 (직종별) */
const activeTaxableOtherFields = computed(() =>
  isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_TAXABLE_OTHER_FIELDS
    : HOME_WAGE_STAFF_TAXABLE_OTHER_FIELDS
);

/** 센터장 시점 — 과세·비과세 수당 통합 카드 (모든 직종) */
const showDirectorMergedAllowanceCard = computed(() => isDirectorPayrollView.value);

/**
 * 센터장 시점 — 연·야·휴 표시 숨김 (계산·DB·공제는 관리자 값 유지)
 * - 월 수가시간 60h 미만 (요양보호사)
 * - 가족요양 전용
 * - 센터장/사복: 월 근무시간 60h 미만
 */
const directorMaskPremiumDisplay = computed(() => {
  if (directorCanonicalActive.value) return false;
  if (!isDirectorPayrollView.value) return false;
  if (isCaregiverEmployee(selectedEmployee.value)) {
    return isAllFamilyCareInClaim.value || totalSugaHours.value < HOME_WAGE_INSURANCE_MIN_HOURS;
  }
  const workHours =
    Number(staffForm.value.workHours)
    || parseHours(String(payrollForm.value.hour || '').replace(/시간/g, ''))
    || 0;
  return workHours > 0 && workHours < HOME_WAGE_INSURANCE_MIN_HOURS;
});

/** 센터장 연·야·휴 지급 override (null = 60h 정책 기본) */
const directorPremiumPayOverride = ref<{
  payOvertime: boolean | null;
  payHoliday: boolean | null;
  payNight: boolean | null;
}>({
  payOvertime: null,
  payHoliday: null,
  payNight: null,
});

const resetDirectorPremiumPayOverride = () => {
  directorPremiumPayOverride.value = { payOvertime: null, payHoliday: null, payNight: null };
};

const directorPayOvertimeEffective = computed(() =>
  resolveDirectorPremiumPayEnabled({
    type: 'overtime',
    payOverride: directorPremiumPayOverride.value.payOvertime,
    totalSugaHours: totalSugaHours.value,
  })
);

const directorPayHolidayEffective = computed(() =>
  resolveDirectorPremiumPayEnabled({
    type: 'holiday',
    payOverride: directorPremiumPayOverride.value.payHoliday,
    totalSugaHours: totalSugaHours.value,
  })
);

const directorPayNightEffective = computed(() =>
  resolveDirectorPremiumPayEnabled({
    type: 'night',
    payOverride: directorPremiumPayOverride.value.payNight,
    totalSugaHours: totalSugaHours.value,
  })
);

/** 센터장 · 일반요양 — 청구 연·야·휴 시간이 있을 때만 지급 선택 표시 */
const directorPremiumPaySwitchItems = computed(() => {
  if (
    !isDirectorPayrollView.value
    || !isCaregiverEmployee(selectedEmployee.value)
    || isAllFamilyCareInClaim.value
  ) {
    return [];
  }
  const split = effectivePremiumTaxSplit.value;
  const otH = Number(payrollForm.value.overtimeHours) || 0;
  const niH = Number(payrollForm.value.nightHours) || 0;
  const hoH = Number(payrollForm.value.holidayHours) || 0;
  const rows: Array<{
    type: 'overtime' | 'night' | 'holiday';
    label: string;
    enabled: boolean;
    hours: number;
    amount: number;
  }> = [];
  if (otH > 0) {
    rows.push({
      type: 'overtime',
      label: '연장수당',
      enabled: directorPayOvertimeEffective.value,
      hours: otH,
      amount: directorPayOvertimeEffective.value
        ? split.overtimeTax + split.overtimeNontax
        : 0,
    });
  }
  if (niH > 0) {
    rows.push({
      type: 'night',
      label: '야간수당',
      enabled: directorPayNightEffective.value,
      hours: niH,
      amount: directorPayNightEffective.value
        ? split.nightTax + split.nightNontax
        : 0,
    });
  }
  if (hoH > 0) {
    rows.push({
      type: 'holiday',
      label: '휴일수당',
      enabled: directorPayHolidayEffective.value,
      hours: hoH,
      amount: directorPayHolidayEffective.value
        ? split.holidayTax + split.holidayNontax
        : 0,
    });
  }
  return rows;
});

const showDirectorPremiumPaySwitches = computed(
  () => directorPremiumPaySwitchItems.value.length > 0
);

const formatDirectorPremiumPayAmount = (amount: number) => {
  if (!(amount > 0)) return '';
  return formatWageNumber(amount);
};

const onDirectorPremiumPaySwitchChange = async (
  type: 'overtime' | 'night' | 'holiday',
  enabled: boolean
) => {
  if (isWageReadOnly.value || !showDirectorPremiumPaySwitches.value) return;
  unlockPayrollLedgerForEdit();
  markDirty();
  if (type === 'overtime') {
    directorPremiumPayOverride.value.payOvertime = enabled;
  } else if (type === 'night') {
    directorPremiumPayOverride.value.payNight = enabled;
  } else {
    directorPremiumPayOverride.value.payHoliday = enabled;
  }
  payrollRecalculating.value = true;
  try {
    syncPremiumNontaxAmounts('director', true);
    ensureCaregiverPremiumAmountsSynced(true);
    reconcileCaregiverHourlyLinkedPayFromClaims({
      restoreLockedDeductions: false,
      force: true,
    });
    recalcWageTotal();
    syncNonTaxTotal();
    syncDirectorInsuranceFromAdminShadow(false);
    syncReferenceValues();
  } finally {
    payrollRecalculating.value = false;
  }
};

/** 센터장 시점 — 식대 수령 표시 숨김 (가족요양 전용, 점심근무 있어도 미수령 표현) */
const directorMaskLunchDisplay = computed(
  () =>
    !directorCanonicalActive.value
    && isDirectorPayrollView.value
    && isCaregiverEmployee(selectedEmployee.value)
    && isAllFamilyCareInClaim.value
);

/** 센터장 시점 — 식대를 연차에 합산 표시 (일반 요양보호사, 통합 수당 카드에서 자동 중식대 행 제외) */
const directorMergeLunchIntoAnnual = computed(
  () =>
    isDirectorPayrollView.value
    && isCaregiverEmployee(selectedEmployee.value)
    && !isAllFamilyCareInClaim.value
);

/**
 * 센터장 · 요양보호사 외 — 근무정보 합계
 * ★ 센터장 표시: 기본급 = 시급×시간(풀) — 자동중식 차감 숨김
 * ★ 관리자/저장: 기본급 = 시급×시간 − 자동중식
 */
const staffWorkInfoDisplayTotal = computed(() => {
  if (isDirectorPayrollView.value) {
    return calcHomeWageDirectorStaffWorkInfoTotal(clientPayDisplayRow.value);
  }
  return staffWorkInfoTotal.value;
});

/** 센터장 통합 수당 — 고정 항목만 (연·야·휴는 하단 지급/미지급 스위치) */
const directorMergedAllowanceFields = computed(() => {
  const base = isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_FIELDS
    : HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_FIELDS;
  let fields: HomeWagePayrollFieldDef[] = [...base];
  if (showLaborDayNontaxField.value && HOME_WAGE_LABOR_DAY_NONTAX_FIELD) {
    fields = [HOME_WAGE_LABOR_DAY_NONTAX_FIELD, ...fields];
  }
  if (directorMaskLunchDisplay.value) {
    fields = fields.filter((f) => f.key !== 'lunchDirector');
  }
  return fields;
});

/** 관리자 시점 — 청구·폼 기준 연·야·휴 시간이 있는 수당만 표시 */
const activeCaregiverPremiumFields = computed(() => {
  const hoursOf = (key: 'overtime' | 'night' | 'holiday') => {
    const map = {
      overtime: 'overtimeHours',
      night: 'nightHours',
      holiday: 'holidayHours',
    } as const;
    return Number(payrollForm.value[map[key]]) || 0;
  };
  return HOME_WAGE_CAREGIVER_PREMIUM_FIELDS.filter((f) => {
    if (f.key === 'overtime') return hoursOf('overtime') > 0;
    if (f.key === 'night') return hoursOf('night') > 0;
    if (f.key === 'holiday') return hoursOf('holiday') > 0;
    return false;
  });
});

/** 센터장 통합 수당 합계 — slip 표시 + 수당 키 직접 합산 */
const directorMergedAllowanceTotal = computed(() => {
  if (isCaregiverEmployee(selectedEmployee.value)) {
    return calcHomeWageDirectorCaregiverAllowanceTotal(
      payrollForm.value,
      clientPayDisplayRow.value,
      yearMonth.value
    );
  }
  return calcHomeWageDirectorStaffAllowanceTotal(
    payrollForm.value,
    clientPayDisplayRow.value
  );
});

/** 노동절수당 — 5월·요양보호사만 비과세 카드에 표시 */
const showLaborDayNontaxField = computed(
  () =>
    isCaregiverEmployee(selectedEmployee.value)
    && isHomeWageLaborDayMonth(yearMonth.value)
);

/** 과세 수당 카드 합계 (직종별) — 요양보호사: 기타과세 + 연·야·휴 과세분 */
const activeTaxableOtherTotal = computed(() => {
  const keys = isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_TAXABLE_OTHER_KEYS
    : HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS;
  let sum = keys.reduce((total, key) => total + (Number(payrollForm.value[key]) || 0), 0);
  if (isCaregiverEmployee(selectedEmployee.value)) {
    if (!isDirectorPayrollView.value || !directorMaskPremiumDisplay.value) {
      sum += isDirectorPayrollView.value
        ? taxablePremiumTotal.value + nontaxPremiumTotal.value
        : taxablePremiumTotal.value;
    }
  }
  return sum;
});
const insurancePairs = HOME_WAGE_INSURANCE_PAIRS;
const insuranceSharedPairs = HOME_WAGE_INSURANCE_SHARED_PAIRS;
const employmentInsurancePair = HOME_WAGE_EMPLOYMENT_INSURANCE_PAIR;

type EmploymentInsuranceSide = 'labor' | 'corp';

const employmentInsuranceSides: { key: EmploymentInsuranceSide; chipLabel: string }[] = [
  { key: 'labor', chipLabel: '근로' },
  { key: 'corp', chipLabel: '기관' },
];

const corpOtherFieldsWithoutRetirement = computed(() =>
  HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS.filter((f) => f.key !== 'c_out')
);

const HOME_WAGE_CORP_RETIREMENT_FIELD = computed(() =>
  HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS.find((f) => f.key === 'c_out')
);

const retirementApplyCheckboxTooltip = computed(() =>
  getHomeWageRetirementApplyCheckboxTooltip(getRetirementApplicableInput())
);

/** 비요양보호사용 입력 폼 (레거시 정책 기반) */
const staffForm = ref<HomeWageStaffInput>({
  hourlyWage: 0,
  workHours: 0,
  annualPay: 0,
  overtimeHours: 0,
  nightHours: 0,
  holidayHours: 0,
  minHourlyWage: 0,
});

const staffHourlyWageDisplay = ref('');
const staffWorkHoursDisplay = ref('');

/** 비요양보호사 계산 결과 */
const staffCalcResult = computed(() => {
  if (!staffForm.value.hourlyWage || !staffForm.value.workHours) {
    return null;
  }
  return calcHomeWageStaffTotal({
    ...staffForm.value,
    minHourlyWage: minHourlyWage.value || staffForm.value.minHourlyWage,
    yearMonth: yearMonth.value,
  });
});

const syncStaffFormFromPayroll = () => {
  staffForm.value.annualPay = Number(payrollForm.value.annual) || 0;
  staffForm.value.overtimeHours = Number(payrollForm.value.overtimeHours) || 0;
  staffForm.value.nightHours = Number(payrollForm.value.nightHours) || 0;
  staffForm.value.holidayHours = Number(payrollForm.value.holidayHours) || 0;
};

const syncStaffFormFromDisplay = () => {
  const parsedHourly = parseWageNumber(staffHourlyWageDisplay.value);
  if (parsedHourly > 0) {
    staffForm.value.hourlyWage = parsedHourly;
  }
  const parsedHours = parseHours(staffWorkHoursDisplay.value.replace(/시간/g, ''));
  if (parsedHours > 0) {
    staffForm.value.workHours = parsedHours;
  }
  syncStaffFormFromPayroll();
};

/**
 * 요양보호사 외 — 시급×시간 = 기본급 + 중식비(자동 20만 채우기)
 * 센터장 화면 form.basic도 차감값을 유지하고, 표시만 slip으로 풀을 보여 줌
 */
const applyStaffFundedAutoLunchCarve = () => {
  if (directorCanonicalActive.value) return;
  const hourly = Number(staffForm.value.hourlyWage) || 0;
  const hours = Number(staffForm.value.workHours) || 0;
  if (hourly <= 0 || hours <= 0) return;

  const pool = calcHomeWageStaffBasePay(hourly, hours, yearMonth.value);
  if (!nontaxSettings.value.lunchExpense) {
    autoBaseLunchRef.value = 0;
    payrollForm.value.lunch = 0;
    payrollForm.value.basic = pool;
    return;
  }

  let director = clampHomeWageLunchDirectorInput(payrollForm.value.lunchDirector);
  const currentAuto = Number(payrollForm.value.lunch) || 0;
  const currentBasic = Number(payrollForm.value.basic) || 0;

  // 레거시(5월): 기본급=풀 + 자동칸만 → 센터장 중식으로 승격 (중식 20만 유지)
  if (
    director <= 0
    && currentAuto > 0
    && Math.abs(currentBasic - pool) <= 1
  ) {
    director = clampHomeWageLunchDirectorInput(currentAuto);
  }

  const split = splitHomeWageStaffPoolByDirectorLunch(pool, director);

  // 채우기 목표(20만) — 요보 연차잉여 autoBase와 별개
  autoBaseLunchRef.value = HOME_WAGE_LUNCH_MONTHLY_LIMIT;
  payrollForm.value.lunch = split.lunch;
  payrollForm.value.basic = split.basic;
  payrollForm.value.lunchDirector = split.lunchDirector;
};

/** 레거시 정책 기반 급여 → payrollForm 반영 */
const syncStaffLinkedPayroll = () => {
  syncStaffFormFromDisplay();
  const result = staffCalcResult.value;
  if (!result) return;

  payrollForm.value.basic = result.basePay;
  payrollForm.value.weekend = 0;
  payrollForm.value.annual = result.annualLeavePay;
  payrollForm.value.severe = 0;

  payrollForm.value.overtime = result.overtimePay;
  payrollForm.value.night = result.nightPay;
  payrollForm.value.holiday = result.holidayPay;

  payrollForm.value.hour =
    staffForm.value.workHours > 0 ? String(staffForm.value.workHours) : '';
  applyStaffFundedAutoLunchCarve();
  recalcWageTotal();
  syncNonTaxTotal();
};

/** 비요양보호사 시급 기본값 = 해당 연도 최저시급 */
const applyStaffDefaultHourlyWage = () => {
  const min = minHourlyWage.value;
  if (min <= 0) return;
  if (staffForm.value.hourlyWage > 0) return;
  staffForm.value.hourlyWage = min;
  staffForm.value.minHourlyWage = min;
  staffHourlyWageDisplay.value = formatWageNumber(min);
};

/** 비요양보호사 폼 초기화 */
const resetStaffForm = () => {
  staffForm.value = {
    hourlyWage: 0,
    workHours: 0,
    annualPay: 0,
    overtimeHours: 0,
    nightHours: 0,
    holidayHours: 0,
    minHourlyWage: 0,
  };
  staffHourlyWageDisplay.value = '';
  staffWorkHoursDisplay.value = '';
  applyStaffDefaultHourlyWage();
};

const syncStaffFormFromLoadedData = (found: any, payCalcHourly: number) => {
  const workHours = parseHours(String(found.hour || ''));
  const lunch = Number(found.lunch) || 0;
  let hourlyWage = payCalcHourly;
  // 시급 역산: 미차감(basic≈풀)이면 basic/시간, 차감 저장이면 (basic+중식)/시간
  if (!hourlyWage && found.basic && workHours > 0) {
    const basic = Number(found.basic) || 0;
    const poolCandidate = lunch > 0 ? basic + lunch : basic;
    const fromBasic = Math.floor(basic / workHours);
    const fromPool = Math.floor(poolCandidate / workHours);
    hourlyWage =
      lunch > 0 && Math.abs(basic - fromBasic * workHours) <= 1
        ? fromBasic
        : fromPool || fromBasic;
  }

  staffForm.value = {
    hourlyWage,
    workHours,
    annualPay: found.annual || 0,
    overtimeHours: found.overtimeHours || 0,
    nightHours: found.nightHours || 0,
    holidayHours: found.holidayHours || 0,
    minHourlyWage: minHourlyWage.value || 0,
  };
  staffHourlyWageDisplay.value = hourlyWage > 0 ? formatWageNumber(hourlyWage) : '';
  staffWorkHoursDisplay.value = workHours > 0 ? formatHoursDisplay(workHours) : '';
  autoBaseLunchRef.value = lunch;
  payrollPremiumHoursDisplay.value = {
    overtimeHours: formatHomeWagePremiumHoursDisplay(found.overtimeHours),
    nightHours: formatHomeWagePremiumHoursDisplay(found.nightHours),
    holidayHours: formatHomeWagePremiumHoursDisplay(found.holidayHours),
  };
  applyStaffDefaultHourlyWage();
  // 자동중식 있으면 시급×시간 풀에서 차감 (관리자 저장 형태)
  // 센터장 화면은 slip으로 풀(시급×시간)만 보여 차감 사실을 숨김
  applyStaffFundedAutoLunchCarve();
  recalcWageTotal();
  syncNonTaxTotal();
};

/** 비요양보호사 근무정보 계산 (시급×근무시간 → 기본급 + 수당) */
const runAutoFillStaffPayCalc = (silent = false): boolean => {
  syncStaffFormFromDisplay();

  if (!staffForm.value.workHours || staffForm.value.workHours <= 0) {
    if (!silent) {
      alert('근무시간을 입력해 주세요.');
    }
    return false;
  }
  if (!staffForm.value.hourlyWage || staffForm.value.hourlyWage <= 0) {
    applyStaffDefaultHourlyWage();
    if (!staffForm.value.hourlyWage || staffForm.value.hourlyWage <= 0) {
      if (!silent) {
        alert('당해 연도 최저시급을 불러오지 못했습니다. 연도를 확인하거나 잠시 후 다시 시도해 주세요.');
      }
      return false;
    }
  }

  syncStaffLinkedPayroll();
  return !!staffCalcResult.value;
};

/** 연·야·휴 시간 표시용 */
const payrollPremiumHoursDisplay = ref<Record<HomeWagePremiumHoursKey, string>>({
  overtimeHours: '',
  nightHours: '',
  holidayHours: '',
});

const resetPayrollPremiumState = () => {
  payrollPremiumHoursDisplay.value = { overtimeHours: '', nightHours: '', holidayHours: '' };
  premiumHoursUserEdited.value = false;
  premiumManualAdjustEnabled.value = false;
  premiumManualPendingEnable.value = false;
  premiumAutoSnapshot.value = null;
  showPremiumAdjustModal.value = false;
  showHolidayPremiumDetailModal.value = false;
  resetDirectorPremiumPayOverride();
};

/** 연·야·휴 수동조정 — 체크 시 모달에서 금액 직접 입력 */
const premiumManualAdjustEnabled = ref(false);
/** 연·야·휴 시간 — 사용자가 직접 입력한 경우 청구(plan) 동기화 스킵 */
const premiumHoursUserEdited = ref(false);
const premiumManualPendingEnable = ref(false);
const showPremiumAdjustModal = ref(false);
const showHolidayPremiumDetailModal = ref(false);
const premiumAutoSnapshot = ref<HomeWageAutoPremiumSnapshot | null>(null);
const premiumManualBaseline = ref({
  annual: 0,
  basic: 0,
  autoAllocatedTotal: 0,
});

const getCurrentPremiumFullFromForm = () =>
  getHomeWagePremiumFullAmounts({
    overtime: Number(payrollForm.value.overtime) || 0,
    night: Number(payrollForm.value.night) || 0,
    holiday: Number(payrollForm.value.holiday) || 0,
    overtimeNontax: Number(payrollForm.value.overtimeNontax) || 0,
    nightNontax: Number(payrollForm.value.nightNontax) || 0,
    holidayNontax: Number(payrollForm.value.holidayNontax) || 0,
  });

const getCurrentPremiumFullTotalFromForm = () => {
  const full = getCurrentPremiumFullFromForm();
  return full.overtime + full.night + full.holiday;
};

const buildPremiumAutoSnapshot = (): HomeWageAutoPremiumSnapshot | null => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return null;
  const hourly = caregiverHourlyWage.value;
  if (hourly <= 0) return null;
  const mode = resolvePremiumPayMode();
  const nonSevereTarget = claimGrandTotal.value - severeSumFromClaim.value;
  const premiumPool =
    mode === 'pool'
      ? Math.max(0, nonSevereTarget - (Number(payrollForm.value.lunch) || 0))
      : undefined;
  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    Number(payrollForm.value.overtimeHours) || 0,
    Number(payrollForm.value.nightHours) || 0,
    Number(payrollForm.value.holidayHours) || 0,
    'admin'
  );
  return calcHomeWageAutoPremiumSnapshot({
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    mode,
    premiumPool,
    productionNontax: productionNontaxActive.value,
    yearMonth: yearMonth.value,
  });
};

const premiumAdjustModalRows = computed(() => {
  const snap = premiumAutoSnapshot.value;
  if (!snap) return [];
  const full = getHomeWagePremiumFullAmounts(snap.split);
  return [
    {
      key: 'overtime' as PremiumAdjustKey,
      label: '연장수당',
      hours: Number(payrollForm.value.overtimeHours) || 0,
      autoAmount: full.overtime,
    },
    {
      key: 'night' as PremiumAdjustKey,
      label: '야간수당',
      hours: Number(payrollForm.value.nightHours) || 0,
      autoAmount: full.night,
    },
    {
      key: 'holiday' as PremiumAdjustKey,
      label: '휴일수당',
      hours: Number(payrollForm.value.holidayHours) || 0,
      autoAmount: full.holiday,
    },
  ];
});

const premiumAdjustInitialManual = computed(() => {
  const full = getCurrentPremiumFullFromForm();
  return {
    overtime: full.overtime,
    night: full.night,
    holiday: full.holiday,
  };
});

const holidayPremiumDetailRows = computed(() =>
  buildHomeWageHolidayPremiumClaimRows(
    caregiverClaimRows.value,
    yearMonth.value,
    payrollViewMode.value
  )
);

/** 수동조정 해제 — 연·야·휴·연차/기본급을 enable 시점 자동값으로 복원 (4대보험 고정 해제와 동일 UX) */
const revertPremiumManualAdjust = () => {
  const wasEnabled = premiumManualAdjustEnabled.value;
  premiumManualPendingEnable.value = false;

  if (!wasEnabled) return;

  unlockPayrollLedgerForEdit();
  markDirty();

  const snap = buildPremiumAutoSnapshot();
  if (snap) {
    const reverted = revertHomeWageManualPremiumAdjustment({
      baselineAnnual: premiumManualBaseline.value.annual,
      baselineBasic: premiumManualBaseline.value.basic,
      isFamilyCare: isAllFamilyCareInClaim.value,
      autoSplit: snap.split,
    });
    applyHomeWagePremiumSplitToForm(payrollForm.value, reverted.split);
    payrollForm.value.annual = reverted.annual;
    payrollForm.value.basic = reverted.basic;
  } else if (isAllFamilyCareInClaim.value) {
    payrollForm.value.basic = premiumManualBaseline.value.basic;
    payrollForm.value.annual = 0;
  } else {
    payrollForm.value.annual = premiumManualBaseline.value.annual;
  }

  premiumManualAdjustEnabled.value = false;
  premiumAutoSnapshot.value = null;

  syncAfterPremiumSplitChange();
  syncHourlyLinkedPayroll();
  recalcWageTotal();
  syncNonTaxTotal();
  syncReferenceValues();
  runFullPayrollAutoCalc(true, true);
};

const onPremiumManualAdjustToggle = (enabled: boolean) => {
  if (enabled && alertDirectorPremiumBlocked()) return;
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    premiumManualAdjustEnabled.value = false;
    return;
  }
  if (!enabled) {
    revertPremiumManualAdjust();
    return;
  }
  if (premiumManualAdjustEnabled.value) {
    premiumAutoSnapshot.value = buildPremiumAutoSnapshot();
    showPremiumAdjustModal.value = true;
    return;
  }
  syncHourlyLinkedPayroll();
  const snap = buildPremiumAutoSnapshot();
  if (!snap) {
    alert('연장야간휴일 자동계산을 할 수 없습니다. 지급계산시급·청구내역·수급자 시급을 먼저 확인해 주세요.');
    premiumManualAdjustEnabled.value = false;
    return;
  }
  premiumAutoSnapshot.value = snap;
  premiumManualBaseline.value = {
    annual: Number(payrollForm.value.annual) || 0,
    basic: Number(payrollForm.value.basic) || 0,
    autoAllocatedTotal: snap.allocatedTotal,
  };
  premiumManualAdjustEnabled.value = true;
  premiumManualPendingEnable.value = true;
  showPremiumAdjustModal.value = true;
};

const closePremiumAdjustModal = () => {
  showPremiumAdjustModal.value = false;
  if (premiumManualPendingEnable.value) {
    premiumManualPendingEnable.value = false;
    premiumManualAdjustEnabled.value = false;
  }
};

const applyPremiumManualAdjust = (manual: Record<PremiumAdjustKey, number>) => {
  premiumManualPendingEnable.value = false;
  const result = applyHomeWageManualPremiumAdjustment({
    manualFull: manual,
    autoAllocatedTotal: premiumManualBaseline.value.autoAllocatedTotal,
    baselineAnnual: premiumManualBaseline.value.annual,
    baselineBasic: premiumManualBaseline.value.basic,
    isFamilyCare: isAllFamilyCareInClaim.value,
    productionNontax: productionNontaxActive.value,
  });
  applyHomeWagePremiumSplitToForm(payrollForm.value, result.split);
  if (isAllFamilyCareInClaim.value) {
    payrollForm.value.basic = result.basic;
  } else {
    payrollForm.value.annual = result.annual;
  }
  syncAfterPremiumSplitChange();
  showPremiumAdjustModal.value = false;
};

const getEmpName = (emp: any | null) => emp?.clci_name || emp?.ce_name || '-';
const getEmpJob = (emp: any | null) => emp?.clci_job_type || emp?.ce_position || '-';

const parseEmpBirthDate = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return null;
};

const getEmpBirth = (emp: any) =>
  parseEmpBirthDate(emp?.clci_birth_date ?? emp?.ce_birth_date ?? emp?.birth);

const getSelectedInsuranceBirth = () =>
  parseEmpBirthDate(payrollForm.value.birth)
  || getEmpBirth(selectedEmployee.value)
  || parseEmpBirthDate(loadedDirectorRow.value?.birth)
  || '';

const getEmpKey = (emp: any) => buildEmpMatchKey(getEmpName(emp), getEmpBirth(emp));

const isCaregiverEmployee = (emp: any | null) => {
  if (!emp) return false;
  return getEmpJob(emp).includes('요양보호사');
};

const isPayrollTotalField = (field: HomeWagePayrollFieldDef) =>
  !!field.isTotal || isHomeWagePayrollTotalKey(field.key);

const payrollTextValue = (key: HomeWagePayrollFieldKey) => {
  if (key === 'hour') {
    if (isCaregiverEmployee(selectedEmployee.value) && totalSugaHours.value > 0) {
      const live = String(Math.round(totalSugaHours.value * 10) / 10);
      return formatHoursDisplay(live) || live;
    }
    const formatted = formatHoursDisplay(payrollForm.value.hour);
    return formatted || String(payrollForm.value.hour ?? '');
  }
  return String(payrollForm.value[key] ?? '');
};

const nonTaxCashInputFields = HOME_WAGE_NON_TAX_CASH_FIELDS;

const nonTaxTotalFields = computed(() =>
  (HOME_WAGE_NON_TAX_CARD.fields ?? []).filter((f) => isPayrollTotalField(f))
);

/** 생산직 비과세 — 요양보호사·조리사·조리원 (레거시 UI·조건모달 표시용) */
const showProductionNontaxOption = computed(() =>
  isProductionWorkerPosition(getEmpJob(selectedEmployee.value))
);

/**
 * 연야휴 월 20만 비과세 — 관리자·회계에서 자동 적용
 * (생산직 체크·직종·통상임금·가족요양 여부와 무관. 센터장 시점은 합산만)
 */
const productionNontaxActive = computed(() => !isDirectorPayrollView.value);

/** 연·야·휴 과세/비과세 — DB 저장값 기준 */
const premiumTaxSplit = computed(() => ({
  overtimeTax: Number(payrollForm.value.overtime) || 0,
  nightTax: Number(payrollForm.value.night) || 0,
  holidayTax: Number(payrollForm.value.holiday) || 0,
  overtimeNontax: Number(payrollForm.value.overtimeNontax) || 0,
  nightNontax: Number(payrollForm.value.nightNontax) || 0,
  holidayNontax: Number(payrollForm.value.holidayNontax) || 0,
  totalNontax:
    (Number(payrollForm.value.overtimeNontax) || 0) +
    (Number(payrollForm.value.nightNontax) || 0) +
    (Number(payrollForm.value.holidayNontax) || 0),
}));

/**
 * 관리자 화면 — 연·야·휴 수동조정 시 자동 산출값 표시
 * (DB·센터장·명세서/임금대장 client 는 수동값 유지)
 */
const adminAutoPremiumTaxSplit = computed(() => {
  if (!premiumManualAdjustEnabled.value || isDirectorPayrollView.value) return null;
  const snap = buildPremiumAutoSnapshot();
  if (!snap) return null;
  const s = snap.split;
  return {
    overtimeTax: s.overtime,
    nightTax: s.night,
    holidayTax: s.holiday,
    overtimeNontax: s.overtimeNontax,
    nightNontax: s.nightNontax,
    holidayNontax: s.holidayNontax,
    totalNontax: s.overtimeNontax + s.nightNontax + s.holidayNontax,
  };
});

const effectivePremiumTaxSplit = computed(
  () => adminAutoPremiumTaxSplit.value ?? premiumTaxSplit.value
);

const savedWageCount = computed(() => listWageByEmpKey.value.size);

const taxablePremiumTotal = computed(
  () =>
    effectivePremiumTaxSplit.value.overtimeTax
    + effectivePremiumTaxSplit.value.nightTax
    + effectivePremiumTaxSplit.value.holidayTax
);

const nontaxPremiumTotal = computed(() => effectivePremiumTaxSplit.value.totalNontax);

/** 과세 카드 접기 행 합계 — 센터장 시점은 과세+비과세 합산(행 표시와 동일) */
const taxablePremiumDisplaySum = computed(() =>
  isDirectorPayrollView.value
    ? taxablePremiumTotal.value + nontaxPremiumTotal.value
    : taxablePremiumTotal.value
);

/** 생산직 비과세 20만 초과 — 과세·비과세 양쪽에 연장야간휴일 표시 */
const isPremiumProductionSplit = computed(() => {
  if (!productionNontaxActive.value) return false;
  const full = getCurrentPremiumFullFromForm();
  return isHomeWagePremiumProductionTaxNontaxSplit(
    full.overtime,
    full.night,
    full.holiday,
    true
  );
});

/** 과세 수당 카드 — 연장야간휴일 (생산직 OFF 또는 20만 초과 분할 시, 센터장 시점은 시간·금액 있으면 표시) */
const hasCaregiverPremiumHoursOrAmounts = computed(() => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return false;
  if (getCurrentPremiumFullTotalFromForm() > 0) return true;
  const f = payrollForm.value;
  const formHours =
    (Number(f.overtimeHours) || 0)
    + (Number(f.nightHours) || 0)
    + (Number(f.holidayHours) || 0);
  if (formHours > 0) return true;
  if (!caregiverClaimRows.value.length) return false;
  const fromClaims = calcPremiumHoursFromClaims(
    caregiverClaimRows.value,
    yearMonth.value,
    resolveCaregiverPayrollViewMode()
  );
  return (
    (fromClaims.overtimeHours || 0)
    + (fromClaims.nightHours || 0)
    + (fromClaims.holidayHours || 0)
  ) > 0;
});

const showCaregiverTaxablePremiumSection = computed(() => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return false;
  if (isDirectorPayrollView.value) return false;
  if (!hasCaregiverPremiumHoursOrAmounts.value) return false;
  if (!productionNontaxActive.value) return true;
  return isPremiumProductionSplit.value;
});

/** 비과세 수당 카드 — 연장야간휴일 (생산직 ON일 때, 센터장 시점 숨김) */
const showCaregiverNontaxPremiumSection = computed(() => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return false;
  if (isDirectorPayrollView.value) return false;
  if (!hasCaregiverPremiumHoursOrAmounts.value) return false;
  return productionNontaxActive.value;
});

const productionNontaxValidation = computed(() =>
  validateProductionWorkerNontax(
    getEmpJob(selectedEmployee.value),
    Number(payrollForm.value.wageTotal) || 0
  )
);

const taxablePremiumExpanded = ref(false);
const nontaxPremiumExpanded = ref(false);

const formatBirthDisplay = (birth: string | null | undefined): string => {
  if (!birth) return '-';
  const s = String(birth).substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '-';
  return s.replace(/-/g, '.');
};

const selectedEmpKey = computed(() =>
  selectedEmployee.value ? getEmpKey(selectedEmployee.value) : ''
);

const {
  summaryRows: claimSummaryRows,
  grandTotal: claimGrandTotal,
  caregiverHourlyWage,
  setCaregiverHourlyWage,
  updateRowHourly,
  updateRowSugaHours,
  updateRowSevereFee,
  rebuildStructureOnly,
  rebuildSummaryRows,
  ensureCaregiverHourlyAtLeastMin,
  resetStoredMaps,
  getMapsForSave,
  loadMapsFromDb,
} = useHomeWageClaimSummary({
  claimRows: caregiverClaimRowsForAgg,
  memberCode,
  yearMonth,
  empKey: selectedEmpKey,
  minHourlyWage,
});

/** 청구 집계 + DB cwhd_svc 병합 — 1회수가·수가시간은 청구 기준, 시급·횟수는 저장값, 합계는 재계산 */
const displayClaimSummaryRows = computed(() => {
  if (payrollDbLock.value && dbSavedSummaryRows.value.length > 0) {
    return mergeLiveClaimSummaryWithDbSnapshot(
      claimSummaryRows.value,
      dbSavedSummaryRows.value
    );
  }
  return claimSummaryRows.value;
});

const displayClaimGrandTotal = computed(() =>
  displayClaimSummaryRows.value.reduce((sum, row) => sum + (row.totalAmount || 0), 0)
);

/** 수급자별 표·청구내역 모달 — 중증수급자가산 제외 행 */
const displayClaimSummaryRowsForTable = computed(() =>
  filterNonSevereSummaryRows(displayClaimSummaryRows.value)
);

/** 수급자별 표 하단 합계 — 중증수급자가산 제외 (중증은 별도 패널) */
const displayClaimGrandTotalForTable = computed(() =>
  displayClaimSummaryRowsForTable.value.reduce(
    (sum, row) => sum + (row.totalAmount || 0),
    0
  )
);

/** 수급자별 표 하단 — 중증수급자가산 별도 확인·수가 편집 */
const severeSummaryRowsForTable = computed(() =>
  displayClaimSummaryRows.value.filter((r) => r.isSevereRecipientSurcharge)
);

/** 수급자별 표 상단 — 총 서비스횟수 (중증수급자가산 제외) */
const displayClaimTotalServiceCount = computed(() =>
  calcTotalServiceCountFromSummary(claimSummaryRows.value)
);

/** 중증수급자가산 서비스횟수 (별도 확인용) */
const displaySevereServiceCount = computed(() =>
  calcSevereServiceCountFromSummary(displayClaimSummaryRows.value)
);

/** 청구내역 모달 — 일반 청구 (중증수급자가산 제외) */
const caregiverClaimRowsNonSevereFiltered = computed(() =>
  filterNonSevereClaimRows(caregiverClaimRows.value)
);

/** 청구내역 모달 list 탭 — 기본 일자·시간순 (빈 건수·달력 탭 등) */
const caregiverClaimRowsNonSevere = computed(() =>
  sortClaimRowsByDateTime(caregiverClaimRowsNonSevereFiltered.value)
);

const compareClaimRowsByDateTime = (a: HomeWageClaimRow, b: HomeWageClaimRow): number => {
  const da = String(a.date || '').substring(0, 10);
  const db = String(b.date || '').substring(0, 10);
  if (da !== db) return da.localeCompare(db);
  const parseTime = (t?: string | null) => {
    if (!t) return 0;
    const parts = String(t).split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  };
  const ta = parseTime(a.plan_start_time);
  const tb = parseTime(b.plan_start_time);
  if (ta !== tb) return ta - tb;
  return parseTime(a.plan_end_time) - parseTime(b.plan_end_time);
};

const getClaimModalPremiumSortScore = (row: HomeWageClaimRow): number => {
  const info = getRowPremiumInfo(row);
  if (!info.isVisitFee) return -1;
  let score = 0;
  if (info.isOvertimeRow) score += 1000;
  score += info.nightHours * 100;
  if (info.isHolidayWork) score += 10;
  return score;
};

/** 청구내역 모달 list 탭 — 사용자 정렬 */
const sortedClaimModalListRows = computed(() => {
  const list = [...caregiverClaimRowsNonSevereFiltered.value];
  const dir = claimModalSortOrder.value === 'asc' ? 1 : -1;
  const key = claimModalSortKey.value;

  list.sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'date':
        cmp = compareClaimRowsByDateTime(a, b);
        break;
      case 'recipient': {
        const nameA = String(a.recipient_name || '');
        const nameB = String(b.recipient_name || '');
        cmp = nameA.localeCompare(nameB, 'ko');
        if (cmp === 0) {
          cmp = String(a.cert_number || '').localeCompare(String(b.cert_number || ''), 'ko');
        }
        break;
      }
      case 'planStart':
        cmp = compareClaimRowsByDateTime(a, b);
        break;
      case 'duration':
        cmp = calcPlanDurationHours(a.plan_start_time, a.plan_end_time)
          - calcPlanDurationHours(b.plan_start_time, b.plan_end_time);
        if (cmp === 0) cmp = compareClaimRowsByDateTime(a, b);
        break;
      case 'feeName':
        cmp = String(a.fee_name || '').localeCompare(String(b.fee_name || ''), 'ko');
        if (cmp === 0) cmp = compareClaimRowsByDateTime(a, b);
        break;
      case 'feeAmount': {
        const amtA = Number(a.fee_amount) || 0;
        const amtB = Number(b.fee_amount) || 0;
        cmp = amtA - amtB;
        if (cmp === 0) cmp = compareClaimRowsByDateTime(a, b);
        break;
      }
      case 'premium':
        cmp = getClaimModalPremiumSortScore(a) - getClaimModalPremiumSortScore(b);
        if (cmp === 0) cmp = compareClaimRowsByDateTime(a, b);
        break;
      case 'lunch': {
        const lunchA = isLunchIncludedRow(a) ? 1 : 0;
        const lunchB = isLunchIncludedRow(b) ? 1 : 0;
        cmp = lunchA - lunchB;
        if (cmp === 0) cmp = compareClaimRowsByDateTime(a, b);
        break;
      }
      default:
        cmp = compareClaimRowsByDateTime(a, b);
    }
    return cmp * dir;
  });

  return list;
});

const toggleClaimModalSort = (key: ClaimModalSortKey) => {
  if (claimModalSortKey.value === key) {
    claimModalSortOrder.value = claimModalSortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    claimModalSortKey.value = key;
    claimModalSortOrder.value = 'asc';
  }
};

const getClaimModalSortIcon = (key: ClaimModalSortKey) => {
  if (claimModalSortKey.value !== key) return 'mdi-unfold-more-horizontal';
  return claimModalSortOrder.value === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down';
};

/** 청구내역 모달 — 중증수급자가산 (조인됨 + 미조인 전용) */
const caregiverClaimRowsSevere = computed(() =>
  sortClaimRowsByDateTime(
    deduplicateClaimRows([
      ...filterSevereClaimRows(caregiverClaimRows.value),
      ...caregiverUnjoinedSevereRows.value,
    ])
  )
);

/** 펼침 뷰 — 사원별 수급자 표 (중증수급자가산 제외) */
const getEmpSummaryRowsForTable = (empKey: string) =>
  filterNonSevereSummaryRows(allEmpSummaryRows.value.get(empKey) || []);

/** 청구 원본 수가금액(clcd_fee) 합계 — 기관수익 산출용 (조인 + 미조인 중증 포함) */
const claimSugaMoneyTotal = computed(() =>
  isCaregiverEmployee(selectedEmployee.value)
    ? calcClaimFeeAmountTotal(caregiverClaimRowsForAgg.value)
    : 0
);

const {
  weekdayLabels: workCalendarWeekdayLabels,
  calendarWeeks: workCalendarWeeks,
  summary: workCalendarSummary,
  recipientSummaries: workCalendarRecipientSummaries,
  calendarMonthLabel: workCalendarMonthLabel,
  premiumSummary: workCalendarPremiumSummary,
  overtimeRowKeys: claimOvertimeRowKeys,
  formatWorkCalendarDuration,
} = useHomeWageWorkCalendar({
  claimRows: caregiverClaimRows,
  year: selectedYear,
  month: selectedMonth,
  holidayMap,
  viewMode: payrollViewMode,
});

/** 청구내역 모달 — 행별 연·야·휴 표시 */
const getRowPremiumInfo = (row: HomeWageClaimRow) =>
  getClaimRowPremiumInfo(
    row,
    claimOvertimeRowKeys.value,
    yearMonth.value,
    resolveCaregiverPayrollViewMode()
  );

const formatClaimPremiumHours = (hours: number) =>
  hours > 0 ? `${hours}h` : '';

const loadHolidays = async () => {
  holidayLoading.value = true;
  try {
    const response = await homeWageApi.getHolidays({ year: selectedYear.value });
    if (!response.data?.success) {
      throw new Error(response.data?.message || '공휴일 조회 실패');
    }
    const map: Record<string, string> = {};
    for (const item of response.data.data || []) {
      const date = String(item.date || '').substring(0, 10);
      if (date) map[date] = String(item.name || '').trim();
    }
    holidayMap.value = map;
    holidaysLoadedYear.value = selectedYear.value;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    console.error('공휴일 조회 오류:', error);
    holidayMap.value = {};
    holidaysLoadedYear.value = null;
    if (showClaimDetailModal.value && claimModalTab.value === 'calendar') {
      alert(`공휴일 조회 실패: ${message}`);
    }
  } finally {
    holidayLoading.value = false;
  }
};

/** 사원 상세·근무달력용 공휴일 선로드 (연·야·휴 휴일시간은 수가명 기준 — 공휴일 테이블과 무관) */
const ensureHolidaysLoaded = async (force = false): Promise<void> => {
  const year = selectedYear.value;
  if (!force && holidaysLoadedYear.value === year && Object.keys(holidayMap.value).length > 0) {
    return;
  }
  if (holidaysLoadingPromise) {
    await holidaysLoadingPromise;
    if (!force && holidaysLoadedYear.value === year && Object.keys(holidayMap.value).length > 0) {
      return;
    }
  }
  holidaysLoadingPromise = loadHolidays();
  try {
    await holidaysLoadingPromise;
  } finally {
    holidaysLoadingPromise = null;
  }
};

watch(showClaimDetailModal, (open) => {
  if (open) {
    void ensureHolidaysLoaded();
  } else {
    claimModalTab.value = 'list';
  }
});

watch(selectedYear, () => {
  holidaysLoadedYear.value = null;
  holidayMap.value = {};
  if (showClaimDetailModal.value) {
    void ensureHolidaysLoaded(true);
  } else if (
    mainView.value === 'detail' &&
    isCaregiverEmployee(selectedEmployee.value) &&
    caregiverClaimRows.value.length > 0
  ) {
    void refreshPremiumHoursFromClaims();
  }
});

watch(claimModalTab, (tab) => {
  if (tab === 'unjoined' && !canViewUnjoinedClaimTab.value) {
    claimModalTab.value = 'list';
    return;
  }
  if (tab === 'calendar' && showClaimDetailModal.value && Object.keys(holidayMap.value).length === 0) {
    void ensureHolidaysLoaded();
  }
});

/** 시급·중증가산 수가 입력 표시용 (콤마) */
const hourlyWageDisplay = ref<Record<string, string>>({});
const severeFeeDisplay = ref<Record<string, string>>({});
const sugaHoursDisplay = ref<Record<string, string>>({});
const caregiverHourlyWageDisplay = ref('');

/** 입력 중인 수급자별 시급·중증수가 → 내부 maps 반영 (blur 전 탭 이동 대비) */
const flushRecipientHourlyDisplayToMaps = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  for (const [groupKey, display] of Object.entries(hourlyWageDisplay.value)) {
    const parsed = parseWageNumber(display);
    if (parsed > 0) {
      updateRowHourly(groupKey, parsed);
    }
  }
  for (const [groupKey, display] of Object.entries(severeFeeDisplay.value)) {
    const parsed = parseWageNumber(display);
    if (parsed > 0) {
      updateRowSevereFee(groupKey, parsed);
    }
  }
};

/** DB cwhd_svc 스냅샷 → live maps (lock 해제 전 필수 — 중증수급자가산·시급 유지) */
const syncLiveRecipientMapsFromDbSnapshot = () => {
  if (!caregiverClaimRowsForAgg.value.length) return;

  // 미조인 중증만 있는 경우 등 — 집계 행 선생성
  if (!claimSummaryRows.value.length) {
    rebuildSummaryRows();
  }

  const savedMaps = getMapsForSave();
  const hourlyMap: Record<string, number> = { ...(savedMaps.hourlyByGroup ?? {}) };
  const severeMap: Record<string, number> = { ...(savedMaps.severeFeeByGroup ?? {}) };

  if (dbSavedSummaryRows.value.length > 0) {
    const merged = mergeLiveClaimSummaryWithDbSnapshot(
      claimSummaryRows.value,
      dbSavedSummaryRows.value
    );
    for (const row of merged) {
      if (row.isSevereRecipientSurcharge) {
        const fee =
          (row.feeAmountPerVisit ?? 0) > 0
            ? row.feeAmountPerVisit
            : (row.serviceCount ?? 0) > 0 && (row.totalAmount ?? 0) > 0
              ? Math.round(row.totalAmount / row.serviceCount)
              : 0;
        if (fee > 0) {
          severeMap[row.groupKey] = fee;
        }
        continue;
      }
      if ((row.hourlyWage ?? 0) > 0) {
        hourlyMap[row.groupKey] = row.hourlyWage;
      }
    }

    // groupKey 변경 시 DB 스냅샷 중증수가를 live 행에 매칭
    for (const snapRow of dbSavedSummaryRows.value) {
      if (!snapRow.isSevereRecipientSurcharge || (snapRow.feeAmountPerVisit ?? 0) <= 0) continue;
      const liveRow = claimSummaryRows.value.find(
        (r) =>
          r.isSevereRecipientSurcharge
          && recipientSummaryMatchKey(r) === recipientSummaryMatchKey(snapRow)
      );
      if (liveRow) {
        severeMap[liveRow.groupKey] = snapRow.feeAmountPerVisit;
      }
    }
  }

  // 저장값 없으면 청구 clcd_fee 기본값
  for (const row of claimSummaryRows.value) {
    if (!row.isSevereRecipientSurcharge) continue;
    const existing = severeMap[row.groupKey];
    if ((!existing || existing <= 0) && (row.feeAmountPerVisit ?? 0) > 0) {
      severeMap[row.groupKey] = row.feeAmountPerVisit;
    }
  }

  // 미조인 중증수급자가산 — raw clcd_fee → maps (집계 행 생성 전에도 반영)
  for (const row of caregiverUnjoinedSevereRows.value) {
    const fee = Number(row.fee_amount) || 0;
    if (fee <= 0) continue;
    const groupKey = buildRecipientFeeGroupKey(row);
    if (!severeMap[groupKey] || severeMap[groupKey] <= 0) {
      severeMap[groupKey] = fee;
    }
  }

  loadMapsFromDb({
    hourlyByGroup: Object.keys(hourlyMap).length > 0 ? hourlyMap : undefined,
    severeFeeByGroup: Object.keys(severeMap).length > 0 ? severeMap : undefined,
  });
};

/** 미조인 중증수급자가산 clcd_fee → 수급자별 maps·집계 합계 동기화 */
const syncUnjoinedSevereFeesFromClaims = () => {
  for (const row of caregiverUnjoinedSevereRows.value) {
    const fee = Number(row.fee_amount) || 0;
    if (fee <= 0) continue;
    const groupKey = buildRecipientFeeGroupKey(row);
    updateRowSevereFee(groupKey, fee);
  }
};

/**
 * 저장 급여대장 lock 해제 — 수급자별 시급 maps·표시값을 먼저 동기화
 * (lock 중에는 displayClaimSummaryRows가 DB 병합값을 보여주므로, 해제만 하면 빈칸이 됨)
 */
const unlockPayrollLedgerForEdit = () => {
  if (!payrollDbLock.value) return;
  flushRecipientHourlyDisplayToMaps();
  syncLiveRecipientMapsFromDbSnapshot();
  payrollDbLock.value = false;
  syncRecipientSettingsDisplay();
  syncSeverePayFromClaim();
};

/** summaryRows(마이그레이션된 groupKey) 기준으로 표시용 시급·수가 맵 동기화 */
const syncRecipientSettingsDisplay = () => {
  const hourlyMap: Record<string, string> = { ...hourlyWageDisplay.value };
  const severeMap: Record<string, string> = { ...severeFeeDisplay.value };
  for (const row of displayClaimSummaryRows.value) {
    if (row.isSevereRecipientSurcharge) {
      let fee = row.feeAmountPerVisit ?? 0;
      if (fee <= 0 && (row.serviceCount ?? 0) > 0 && (row.totalAmount ?? 0) > 0) {
        fee = Math.round(row.totalAmount / row.serviceCount);
      }
      if (fee > 0) {
        severeMap[row.groupKey] = formatWageNumber(fee);
      }
      continue;
    }
    const wage = row.hourlyWage ?? 0;
    if (wage > 0) {
      hourlyMap[row.groupKey] = formatWageNumber(wage);
    }
  }
  hourlyWageDisplay.value = hourlyMap;
  severeFeeDisplay.value = severeMap;
};

const getRecipientHourlyDisplay = (row: { groupKey: string; hourlyWage?: number }) => {
  const fromDisplay = hourlyWageDisplay.value[row.groupKey];
  if (fromDisplay) return fromDisplay;
  const wage = row.hourlyWage ?? 0;
  return wage > 0 ? formatWageNumber(wage) : '';
};
const getEmpJoinDate = (emp: any | null) => {
  if (!emp) return null;
  const raw = emp.ce_join_date || emp.clci_work_start_date;
  return parseJoinDate(raw);
};

/** 방문요양 — 나이 면제 (만 65세 이상 고용보험 0, 입사일 무관) */
const resolveSelectedHomeWageAgeInsurance = (
  birthOverride?: string,
  joinDateOverride?: string | null
) => {
  const birth =
    birthOverride ||
    getSelectedInsuranceBirth();
  const joinDate =
    joinDateOverride !== undefined
      ? joinDateOverride
      : getEmpJoinDate(selectedEmployee.value);
  return resolveHomeWageAgeInsurance(
    birth,
    selectedYear.value,
    selectedMonth.value,
    joinDate
  );
};

/** 선택 연월 말일 기준 근로개월수 */
const getEmpTenureMonths = (emp: any | null): number | null => {
  const join = getEmpJoinDate(emp);
  if (!join) return null;
  return calcTenureMonths(join, selectedYear.value, selectedMonth.value);
};

/** 상세 툴바 칩용 (직종 오른쪽) */
const formatEmpTenureChip = (emp: any | null) => {
  const months = getEmpTenureMonths(emp);
  if (months === null) return '-';
  return `${months}개월`;
};

/** 만나이 계산 */
const getEmpAge = (emp: any | null): number | null => {
  const birth = getEmpBirth(emp);
  if (!birth) return null;
  const birthDate = new Date(birth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
};

/** 만나이 칩 표시 */
const formatEmpAgeChip = (emp: any | null) => {
  const age = getEmpAge(emp);
  if (age === null) return null;
  return `만${age}세`;
};

const selectedEmpJoinDate = computed(() => getEmpJoinDate(selectedEmployee.value));

/** 총 수가시간 — 청구 집계 우선 (DB cwhd_hour는 레거시 오조인 과다값일 수 있음) */
const totalSugaHours = computed(() => {
  if (isCaregiverEmployee(selectedEmployee.value) && claimSummaryRows.value.length > 0) {
    return calcTotalSugaHoursFromSummary(claimSummaryRows.value);
  }
  if (payrollDbLock.value) {
    const fromDb = parseHours(String(payrollForm.value.hour || ''));
    if (fromDb > 0) return fromDb;
  }
  return calcTotalSugaHoursFromSummary(claimSummaryRows.value);
});

/** 저장·일괄처리 — 청구 집계 hour 우선, 없으면 DB/폼 fallback */
const resolveLiveCaregiverSugaHours = (empKey?: string, fallback?: string | number): number => {
  if (empKey) {
    const summaryRows = allEmpSummaryRows.value.get(empKey);
    if (summaryRows?.length) {
      const live = calcTotalSugaHoursFromSummary(summaryRows);
      if (live > 0) return live;
    }
  }
  if (
    selectedEmployee.value &&
    empKey &&
    getEmpKey(selectedEmployee.value) === empKey &&
    claimSummaryRows.value.length > 0
  ) {
    const live = calcTotalSugaHoursFromSummary(claimSummaryRows.value);
    if (live > 0) return live;
  }
  return parseHours(String(fallback ?? '')) || Number(fallback) || 0;
};

/** 상단 툴바·수급자별 시급 — 당월 근로일·근로시간 */
const formatEmpWorkHoursChip = (hours: number) => {
  const formatted = formatDurationHoursMinutes(hours);
  if (!formatted) return '';
  return `근무시간 ${formatted}`;
};

const parseClaimRowDateKey = (date?: string | null): string | null => {
  if (!date) return null;
  const s = String(date).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const getEmpWorkMeta = (emp: any | null) => {
  if (!emp || !isCaregiverEmployee(emp)) {
    return { workDays: 0, workHoursLabel: '', showWorkDays: false, showWorkHours: false };
  }

  const empKey = getEmpKey(emp);
  const isCurrentSelected =
    !!selectedEmployee.value && getEmpKey(selectedEmployee.value) === empKey;

  let workDays = 0;
  let workHours = 0;

  if (isCurrentSelected) {
    workDays =
      workCalendarSummary.value.workDayCount ||
      parseInt(String(payrollForm.value.days || ''), 10) ||
      0;
    workHours = totalSugaHours.value || workCalendarSummary.value.totalHours || 0;
  } else {
    const claims = allEmpClaimData.value.get(empKey) || [];
    workDays = calcWorkDaysFromClaimRows(claims);
    workHours = calcTotalSugaHoursFromSummary(
      filterNonSevereSummaryRows(allEmpSummaryRows.value.get(empKey) || [])
    );
  }

  const workHoursLabel = formatEmpWorkHoursChip(workHours);
  return {
    workDays,
    workHoursLabel,
    showWorkDays: workDays > 0,
    showWorkHours: workHoursLabel.length > 0,
  };
};

const selectedEmpWorkMeta = computed(() => getEmpWorkMeta(selectedEmployee.value));

/** 퇴직금 60h 판단용 월 근무시간 (요양: 수가시간 합계 / 비요양: 근무시간) */
const getRetirementWorkHours = (): number => {
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    return (
      staffForm.value.workHours ||
      parseHours(String(payrollForm.value.hour || '')) ||
      0
    );
  }
  return totalSugaHours.value;
};

/** 퇴직금 산정 가능 여부 입력 (60h·대표자) */
const getRetirementApplicableInput = () => ({
  workHours: getRetirementWorkHours(),
  isRepresentative: isRepresentative.value,
});

/** 퇴직금 미적용 경고 (60h 미만·대표자) */
const retirementWarningMessage = computed(() => {
  const result = resolveHomeWageRetirementApplicable(getRetirementApplicableInput());
  return result.message || '';
});

/** 퇴직금(c_out) 입력 — 적립 불가 시 금액 대신 안내 문구 표시 */
const showRetirementBlockedInInput = computed(() => {
  if (applyRetirement.value) return false;
  return !resolveHomeWageRetirementApplicable(getRetirementApplicableInput()).applicable;
});

const formatRetirementInputDisplay = computed(() => {
  if (showRetirementBlockedInInput.value) return '퇴직금적립불가';
  return formatPayrollMoneyField('c_out');
});

/** 요양보호사 + 퇴직금 고정 — 메인 입력은 표시 전용(클릭 시 모달) */
const isRetirementCaregiverFixedInput = computed(
  () =>
    applyRetirement.value &&
    isCaregiverEmployee(selectedEmployee.value) &&
    !showRetirementBlockedInInput.value
);

/** 청구·지급금 기준 B1 일반 / B2 목욕 / 가족 지급금액 배분 */
const getRetirementPaySplitLive = () => {
  return calcHomeWageRetirementPaySplit({
    summaryRows: displayClaimSummaryRows.value,
    grossPay: getHomeWageLedgerGrossPay(payrollForm.value),
    severePay: Number(payrollForm.value.severe) || 0,
  });
};

const retirementModalVisitCarePay = computed(() => getRetirementPaySplitLive().visitCarePay);
const retirementModalVisitBathPay = computed(() => getRetirementPaySplitLive().visitBathPay);
const retirementModalFamilyPay = computed(() => getRetirementPaySplitLive().familyPay);

const retirementModalHasSourceSelection = computed(
  () =>
    (retirementModalVisitCarePay.value > 0 && retirementModalUseVisitCare.value) ||
    (retirementModalVisitBathPay.value > 0 && retirementModalUseVisitBath.value) ||
    (retirementModalFamilyPay.value > 0 && retirementModalUseFamily.value)
);

const hasRetirementSourceFlags = (
  src: Pick<typeof retirementSource.value, 'useVisitCare' | 'useVisitBath' | 'useFamily'>
) => src.useVisitCare || src.useVisitBath || src.useFamily;

/** 퇴직금 식대 차감 — 센터장 시점 form.lunch=0 이어도 자동중식(ref) 사용 */
const resolveRetirementLunchPartsForCalc = () => {
  const lunchDirector = Number(payrollForm.value.lunchDirector) || 0;
  if (isCaregiverEmployee(selectedEmployee.value)) {
    return {
      lunch: resolveCaregiverFundedAutoLunchForPay(),
      lunchDirector,
    };
  }
  const lunch = Math.max(
    Number(payrollForm.value.lunch) || 0,
    Number(autoBaseLunchRef.value) || 0,
    Number(dbSavedAutoLunchRef.value) || 0
  );
  return { lunch, lunchDirector };
};

const retirementModalPreviewAmount = computed(() => {
  const lunchParts = resolveRetirementLunchPartsForCalc();
  return calcHomeWageInstitutionRetirementFromSource({
    useVisitCare: retirementModalUseVisitCare.value && retirementModalVisitCarePay.value > 0,
    useVisitBath: retirementModalUseVisitBath.value && retirementModalVisitBathPay.value > 0,
    useFamily: retirementModalUseFamily.value && retirementModalFamilyPay.value > 0,
    visitCarePay: retirementModalVisitCarePay.value,
    visitBathPay: retirementModalVisitBathPay.value,
    familyPay: retirementModalFamilyPay.value,
    manualAmount: 0,
    lunchExpense: nontaxSettings.value.lunchExpense,
    lunch: lunchParts.lunch,
    lunchDirector: lunchParts.lunchDirector,
  });
});

const buildRetirementSourceSelection = (
  overrides: Partial<{
    useVisitCare: boolean;
    useVisitBath: boolean;
    useFamily: boolean;
    visitCarePay: number;
    visitBathPay: number;
    familyPay: number;
    manualAmount: number;
  }> = {}
) => {
  const split = getRetirementPaySplitLive();
  const lunchParts = resolveRetirementLunchPartsForCalc();
  return {
    useVisitCare: overrides.useVisitCare ?? retirementSource.value.useVisitCare,
    useVisitBath: overrides.useVisitBath ?? retirementSource.value.useVisitBath,
    useFamily: overrides.useFamily ?? retirementSource.value.useFamily,
    visitCarePay: overrides.visitCarePay ?? split.visitCarePay,
    visitBathPay: overrides.visitBathPay ?? split.visitBathPay,
    familyPay: overrides.familyPay ?? split.familyPay,
    manualAmount: overrides.manualAmount ?? (Number(payrollForm.value.c_out) || 0),
    lunchExpense: nontaxSettings.value.lunchExpense,
    lunch: lunchParts.lunch,
    lunchDirector: lunchParts.lunchDirector,
  };
};

const syncRetirementFromSourceSelection = () => {
  if (!applyRetirement.value) return;
  if (!isCaregiverEmployee(selectedEmployee.value)) return;

  const split = getRetirementPaySplitLive();
  retirementSource.value.visitCarePay = split.visitCarePay;
  retirementSource.value.visitBathPay = split.visitBathPay;
  retirementSource.value.familyPay = split.familyPay;

  if (!hasRetirementSourceFlags(retirementSource.value)) {
    return;
  }

  payrollForm.value.c_out = calcHomeWageInstitutionRetirementFromSource(
    buildRetirementSourceSelection({
      visitCarePay: split.visitCarePay,
      visitBathPay: split.visitBathPay,
      familyPay: split.familyPay,
    })
  );
};

const loadRetirementSourceFromRow = (row: any) => {
  retirementSource.value.useVisitCare = parseHomeWageApplyRetirementFlag(
    row?.retirementUseVisitCare ?? row?.retirementUseGeneral
  );
  retirementSource.value.useVisitBath = parseHomeWageApplyRetirementFlag(row?.retirementUseVisitBath);
  retirementSource.value.useFamily = parseHomeWageApplyRetirementFlag(row?.retirementUseFamily);
  retirementSource.value.visitCarePay =
    Number(row?.retirementVisitCarePay ?? row?.retirementGeneralPay) || 0;
  retirementSource.value.visitBathPay = Number(row?.retirementVisitBathPay) || 0;
  retirementSource.value.familyPay = Number(row?.retirementFamilyPay) || 0;
};

const resetRetirementSource = () => {
  retirementSource.value = {
    useVisitCare: false,
    useVisitBath: false,
    useFamily: false,
    visitCarePay: 0,
    visitBathPay: 0,
    familyPay: 0,
  };
};

const openRetirementSourceModal = () => {
  if (!applyRetirement.value || !isCaregiverEmployee(selectedEmployee.value)) return;
  if (isWageReadOnly.value || showRetirementBlockedInInput.value) return;

  const split = getRetirementPaySplitLive();
  retirementModalUseVisitCare.value =
    retirementSource.value.useVisitCare && split.visitCarePay > 0;
  retirementModalUseVisitBath.value =
    retirementSource.value.useVisitBath && split.visitBathPay > 0;
  retirementModalUseFamily.value =
    retirementSource.value.useFamily && split.familyPay > 0;

  if (
    !retirementModalUseVisitCare.value &&
    !retirementModalUseVisitBath.value &&
    !retirementModalUseFamily.value
  ) {
    retirementModalManualDisplay.value = formatPayrollMoneyField('c_out');
  } else {
    retirementModalManualDisplay.value = '';
  }

  showRetirementSourceModal.value = true;
};

const closeRetirementSourceModal = () => {
  showRetirementSourceModal.value = false;
};

const onRetirementModalManualInput = (event: Event) => {
  const raw = (event.target as HTMLInputElement).value.replace(/[^\d]/g, '');
  retirementModalManualDisplay.value = raw ? formatWageNumber(Number(raw)) : '';
};

const applyRetirementSourceModal = () => {
  unlockPayrollLedgerForEdit();
  markDirty();

  const split = getRetirementPaySplitLive();
  const useVisitCare = retirementModalUseVisitCare.value && split.visitCarePay > 0;
  const useVisitBath = retirementModalUseVisitBath.value && split.visitBathPay > 0;
  const useFamily = retirementModalUseFamily.value && split.familyPay > 0;

  retirementSource.value.useVisitCare = useVisitCare;
  retirementSource.value.useVisitBath = useVisitBath;
  retirementSource.value.useFamily = useFamily;
  retirementSource.value.visitCarePay = split.visitCarePay;
  retirementSource.value.visitBathPay = split.visitBathPay;
  retirementSource.value.familyPay = split.familyPay;

  if (!useVisitCare && !useVisitBath && !useFamily) {
    const manual = parseWageNumber(retirementModalManualDisplay.value);
    payrollForm.value.c_out = Math.max(0, manual);
  } else {
    const lunchParts = resolveRetirementLunchPartsForCalc();
    payrollForm.value.c_out = calcHomeWageInstitutionRetirementFromSource({
      useVisitCare,
      useVisitBath,
      useFamily,
      visitCarePay: split.visitCarePay,
      visitBathPay: split.visitBathPay,
      familyPay: split.familyPay,
      lunchExpense: nontaxSettings.value.lunchExpense,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
    });
  }

  syncSupportDeduction(true);
  syncCorpDeductTotal();
  syncReferenceValues();
  if (selectedEmployee.value) {
    captureEmployeeWageDraft(selectedEmployee.value);
  }
  closeRetirementSourceModal();
};

const onRetirementInputClick = () => {
  if (isRetirementCaregiverFixedInput.value) {
    openRetirementSourceModal();
  }
};

const onRetirementInputFocus = (event: FocusEvent) => {
  if (!isRetirementCaregiverFixedInput.value) return;
  (event.target as HTMLInputElement)?.blur();
  openRetirementSourceModal();
};

const isDeductionFieldFixed = (key: HomeWageDeductionFixFieldKey): boolean =>
  deductionFixed.value[key] || false;

const toggleDeductionFixed = (key: HomeWageDeductionFixFieldKey, checked: boolean) => {
  unlockPayrollLedgerForEdit();
  markDirty();
  deductionFixed.value[key] = checked;
  if (key === 'l_jiwon') {
    if (checked) {
      supportDeductionDraftFlags.value = { ...supportDeductionItemFlags.value };
      nextTick(() => {
        showSupportDeductionModal.value = true;
      });
    } else {
      const emp = selectedEmployee.value;
      supportDeductionItemFlags.value = resolveAutoSupportDeductionFlags(
        buildSupportDeductionAmounts(payrollForm.value),
        emp ? isCaregiverEmployee(emp) : true
      );
      syncSupportDeductionValue();
      syncLaborDeductTotal();
      syncReferenceValues();
    }
    return;
  }
  runInsuranceDeductionAutoCalc(true, false);
  openDeductionFixModalOnCheck(key, checked);
};

const getDeductionFixCheckboxTooltip = (key: HomeWageDeductionFixFieldKey): string =>
  key === 'l_jiwon'
    ? HOME_WAGE_SUPPORT_DEDUCTION_SELECTIVE_TOOLTIP
    : HOME_WAGE_DEDUCTION_FIX_CHECKBOX_TOOLTIP;

const getDeductionFieldLabel = (key: HomeWageDeductionFixFieldKey): string => {
  const fromLabor = HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS.find((f) => f.key === key);
  if (fromLabor) return fromLabor.label;
  const fromCorp = HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS.find((f) => f.key === key);
  if (fromCorp) return fromCorp.label;
  const fromInsurance = HOME_WAGE_INSURANCE_SHARED_PAIRS.find((p) => `l_${p.key}` === key);
  if (fromInsurance) return fromInsurance.label;
  if (key === 'l_go' || key === 'c_go') return employmentInsurancePair.label;
  return key;
};

const buildNominalDeductionRates = (): DeductionRates => {
  const t = taxRateInfo.value!;
  const san =
    sanRate.value > 0
      ? sanRate.value
      : parseHomeWageTaxRateValue(t.rateSan, 0.007);
  return {
    rateYeon: parseHomeWageTaxRateValue(t.rateYeon, 0.045),
    rateGun: parseHomeWageTaxRateValue(t.rateGun, 0.03545),
    rateJang: parseHomeWageTaxRateValue(t.rateJang, 0.1295),
    rateGo: parseHomeWageTaxRateValue(t.rateGo, 0.009),
    rateSil: parseHomeWageTaxRateValue(t.rateSil, 0.0065),
    rateSan: san,
  };
};

/** 모달 미리보기 — 현재 화면 정책 적용액 vs 요율 기준액 비교 안내 */
const buildDeductionAppliedPolicyNote = (
  fieldKey: HomeWageDeductionFixFieldKey,
  nominalAmount: number,
  appliedAmount: number
): string | undefined => {
  if (nominalAmount === appliedAmount) return undefined;

  const f = formatWageNumber;
  const birth = getSelectedInsuranceBirth();
  const ageExempt = resolveSelectedHomeWageAgeInsurance(birth);
  const ageDisplay = ageExempt.age !== null ? `(만 ${ageExempt.age}세)` : '';

  const lines = [`현재 화면: ${f(appliedAmount)}원`];

  switch (fieldKey) {
    case 'l_yeon':
    case 'c_yeon':
      if (ageExempt.pensionExempt) {
        lines.push(`만 60세 이상 국민연금 면제 ${ageDisplay}`);
      }
      break;
    case 'l_gun':
    case 'c_gun':
      if (isFamilyUnder60HealthExempt.value) {
        lines.push(HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE);
      }
      break;
    case 'l_jang':
    case 'c_jang':
      if (isFamilyUnder60HealthExempt.value) {
        lines.push('가족요양 건강·장기요양 면제');
      }
      break;
    case 'l_go':
      if (ageExempt.employmentExempt) {
        lines.push(`만 65세 이상 고용보험(근로) 면제 ${ageDisplay}`);
      }
      break;
    case 'c_go':
      if (isRepresentative.value) {
        lines.push('대표자 기관 고용보험 면제');
      } else if (ageExempt.employmentExempt) {
        lines.push(`만 65세 이상 고용보험(기관) 면제 ${ageDisplay}`);
      }
      break;
    case 'c_sil':
      if (isRepresentative.value) lines.push('대표자 실업급여(기관) 면제');
      break;
    case 'c_san':
      if (isRepresentative.value) lines.push('대표자 산재보험 면제');
      break;
    default:
      break;
  }

  return lines.length > 1 ? lines.join('\n') : `현재 화면: ${f(appliedAmount)}원`;
};

const calcDeductionAutoPreview = (
  fieldKey: HomeWageDeductionFixFieldKey
): HomeWageDeductionAutoPreview => {
  const f = formatWageNumber;

  if (fieldKey === 'l_jiwon') {
    return buildSupportDeductionPolicyPreview();
  }

  if (!taxRateInfo.value) {
    return { amount: 0, formula: '', hasAuto: false };
  }

  const taxable = getInsuranceTaxableBase();
  if (taxable <= 0) {
    return { amount: 0, formula: '', hasAuto: false };
  }

  const taxableFmt = f(taxable);
  const taxRate = taxRateInfo.value;
  const nominalRates = buildNominalDeductionRates();
  const incomeTax = calculateIncomeTax(taxable, incomeTaxTable.value);
  const nomEmp = calcEmployeeDeductions(taxable, nominalRates, incomeTax);
  const nomCorp = calcEmployerDeductions(taxable, nominalRates);

  // ★ resolveInsuranceCalcContext 사용 금지 — computed에서 shadow write → 무한 루프
  const birth = getSelectedInsuranceBirth();
  const ageExempt = resolveSelectedHomeWageAgeInsurance(birth);
  const appliedRates = buildDeductionRates(
    !ageExempt.pensionExempt,
    !ageExempt.employmentExempt
  );
  const appliedEmp = calcEmployeeDeductions(taxable, appliedRates, incomeTax);
  const appliedCorp = calcEmployerDeductions(taxable, appliedRates);

  const pct = (rate: number, fallback: number) =>
    formatHomeWageRatePercent(rate || fallback);

  const rateAmountNote = (
    key: HomeWageDeductionFixFieldKey,
    nominal: number,
    applied: number,
    formula: string
  ): HomeWageDeductionAutoPreview => ({
    amount: nominal,
    hasAuto: true,
    formula,
    notes: buildDeductionAppliedPolicyNote(key, nominal, applied),
  });

  switch (fieldKey) {
    case 'l_yeon':
      return rateAmountNote(
        fieldKey,
        nomEmp.yeon,
        appliedEmp.yeon,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateYeon as number) || 0.045, 0.045)}\n= ${f(nomEmp.yeon)}원`
      );
    case 'c_yeon':
      return rateAmountNote(
        fieldKey,
        nomCorp.yeon,
        appliedCorp.yeon,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateYeon as number) || 0.045, 0.045)} (기관)\n= ${f(nomCorp.yeon)}원`
      );
    case 'l_gun':
      return rateAmountNote(
        fieldKey,
        nomEmp.gun,
        appliedEmp.gun,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateGun as number) || 0.03545, 0.03545)}\n= ${f(nomEmp.gun)}원`
      );
    case 'c_gun':
      return rateAmountNote(
        fieldKey,
        nomCorp.gun,
        appliedCorp.gun,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateGun as number) || 0.03545, 0.03545)} (기관)\n= ${f(nomCorp.gun)}원`
      );
    case 'l_jang':
      return rateAmountNote(
        fieldKey,
        nomEmp.jang,
        appliedEmp.jang,
        [
          `건강보험료 ${f(nomEmp.gun)}원 × ${pct((taxRate?.rateJang as number) || 0.1295, 0.1295)}`,
          `= ${f(nomEmp.jang)}원`,
        ].join('\n')
      );
    case 'c_jang':
      return rateAmountNote(
        fieldKey,
        nomCorp.jang,
        appliedCorp.jang,
        [
          `건강보험료 ${f(nomCorp.gun)}원 × ${pct((taxRate?.rateJang as number) || 0.1295, 0.1295)} (기관)`,
          `= ${f(nomCorp.jang)}원`,
        ].join('\n')
      );
    case 'l_go':
      return rateAmountNote(
        fieldKey,
        nomEmp.go,
        appliedEmp.go,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateGo as number) || 0.009, 0.009)}\n= ${f(nomEmp.go)}원`
      );
    case 'c_go':
      return rateAmountNote(
        fieldKey,
        nomCorp.go,
        appliedCorp.go,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateGo as number) || 0.009, 0.009)} (기관)\n= ${f(nomCorp.go)}원`
      );
    case 'l_so': {
      const hasTable = (incomeTaxTable.value?.length ?? 0) > 0;
      return rateAmountNote(
        fieldKey,
        nomEmp.so,
        appliedEmp.so,
        [
          `과세임금 ${taxableFmt}원 → 간이세액표`,
          hasTable ? `= ${f(incomeTax)}원` : '= 0원 (간이세액표 미로드)',
        ].join('\n')
      );
    }
    case 'l_ji':
      return rateAmountNote(
        fieldKey,
        nomEmp.ji,
        appliedEmp.ji,
        `소득세 ${f(nomEmp.so)}원 × 10%\n= ${f(nomEmp.ji)}원`
      );
    case 'c_sil':
      return rateAmountNote(
        fieldKey,
        nomCorp.sil,
        appliedCorp.sil,
        `과세임금 ${taxableFmt}원 × ${pct((taxRate?.rateSil as number) || 0.0065, 0.0065)} (기관)\n= ${f(nomCorp.sil)}원`
      );
    case 'c_san': {
      const rateVal =
        sanRate.value > 0
          ? sanRate.value
          : parseHomeWageTaxRateValue(taxRate?.rateSan, 0.007);
      const appliedSan = isRepresentative.value ? 0 : appliedCorp.san;
      return rateAmountNote(
        fieldKey,
        nomCorp.san,
        appliedSan,
        `과세임금 ${taxableFmt}원 × ${pct(rateVal, 0.007)} (산재)\n= ${f(nomCorp.san)}원`
      );
    }
    default:
      return { amount: 0, formula: '', hasAuto: false };
  }
};

/** 지원공제 — 요율 기준 산출 (5종 수당 + 항목 선택 반영) */
const buildSupportDeductionPolicyPreview = (): HomeWageDeductionAutoPreview => {
  const f = formatWageNumber;
  const calc = getSupportDeductionResult();
  if (!calc.valid || calc.base <= 0) {
    return {
      amount: 0,
      hasAuto: false,
      formula: HOME_WAGE_SUPPORT_DEDUCTION_UI.emptyFormulaHint,
    };
  }

  if (!taxRateInfo.value) {
    return { amount: 0, formula: '', hasAuto: false };
  }

  const nominalRates = buildNominalDeductionRates();
  const fullResult = calcHomeWageSupportDeductionWithItems({
    amounts: buildSupportDeductionAmounts(payrollForm.value),
    flags: supportDeductionItemFlags.value,
    selectiveMode: false,
    isCaregiver: selectedEmployee.value ? isCaregiverEmployee(selectedEmployee.value) : true,
    rates: nominalRates,
    yeonDeduct: true,
    goDeduct: true,
    includeRetirement: true,
  });

  const deductLines: string[] = [];
  if (fullResult.yeon > 0) deductLines.push(`국민 ${f(fullResult.yeon)}`);
  if (fullResult.gun > 0) deductLines.push(`건강 ${f(fullResult.gun)}`);
  if (fullResult.jang > 0) deductLines.push(`장기 ${f(fullResult.jang)}`);
  if (fullResult.go > 0) deductLines.push(`고용 ${f(fullResult.go)}`);
  if (fullResult.sil > 0) deductLines.push(`실업 ${f(fullResult.sil)}`);
  if (fullResult.san > 0) deductLines.push(`산재 ${f(fullResult.san)}`);
  if (fullResult.retirement > 0) deductLines.push(`퇴직분 ${f(fullResult.retirement)}`);

  const noteLines: string[] = [];
  if (deductionFixed.value.l_jiwon) {
    noteLines.push(HOME_WAGE_SUPPORT_DEDUCTION_UI.selectiveNote);
  }
  if (calc.result.total !== fullResult.total) {
    noteLines.push(`현재 화면: ${f(calc.result.total)}원`);
    if (!calc.yeonDeduct) noteLines.push('국민연금 면제 반영');
    if (!calc.goDeduct) noteLines.push('고용보험 면제 반영');
    if (!calc.includeRetirement) noteLines.push('퇴직금분 미포함');
    if (isFamilyUnder60HealthExempt.value) noteLines.push('가족요양 건강·장기 면제');
  }

  return {
    amount: calc.result.total,
    hasAuto: true,
    formula: [
      formatSupportDeductionTargetSummaryLine(calc.parts, calc.base, f),
      deductLines.join(' + '),
      `= ${f(calc.result.total)}원`,
    ].join('\n'),
    notes: noteLines.length ? noteLines.join('\n') : undefined,
  };
};

const deductionFixModalPreview = computed((): HomeWageDeductionAutoPreview => {
  if (!deductionFixModalTarget.value) {
    return { amount: 0, formula: '', hasAuto: false };
  }
  return calcDeductionAutoPreview(deductionFixModalTarget.value);
});

const deductionFixModalCurrentAmount = computed(() => {
  if (!deductionFixModalTarget.value) return 0;
  return parseWageNumber(formatPayrollMoneyField(deductionFixModalTarget.value));
});

const deductionFixModalTitle = computed(() => {
  if (!deductionFixModalTarget.value) return '공제 금액 설정';
  return `${getDeductionFieldLabel(deductionFixModalTarget.value)} 금액 설정`;
});

const openDeductionFixModal = (key: HomeWageDeductionFixFieldKey) => {
  if (key === 'l_jiwon') {
    supportDeductionDraftFlags.value = { ...supportDeductionItemFlags.value };
    showSupportDeductionModal.value = true;
    return;
  }
  // 모달 미리보기(computed) 전에 shadow를 한 번만 갱신 — computed 안에서는 write 금지
  if (isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value)) {
    syncAccountingPayrollShadow();
  }
  deductionFixModalTarget.value = key;
  deductionFixModalManualDisplay.value = formatPayrollMoneyField(key);
  showDeductionFixModal.value = true;
};

/** 고정 체크 시에만 금액 설정 모달 자동 오픈 (해제 시 모달 없음) */
const openDeductionFixModalOnCheck = (key: HomeWageDeductionFixFieldKey, checked: boolean) => {
  if (!checked || isWageReadOnly.value || key === 'l_jiwon') return;
  nextTick(() => openDeductionFixModal(key));
};

let deductionFixModalClosedAt = 0;

const closeDeductionFixModal = () => {
  deductionFixModalClosedAt = Date.now();
  showDeductionFixModal.value = false;
  deductionFixModalTarget.value = null;
  deductionFixModalManualDisplay.value = '';
};

const applyDeductionFixAmount = (amount: number) => {
  if (!deductionFixModalTarget.value) return;
  const key = deductionFixModalTarget.value;
  clearPendingPayrollAutoCalc();
  unlockPayrollLedgerForEdit();
  markDirty();

  if (key === 'l_yeon' || key === 'l_gun' || key === 'l_jang') {
    const pairKey = key.slice(2) as 'yeon' | 'gun' | 'jang';
    const pair = HOME_WAGE_INSURANCE_SHARED_PAIRS.find((p) => p.key === pairKey);
    if (pair) setSharedInsuranceAmount(pair, amount);
  } else {
    setPayrollMoneyField(key, amount);
  }

  if (key === 'l_jiwon') {
    syncSupportDeduction(true);
  }
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  syncReferenceValues();
  if (selectedEmployee.value) {
    captureEmployeeWageDraft(selectedEmployee.value);
  }
  closeDeductionFixModal();
};

const onDeductionFixModalManualInput = (event: Event) => {
  const raw = (event.target as HTMLInputElement).value.replace(/[^\d]/g, '');
  deductionFixModalManualDisplay.value = raw ? formatWageNumber(Number(raw)) : '';
};

const applyDeductionFixModalManual = () => {
  applyDeductionFixAmount(parseWageNumber(deductionFixModalManualDisplay.value));
};

const applyDeductionFixModalAuto = () => {
  applyDeductionFixAmount(deductionFixModalPreview.value.amount);
};

const onDeductionFixInputClick = (key: HomeWageDeductionFixFieldKey) => {
  if (isDeductionFieldFixed(key)) openDeductionFixModal(key);
};

const onDeductionFixInputFocus = (key: HomeWageDeductionFixFieldKey, event: FocusEvent) => {
  if (!isDeductionFieldFixed(key)) return;
  (event.target as HTMLInputElement)?.blur();
  // 모달을 막 닫은 직후 포커스가 입력칸으로 돌아오면 다시 열지 않는다
  if (Date.now() - deductionFixModalClosedAt < 400) return;
  openDeductionFixModal(key);
};

const supportDeductionModalItems = computed(() => {
  const emp = selectedEmployee.value;
  if (!emp) return [];
  return listSupportDeductionItemRows(
    buildSupportDeductionAmounts(payrollForm.value),
    isCaregiverEmployee(emp)
  );
});

const supportDeductionModalPreview = computed(() => {
  const emp = selectedEmployee.value;
  if (!emp || !taxRateInfo.value) {
    return { base: 0, total: 0, formula: '' };
  }
  const yeonDeduct = (Number(payrollForm.value.l_yeon) || 0) > 0;
  const goDeduct = (Number(payrollForm.value.l_go) || 0) > 0;
  const includeRetirement = (Number(payrollForm.value.c_out) || 0) > 0;
  const rates = buildDeductionRates(yeonDeduct, goDeduct);
  if ((Number(payrollForm.value.l_gun) || 0) <= 0) {
    rates.rateGun = 0;
    rates.rateJang = 0;
  }
  const calc = calcHomeWageSupportDeductionWithItems({
    amounts: buildSupportDeductionAmounts(payrollForm.value),
    flags: supportDeductionDraftFlags.value,
    selectiveMode: true,
    isCaregiver: isCaregiverEmployee(emp),
    rates,
    yeonDeduct,
    goDeduct,
    includeRetirement,
  });
  return {
    base: calc.base,
    total: calc.total,
    formula: formatSupportDeductionTargetDetailLine(calc.parts, formatWageNumber),
  };
});

const onSupportDeductionDraftFlagsUpdate = (flags: HomeWageSupportDeductionItemFlags) => {
  supportDeductionDraftFlags.value = { ...flags };
};

const closeSupportDeductionModal = (revertSelective = false) => {
  if (revertSelective && deductionFixed.value.l_jiwon) {
    deductionFixed.value.l_jiwon = false;
    const emp = selectedEmployee.value;
    supportDeductionItemFlags.value = resolveAutoSupportDeductionFlags(
      buildSupportDeductionAmounts(payrollForm.value),
      emp ? isCaregiverEmployee(emp) : true
    );
    syncSupportDeductionValue();
    syncLaborDeductTotal();
    syncReferenceValues();
  }
  showSupportDeductionModal.value = false;
};

const applySupportDeductionModal = () => {
  unlockPayrollLedgerForEdit();
  markDirty();
  supportDeductionItemFlags.value = { ...supportDeductionDraftFlags.value };
  syncSupportDeductionValue();
  syncLaborDeductTotal();
  syncReferenceValues();
  if (selectedEmployee.value) {
    captureEmployeeWageDraft(selectedEmployee.value);
  }
  showSupportDeductionModal.value = false;
};

const onInsuranceSharedInputClick = (pair: HomeWageInsurancePairDef) => {
  if (!getInsuranceFixedByPairKey(pair.key)) return;
  openDeductionFixModal(pair.laborKey as HomeWageDeductionFixFieldKey);
};

const onInsuranceSharedInputFocus = (pair: HomeWageInsurancePairDef, event: FocusEvent) => {
  const key = pair.laborKey as HomeWageDeductionFixFieldKey;
  onDeductionFixInputFocus(key, event);
};

const onEmploymentInsuranceInputClick = (side: EmploymentInsuranceSide) => {
  const key = getEmploymentInsuranceFieldKey(side) as HomeWageDeductionFixFieldKey;
  if (isDeductionFieldFixed(key)) openDeductionFixModal(key);
};

const onEmploymentInsuranceInputFocus = (side: EmploymentInsuranceSide, event: FocusEvent) => {
  const key = getEmploymentInsuranceFieldKey(side) as HomeWageDeductionFixFieldKey;
  onDeductionFixInputFocus(key, event);
};

/** 요양보호사가 아닐 때만 메인 입력에서 c_out 직접 수정 */
const onRetirementMoneyInput = (event: Event) => {
  if (isRetirementCaregiverFixedInput.value) return;
  onPayrollMoneyInput('c_out', event);
};

const getRetirementSourceSavePayload = () => {
  if (!applyRetirement.value) {
    return {
      retirementVisitCarePay: 0,
      retirementVisitBathPay: 0,
      retirementFamilyPay: 0,
      retirementUseVisitCare: 'N',
      retirementUseVisitBath: 'N',
      retirementUseFamily: 'N',
    };
  }

  const split = getRetirementPaySplitLive();
  return {
    retirementVisitCarePay:
      retirementSource.value.visitCarePay > 0
        ? retirementSource.value.visitCarePay
        : split.visitCarePay,
    retirementVisitBathPay:
      retirementSource.value.visitBathPay > 0
        ? retirementSource.value.visitBathPay
        : split.visitBathPay,
    retirementFamilyPay:
      retirementSource.value.familyPay > 0 ? retirementSource.value.familyPay : split.familyPay,
    retirementUseVisitCare: formatHomeWageApplyRetirementFlag(retirementSource.value.useVisitCare),
    retirementUseVisitBath: formatHomeWageApplyRetirementFlag(retirementSource.value.useVisitBath),
    retirementUseFamily: formatHomeWageApplyRetirementFlag(retirementSource.value.useFamily),
  };
};

const annualLeaveDays = computed(() =>
  getAnnualLeaveDays(
    selectedEmpJoinDate.value,
    selectedYear.value,
    selectedMonth.value
  )
);

/** 중증수급자가산 합계 (실시간 계산) */
const severeSumFromClaim = computed(() =>
  claimSummaryRows.value
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + r.totalAmount, 0)
);

// ─────────────────────────────────────────────
// 가족요양 판별 computed
// ─────────────────────────────────────────────

const familyCareRowsForPolicy = () => {
  const fromDisplay = displayClaimSummaryRows.value.filter((r) => !r.isSevereRecipientSurcharge);
  if (fromDisplay.length > 0) return fromDisplay;
  return claimSummaryRows.value.filter((r) => !r.isSevereRecipientSurcharge);
};

/** 가족요양 수급자 포함 여부 (하나라도 가족요양인 경우) */
const hasFamilyCareInClaim = computed(() => {
  const nonSevereRows = familyCareRowsForPolicy();
  if (nonSevereRows.length > 0) {
    return hasFamilyCarePatient(
      nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
    );
  }
  return isHomeWageRowAllFamilyCareFromSvc(
    payrollForm.value.svc || String(loadedDirectorRow.value?.svc || '')
  );
});

/** 가족요양 전용 여부 — 화면 표·저장 svc 기준 (청구 집계가 비어 있어도 대장과 동일) */
const isAllFamilyCareInClaim = computed(() => {
  const nonSevereRows = familyCareRowsForPolicy();
  if (nonSevereRows.length > 0) {
    return isAllFamilyCaregiver(
      nonSevereRows.map((r) => ({ feeName: r.feeName, sugaName: r.feeName }))
    );
  }
  return isHomeWageRowAllFamilyCareFromSvc(
    payrollForm.value.svc || String(loadedDirectorRow.value?.svc || '')
  );
});

/** 자동 중식대 — 정책 단일 함수 래퍼 (청구·달력 기준) */
const resolveAutoLunchFromClaims = (opts: {
  isFamily: boolean;
  nonSevereTarget: number;
  annualLeaveBeforeLunch?: number;
  linkedMinAnnual?: number;
}) =>
  calcHomeWageAutoLunch({
    lunchExpense: nontaxSettings.value.lunchExpense,
    isFamilyCareOnly: opts.isFamily,
    nonSevereTarget: opts.nonSevereTarget,
    lunchWorkDayCount: workCalendarSummary.value.lunchWorkDayCount,
    annualLeaveBeforeLunch: opts.annualLeaveBeforeLunch,
    linkedMinAnnual: opts.linkedMinAnnual,
  });

/** 센터장 연차에 더할 자동중식 원본 (폼·dbSaved·shadow) — 흡수 고스트 판별 전 */
const resolveDirectorAutoLunchRaw = () => {
  const fromForm = Math.max(0, Number(payrollForm.value.lunch) || 0);
  if (fromForm > 0) return fromForm;
  const fromSaved = Math.max(0, Number(dbSavedAutoLunchRef.value) || 0);
  if (fromSaved > 0) return fromSaved;
  return Math.max(0, Number(autoBaseLunchRef.value) || 0);
};

/**
 * 요보 자동중식 — 실지급분만 (연차 흡수 고스트=0)
 * ★ 고영이형: cwhd_lunch>0 이어도 funded=0 → 지급금액·연차 합산 이중가산 금지
 */
const resolveCaregiverFundedAutoLunchForPay = (rawLunch?: number) => {
  const raw =
    rawLunch != null && rawLunch > 0
      ? rawLunch
      : Math.max(
          Number(payrollForm.value.lunch) || 0,
          Number(dbSavedAutoLunchRef.value) || 0,
          Number(autoBaseLunchRef.value) || 0
        );
  return resolveHomeWageFundedAutoLunch({
    ...payrollForm.value,
    lunch: raw,
    job: getEmpJob(selectedEmployee.value),
    svc: lunchExtractCtx().svc,
    recipientTotal: lunchExtractCtx().recipientTotal,
    wageTotal: payrollForm.value.wageTotal,
    nonTax: payrollForm.value.nonTax,
    grossPay: payrollForm.value.grossPay,
  });
};

/** 센터장 연차에 더할 자동중식 = 실지급분만요 (흡수 고스트 제외) */
const resolveDirectorAutoLunchToFold = () =>
  resolveCaregiverFundedAutoLunchForPay(resolveDirectorAutoLunchRaw());

/** 센터장·통합 지급금액용 자동중식 — 실지급분만 */
const resolveSlipAutoLunchForDisplay = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    return Number(payrollForm.value.lunch) || 0;
  }
  if (
    isDirectorPayrollView.value
    && !isAllFamilyCareInClaim.value
  ) {
    return resolveDirectorAutoLunchToFold();
  }
  const raw = resolveHomeWageSlipAutoLunch(
    payrollForm.value.lunch,
    autoBaseLunchRef.value,
    accountingPayrollShadow.value?.lunch
  );
  return resolveCaregiverFundedAutoLunchForPay(raw);
};

/**
 * 관리자 시점 — 연차수당에서 자동 중식대만큼 떼어 lunch 필드로 이동 (폼 값 직접 갱신)
 * 센터장 시점에서는 호출하지 않음
 */
const applyAdminAnnualLunchExtraction = () => {
  if (shouldSkipAdminLiveCalc()) return;
  if (directorCanonicalActive.value) return;
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (isAllFamilyCareInClaim.value) return;
  if (isDirectorPayrollView.value) return;

  if (!nontaxSettings.value.lunchExpense) {
    payrollForm.value.lunch = 0;
    autoBaseLunchRef.value = 0;
    return;
  }

  // 연차+기본+주휴만으로 수급자합 — 중식 고스트를 연차에서 새로 깎지 않음 (박소라 6월)
  const rt = lunchExtractCtx().recipientTotal;
  const basic = dbWageAmount(payrollForm.value.basic);
  const weekend = dbWageAmount(payrollForm.value.weekend);
  const annualNow = dbWageAmount(payrollForm.value.annual);
  if (rt > 0 && Math.abs(basic + weekend + annualNow - rt) <= 1) {
    const ghostLunch =
      autoBaseLunchRef.value
      || Number(payrollForm.value.lunch)
      || Number(dbSavedAutoLunchRef.value)
      || 0;
    if (
      ghostLunch > 0
      && isHomeWageAutoLunchAbsorbedInAnnual({
        ...payrollForm.value,
        lunch: ghostLunch,
        job: getEmpJob(selectedEmployee.value),
        svc: lunchExtractCtx().svc,
        recipientTotal: rt,
        wageTotal: payrollForm.value.wageTotal,
        nonTax: payrollForm.value.nonTax,
      })
    ) {
      payrollForm.value.lunch = 0;
      autoBaseLunchRef.value = 0;
      return;
    }
    // 수급자합이 이미 맞으면 연차에서 중식 신규 산출 금지
    if (
      (Number(payrollForm.value.lunch) || 0) <= 0
      && (Number(autoBaseLunchRef.value) || 0) <= 0
      && (Number(dbSavedAutoLunchRef.value) || 0) <= 0
    ) {
      return;
    }
  }

  let autoLunch =
    autoBaseLunchRef.value
    || Number(payrollForm.value.lunch)
    || Number(dbSavedAutoLunchRef.value)
    || 0;

  if (
    autoLunch <= 0
    && caregiverHourlyWage.value > 0
    && totalSugaHours.value > 0
    && workCalendarSummary.value.lunchWorkDayCount > 0
  ) {
    const nonSevereTarget = claimGrandTotal.value - severeSumFromClaim.value;
    const workDays = workCalendarSummary.value.workDayCount;
    const linkedMin = calcHomeWageHourlyLinkedPay(getHourlyLinkedPayInput());
    const breakdown = calcHomeWagePayBreakdown({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: caregiverHourlyWage.value,
      minHourlyWage: minHourlyWage.value,
      totalSugaHours: totalSugaHours.value,
      workDays,
      annualLeaveDays: annualLeaveDays.value,
    });
    const lunchResult = resolveAutoLunchFromClaims({
      isFamily: false,
      nonSevereTarget,
      annualLeaveBeforeLunch: breakdown.annualLeave,
      linkedMinAnnual: linkedMin.annual,
    });
    autoLunch = lunchResult.autoLunch;
    if (autoLunch > 0) {
      autoBaseLunchRef.value = autoLunch;
      payrollForm.value.lunch = autoLunch;
      payrollForm.value.annual = Math.max(
        0,
        lunchResult.annualAfterLunch ?? breakdown.annualLeave
      );
      if (Number(payrollForm.value.lunchDirector) > 0) {
        applyAutoLunchDirectorAdjustment({
          autoBase: autoLunch,
          adjustPayField: 'annual',
        });
      }
      return;
    }
  }

  const currentAnnual = Number(payrollForm.value.annual) || 0;
  const currentLunch = Number(payrollForm.value.lunch) || 0;
  if (
    autoLunch > 0
    && currentLunch === autoLunch
    && !shouldExtractAutoLunchFromAnnualField(currentAnnual, autoLunch, lunchExtractCtx())
  ) {
    // 연차 흡수 고스트 — 폼/비과세에 남기지 않음
    if (
      isHomeWageAutoLunchAbsorbedInAnnual({
        ...payrollForm.value,
        lunch: autoLunch,
        job: getEmpJob(selectedEmployee.value),
        svc: lunchExtractCtx().svc,
        recipientTotal: lunchExtractCtx().recipientTotal,
        wageTotal: payrollForm.value.wageTotal,
        nonTax: payrollForm.value.nonTax,
      })
    ) {
      payrollForm.value.lunch = 0;
      autoBaseLunchRef.value = 0;
      return;
    }
    autoBaseLunchRef.value = autoLunch;
    return;
  }

  const extracted = applyHomeWageExtractLunchFromAnnual({
    annual: payrollForm.value.annual,
    lunch: payrollForm.value.lunch,
    autoLunch,
  });
  autoBaseLunchRef.value = extracted.autoLunch;
  payrollForm.value.lunch = extracted.lunch;
  payrollForm.value.annual = extracted.annual;

  if (extracted.lunch > 0 && Number(payrollForm.value.lunchDirector) > 0) {
    applyAutoLunchDirectorAdjustment({
      autoBase: extracted.lunch,
      adjustPayField: 'annual',
    });
  }
};

/** 센터장 시점 — 자동중식 칸만 숨김. 연차 필드는 관리자 저장값 유지 */
const applyDirectorAnnualLunchDisplayPrep = () => {
  if (directorCanonicalActive.value) return;
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (!isDirectorPayrollView.value) return;
  if (isAllFamilyCareInClaim.value) return;

  const fromForm = Number(payrollForm.value.lunch) || 0;
  if (fromForm > 0) {
    dbSavedAutoLunchRef.value = fromForm;
  }
  payrollForm.value.lunch = 0;
};

/** 중식 연차추출 판정 — 수급자합으로 분리저장 오탐 방지(김영남 5월 등) */
const lunchExtractCtx = () => ({
  basic: payrollForm.value.basic,
  weekend: payrollForm.value.weekend,
  recipientTotal: claimGrandTotal.value - severeSumFromClaim.value,
  svc: dbSavedSummaryRows.value.length
    ? serializeHomeWageRecipientSummaryToSvc(dbSavedSummaryRows.value)
    : (payrollForm.value.svc || null),
});

/**
 * 관리자 요보 — 비과세·툴팁·합계에 쓸 자동중식 (실지급분만)
 * 연차 흡수 고스트(고영이·박소라 cwhd_lunch만 있고 non_tax=0) → 0
 */
const resolveAdminCaregiverAutoLunchForNontax = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    return dbWageAmount(payrollForm.value.lunch);
  }
  return resolveCaregiverFundedAutoLunchForPay();
};

/** 센터장 연차 합산용 — 관리자 저장 연차 (이미 자동중식이 빠진 값) */
const resolveDirectorSlipAdminAnnual = (): number =>
  Number(payrollForm.value.annual) || 0;

/** 센터장 slip 표시용 — payrollForm + 청구·근무시간 (getHomeWageCaregiverSlipPayDisplay 입력) */
const clientPayDisplayRow = computed((): HomeWageClientPayDisplayRow => {
  const f = payrollForm.value;
  /** 센터장 — 관리자 저장값 기준( pool 재산출 연·야·휴 제외 ) */
  const split = isDirectorPayrollView.value
    ? premiumTaxSplit.value
    : effectivePremiumTaxSplit.value;
  const emp = selectedEmployee.value;
  const isCg = isCaregiverEmployee(emp);
  const directorGeneralCg =
    isDirectorPayrollView.value && isCg && !isAllFamilyCareInClaim.value;
  return {
    basic: Number(f.basic) || 0,
    weekend: Number(f.weekend) || 0,
    annual:
      directorCanonicalActive.value
        ? Number(f.annual) || 0
        : isDirectorPayrollView.value && isCg && !isAllFamilyCareInClaim.value
          ? resolveDirectorSlipAdminAnnual()
          : Number(f.annual) || 0,
    lunch: directorCanonicalActive.value
      ? 0
      : isDirectorPayrollView.value
        ? resolveDirectorAutoLunchToFold()
        : (Number(f.lunch) || 0),
    lunchDirector: Number(f.lunchDirector) || 0,
    overtime: split.overtimeTax,
    overtimeNontax: split.overtimeNontax,
    night: split.nightTax,
    nightNontax: split.nightNontax,
    holiday: split.holidayTax,
    holidayNontax: split.holidayNontax,
    overtimeHours: f.overtimeHours,
    nightHours: f.nightHours,
    holidayHours: f.holidayHours,
    hour: isCg
      ? totalSugaHours.value
      : (Number(staffForm.value.workHours) || parseHours(String(f.hour || '')) || 0),
    payCalcHourly: isCg
      ? caregiverHourlyWage.value
      : (Number(staffForm.value.hourlyWage) || 0),
    job: getEmpJob(emp),
    svc: dbSavedSummaryRows.value.length
      ? serializeHomeWageRecipientSummaryToSvc(dbSavedSummaryRows.value)
      : null,
    forceFamilyCareOnly: isCg && isAllFamilyCareInClaim.value ? true : undefined,
    payOvertime: directorPremiumPayOverride.value.payOvertime,
    payHoliday: directorPremiumPayOverride.value.payHoliday,
    payNight: directorPremiumPayOverride.value.payNight,
    skipPremiumAnnualAbsorb: directorGeneralCg,
    isDirectorCanonical: directorCanonicalActive.value,
    recipientTotal:
      isDirectorPayrollView.value && isCg
        ? claimGrandTotal.value - severeSumFromClaim.value
        : undefined,
    // 자동중식 실지급 판별 — 목록 월급여(gross)와 대조
    wageTotal: Number(f.wageTotal) || 0,
    nonTax: Number(f.nonTax) || 0,
    grossPay: Number(f.grossPay) || 0,
  };
});

const directorSlipPayDisplay = computed(() =>
  getHomeWageCaregiverSlipPayDisplay(clientPayDisplayRow.value)
);

/** 센터장 참고바·툴팁 — slip 표시 항목 합계 (자동 중식대 포함) */
const clientReportPayRow = computed((): HomeWageClientReportRow => {
  const f = payrollForm.value;
  return {
    ...clientPayDisplayRow.value,
    senior: f.senior,
    sunim: f.sunim,
    bosu: f.bosu,
    severe: f.severe,
    bonus: f.bonus,
    car: f.car,
    child: f.child,
    welfare: f.welfare,
    other: f.other,
    laborDay: f.laborDay,
  };
});

/**
 * ★ 관리자·센터장 동일 지급금액 — 시점 전환·연차/중식 분리와 무관한 slip 입력
 */
const unifiedGrossPayReportRow = computed((): HomeWageClientReportRow => {
  const f = payrollForm.value;
  const emp = selectedEmployee.value;
  const isCg = isCaregiverEmployee(emp);
  const autoLunch = resolveSlipAutoLunchForDisplay();
  return buildHomeWageCaregiverUnifiedGrossReportRow({
    form: f,
    job: getEmpJob(emp),
    hour: isCg
      ? totalSugaHours.value
      : (Number(staffForm.value.workHours) || parseHours(String(f.hour || '')) || 0),
    autoLunch,
    adminAnnual: isCg && !isAllFamilyCareInClaim.value
      ? resolveDirectorSlipAdminAnnual()
      : undefined,
    svc: dbSavedSummaryRows.value.length
      ? serializeHomeWageRecipientSummaryToSvc(dbSavedSummaryRows.value)
      : null,
    recipientTotal: isCg ? claimGrandTotal.value - severeSumFromClaim.value : undefined,
    forceFamilyCareOnly: isCg && isAllFamilyCareInClaim.value ? true : undefined,
    payOvertime: directorPremiumPayOverride.value.payOvertime,
    payHoliday: directorPremiumPayOverride.value.payHoliday,
    payNight: directorPremiumPayOverride.value.payNight,
  });
});

/** 저장 payload — 요양보호사는 slip 통합 지급금액, 그 외 과세+비과세(미차감 중식 이중계산 제거) */
const resolveSaveGrossPay = (emp: any, form: HomeWagePayrollFormData): number => {
  if (!isCaregiverEmployee(emp)) {
    return getHomeWageStaffLedgerGrossPay({
      ...form,
      job: getEmpJob(emp),
    });
  }
  if (emp === selectedEmployee.value) {
    return caregiverDisplayGrossPay.value;
  }
  return calcHomeWageStoredUnifiedGrossPay(
    {
      ...form,
      job: form.job || getEmpJob(emp) || '',
    },
    yearMonth.value
  );
};

/** 통합 slip — 기본급 카드·수당 합계 (지급금액 툴팁용) */
const unifiedSlipPayDisplay = computed(() =>
  getHomeWageCaregiverSlipPayDisplay(unifiedGrossPayReportRow.value)
);

const unifiedCaregiverBasePayTotal = computed(() =>
  unifiedSlipPayDisplay.value.basic
  + unifiedSlipPayDisplay.value.weekend
  + unifiedSlipPayDisplay.value.annual
  + (Number(payrollForm.value.severe) || 0)
);

const unifiedCaregiverAllowanceTotal = computed(() => {
  const slip = unifiedSlipPayDisplay.value;
  const f = payrollForm.value;
  let sum = slip.overtime + slip.night + slip.holiday + slip.lunch;
  for (const key of HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS) {
    if (key === 'lunchDirector') continue;
    sum += Number(f[key as keyof typeof f]) || 0;
  }
  if (isHomeWageLaborDayMonth(yearMonth.value)) {
    sum += Number(f.laborDay) || 0;
  }
  return Math.max(0, sum);
});

/** 관리자 참고바·툴팁 — 과세합계 + 비과세(식대 등, DB nonTax=0 레거시 포함) */
const adminDisplayGrossPay = computed(() =>
  calcHomeWageAdminDisplayGrossPay(
    {
      ...payrollForm.value,
      job: getEmpJob(selectedEmployee.value),
      hour: isCaregiverEmployee(selectedEmployee.value)
        ? totalSugaHours.value
        : (staffForm.value.workHours || payrollForm.value.hour),
      payCalcHourly: isCaregiverEmployee(selectedEmployee.value)
        ? caregiverHourlyWage.value
        : staffForm.value.hourlyWage,
    },
    resolveProductionNontaxForDisplay(),
    yearMonth.value
  )
);

/**
 * ★ 요양보호사 지급금액 — 관리자·센터장 시점 동일 (= 기본급 합계 + 수당 합계)
 */
const caregiverDisplayGrossPay = computed(() =>
  calcHomeWageCaregiverUnifiedDisplayGrossPay(
    unifiedGrossPayReportRow.value,
    yearMonth.value
  )
);

/** 관리자 상세 = 회계처리용 대장과 동일 입력 행 */
const adminSettingSourceRow = computed(() => {
  const f = payrollForm.value;
  const emp = selectedEmployee.value;
  const isCg = isCaregiverEmployee(emp);
  const job = getEmpJob(emp);
  const hour = isCg
    ? totalSugaHours.value
    : (staffForm.value.workHours || f.hour);
  const payCalcHourly = isCg
    ? caregiverHourlyWage.value
    : staffForm.value.hourlyWage;
  const loaded = loadedDirectorRow.value;
  let lunchAuto = Number(dbSavedAutoLunchRef.value) || Number(autoBaseLunchRef.value) || 0;
  if (loaded) {
    if (hasHomeWageStoredAdParts(loaded)) {
      lunchAuto = readHomeWageStoredAdminAutoLunch(loaded);
    } else if (isHomeWageDirectorCanonicalRow(loaded)) {
      lunchAuto = getHomeWageAdminPartsFromDirectorStore(
        mapHomeWageRowToDirectorStore(loaded, yearMonth.value),
        yearMonth.value
      ).lunchAuto;
    }
  }
  if (lunchAuto <= 0 && !isDirectorPayrollView.value) {
    lunchAuto = Number(f.lunch) || 0;
  }
  return {
    ...f,
    ...(loaded || {}),
    job,
    cwhd_job: job,
    hour,
    cwhd_hour: hour,
    payCalcHourly,
    cwhd_pay_calc_hourly: payCalcHourly,
    svc: dbSavedSummaryRows.value.length
      ? serializeHomeWageRecipientSummaryToSvc(dbSavedSummaryRows.value)
      : (f.svc || null),
    recipientTotal: isCg ? claimGrandTotal.value - severeSumFromClaim.value : undefined,
    yearMonth: yearMonth.value,
    cwhd_ym: yearMonth.value,
    lunchAuto,
    isDirectorCanonical:
      directorCanonicalActive.value
      || isHomeWageDirectorCanonicalRow(loaded),
  } as Record<string, unknown>;
});

const adminSettingDisplayParts = computed(() =>
  getHomeWageAdminSettingDisplayParts(adminSettingSourceRow.value, yearMonth.value)
);

/**
 * ★ 시점별 지급금액 단일 소스
 * 관리자 = 회계처리용 대장 · 센터장 = 고객용 대장·명세서
 */
const displayGrossPay = computed(() =>
  isDirectorPayrollView.value
    ? calcHomeWageClientReportGrossPay(clientReportPayRow.value, yearMonth.value)
    : getHomeWageAdminSettingGrossPay(adminSettingSourceRow.value, yearMonth.value)
);

const directorDisplayGrossPay = displayGrossPay;

const adminDisplayNonTax = computed(() =>
  calcHomeWageTotalNontax(
    payrollForm.value,
    resolveProductionNontaxForDisplay(),
    yearMonth.value,
    'admin'
  )
);

const directorDisplayNetPay = computed(() =>
  Math.max(0, displayGrossPay.value - (Number(payrollForm.value.l_total) || 0))
);

const adminDisplayNetPay = computed(() =>
  Math.max(0, displayGrossPay.value - (Number(payrollForm.value.l_total) || 0))
);

/**
 * 센터장 시점 · 연차수당 = 기본급 카드 연차 (자동중식 합산 없음)
 */
const directorDisplayAnnualAmount = computed(() => {
  if (!isDirectorPayrollView.value || !isCaregiverEmployee(selectedEmployee.value)) {
    return Number(payrollForm.value.annual) || 0;
  }
  return directorSlipPayDisplay.value.annual;
});

/** 저장된 근무시간 — lock 중 건강 면제는 실시간 청구가 아니라 대장 hour를 본다 */
const getLockedFamilyHealthHours = () => {
  const fromForm = parseHours(String(payrollForm.value.hour || ''));
  if (fromForm > 0) return fromForm;
  const row = loadedDirectorRow.value;
  if (!row) return 0;
  const raw = row.hour ?? row.cwhdir_hour;
  const n = typeof raw === 'number' ? raw : parseHours(String(raw ?? ''));
  return n > 0 ? n : 0;
};

/** 가족요양 전용 + 월 60h 미만 — 건강·장기요양 0원 (저장 대장이 있으면 그 기준) */
const isFamilyUnder60HealthExempt = computed(() => {
  const svc = payrollForm.value.svc
    || String(
      loadedDirectorRow.value?.svc
      || loadedDirectorRow.value?.cwhdir_svc
      || loadedDirectorRow.value?.cwhd_svc
      || ''
    );
  const storedHours = getLockedFamilyHealthHours();
  const isAllFamily = payrollDbLock.value && svc
    ? isHomeWageRowAllFamilyCareFromSvc(svc)
    : isAllFamilyCareInClaim.value;
  const hours = payrollDbLock.value && storedHours > 0
    ? storedHours
    : totalSugaHours.value;
  return resolveHomeWageFamilyUnder60HealthInsuranceExempt({
    isAllFamilyCare: isAllFamily,
    totalSugaHours: hours,
  });
});

/** 가족 60h 미만 면제 조건 충족 시 건강·장기요양 고정(체크) 자동 해제 → 정책 자동계산 */
watch(
  [isFamilyUnder60HealthExempt, () => selectedEmployee.value, totalSugaHours],
  ([exempt]) => {
    if (!exempt || !selectedEmployee.value) return;
    if (payrollHydrating.value || importingPrevMonth.value) return;
    if (!syncFamilyUnder60HealthInsuranceFixedRelease()) return;
    schedulePayrollAutoCalc('downstream', true, true);
  }
);

/** 가족요양 수급자 수 */
const familyCarePatientCount = computed(() => {
  return claimSummaryRows.value.filter(
    (r) => !r.isSevereRecipientSurcharge && isFamilyCare(r.feeName)
  ).length;
});

/** 가족요양 총 수가시간 (가족요양 수급자만) */
const familyCareTotalSugaHours = computed(() => {
  return claimSummaryRows.value
    .filter((r) => !r.isSevereRecipientSurcharge && isFamilyCare(r.feeName))
    .reduce((sum, r) => sum + r.serviceCount * r.sugaHoursPerVisit, 0);
});

/** 가족요양 툴팁 상세 설명 */
const familyCareTooltip = computed(() => {
  if (!hasFamilyCareInClaim.value) return '';
  const isAllFamily = isAllFamilyCareInClaim.value;
  const hours = isAllFamily ? totalSugaHours.value : familyCareTotalSugaHours.value;
  const under60 = hours < HOME_WAGE_INSURANCE_MIN_HOURS;

  let tooltip = `★ 가족요양 ${isAllFamily ? '전용' : '포함'}\n\n`;

  if (isAllFamily) {
    tooltip += `[급여 구조]\n`;
    tooltip += `• 기본급 = 월급여 전체 (주휴·연차 포함)\n`;
    tooltip += `• 주휴수당 = 0원 (기본급에 포함)\n`;
    tooltip += `• 연차수당 = 0원 (기본급에 포함)\n`;
    tooltip += `• 휴일수당 = 센터장 0원 (기본급 포함) / 관리자는 기본급에서 분리\n`;
    tooltip += `• ${HOME_WAGE_NONTAX_TERM.lunch} = 지급 가능\n\n`;
  }

  tooltip += `[현재 수가시간: ${formatDurationHoursMinutes(hours) || '0분'}]\n`;

  if (under60) {
    tooltip += `\n⚠️ 60시간 미만 제약사항:\n`;
    tooltip += `• 건강보험: 0원\n`;
    tooltip += `• 장기요양보험: 0원\n`;
    tooltip += `• 퇴직금: 적립 불가\n`;
    tooltip += `• 국민연금·고용보험: 일반 산출\n`;
  } else {
    tooltip += `\n✓ 60시간 이상:\n`;
    tooltip += `• 4대보험: 가입 가능\n`;
    tooltip += `• 퇴직금: 적립 가능\n`;
  }

  tooltip += `\n[본인부담금공제]\n`;
  tooltip += `• 가족요양만 사용 가능\n`;
  tooltip += `• 공식: 가족수가금액 × 비율`;

  return tooltip;
});

/** 표 합계 기준 10원 단위 역산 최적시급 (중증수급자가산 제외, 최저시급 초과 시만 표시) */
const optimalHourlyWage = computed(() => {
  const raw = calcOptimalHourlyWage(
    claimGrandTotal.value - severeSumFromClaim.value,
    totalSugaHours.value,
    workCalendarSummary.value.workDayCount,
    annualLeaveDays.value
  );
  if (raw == null || raw <= 0) return null;
  const min = minHourlyWage.value;
  if (min > 0 && raw <= min) return null;
  return raw;
});

const formatPayrollMoneyField = (key: HomeWagePayrollFieldKey) => {
  if (
    isDirectorPayrollView.value
    && directorMaskPremiumDisplay.value
    && (key === 'overtime' || key === 'night' || key === 'holiday')
  ) {
    return '';
  }
  if (isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value)) {
    const slip = directorSlipPayDisplay.value;
    if (key === 'basic' && slip.isFamilyCareOnly) {
      const n = slip.basic;
      return n > 0 ? formatWageNumber(n) : '';
    }
    if (
      key === 'lunchDirector'
      && (directorMaskLunchDisplay.value || slip.lunch === 0)
    ) {
      return '';
    }
    if (
      key === 'lunch'
      && (directorMergeLunchIntoAnnual.value || directorMaskLunchDisplay.value)
    ) {
      return '';
    }
    if (key === 'annual' && !slip.isFamilyCareOnly) {
      const n = directorDisplayAnnualAmount.value;
      return n > 0 ? formatWageNumber(n) : '';
    }
  }
  if (isDirectorPayrollView.value && !isCaregiverEmployee(selectedEmployee.value)) {
    const slip = directorSlipPayDisplay.value;
    // 센터장 · 사복/운전원: 기본급 = 시급×시간(풀)
    if (key === 'basic') {
      return slip.basic > 0 ? formatWageNumber(slip.basic) : '';
    }
    if (key === 'annual') {
      return slip.annual > 0 ? formatWageNumber(slip.annual) : '';
    }
    // 자동중식 칸 숨김 — 중식비는 센터장 입력만 (자동은 기본급=시급×시간에 포함 표시)
    if (key === 'lunch') return '';
    if (key === 'lunchDirector') {
      const n = clampHomeWageLunchDirectorInput(payrollForm.value.lunchDirector);
      return n > 0 ? formatWageNumber(n) : '';
    }
  }
  // 관리자 시점 — 회계처리용 대장과 동일 칸
  if (!isDirectorPayrollView.value) {
    const p = adminSettingDisplayParts.value;
    const adminField: Partial<Record<string, number>> = {
      basic: p.basic,
      weekend: p.weekend,
      annual: p.annual,
      laborDay: p.laborDay,
      overtime: p.overtime,
      overtimeNontax: p.overtimeNontax,
      night: p.night,
      nightNontax: p.nightNontax,
      holiday: p.holiday,
      holidayNontax: p.holidayNontax,
      senior: p.senior,
      sunim: p.sunim,
      bosu: p.bosu,
      severe: p.severe,
      bonus: p.bonus,
      lunch: p.lunchAuto,
      lunchDirector: p.lunchDirector,
      car: p.car,
      child: p.child,
      welfare: p.welfare,
      other: p.other,
    };
    if (key in adminField) {
      const n = adminField[key] || 0;
      return n > 0 ? formatWageNumber(n) : '';
    }
  }
  const n = Number(payrollForm.value[key]) || 0;
  // 비과세·합계 칸은 0원도 "0" 표시 (빈칸이면 미계산으로 오인 — 고영이)
  if (key === 'nonTax' || key === 'wageTotal' || key === 'l_total' || key === 'c_total') {
    return formatWageNumber(n);
  }
  return n > 0 ? formatWageNumber(n) : '';
};

/** 참고 바 — 0원도 표시 (센터장·관리자 시점 각각 표시용 지급금액 기준) */
const formatReferenceMoneyField = (key: HomeWagePayrollFieldKey) => {
  if (isDirectorPayrollView.value) {
    if (key === 'grossPay') {
      return formatWageNumber(
        isCaregiverEmployee(selectedEmployee.value)
          ? displayGrossPay.value
          : directorDisplayGrossPay.value
      );
    }
    if (key === 'net') {
      return formatWageNumber(directorDisplayNetPay.value);
    }
    if (key === 'profit' && isCaregiverEmployee(selectedEmployee.value)) {
      return formatWageNumber(
        calcHomeWageCenterProfit(
          claimSugaMoneyTotal.value,
          displayGrossPay.value,
          Number(payrollForm.value.c_total) || 0
        )
      );
    }
  } else {
    // 관리자 시점 — 요양보호사는 지급금액 필드·미리보기와 동일(displayGrossPay)
    // (adminDisplayGrossPay는 extract 시 lunchDirector까지 빠질 수 있음 — 김영남 6월)
    if (key === 'grossPay') {
      return formatWageNumber(displayGrossPay.value);
    }
    if (key === 'net') {
      return formatWageNumber(adminDisplayNetPay.value);
    }
    if (key === 'profit' && isCaregiverEmployee(selectedEmployee.value)) {
      return formatWageNumber(
        calcHomeWageCenterProfit(
          claimSugaMoneyTotal.value,
          displayGrossPay.value,
          Number(payrollForm.value.c_total) || 0
        )
      );
    }
  }
  return formatWageNumber(Number(payrollForm.value[key]) || 0);
};

/** 연·야·휴 관리자 툴팁용 — pool/additive·이론상/실제 금액 */
const getPremiumAdminTooltipContext = () => {
  const hourly = caregiverHourlyWage.value;
  const overtimeHours = Number(payrollForm.value.overtimeHours) || 0;
  const nightHours = Number(payrollForm.value.nightHours) || 0;
  const holidayHoursRaw = Number(payrollForm.value.holidayHours) || 0;
  const viewMode = resolveCaregiverPayrollViewMode();
  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    overtimeHours,
    nightHours,
    holidayHoursRaw,
    viewMode
  );
  const mode = resolvePremiumPayMode();
  const severeSum = claimSummaryRows.value
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + r.totalAmount, 0);
  const nonSevereTarget = claimGrandTotal.value - severeSum;
  const linked = calcHomeWageHourlyLinkedPay(getHourlyLinkedPayInput());
  const lunch = Number(payrollForm.value.lunch) || 0;

  let premiumPool = 0;
  if (mode === 'pool') {
    premiumPool = Math.max(0, nonSevereTarget - lunch);
  }

  const fullPremiums = calcHomeWageCaregiverPremiumPays(
    hourly,
    hoursForAlloc.overtimeHours,
    hoursForAlloc.nightHours,
    hoursForAlloc.holidayHours,
    yearMonth.value
  );
  const allocatedPremiums = allocateHomeWagePremiumPays({
    mode,
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    premiumPool: mode === 'pool' ? premiumPool : undefined,
    yearMonth: yearMonth.value,
  });

  return {
    hourlyWage: hourly,
    mode,
    totalSugaHours: totalSugaHours.value,
    isFamilyCare: isAllFamilyCareInClaim.value,
    nonSevereTarget,
    linkedBasic: linked.basic,
    linkedWeekend: linked.weekend,
    linkedAnnual: linked.annual,
    lunch,
    fullPremiums,
    allocatedPremiums,
    premiumPool,
    hoursByKind: {
      overtime: hoursForAlloc.overtimeHours,
      night: hoursForAlloc.nightHours,
      holiday: hoursForAlloc.holidayHours,
    },
    amountByKind: {
      overtime:
        (Number(payrollForm.value.overtime) || 0) +
        (Number(payrollForm.value.overtimeNontax) || 0),
      night:
        (Number(payrollForm.value.night) || 0) + (Number(payrollForm.value.nightNontax) || 0),
      holiday:
        (Number(payrollForm.value.holiday) || 0) +
        (Number(payrollForm.value.holidayNontax) || 0),
    },
  };
};

const getPremiumAdminFieldTooltip = (kind: 'overtime' | 'night' | 'holiday'): string => {
  if (
    !canEditTaxRate.value
    || !isCaregiverEmployee(selectedEmployee.value)
    || isDirectorPayrollView.value
  ) {
    return '';
  }
  const ctx = getPremiumAdminTooltipContext();
  return buildHomeWagePremiumAdminTooltip({
    kind,
    hourlyWage: ctx.hourlyWage,
    hours: ctx.hoursByKind[kind],
    amount: ctx.amountByKind[kind],
    mode: ctx.mode,
    totalSugaHours: ctx.totalSugaHours,
    isFamilyCare: ctx.isFamilyCare,
    nonSevereTarget: ctx.nonSevereTarget,
    linkedBasic: ctx.linkedBasic,
    linkedWeekend: ctx.linkedWeekend,
    linkedAnnual: ctx.linkedAnnual,
    lunch: ctx.lunch,
    fullPremiums: ctx.fullPremiums,
    allocatedPremiums: ctx.allocatedPremiums,
    premiumPool: ctx.premiumPool,
  });
};

/** 동적 툴팁 생성 — 계산 공식 + 모든 금액 표시 */
const getFieldTooltip = (key: HomeWagePayrollFieldKey, forAdminMemo = false): string => {
  const f = formatWageNumber;
  const v = (k: HomeWagePayrollFieldKey) => Number(payrollForm.value[k]) || 0;
  const hourly = caregiverHourlyWage.value;
  const hours = totalSugaHours.value;
  const workDays = workCalendarSummary.value.workDayCount;
  const annualDays = annualLeaveDays.value;
  const taxRate = taxRateInfo.value;
  const taxableBase = getInsuranceTaxableBase();
  const payrollWageTotal = v('wageTotal');

  switch (key) {
    case 'basic': {
      if (!isCaregiverEmployee(selectedEmployee.value)) {
        const staffHourly = Number(staffForm.value.hourlyWage) || hourly;
        const staffHours = Number(staffForm.value.workHours) || hours;
        const pool = calcHomeWageStaffBasePay(staffHourly, staffHours, yearMonth.value);
        const autoLunch = Number(v('lunch')) || 0;
        const adminBasic = Math.max(0, pool - autoLunch);
        if (isDirectorPayrollView.value) {
          return `기본급 = 시급 × 근무시간\n${f(staffHourly)}원 × ${staffHours}시간 = ${f(pool)}원`;
        }
        return [
          '【사복·운전원 기본급 — 관리자】',
          '시급 × 근무시간 = 기본급 + 자동중식',
          `${f(staffHourly)}원 × ${staffHours}시간 = ${f(pool)}원`,
          `기본급 ${f(adminBasic)}원 + 자동중식 ${f(autoLunch)}원 = ${f(pool)}원`,
        ].join('\n');
      }
      return buildHomeWageBasicPayAdminTooltip({
        payCalcHourlyWage: hourly,
        totalSugaHours: hours,
        workDays,
        annualLeaveDays: annualDays,
        actualBasic: v('basic'),
        nonSevereTarget: claimGrandTotal.value - severeSumFromClaim.value,
        claimGrandTotal: claimGrandTotal.value,
        severeSum: severeSumFromClaim.value,
        lunch: v('lunch'),
        isFamilyCareOnly: isAllFamilyCareInClaim.value,
        payrollDbLock: payrollDbLock.value,
      });
    }
    case 'weekend':
      // 가족요양 전용: 주휴수당 = 0 (기본급에 포함)
      if (isAllFamilyCareInClaim.value) {
        return `★ 가족요양 전용 ★\n주휴수당 = 0원 (기본급에 포함됨)`;
      }
      return `기본급 × 20%\n${f(v('basic'))}원 × 0.2 = ${f(v('weekend'))}원`;
    case 'annual': {
      // 가족요양 전용: 연차수당 = 0 (기본급에 포함)
      if (isAllFamilyCareInClaim.value) {
        return `★ 가족요양 전용 ★\n연차수당 = 0원 (기본급에 포함됨)`;
      }
      if (isDirectorPayrollView.value && !forAdminMemo) {
        const displayAnnual = directorDisplayAnnualAmount.value;
        const recipientTotal = claimGrandTotal.value - severeSumFromClaim.value;
        return `연차수당 = 수급자별 합계 − 기본급 − 주휴수당\n${f(recipientTotal)}원 − ${f(v('basic'))}원 − ${f(v('weekend'))}원\n= ${f(displayAnnual)}원`;
      }
      const baseAnnual = workDays > 0 ? Math.round((hourly * hours / workDays) * annualDays / 12) : 0;
      return `(시급 × 총수가시간 ÷ 근무일수) × 연차일수 ÷ 12 + 차액\n(${f(hourly)}원 × ${hours.toFixed(1)}시간 ÷ ${workDays}일) × ${annualDays}일 ÷ 12\n= ${f(baseAnnual)}원 + 차액 = ${f(v('annual'))}원`;
    }
    case 'laborDay':
      return buildHomeWageLaborDayTooltip({
        yearMonth: yearMonth.value,
        payCalcHourlyWage: hourly,
        totalSugaHours: hours,
        workDays,
        isCaregiver: isCaregiverEmployee(selectedEmployee.value),
        workedOnMayFirst: hasWorkedOnMayFirstFromClaims(
          caregiverClaimRows.value,
          yearMonth.value
        ),
        mayFirstPlanHours: calcMayFirstPlanHoursFromClaims(
          caregiverClaimRows.value,
          yearMonth.value
        ),
        laborDayAmount: v('laborDay'),
        weekendPay: v('weekend'),
      });
    case 'overtime':
      return getPremiumAdminFieldTooltip('overtime');
    case 'night':
      return getPremiumAdminFieldTooltip('night');
    case 'holiday':
      if (isAllFamilyCareInClaim.value && isDirectorPayrollView.value) {
        return `★ 가족요양 전용 ★\n센터장 시점 — 휴일수당 = 0원 (기본급에 포함됨)`;
      }
      if (isAllFamilyCareInClaim.value && !isDirectorPayrollView.value) {
        const holidayTotal = v('holiday') + v('holidayNontax');
        return `★ 가족요양 — 관리자 시점 ★\n휴일수당 = 시급 × 0.5 × 휴일시간\n기본급에서 분리 (총지급 추가 아님)\n= ${f(holidayTotal)}원`;
      }
      return getPremiumAdminFieldTooltip('holiday');
    case 'senior':
      return `근속연수에 따른 장기근속수당 (공단 지원)\n${f(v('senior'))}원`;
    case 'bosu':
      return `보수교육 수당\n${f(v('bosu'))}원`;
    case 'severe':
      return `중증수급자 가산금 (서비스횟수 × 수가)\n${f(v('severe'))}원`;
    case 'bonus':
      return `성과금/상여금\n${f(v('bonus'))}원`;
    case 'welfare':
      return `처우개선 수당\n${f(v('welfare'))}원`;
    case 'other':
      return `기타 수당\n${f(v('other'))}원`;
    case 'wageTotal': {
      if (isCaregiverEmployee(selectedEmployee.value) && !forAdminMemo) {
        return `지급금액 (시점과 무관·동일 합계)\n기본급 합계 ${f(unifiedCaregiverBasePayTotal.value)}원 + 수당 합계 ${f(unifiedCaregiverAllowanceTotal.value)}원\n= ${f(displayGrossPay.value)}원`;
      }
      return `과세 임금 합계\n기본급·주휴·연차·중증·기타과세 + 연·야·휴 과세분\n= ${f(payrollWageTotal)}원`;
    }
    case 'lunch': {
      if (isDirectorPayrollView.value && !forAdminMemo) {
        return '';
      }
      const lunchDays = workCalendarSummary.value.lunchWorkDayCount;
      return `${HOME_WAGE_NONTAX_TERM.lunchAuto} — 연차수당 잉여에서 산출\nmin(점심근무일 × 1일 ${HOME_WAGE_NONTAX_TERM.lunch}, 남은금액, 20만원)\n${lunchDays}일 × 10,000원 = ${f(v('lunch'))}원\n(5천원 미만 시 0원 처리)`;
    }
    case 'lunchDirector': {
      if (isDirectorPayrollView.value && !forAdminMemo) {
        // 사복·운전원: 자동중식은 기본급에 숨김 — 센터장 입력 중식만 표시
        if (!isCaregiverEmployee(selectedEmployee.value)) {
          const directorLunch = clampHomeWageLunchDirectorInput(v('lunchDirector'));
          return `${HOME_WAGE_NONTAX_TERM.lunch} — 센터장 입력 (월 최대 ${f(HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX)}원)\n= ${f(directorLunch)}원`;
        }
        return `${HOME_WAGE_NONTAX_TERM.lunch} — 센터장 입력 (월 최대 ${f(HOME_WAGE_LUNCH_DIRECTOR_INPUT_MAX)}원)\n입력: ${f(v('lunchDirector'))}원\n※ 자동 산출 ${HOME_WAGE_NONTAX_TERM.lunch}는 별도 반영되며, 저장 시 합산 20만원 한도 적용`;
      }
      const combinedLunch = calcHomeWageCombinedLunch(v('lunch'), v('lunchDirector'));
      return `${HOME_WAGE_NONTAX_TERM.lunchDirector} — 총급여에 추가되는 수당\n자동 ${f(v('lunch'))} + 센터장 ${f(v('lunchDirector'))} = 합산 ${f(combinedLunch)}원 (최대 20만원)`;
    }
    case 'car':
      return `${HOME_WAGE_NONTAX_TERM.car} (비과세 월 20만원 한도)\n${f(v('car'))}원`;
    case 'child':
      return `${HOME_WAGE_NONTAX_TERM.child} 지원 (비과세 월 20만원 한도)\n${f(v('child'))}원`;
    case 'nonTax': {
      const split = premiumTaxSplit.value;
      const laborPart =
        showLaborDayNontaxField.value && v('laborDay') > 0
          ? ` + 노동절 ${f(v('laborDay'))}`
          : '';
      const laborLabel =
        showLaborDayNontaxField.value && v('laborDay') > 0 ? ' + 노동절' : '';
      if (isDirectorPayrollView.value && !forAdminMemo) {
        const directorLunch = v('lunchDirector');
        return `비과세 — ${HOME_WAGE_NONTAX_TERM.lunch} + ${HOME_WAGE_NONTAX_TERM.car} + ${HOME_WAGE_NONTAX_TERM.child}${laborLabel}\n${f(directorLunch)} + ${f(v('car'))} + ${f(v('child'))}${laborPart}\n= ${f(v('nonTax'))}원`;
      }
      // 툴팁도 실지급 자동중식만 (연차 흡수 고스트 제외)
      const autoLunchForTip =
        isCaregiverEmployee(selectedEmployee.value)
          ? resolveAdminCaregiverAutoLunchForNontax()
          : v('lunch');
      const combinedLunch = calcHomeWageCombinedLunch(autoLunchForTip, v('lunchDirector'));
      const premiumPart = productionNontaxActive.value
        ? ` + 연·야·휴(비) ${f(split.totalNontax)}`
        : '';
      return `${HOME_WAGE_NONTAX_TERM.lunchCombined}${laborLabel} + ${HOME_WAGE_NONTAX_TERM.car} + ${HOME_WAGE_NONTAX_TERM.child}${premiumPart}\n자동 ${f(autoLunchForTip)} + 센터장 ${f(v('lunchDirector'))} = ${f(combinedLunch)}${laborPart} + ${f(v('car'))} + ${f(v('child'))}${productionNontaxActive.value ? ` + ${f(split.totalNontax)}` : ''}\n= ${f(v('nonTax'))}원${showLaborDayNontaxField.value && v('laborDay') > 0 ? ' (노동절수당 비과세)' : ''}`;
    }
    case 'l_yeon':
    case 'c_yeon': {
      const rate = formatHomeWageRatePercent((taxRate?.rateYeon as number) || 0.045);
      return `과세임금 × ${rate}\n${f(taxableBase)}원 × ${rate} = ${f(v(key))}원`;
    }
    case 'l_gun':
    case 'c_gun': {
      if (isFamilyUnder60HealthExempt.value) {
        return `${HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE}\n\n= 0원`;
      }
      const rate = formatHomeWageRatePercent((taxRate?.rateGun as number) || 0.03545);
      return `과세임금 × ${rate}\n${f(taxableBase)}원 × ${rate} = ${f(v(key))}원`;
    }
    case 'l_jang':
    case 'c_jang': {
      if (isFamilyUnder60HealthExempt.value) {
        return `${HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE}\n\n건강보험 0원 → 장기요양 0원`;
      }
      const rate = formatHomeWageRatePercent((taxRate?.rateJang as number) || 0.1295);
      const gunVal = key === 'l_jang' ? v('l_gun') : v('c_gun');
      return `건강보험료 × ${rate}\n${f(gunVal)}원 × ${rate} = ${f(v(key))}원`;
    }
    case 'l_go': {
      const rate = formatHomeWageRatePercent((taxRate?.rateGo as number) || 0.009);
      return `과세임금 × ${rate}\n${f(taxableBase)}원 × ${rate} = ${f(v('l_go'))}원`;
    }
    case 'c_go': {
      const rate = formatHomeWageRatePercent((taxRate?.rateGo as number) || 0.009);
      return `과세임금 × ${rate} (사업주)\n${f(taxableBase)}원 × ${rate} = ${f(v('c_go'))}원`;
    }
    case 'l_so':
      return `간이세액표 기준 소득세\n과세임금 ${f(taxableBase)}원 기준\n= ${f(v('l_so'))}원`;
    case 'l_ji':
      return `소득세 × 10%\n${f(v('l_so'))}원 × 10% = ${f(v('l_ji'))}원`;
    case 'l_jiwon': {
      const calc = getSupportDeductionResult();
      const { parts, base, yeonDeduct, goDeduct, result: sup } = calc;

      if (base <= 0) {
        return `${HOME_WAGE_SUPPORT_DEDUCTION_UI.formulaIntro}`;
      }

      const deductParts = [];
      if (sup.yeon > 0) deductParts.push(`국민 ${f(sup.yeon)}`);
      if (sup.gun > 0) deductParts.push(`건강 ${f(sup.gun)}`);
      if (sup.jang > 0) deductParts.push(`장기 ${f(sup.jang)}`);
      if (sup.go > 0) deductParts.push(`고용 ${f(sup.go)}`);
      if (sup.sil > 0) deductParts.push(`실업 ${f(sup.sil)}`);
      if (sup.san > 0) deductParts.push(`산재 ${f(sup.san)}`);
      if (sup.retirement > 0) deductParts.push(`퇴직 ${f(sup.retirement)}`);

      const excludedParts = [];
      if (!yeonDeduct) excludedParts.push('국민연금');
      if (!goDeduct) excludedParts.push('고용보험');
      if (deductionFixed.value.l_jiwon) excludedParts.push(HOME_WAGE_SUPPORT_DEDUCTION_UI.selectiveNote);

      return [
        formatSupportDeductionTargetSummaryLine(parts, base, f),
        deductParts.join('\n'),
        excludedParts.length > 0 ? `(${excludedParts.join(', ')})` : '',
        `= ${f(sup.total)}원`,
      ].filter(Boolean).join('\n');
    }
    case 'l_bonin':
      return `본인부담금 공제액\n${f(v('l_bonin'))}원`;
    case 'l_etc':
      return `기타 근로자 공제액\n${f(v('l_etc'))}원`;
    case 'l_total': {
      const ins4 = v('l_yeon') + v('l_gun') + v('l_jang') + v('l_go');
      const jiwon = v('l_jiwon');
      const bonin = v('l_bonin');
      const etc = v('l_etc');
      let parts = [`4대보험: ${f(ins4)}원`, `소득세: ${f(v('l_so'))}원`, `지방세: ${f(v('l_ji'))}원`];
      if (jiwon > 0) parts.push(`지원공제: ${f(jiwon)}원`);
      if (bonin > 0) parts.push(`본인부담금공제: ${f(bonin)}원`);
      if (etc > 0) parts.push(`기타: ${f(etc)}원`);
      return `${parts.join(' + ')}\n= ${f(v('l_total'))}원`;
    }
    case 'c_sil': {
      const rate = formatHomeWageRatePercent((taxRate?.rateSil as number) || 0.0065);
      return `과세임금 × ${rate}\n${f(taxableBase)}원 × ${rate} = ${f(v('c_sil'))}원`;
    }
    case 'c_san': {
      const rate = formatHomeWageRatePercent(sanRate.value);
      return `과세임금 × ${rate} (산재요율)\n${f(taxableBase)}원 × ${rate} = ${f(v('c_san'))}원`;
    }
    case 'c_out': {
      const lunchExpense = nontaxSettings.value.lunchExpense;
      const lunchParts = resolveRetirementLunchPartsForCalc();
      const gross = getHomeWageLedgerGrossPay({
        ...payrollForm.value,
        lunch: lunchParts.lunch,
        lunchDirector: lunchParts.lunchDirector,
      });
      const lunchAmt = calcHomeWageCombinedLunch(lunchParts.lunch, lunchParts.lunchDirector);
      const lines = [`기관 부담금 퇴직금(c_out)`];

      if (applyRetirement.value && isCaregiverEmployee(selectedEmployee.value)) {
        const split = getRetirementPaySplitLive();
        lines.push('');
        lines.push('【산정 지급금】');
        if (split.visitCarePay > 0) {
          lines.push(`일반 방문요양(B1) ${f(split.visitCarePay)}원`);
        }
        if (split.visitBathPay > 0) {
          lines.push(`방문목욕(B2) ${f(split.visitBathPay)}원`);
        }
        if (split.familyPay > 0) {
          lines.push(`가족 방문요양 ${f(split.familyPay)}원`);
        }
        if (hasRetirementSourceFlags(retirementSource.value)) {
          const parts: string[] = [];
          if (retirementSource.value.useVisitCare) parts.push('일반 방문요양');
          if (retirementSource.value.useVisitBath) parts.push('방문목욕');
          if (retirementSource.value.useFamily) parts.push('가족');
          lines.push(`선택: ${parts.join(' + ')}`);
          let base = 0;
          if (retirementSource.value.useVisitCare) base += split.visitCarePay;
          if (retirementSource.value.useVisitBath) base += split.visitBathPay;
          if (retirementSource.value.useFamily) base += split.familyPay;
          if (lunchExpense) {
            lines.push(
              `(${f(base)}원 − ${f(lunchAmt)}원) ÷ 12 = ${f(v('c_out'))}원`
            );
          } else {
            lines.push(`${f(base)}원 ÷ 12 = ${f(v('c_out'))}원`);
          }
        } else {
          lines.push('선택: 직접 입력');
          lines.push(`= ${f(v('c_out'))}원`);
        }
      } else if (applyRetirement.value) {
        lines.push(`직접 입력 = ${f(v('c_out'))}원`);
      } else {
        lines.push(
          lunchExpense
            ? `(총 지급금액 − ${HOME_WAGE_NONTAX_TERM.lunch}) ÷ 12`
            : `총 지급금액 ÷ 12`
        );
        lines.push(
          lunchExpense
            ? `(${f(gross)}원 − ${f(lunchAmt)}원) ÷ 12 = ${f(v('c_out'))}원`
            : `${f(gross)}원 ÷ 12 = ${f(v('c_out'))}원`
        );
      }

      lines.push('');
      lines.push(
        applyRetirement.value ? '고정: 체크됨 → 입력값·선택 유지' : '고정: 미체크 → 자동 산출'
      );
      if (!applyRetirement.value && retirementWarningMessage.value) {
        lines.push(`미적용 사유: ${retirementWarningMessage.value}`);
      }
      return lines.join('\n');
    }
    case 'c_total': {
      const ins4 = v('c_yeon') + v('c_gun') + v('c_jang') + v('c_go');
      return `4대보험 + 실업 + 산재 + 퇴직금\n${f(ins4)}원 + ${f(v('c_sil'))}원 + ${f(v('c_san'))}원 + ${f(v('c_out'))}원\n= ${f(v('c_total'))}원`;
    }
    case 'grossPay': {
      if (isCaregiverEmployee(selectedEmployee.value) && !forAdminMemo) {
        return `지급금액 (시점과 무관·동일 합계)\n기본급 합계 ${f(unifiedCaregiverBasePayTotal.value)}원 + 수당 합계 ${f(unifiedCaregiverAllowanceTotal.value)}원\n= ${f(displayGrossPay.value)}원`;
      }
      if (isDirectorPayrollView.value && !forAdminMemo) {
        const autoLunch = resolveSlipAutoLunchForDisplay();
        const lunchNote =
          autoLunch > 0
            ? `\n(자동 ${HOME_WAGE_NONTAX_TERM.lunch} ${f(autoLunch)}원 → 연차수당 합산 표시)`
            : '';
        return `지급금액 (시점과 무관·동일 합계)${lunchNote}\n= ${f(displayGrossPay.value)}원`;
      }
      return `지급금액 (시점과 무관·동일 합계)\n과세 ${f(payrollWageTotal)}원 + 비과세 ${f(adminDisplayNonTax.value)}원\n= ${f(adminDisplayGrossPay.value)}원`;
    }
    case 'net': {
      return `지급금액 - 근로자공제합계\n${f(displayGrossPay.value)}원 - ${f(v('l_total'))}원 = ${f(directorDisplayNetPay.value)}원`;
    }
    case 'c_save': {
      const totalNontax = calcHomeWageTotalNontax(
        payrollForm.value,
        productionNontaxActive.value,
        yearMonth.value
      );
      const carAmt = v('car');
      const childAmt = v('child');
      const base = calcHomeWageCenterSavingsNontaxBase(
        payrollForm.value,
        productionNontaxActive.value,
        yearMonth.value
      );
      const savingsInput = buildCenterTaxSavingsInput(base);
      const appliedItems = savingsInput ? calcHomeWageCenterTaxSavingsBreakdown(savingsInput) : [];

      const parts = ['【절감 기준 비과세】'];
      parts.push(`비과세 합계 ${f(totalNontax)}원`);
      if (carAmt > 0 || childAmt > 0) {
        const minus: string[] = [];
        if (carAmt > 0) minus.push(`${HOME_WAGE_NONTAX_TERM.car} ${f(carAmt)}원`);
        if (childAmt > 0) minus.push(`${HOME_WAGE_NONTAX_TERM.child} ${f(childAmt)}원`);
        parts.push(`− ${minus.join(' − ')}`);
      }
      parts.push(`= ${f(base)}원`);
      parts.push('');
      parts.push('【기관 절감】');
      if (appliedItems.length > 0) {
        appliedItems.forEach((item) => parts.push(`${item.label} ${f(item.amount)}원`));
        parts.push(`= ${f(v('c_save'))}원`);
      } else {
        parts.push('적용 공제 없음');
      }
      return parts.join('\n');
    }
    case 'profit': {
      if (!isCaregiverEmployee(selectedEmployee.value)) {
        return `수가(청구) 데이터 없음 → 0원`;
      }
      const total = claimSugaMoneyTotal.value;
      const displayGross = displayGrossPay.value;
      const cost = displayGross + v('c_total');
      const profitVal = calcHomeWageCenterProfit(total, displayGross, v('c_total'));
      return `청구 수가금액 합계 - (지급금액 + 기관공제)\n${f(total)}원 - ${f(cost)}원 = ${f(profitVal)}원`;
    }
    default:
      return '';
  }
};

/** 4대보험 동적 툴팁 (나이별 면제 정보 포함) */
const getInsuranceTooltip = (pair: HomeWageInsurancePairDef): string => {
  const f = formatWageNumber;
  const amount = getSharedInsuranceAmount(pair);
  const taxableBase = getInsuranceTaxableBase();
  const taxRate = taxRateInfo.value;
  const gunAmount = getSharedInsuranceAmount(insurancePairs.find(p => p.key === 'gun')!);
  
  // 나이 기반 면제 확인
  const birth = getSelectedInsuranceBirth();
  const ageExempt = resolveSelectedHomeWageAgeInsurance(birth);
  const ageDisplay = ageExempt.age !== null ? `(만 ${ageExempt.age}세)` : '';
  
  switch (pair.key) {
    case 'yeon': {
      if (ageExempt.pensionExempt) {
        return `★ 만 60세 이상 국민연금 제외 ${ageDisplay}\n\n과세임금 × 4.5% → 0원`;
      }
      const rate = formatHomeWageRatePercent((taxRate?.rateYeon as number) || 0.045);
      return `과세임금 × ${rate} (근로자·기관 각각)\n${f(taxableBase)}원 × ${rate} = ${f(amount)}원`;
    }
    case 'gun': {
      if (isFamilyUnder60HealthExempt.value) {
        return `${HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE}\n\n= 0원`;
      }
      const rate = formatHomeWageRatePercent((taxRate?.rateGun as number) || 0.03545);
      return `과세임금 × ${rate} (근로자·기관 각각)\n${f(taxableBase)}원 × ${rate} = ${f(amount)}원`;
    }
    case 'jang': {
      if (isFamilyUnder60HealthExempt.value) {
        return `${HOME_WAGE_FAMILY_UNDER60_HEALTH_EXEMPT_MESSAGE}\n\n건강보험 0원 → 장기요양 0원`;
      }
      const rate = formatHomeWageRatePercent((taxRate?.rateJang as number) || 0.1295);
      return `건강보험료 × ${rate} (근로자·기관 각각)\n${f(gunAmount)}원 × ${rate} = ${f(amount)}원`;
    }
    default:
      return '';
  }
};

/** 고용보험 — 근로·기관 분리 툴팁 */
const getEmploymentInsuranceTooltip = (side: EmploymentInsuranceSide): string => {
  const f = formatWageNumber;
  const taxableBase = getInsuranceTaxableBase();
  const taxRate = taxRateInfo.value;
  const fieldKey = side === 'labor' ? 'l_go' : 'c_go';
  const amount = Number(payrollForm.value[fieldKey]) || 0;
  const sideLabel = side === 'labor' ? '근로자' : '기관(사업주)';

  const birth = getSelectedInsuranceBirth();
  const ageExempt = resolveSelectedHomeWageAgeInsurance(birth);
  const ageDisplay = ageExempt.age !== null ? `(만 ${ageExempt.age}세)` : '';

  if (side === 'labor' && ageExempt.employmentExempt) {
    return `★ 만 65세 이상 고용보험(근로) 제외 ${ageDisplay}\n\n과세임금 × 0.9% → 0원`;
  }
  if (side === 'corp' && isRepresentative.value) {
    return `★ 대표자 기관 고용보험 면제\n\n= 0원`;
  }

  const rate = formatHomeWageRatePercent((taxRate?.rateGo as number) || 0.009);
  if (ageExempt.employmentExempt) {
    return `★ 만 65세 이상 고용보험 제외 ${ageDisplay}\n\n과세임금 × ${rate} → 0원`;
  }
  return `과세임금 × ${rate} (${sideLabel})\n${f(taxableBase)}원 × ${rate} = ${f(amount)}원`;
};

const employeeConditionYearMonthLabel = computed(
  () => `${selectedYear.value}년 ${String(selectedMonth.value).padStart(2, '0')}월`
);

/** 사원 멘트 — 센터장 시점 마스킹 없이 DB·회계 금액 표시 */
const formatEmployeeConditionAmount = (key: HomeWagePayrollFieldKey): string => {
  if (key === 'overtime' || key === 'night' || key === 'holiday') {
    if (premiumManualAdjustEnabled.value) {
      const snap = buildPremiumAutoSnapshot();
      if (snap) {
        const full = getHomeWagePremiumFullAmounts(snap.split);
        const map = {
          overtime: full.overtime,
          night: full.night,
          holiday: full.holiday,
        };
        const n = map[key] || 0;
        return n > 0 ? formatWageNumber(n) : '';
      }
    }
  }
  const n = Number(payrollForm.value[key]) || 0;
  return n > 0 ? formatWageNumber(n) : '';
};

const appendTooltipFieldSection = (
  sections: HomeWageEmployeeConditionSection[],
  id: string,
  title: string,
  icon: string,
  fields: Array<{ key: HomeWagePayrollFieldKey; label: string }>,
  resolveTooltip: (key: HomeWagePayrollFieldKey) => string,
  resolveAmount?: (key: HomeWagePayrollFieldKey) => string
) => {
  const fieldItems: HomeWageEmployeeConditionFieldItem[] = [];
  for (const field of fields) {
    const detail = resolveTooltip(field.key)?.trim();
    if (!detail) continue;
    const amountRaw = resolveAmount?.(field.key)?.replace(/,/g, '') || '';
    fieldItems.push({
      label: field.label,
      amount: amountRaw && amountRaw !== '0' ? resolveAmount!(field.key) : undefined,
      detail,
    });
  }
  if (fieldItems.length > 0) {
    sections.push({
      id,
      title,
      type: 'fields',
      icon,
      badge: `${fieldItems.length}항목`,
      fieldItems,
    });
  }
};

/** 관리자 — 해당 사원 적용 조건·산출 근거 전체 */
const employeeConditionSections = computed((): HomeWageEmployeeConditionSection[] => {
  if (!selectedEmployee.value) return [];

  const sections: HomeWageEmployeeConditionSection[] = [];
  const emp = selectedEmployee.value;
  const f = formatWageNumber;
  const isCaregiver = isCaregiverEmployee(emp);

  sections.push({
    id: 'summary',
    title: '사원·청구 요약',
    type: 'summary',
    icon: 'mdi-account-details-outline',
    kvItems: [
      { label: '성명', value: getEmpName(emp) },
      { label: '직종', value: getEmpJob(emp) || '-' },
      { label: '생년월일', value: formatBirthDisplay(getEmpBirth(emp)) },
      { label: '입사일', value: getEmpJoinDate(emp) || '-' },
      { label: '사업구분', value: selectedBizGubun.value || '-' },
      { label: '귀속연월', value: employeeConditionYearMonthLabel.value },
      { label: '지급계산시급', value: `${f(caregiverHourlyWage.value)}원`, highlight: true },
      { label: '청구 수가 합계', value: `${f(claimGrandTotal.value)}원`, highlight: true },
      { label: '총 수가시간', value: formatSugaHours(totalSugaHours.value) || '0분' },
      { label: '근무일수', value: `${workCalendarSummary.value.workDayCount}일` },
      { label: '서비스 횟수', value: `${workCalendarSummary.value.serviceCount}회` },
      { label: '연차일수', value: `${annualLeaveDays.value}일` },
      ...(severeSumFromClaim.value > 0
        ? [{ label: '중증가산 합계', value: `${f(severeSumFromClaim.value)}원`, highlight: true }]
        : []),
      ...(getEmpClaimBizChips(emp).length > 0
        ? [{ label: '청구 유형', value: getEmpClaimBizChips(emp).join(', ') }]
        : []),
    ],
  });

  if (familyCareTooltip.value) {
    sections.push({
      id: 'family-care',
      title: '가족요양 조건',
      type: 'note',
      icon: 'mdi-home-heart-outline',
      noteLines: familyCareTooltip.value.split('\n').filter((line) => line.trim()),
    });
  }

  if (isCaregiver) {
    const mode = resolvePremiumPayMode();
    sections.push({
      id: 'premium-mode',
      title: '연·야·휴 산출 방식',
      type: 'note',
      icon: 'mdi-clock-outline',
      noteLines: mode === 'pool'
        ? [
            '가족요양 전용 pool 방식이 적용됩니다.',
            `월급여 풀(비중증·${HOME_WAGE_NONTAX_TERM.lunch} 제외)에서 연장·야간·휴일·기본급을 함께 배분합니다.`,
          ]
        : [
            '가산(additive) 방식이 적용됩니다.',
            '시급 × 시간 기준으로 연장·야간·휴일을 각각 독립 계산합니다.',
          ],
    });
  }

  const settingItems: HomeWageEmployeeConditionSettingItem[] = [
    {
      label: HOME_WAGE_NONTAX_TERM.lunchExpense,
      active: nontaxSettings.value.lunchExpense,
      description: nontaxSettings.value.lunchExpense
        ? `퇴직금 산정 시 ${HOME_WAGE_NONTAX_TERM.lunch} 금액을 제외합니다`
        : `${HOME_WAGE_NONTAX_TERM.lunch}를 통상 임금에 포함하여 처리합니다`,
    },
    {
      label: '연야휴 비과세',
      active: productionNontaxActive.value,
      description: productionNontaxActive.value
        ? '연야휴 반영 시 월 20만원까지 비과세 자동(가족요양 포함, 생산직 체크 불필요)'
        : '센터장 시점은 연·야·휴를 합산 표시(과세/비과세 칸 분리 없음)',
    },
    {
      label: '대표자 여부',
      active: isRepresentative.value,
      description: isRepresentative.value
        ? '기관 고용보험·산재보험·실업급여가 면제됩니다'
        : '일반 사원과 동일한 기관 부담 규칙 적용',
    },
    {
      label: '퇴직금 적립',
      active: applyRetirement.value,
      description: applyRetirement.value
        ? '퇴직금 산정·적립 규칙이 적용됩니다'
        : retirementWarningMessage.value || '퇴직금 적립이 제외됩니다',
    },
    {
      label: '연·야·휴 수동입력',
      active: premiumManualAdjustEnabled.value,
      description: premiumManualAdjustEnabled.value
        ? '관리자가 직접 입력한 금액을 우선 사용합니다'
        : '청구·시급 기준 자동 계산값을 사용합니다',
    },
  ];
  if (!productionNontaxValidation.value.canApply) {
    const prodMsg =
      productionNontaxValidation.value.positionMessage
      || productionNontaxValidation.value.usualMessage;
    if (prodMsg) {
      settingItems.push({ label: '생산직 검증', active: false, description: prodMsg });
    }
  }
  sections.push({
    id: 'settings',
    title: '관리 설정',
    type: 'settings',
    icon: 'mdi-tune-variant',
    settingItems,
  });

  if (!isCaregiver && staffCalcResult.value?.validation?.warnings?.length) {
    sections.push({
      id: 'staff-warnings',
      title: '확인이 필요한 항목',
      type: 'warning',
      icon: 'mdi-alert-circle-outline',
      warningLines: staffCalcResult.value.validation.warnings,
    });
  }

  const resolveAmount = (key: HomeWagePayrollFieldKey) => formatEmployeeConditionAmount(key);
  const resolveTooltip = (key: HomeWagePayrollFieldKey) => getFieldTooltip(key, true);

  if (isCaregiver) {
    appendTooltipFieldSection(
      sections,
      'base-pay',
      '기본급·주휴·연차',
      'mdi-cash-multiple',
      basePayFields,
      resolveTooltip,
      resolveAmount
    );

    const nontaxFields = [
      ...(showLaborDayNontaxField.value && HOME_WAGE_LABOR_DAY_NONTAX_FIELD
        ? [HOME_WAGE_LABOR_DAY_NONTAX_FIELD]
        : []),
      ...nonTaxCashInputFields,
    ];
    appendTooltipFieldSection(
      sections,
      'nontax',
      '비과세 수당',
      'mdi-shield-check-outline',
      nontaxFields,
      resolveTooltip,
      resolveAmount
    );
    appendTooltipFieldSection(
      sections,
      'premium',
      '연장·야간·휴일',
      'mdi-clock-alert-outline',
      HOME_WAGE_CAREGIVER_PREMIUM_FIELDS,
      resolveTooltip,
      resolveAmount
    );
    appendTooltipFieldSection(
      sections,
      'taxable-other',
      '과세 수당',
      'mdi-plus-circle-outline',
      activeTaxableOtherFields.value,
      resolveTooltip,
      resolveAmount
    );
  } else {
    appendTooltipFieldSection(
      sections,
      'staff-work',
      '근무정보',
      'mdi-briefcase-clock-outline',
      staffWorkInfoFields.value,
      resolveTooltip,
      resolveAmount
    );
    appendTooltipFieldSection(
      sections,
      'taxable-other',
      '과세 수당',
      'mdi-plus-circle-outline',
      activeTaxableOtherFields.value,
      resolveTooltip,
      resolveAmount
    );
    appendTooltipFieldSection(
      sections,
      'nontax',
      '비과세 수당',
      'mdi-shield-check-outline',
      nonTaxCashInputFields,
      resolveTooltip,
      resolveAmount
    );
  }

  const insuranceFieldItems: HomeWageEmployeeConditionFieldItem[] = [];
  for (const pair of insuranceSharedPairs) {
    const detail = getInsuranceTooltip(pair)?.trim();
    if (!detail) continue;
    const amountKey = `l_${pair.key}` as HomeWagePayrollFieldKey;
    insuranceFieldItems.push({
      label: pair.label,
      amount: formatEmployeeConditionAmount(amountKey) || undefined,
      detail,
    });
  }
  for (const side of employmentInsuranceSides) {
    const detail = getEmploymentInsuranceTooltip(side.key)?.trim();
    if (!detail) continue;
    const amountKey = side.key === 'labor' ? 'l_go' : 'c_go';
    insuranceFieldItems.push({
      label: `고용보험 (${side.chipLabel})`,
      amount: formatEmployeeConditionAmount(amountKey) || undefined,
      detail,
    });
  }
  if (insuranceFieldItems.length > 0) {
    sections.push({
      id: 'insurance',
      title: '4대보험',
      type: 'fields',
      icon: 'mdi-hospital-box-outline',
      badge: `${insuranceFieldItems.length}항목`,
      fieldItems: insuranceFieldItems,
    });
  }

  const laborDeductFields = [
    ...HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS,
    ...HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELDS,
    HOME_WAGE_LABOR_TOTAL_FIELD,
  ].filter((field): field is { key: HomeWagePayrollFieldKey; label: string } => !!field);
  appendTooltipFieldSection(
    sections,
    'labor-deduct',
    '근로자 공제',
    'mdi-account-arrow-down-outline',
    laborDeductFields,
    resolveTooltip,
    resolveAmount
  );

  const corpFields = [
    ...HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS.filter((field) => field.key !== 'c_out'),
    ...(HOME_WAGE_CORP_RETIREMENT_FIELD.value ? [HOME_WAGE_CORP_RETIREMENT_FIELD.value] : []),
    HOME_WAGE_CORP_TOTAL_FIELD,
  ].filter((field): field is { key: HomeWagePayrollFieldKey; label: string } => !!field);
  appendTooltipFieldSection(
    sections,
    'corp-deduct',
    '기관 공제',
    'mdi-domain',
    corpFields,
    resolveTooltip,
    resolveAmount
  );

  appendTooltipFieldSection(
    sections,
    'result',
    '최종 결과',
    'mdi-chart-line',
    referenceBarFields.value,
    resolveTooltip,
    resolveAmount
  );

  return sections;
});

const openEmployeeConditionModal = () => {
  if (!selectedEmployee.value) {
    alert('사원을 선택해 주세요.');
    return;
  }
  showEmployeeConditionModal.value = true;
};

const PAYROLL_MONEY_KEYS = new Set<HomeWagePayrollFieldKey>([
  'basic', 'weekend', 'annual', 'laborDay', 'overtime', 'night', 'holiday', 'senior',
  'sunim', 'bosu', 'severe', 'lunch', 'lunchDirector', 'bonus', 'car', 'child', 'welfare', 'other',
  'wageTotal', 'l_yeon', 'l_gun', 'l_jang', 'l_go', 'l_so', 'l_ji', 'l_jiwon', 'l_bonin',
  'l_etc', 'l_total', 'c_yeon', 'c_gun', 'c_jang', 'c_go', 'c_sil', 'c_san', 'c_out',
  'c_total', 'grossPay', 'net', 'nonTax', 'l_save', 'c_save', 'profit',
]);

/** 비과세액 합계 동기화: 식대 + 차량 + 자녀 + (생산직) 연·야·휴 비과세분 */
const NON_TAX_INPUT_KEYS: HomeWagePayrollFieldKey[] = ['lunch', 'lunchDirector', 'car', 'child'];

const setPayrollMoneyField = (key: HomeWagePayrollFieldKey, amount: number) => {
  if (!PAYROLL_MONEY_KEYS.has(key)) return;
  let normalized = amount;
  if (key === 'lunchDirector') {
    normalized = clampHomeWageLunchDirectorInput(amount);
  } else if (NON_TAX_INPUT_KEYS.includes(key)) {
    normalized = clampNontaxCashFieldInput(key, amount, { silent: true });
  }
  (payrollForm.value as Record<HomeWagePayrollFieldKey, number | string>)[key] = normalized;
};

const onPayrollMoneyInput = (key: HomeWagePayrollFieldKey, event: Event) => {
  if (key === 'laborDay') return;
  if (isPremiumPayrollFieldKey(key) && alertDirectorPremiumBlocked()) return;
  const parsed = parseWageNumber((event.target as HTMLInputElement).value);
  setPayrollMoneyField(key, parsed);

  // ★ 수당 입력 중에도 과세합계·비과세·지급금액 즉시 반영 (blur 전 미갱신 방지)
  const taxableKeys = isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_TAXABLE_COMPONENT_KEYS
    : HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS;
  const isDirectorAllowanceInput =
    isDirectorPayrollView.value
    && (
      (isCaregiverEmployee(selectedEmployee.value)
        && HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS.includes(key))
      || (!isCaregiverEmployee(selectedEmployee.value)
        && HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_KEYS.includes(key))
    );
  if (taxableKeys.includes(key) || isDirectorAllowanceInput) {
    recalcWageTotal();
  }
  if (NON_TAX_INPUT_KEYS.includes(key) || isDirectorAllowanceInput) {
    syncNonTaxTotal();
  }

  schedulePayrollAutoCalc('downstream');
};

/**
 * 관리자 — 자동+센터장 중식대 합산 표시
 * ★ 요보: 연차 흡수·미지급 orphan(cwhd_lunch만 있고 실지급 아님)은 합계에 넣지 않음
 *   (박소라 6월: 칸 0인데 합계만 16,000 보이던 불일치 방지)
 */
const combinedLunchDisplay = computed(() => {
  const director = clampHomeWageLunchDirectorInput(payrollForm.value.lunchDirector);
  if (
    !isDirectorPayrollView.value
    && isCaregiverEmployee(selectedEmployee.value)
  ) {
    return calcHomeWageCombinedLunch(
      resolveAdminCaregiverAutoLunchForNontax(),
      director
    );
  }
  return calcHomeWageCombinedLunch(payrollForm.value.lunch, director);
});

/** 연·야·휴 금액 키 (과세합계 제외, 비과세 합계 포함) */
const PREMIUM_AMOUNT_KEYS: HomeWagePremiumAmountKey[] = ['overtime', 'night', 'holiday'];

/** 근로자공제 합계 동기화 대상 키 (4대보험 제외 - 별도 입력) */
const LABOR_DEDUCT_KEYS: HomeWagePayrollFieldKey[] = ['l_so', 'l_ji', 'l_jiwon', 'l_bonin', 'l_etc'];

/** 기관공제 합계 동기화 대상 키 (4대보험 제외 - 별도 입력) */
const CORP_DEDUCT_KEYS: HomeWagePayrollFieldKey[] = ['c_sil', 'c_san', 'c_out'];

const resolveProductionNontaxForDisplay = () =>
  productionNontaxActive.value && !isDirectorPayrollView.value;

const syncNonTaxTotal = () => {
  payrollForm.value.lunchDirector = clampHomeWageLunchDirectorInput(
    payrollForm.value.lunchDirector
  );
  const nontaxForm = { ...payrollForm.value };
  if (
    !isDirectorPayrollView.value
    && isCaregiverEmployee(selectedEmployee.value)
  ) {
    nontaxForm.lunch = resolveAdminCaregiverAutoLunchForNontax();
  }
  payrollForm.value.nonTax = calcHomeWageTotalNontax(
    nontaxForm,
    resolveProductionNontaxForDisplay(),
    yearMonth.value,
    resolveNontaxViewMode()
  );
};

/** 연·야·휴 과세/비과세 분리 반영 — wageTotal·nonTax·참고값 갱신 */
const syncAfterPremiumSplitChange = () => {
  recalcWageTotal();
  syncNonTaxTotal();
  syncReferenceValues();
};

const formatTaxablePremiumMoney = (key: HomeWagePayrollFieldKey) => {
  if (
    isDirectorPayrollView.value
    && directorMaskPremiumDisplay.value
    && (key === 'overtime' || key === 'night' || key === 'holiday')
  ) {
    return '';
  }
  const split = effectivePremiumTaxSplit.value;
  const map: Record<string, number> = isDirectorPayrollView.value
    ? {
        overtime: split.overtimeTax + split.overtimeNontax,
        night: split.nightTax + split.nightNontax,
        holiday: split.holidayTax + split.holidayNontax,
      }
    : {
        overtime: split.overtimeTax,
        night: split.nightTax,
        holiday: split.holidayTax,
      };
  const n = map[key] ?? 0;
  return n > 0 ? formatWageNumber(n) : '';
};

/** 센터장 통합 수당 — 연·야·휴는 과세+비과세 합산, 나머지는 payrollForm 그대로 */
const formatDirectorAllowanceMoney = (key: HomeWagePayrollFieldKey) => {
  if (key === 'overtime' || key === 'night' || key === 'holiday') {
    return formatTaxablePremiumMoney(key);
  }
  return formatPayrollMoneyField(key);
};

const formatNontaxPremiumMoney = (key: HomeWagePayrollFieldKey) => {
  const split = effectivePremiumTaxSplit.value;
  const map: Record<string, number> = {
    overtime: split.overtimeNontax,
    night: split.nightNontax,
    holiday: split.holidayNontax,
  };
  const n = map[key] ?? 0;
  return n > 0 ? formatWageNumber(n) : '';
};

const onPayrollMoneyBlur = (key: HomeWagePayrollFieldKey) => {
  if (isPremiumPayrollFieldKey(key) && alertDirectorPremiumBlocked()) return;
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  let amount = Number(payrollForm.value[key]) || 0;
  if (key === 'lunchDirector') {
    amount = clampHomeWageLunchDirectorInput(amount);
    payrollForm.value.lunchDirector = amount;
    if (isCaregiverEmployee(selectedEmployee.value)) {
      if (isDirectorPayrollView.value) {
        syncAccountingPayrollShadow();
      } else if (autoBaseLunchRef.value > 0) {
        applyAutoLunchDirectorAdjustment({
          autoBase: autoBaseLunchRef.value,
          adjustPayField: isAllFamilyCareInClaim.value ? 'basic' : 'annual',
        });
        syncAccountingPayrollShadow();
      } else {
        syncAccountingPayrollShadow();
      }
    } else {
      // 요양보호사 외: 시급×시간 = 기본급 + 자동중식(20만−센터장입력)
      applyStaffFundedAutoLunchCarve();
    }
  } else if (key === 'lunch' && !isCaregiverEmployee(selectedEmployee.value)) {
    // 자동중식은 20만 채우기로 재계산 (수동 입력 무시)
    applyStaffFundedAutoLunchCarve();
    amount = Number(payrollForm.value.lunch) || 0;
  } else if (NON_TAX_INPUT_KEYS.includes(key)) {
    amount = clampNontaxCashFieldInput(key, amount);
  }
  setPayrollMoneyField(key, amount);
  const taxableKeys = isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_TAXABLE_COMPONENT_KEYS
    : HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS;
  const isDirectorCaregiverBlur =
    isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value);
  const isDirectorAllowanceFieldBlur =
    isDirectorCaregiverBlur
    && (
      HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS.includes(key)
      || (showLaborDayNontaxField.value && key === 'laborDay')
    );

  if (isDirectorAllowanceFieldBlur) {
    ensureCaregiverPremiumAmountsSynced();
  }
  if (taxableKeys.includes(key) || isDirectorAllowanceFieldBlur) {
    recalcWageTotal();
  }
  if (key === 'senior' || key === 'sunim' || key === 'bosu' || key === 'severe' || key === 'welfare') {
    syncSupportDeduction(true);
  }
  if (!isCaregiverEmployee(selectedEmployee.value) && key === 'annual') {
    staffForm.value.annualPay = Number(payrollForm.value.annual) || 0;
  }
  if (NON_TAX_INPUT_KEYS.includes(key) || isDirectorAllowanceFieldBlur) {
    syncNonTaxTotal();
    syncReferenceValues();
  }
  if (LABOR_DEDUCT_KEYS.includes(key)) {
    syncLaborDeductTotal();
  }
  if (CORP_DEDUCT_KEYS.includes(key)) {
    syncCorpDeductTotal();
  }
  if (
    isRepresentative.value &&
    (key === 'c_go' || key === 'c_sil' || key === 'c_out' || key === 'l_go')
  ) {
    applyRepresentativeCorpRules();
    syncLaborDeductTotal();
    syncCorpDeductTotal();
  }
  if (isDirectorCaregiverBlur) {
    syncDirectorInsuranceFromAdminShadow(payrollDbLock.value);
  } else {
    schedulePayrollAutoCalc('downstream', true);
  }
};

/** 4대보험 — 근로자·기관 동일 금액 표시·입력 */
const getSharedInsuranceAmount = (pair: HomeWageInsurancePairDef) => {
  const labor = Number(payrollForm.value[pair.laborKey]) || 0;
  const corp = Number(payrollForm.value[pair.corpKey]) || 0;
  return Math.max(labor, corp);
};

const formatSharedInsuranceField = (pair: HomeWageInsurancePairDef) => {
  const n = getSharedInsuranceAmount(pair);
  return n > 0 ? formatWageNumber(n) : '';
};

/** 4대보험 고정값 체크 여부 조회 (pair.key = 'yeon'|'gun'|'jang'|'go') — 근로·기관 중 하나라도 고정이면 true */
const getInsuranceFixedByPairKey = (pairKey: string): boolean => {
  const laborKey = `l_${pairKey}` as keyof HomeWageDeductionFixedFlags;
  const corpKey = `c_${pairKey}` as keyof HomeWageDeductionFixedFlags;
  return deductionFixed.value[laborKey] || deductionFixed.value[corpKey] || false;
};

/** 센터장 시점 — 관리자 shadow 반영 후 4대보험·공제 재산출 (DB lock 시 고정 항목만 유지) */
const syncDirectorInsuranceFromAdminShadow = (preservePayrollLock = false) => {
  if (!isDirectorPayrollView.value || !isCaregiverEmployee(selectedEmployee.value)) return;
  syncAccountingPayrollShadow();
  // ★ 초기 로드 중 — shadow만 구축, DB에서 불러온 세금·4대보험은 유지
  if (payrollHydrating.value) return;
  runInsuranceDeductionAutoCalc(true, preservePayrollLock);
};

/** 저장·입력 변경 시 — 관리자 shadow 기준으로 4대보험·세금 재점검 (급여 항목은 payrollForm=센터장 유지) */
const verifyAdminBasedInsuranceBeforeSave = () => {
  if (!selectedEmployee.value) return;
  if (isCaregiverEmployee(selectedEmployee.value)) {
    syncAccountingPayrollShadow();
    runInsuranceDeductionAutoCalc(true, false);
  } else {
    runInsuranceDeductionAutoCalc(true, false);
  }
  syncReferenceValues();
};

/** 저장 데이터 lock 중 — 조건 0(국민·건강·장기·고용)은 대장 저장값을 유지 */
const syncLoadedPayrollDeductions = () => {
  if (payrollHydrating.value) return;
  if (!payrollDbLock.value) return;
  syncAccountingPayrollShadow();
  runInsuranceDeductionAutoCalc(true, true);
  applyInsurancePolicyZeros();
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  syncSupportDeductionValue();
};

/** 4대보험·공제 연쇄 자동 계산 (고정 해제 항목만 재산출) */
const runInsuranceDeductionAutoCalc = (
  silent = true,
  preservePayrollLock = false
) => {
  if (payrollHydrating.value) return;
  if (isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value)) {
    syncAccountingPayrollShadow();
  }
  syncFamilyUnder60HealthInsuranceFixedRelease();
  runAutoFillSocialInsurance(silent, preservePayrollLock);
  runAutoFillLaborDeduct(silent, preservePayrollLock);
  runAutoFillCorpDeduct(silent, preservePayrollLock);
  applyInsurancePolicyZeros();
  applyRepresentativeCorpRules();
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  syncReferenceValues();
};

/** 4대보험 고정값 체크 토글 (근로자·기관 동시 설정) */
const toggleInsuranceFixed = (pairKey: string, checked: boolean) => {
  const laborKey = `l_${pairKey}` as keyof HomeWageDeductionFixedFlags;
  const corpKey = `c_${pairKey}` as keyof HomeWageDeductionFixedFlags;
  deductionFixed.value[laborKey] = checked;
  deductionFixed.value[corpKey] = checked;
  markDirty();
  // ★ DB lock 중에도 고정 토글 시 4대보험 재계산 (schedulePayrollAutoCalc는 lock 시 skip)
  runInsuranceDeductionAutoCalc(true, false);
  openDeductionFixModalOnCheck(laborKey as HomeWageDeductionFixFieldKey, checked);
};

/** 4대보험 고정값 초기화 */
const resetDeductionFixed = () => {
  deductionFixed.value = createEmptyHomeWageDeductionFixedFlags();
  supportDeductionItemFlags.value = createAllIncludedSupportDeductionFlags();
};
/** @deprecated resetInsuranceFixed alias */
const resetInsuranceFixed = resetDeductionFixed;

const setSharedInsuranceAmount = (pair: HomeWageInsurancePairDef, amount: number) => {
  setPayrollMoneyField(pair.laborKey, amount);
  setPayrollMoneyField(pair.corpKey, amount);
};

const onSharedInsuranceInput = (pair: HomeWageInsurancePairDef, event: Event) => {
  if (getInsuranceFixedByPairKey(pair.key)) return;
  setSharedInsuranceAmount(
    pair,
    parseWageNumber((event.target as HTMLInputElement).value)
  );
  schedulePayrollAutoCalc('downstream');
};

const onSharedInsuranceBlur = (pair: HomeWageInsurancePairDef) => {
  setSharedInsuranceAmount(pair, getSharedInsuranceAmount(pair));
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  schedulePayrollAutoCalc('downstream', true);
};

const getEmploymentInsuranceFixedKey = (side: EmploymentInsuranceSide): keyof HomeWageDeductionFixedFlags =>
  side === 'labor' ? 'l_go' : 'c_go';

const getEmploymentInsuranceFieldKey = (side: EmploymentInsuranceSide): 'l_go' | 'c_go' =>
  side === 'labor' ? 'l_go' : 'c_go';

const getEmploymentInsuranceFixed = (side: EmploymentInsuranceSide): boolean =>
  deductionFixed.value[getEmploymentInsuranceFixedKey(side)];

const toggleEmploymentInsuranceFixed = (side: EmploymentInsuranceSide, checked: boolean) => {
  const fieldKey = getEmploymentInsuranceFieldKey(side);
  deductionFixed.value[getEmploymentInsuranceFixedKey(side)] = checked;
  markDirty();
  runInsuranceDeductionAutoCalc(true, false);
  openDeductionFixModalOnCheck(fieldKey, checked);
};

const formatEmploymentInsuranceField = (side: EmploymentInsuranceSide) => {
  const n = Number(payrollForm.value[getEmploymentInsuranceFieldKey(side)]) || 0;
  return n > 0 ? formatWageNumber(n) : '';
};

const onEmploymentInsuranceInput = (side: EmploymentInsuranceSide, event: Event) => {
  if (getEmploymentInsuranceFixed(side)) return;
  setPayrollMoneyField(
    getEmploymentInsuranceFieldKey(side),
    parseWageNumber((event.target as HTMLInputElement).value)
  );
  schedulePayrollAutoCalc('downstream');
};

const onEmploymentInsuranceBlur = (side: EmploymentInsuranceSide) => {
  const key = getEmploymentInsuranceFieldKey(side);
  if (getEmploymentInsuranceFixed(side)) {
    syncLaborDeductTotal();
    syncCorpDeductTotal();
    return;
  }
  setPayrollMoneyField(key, Number(payrollForm.value[key]) || 0);
  if (isRepresentative.value) {
    applyRepresentativeCorpRules();
  }
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  schedulePayrollAutoCalc('downstream', true);
};

const getHourlyLinkedPayInput = () => ({
  payCalcHourlyWage: caregiverHourlyWage.value,
  totalSugaHours: totalSugaHours.value,
  workDays: workCalendarSummary.value.workDayCount,
  annualLeaveDays: annualLeaveDays.value,
  overtimeHours: payrollForm.value.overtimeHours,
  nightHours: payrollForm.value.nightHours,
  holidayHours: payrollForm.value.holidayHours,
  yearMonth: yearMonth.value,
});

/** 청구(plan) 기준 연·야·휴 시간 → 폼·표시값 동기화 (시간은 plan 단일 소스 — lock과 무관) */
const syncPremiumHoursFromClaims = (force = false, viewMode = resolveCaregiverPayrollViewMode()) => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (!caregiverClaimRows.value.length) return;
  if (!force && (premiumHoursUserEdited.value || premiumManualAdjustEnabled.value)) return;
  const hours = calcPremiumHoursFromClaims(
    caregiverClaimRows.value,
    yearMonth.value,
    viewMode
  );
  payrollForm.value.overtimeHours = hours.overtimeHours;
  payrollForm.value.nightHours = hours.nightHours;
  payrollForm.value.holidayHours = hours.holidayHours;
  payrollPremiumHoursDisplay.value = {
    overtimeHours: formatHomeWagePremiumHoursDisplay(hours.overtimeHours),
    nightHours: formatHomeWagePremiumHoursDisplay(hours.nightHours),
    holidayHours: formatHomeWagePremiumHoursDisplay(hours.holidayHours),
  };
};

const PREMIUM_MANUAL_MEMO_PREFIX = '__PM__';

const encodePremiumManualMemo = (memo: string, manual: HomeWagePremiumPayAmounts): string => {
  const json = JSON.stringify({
    o: manual.overtime,
    n: manual.night,
    h: manual.holiday,
  });
  const clean = String(memo || '').replace(/^__PM__[^\n]*\n?/, '').trim();
  return clean ? `${PREMIUM_MANUAL_MEMO_PREFIX}${json}\n${clean}` : `${PREMIUM_MANUAL_MEMO_PREFIX}${json}`;
};

const decodePremiumManualMemo = (memo: string | null | undefined): HomeWagePremiumPayAmounts | null => {
  const line = String(memo || '').split('\n').find((l) => l.startsWith(PREMIUM_MANUAL_MEMO_PREFIX));
  if (!line) return null;
  try {
    const parsed = JSON.parse(line.slice(PREMIUM_MANUAL_MEMO_PREFIX.length)) as {
      o?: number;
      n?: number;
      h?: number;
    };
    return {
      overtime: Math.max(0, Number(parsed.o) || 0),
      night: Math.max(0, Number(parsed.n) || 0),
      holiday: Math.max(0, Number(parsed.h) || 0),
    };
  } catch {
    return null;
  }
};

const stripPremiumManualMemo = (memo: string | null | undefined): string =>
  String(memo || '').replace(/^__PM__[^\n]*\n?/, '').trim();
const ensureAccountingPremiumsForSave = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (premiumManualAdjustEnabled.value) return;

  ensureCaregiverPremiumAmountsSynced();

  const f = payrollForm.value;
  const hoursSum =
    (Number(f.overtimeHours) || 0)
    + (Number(f.nightHours) || 0)
    + (Number(f.holidayHours) || 0);
  if (hoursSum <= 0 || getCurrentPremiumFullTotalFromForm() > 0) return;

  const hourly = caregiverHourlyWage.value;
  if (hourly <= 0) return;

  const mode = resolvePremiumPayMode();
  const nonSevereTarget = claimGrandTotal.value - severeSumFromClaim.value;
  const viewMode = resolveCaregiverPayrollViewMode();
  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    Number(f.overtimeHours) || 0,
    Number(f.nightHours) || 0,
    Number(f.holidayHours) || 0,
    viewMode
  );
  const allocated = allocateHomeWagePremiumPays({
    mode,
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    premiumPool:
      mode === 'pool'
        ? Math.max(0, nonSevereTarget - (Number(f.lunch) || 0))
        : undefined,
    yearMonth: yearMonth.value,
  });
  applyPremiumSplitToForm(allocated.overtime, allocated.night, allocated.holiday);
};

/**
 * 수동조정 ON — DB에는 자동 산출 연·야·휴 저장, 연차/기본급에서 환원분 제거(총액 유지)
 * 화면·client 출력은 수동값 유지
 */
const resolveAccountingPremiumSaveFields = () => {
  if (!premiumManualAdjustEnabled.value) return null;
  const snap = buildPremiumAutoSnapshot();
  if (!snap) return null;

  const manualFull = getCurrentPremiumFullFromForm();
  const manualTotal = manualFull.overtime + manualFull.night + manualFull.holiday;
  const returnedAmount = Math.max(0, snap.allocatedTotal - manualTotal);
  const s = snap.split;

  return {
    overtime: s.overtime,
    overtimeNontax: s.overtimeNontax,
    night: s.night,
    nightNontax: s.nightNontax,
    holiday: s.holiday,
    holidayNontax: s.holidayNontax,
    annualDelta: isAllFamilyCareInClaim.value ? 0 : returnedAmount,
    basicDelta: isAllFamilyCareInClaim.value ? returnedAmount : 0,
  };
};

/** 연·야·휴 전액 → 과세/비과세 분리 후 지정 폼 반영 */
const applyPremiumSplitToFormOn = (
  form: HomeWagePayrollFormData,
  fullOvertime: number,
  fullNight: number,
  fullHoliday: number,
  productionNontax: boolean
) => {
  const split = splitHomeWagePremiumForStorage(
    fullOvertime,
    fullNight,
    fullHoliday,
    productionNontax
  );
  applyHomeWagePremiumSplitToForm(form, split);
};

/** 연·야·휴 전액 → 과세/비과세 분리 후 폼 반영 */
const applyPremiumSplitToForm = (fullOvertime: number, fullNight: number, fullHoliday: number) => {
  applyPremiumSplitToFormOn(
    payrollForm.value,
    fullOvertime,
    fullNight,
    fullHoliday,
    productionNontaxActive.value
  );
};

/** 생산직 비과세 토글 등 — 현재 저장된 분리값 기준 재분배 */
const resyncPremiumSplitForStorage = () => {
  const full = getHomeWagePremiumFullAmounts({
    overtime: Number(payrollForm.value.overtime) || 0,
    night: Number(payrollForm.value.night) || 0,
    holiday: Number(payrollForm.value.holiday) || 0,
    overtimeNontax: Number(payrollForm.value.overtimeNontax) || 0,
    nightNontax: Number(payrollForm.value.nightNontax) || 0,
    holidayNontax: Number(payrollForm.value.holidayNontax) || 0,
  });
  applyPremiumSplitToForm(full.overtime, full.night, full.holiday);
};

/** 비과세 연·야·휴 금액 — 지급계산시급 × 청구(plan) 시간 (수급자별 시급 불필요) */
const syncPremiumNontaxAmounts = (
  viewMode = resolveCaregiverPayrollViewMode(),
  force = false
) => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (premiumManualAdjustEnabled.value && !force) return;
  const hourly = caregiverHourlyWage.value;
  if (hourly <= 0) return;

  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    Number(payrollForm.value.overtimeHours) || 0,
    Number(payrollForm.value.nightHours) || 0,
    Number(payrollForm.value.holidayHours) || 0,
    viewMode
  );

  const allocated = allocateHomeWagePremiumPays({
    mode: 'additive',
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    yearMonth: yearMonth.value,
  });
  const useProductionNontax =
    viewMode === 'admin' && productionNontaxActive.value;
  applyPremiumSplitToFormOn(
    payrollForm.value,
    allocated.overtime,
    allocated.night,
    allocated.holiday,
    useProductionNontax
  );
  syncAfterPremiumSplitChange();
};

/** 항목별 시간은 있는데 금액(과세+비과세)이 비어 있으면 true */
const premiumCategoryAmountNeedsSync = (): boolean => {
  const f = payrollForm.value;
  const needs = (hours: number, tax: number, nontax: number) =>
    hours > 0 && tax + nontax <= 0;
  return (
    needs(Number(f.overtimeHours) || 0, Number(f.overtime) || 0, Number(f.overtimeNontax) || 0)
    || needs(Number(f.nightHours) || 0, Number(f.night) || 0, Number(f.nightNontax) || 0)
    || needs(Number(f.holidayHours) || 0, Number(f.holiday) || 0, Number(f.holidayNontax) || 0)
  );
};

/** override 미지급인데 폼에 연·휴 금액이 남아 있으면 true */
const premiumOverrideAmountNeedsSync = (): boolean => {
  if (isAllFamilyCareInClaim.value) return false;
  const split = effectivePremiumTaxSplit.value;
  if (
    !directorPayOvertimeEffective.value
    && split.overtimeTax + split.overtimeNontax > 0
  ) {
    return true;
  }
  if (
    !directorPayHolidayEffective.value
    && split.holidayTax + split.holidayNontax > 0
  ) {
    return true;
  }
  if (
    !directorPayNightEffective.value
    && split.nightTax + split.nightNontax > 0
  ) {
    return true;
  }
  return false;
};

/** 연·야·휴 시간·금액 동기화 — 청구(plan) 시간 반영 후 금액 산출 */
const ensureCaregiverPremiumAmountsSynced = (force = false) => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (premiumManualAdjustEnabled.value && !force) return;
  if (caregiverHourlyWage.value <= 0) return;
  if (!caregiverClaimRows.value.length) return;

  if (!premiumHoursUserEdited.value) {
    syncPremiumHoursFromClaims();
    syncPremiumNontaxAmounts();
    return;
  }

  const otH = Number(payrollForm.value.overtimeHours) || 0;
  const niH = Number(payrollForm.value.nightHours) || 0;
  const hoH = Number(payrollForm.value.holidayHours) || 0;
  if (otH + niH + hoH <= 0) return;

  if (
    getCurrentPremiumFullTotalFromForm() <= 0
    || premiumCategoryAmountNeedsSync()
    || premiumOverrideAmountNeedsSync()
  ) {
    syncPremiumNontaxAmounts();
  }
};

/** 청구(plan) 기준 연·야·휴 시간·금액 동기화 */
const refreshPremiumHoursFromClaims = async () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (!caregiverClaimRows.value.length) return;
  premiumHoursUserEdited.value = false;
  ensureCaregiverPremiumAmountsSynced();
};

const resolvePremiumPayMode = () =>
  resolveHomeWagePremiumPayMode({
    totalSugaHours: totalSugaHours.value,
    isFamilyCare: isAllFamilyCareInClaim.value,
  });

const resolvePremiumHoursForPayAllocation = (
  overtimeHours: number,
  nightHours: number,
  holidayHours: number,
  viewMode: HomeWagePayrollViewMode = resolveCaregiverPayrollViewMode()
) =>
  resolvePremiumHoursForAllocation({
    overtimeHours,
    nightHours,
    holidayHours,
    viewMode,
    isFamilyCareOnly: isAllFamilyCareInClaim.value,
    totalSugaHours: totalSugaHours.value,
    ...(!isAllFamilyCareInClaim.value
      ? {
          directorPayOvertime: directorPremiumPayOverride.value.payOvertime,
          directorPayHoliday: directorPremiumPayOverride.value.payHoliday,
          directorPayNight: directorPremiumPayOverride.value.payNight,
        }
      : {}),
  });

const alertDirectorPremiumBlocked = (): boolean => {
  if (!directorMaskPremiumDisplay.value) return false;
  alert(
    getHomeWageDirectorPremiumBlockedAlert({
      isFamilyCareOnly: isAllFamilyCareInClaim.value,
      totalSugaHours: totalSugaHours.value,
    })
  );
  return true;
};

const isPremiumPayrollFieldKey = (key: HomeWagePayrollFieldKey): boolean =>
  key === 'overtime' || key === 'night' || key === 'holiday'
  || key === 'overtimeNontax' || key === 'nightNontax' || key === 'holidayNontax';

/** 연·야·휴 금액 산출·적용 (pool/additive) */
const applyCaregiverPremiumAmounts = (
  input: {
    hourly: number;
    nonSevereTarget: number;
    linkedBasic: number;
    linkedWeekend: number;
    linkedAnnual: number;
    finalLunch: number;
    premiumPool?: number;
  },
  options?: {
    form?: HomeWagePayrollFormData;
    viewMode?: HomeWagePayrollViewMode;
  }
) => {
  const form = options?.form ?? payrollForm.value;
  const viewMode = options?.viewMode ?? resolveCaregiverPayrollViewMode();
  const mode = resolvePremiumPayMode();
  const premiumPool =
    input.premiumPool ??
    (mode === 'pool'
      ? Math.max(0, input.nonSevereTarget - input.finalLunch)
      : undefined);

  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    Number(form.overtimeHours) || 0,
    Number(form.nightHours) || 0,
    Number(form.holidayHours) || 0,
    viewMode
  );

  const allocated = allocateHomeWagePremiumPays({
    mode,
    hourlyWage: input.hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    premiumPool,
    yearMonth: yearMonth.value,
  });

  applyPremiumSplitToFormOn(
    form,
    allocated.overtime,
    allocated.night,
    allocated.holiday,
    viewMode === 'admin' && productionNontaxActive.value
  );

  const premiumTotal =
    (Number(form.overtime) || 0) +
    (Number(form.night) || 0) +
    (Number(form.holiday) || 0) +
    (Number(form.overtimeNontax) || 0) +
    (Number(form.nightNontax) || 0) +
    (Number(form.holidayNontax) || 0);

  return { mode, premiumTotal };
};

const recalcWageTotalOn = (
  form: HomeWagePayrollFormData,
  productionNontax: boolean
) => {
  const keys = HOME_WAGE_TAXABLE_COMPONENT_KEYS;
  let total = keys.reduce((sum, key) => sum + (Number(form[key]) || 0), 0);
  total += calcHomeWagePremiumTaxableAmount(form, productionNontax);
  form.wageTotal = total;
};

const syncNonTaxTotalOn = (
  form: HomeWagePayrollFormData,
  productionNontax: boolean,
  viewMode: HomeWageNontaxViewMode = 'admin'
) => {
  form.nonTax = calcHomeWageTotalNontax(form, productionNontax, yearMonth.value, viewMode);
};

/**
 * 자동 중식대 원값 + 센터장 입력 → lunch(조정된 자동) · 연차/기본급 복원
 * 센터장 시점: form.lunch=0 유지, shadow에서 회계용 산출
 */
const applyAutoLunchDirectorAdjustment = (options: {
  autoBase: number;
  adjustPayField: 'annual' | 'basic';
  keepFormLunchZero?: boolean;
}) => {
  const { autoBase, adjustPayField, keepFormLunchZero = false } = options;
  autoBaseLunchRef.value = Math.max(0, Number(autoBase) || 0);
  const director = clampHomeWageLunchDirectorInput(payrollForm.value.lunchDirector);
  payrollForm.value.lunchDirector = director;
  const payCurrent =
    adjustPayField === 'annual'
      ? Number(payrollForm.value.annual) || 0
      : Number(payrollForm.value.basic) || 0;
  const adj = applyHomeWageLunchDirectorAdjustment(autoBase, director, payCurrent);
  if (!keepFormLunchZero && !isDirectorPayrollView.value) {
    payrollForm.value.lunch = adj.lunch;
    if (adjustPayField === 'annual') {
      payrollForm.value.annual = adj.annualOrBasic;
    } else {
      payrollForm.value.basic = adj.annualOrBasic;
    }
  } else if (isDirectorPayrollView.value) {
    payrollForm.value.lunch = 0;
  }
  return adj;
};

/** 폼(병합·저장) — 자동 중식대 조정 + 연차/기본급 복원 */
const applyLunchDirectorAdjustmentToForm = (
  form: HomeWagePayrollFormData,
  options?: { autoBase?: number; adjustPayField?: 'annual' | 'basic' }
) => {
  const director = clampHomeWageLunchDirectorInput(form.lunchDirector);
  form.lunchDirector = director;
  const autoBase =
    options?.autoBase ?? autoBaseLunchRef.value ?? (Number(form.lunch) || 0);
  const adjustPayField =
    options?.adjustPayField
    ?? (isAllFamilyCareInClaim.value ? 'basic' : 'annual');
  const payCurrent =
    adjustPayField === 'annual'
      ? Number(form.annual) || 0
      : Number(form.basic) || 0;
  const adj = applyHomeWageLunchDirectorAdjustment(autoBase, director, payCurrent);
  form.lunch = adj.lunch;
  if (adjustPayField === 'annual') {
    form.annual = adj.annualOrBasic;
  } else {
    form.basic = adj.annualOrBasic;
  }
  return adj;
};

const fetchDirectorWageRows = async (
  mCode: string,
  ym: string,
  biz: string
): Promise<Record<string, unknown>[]> => {
  if (!isHomeWageDirectorStorePage.value) return [];
  try {
    const res = await homeScheduleApi.getDirectorWageData(mCode, ym, biz);
    if (res.data?.success && Array.isArray(res.data.data)) {
      return res.data.data as Record<string, unknown>[];
    }
  } catch (error) {
    console.error('센터장 정본 조회 오류:', error);
  }
  return [];
};

const pickDirectorMoney = (row: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (row[key] == null || row[key] === '') continue;
    const n = Number(row[key]);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const applyDirectorCanonicalRowToForm = (director: Record<string, unknown>, emp: any) => {
  loadedDirectorRow.value = director;
  const store = mapHomeWageRowToDirectorStore({
    ...director,
    isDirectorCanonical: true,
  });
  const otSplit = calcHomeWageOvertimeNontax(
    store.overtime,
    store.night,
    store.holiday,
    true
  );
  directorCanonicalActive.value = true;
  payrollForm.value = {
    ...payrollForm.value,
    birth:
      getEmpBirth(emp)
      || parseEmpBirthDate(director.birth)
      || payrollForm.value.birth
      || '',
    job: store.job || payrollForm.value.job,
    basic: store.basic,
    weekend: store.weekend,
    annual: store.annual,
    laborDay: store.laborDay,
    overtime: otSplit.overtimeTax,
    night: otSplit.nightTax,
    holiday: otSplit.holidayTax,
    overtimeNontax: otSplit.overtimeNontax,
    nightNontax: otSplit.nightNontax,
    holidayNontax: otSplit.holidayNontax,
    senior: store.senior,
    sunim: store.sunim,
    bosu: store.bosu,
    severe: store.severe,
    lunch: hasHomeWageStoredAdParts(director)
      ? (director.lunchAuto != null
        ? Number(director.lunchAuto) || 0
        : deriveHomeWageAdminAutoLunchFromCtAd(director))
      : store.lunchAuto,
    lunchDirector: store.lunch,
    bonus: store.bonus,
    car: store.car,
    child: store.child,
    welfare: store.welfare,
    other: store.other,
    overtimeHours: store.overtimeHours,
    nightHours: store.nightHours,
    holidayHours: store.holidayHours,
    hour: store.hour > 0 ? String(store.hour) : payrollForm.value.hour,
    days: store.days > 0 ? String(store.days) : payrollForm.value.days,
    svc: store.svc || payrollForm.value.svc,
    l_yeon: pickDirectorMoney(director, 'l_yeon', 'cwhdir_l_yeon', 'lYeon'),
    l_gun: pickDirectorMoney(director, 'l_gun', 'cwhdir_l_gun', 'lGun'),
    l_jang: pickDirectorMoney(director, 'l_jang', 'cwhdir_l_jang', 'lJang'),
    l_go: pickDirectorMoney(director, 'l_go', 'cwhdir_l_go', 'lGo'),
    l_so: pickDirectorMoney(director, 'l_so', 'cwhdir_l_so', 'lSo'),
    l_ji: pickDirectorMoney(director, 'l_ji', 'cwhdir_l_ji', 'lJi'),
    l_jiwon: pickDirectorMoney(director, 'l_jiwon', 'cwhdir_l_jiwon', 'lJiwon'),
    l_bonin: pickDirectorMoney(director, 'l_bonin', 'cwhdir_l_bonin', 'lBonin'),
    l_etc: pickDirectorMoney(director, 'l_etc', 'cwhdir_l_etc', 'lEtc'),
    l_total: pickDirectorMoney(director, 'l_total', 'cwhdir_l_total', 'lTotal'),
    c_yeon: pickDirectorMoney(director, 'c_yeon', 'cwhdir_c_yeon', 'cYeon'),
    c_gun: pickDirectorMoney(director, 'c_gun', 'cwhdir_c_gun', 'cGun'),
    c_jang: pickDirectorMoney(director, 'c_jang', 'cwhdir_c_jang', 'cJang'),
    c_go: pickDirectorMoney(director, 'c_go', 'cwhdir_c_go', 'cGo'),
    c_sil: pickDirectorMoney(director, 'c_sil', 'cwhdir_c_sil', 'cSil'),
    c_san: pickDirectorMoney(director, 'c_san', 'cwhdir_c_san', 'cSan'),
    c_out: pickDirectorMoney(director, 'c_out', 'cwhdir_c_out', 'cOut'),
    c_total: pickDirectorMoney(director, 'c_total', 'cwhdir_c_total', 'cTotal'),
    net: pickDirectorMoney(director, 'net', 'cwhdir_net'),
    l_save: pickDirectorMoney(director, 'l_save', 'cwhdir_l_save', 'lSave'),
    c_save: pickDirectorMoney(director, 'c_save', 'cwhdir_c_save', 'cSave'),
    profit: pickDirectorMoney(director, 'profit', 'cwhdir_profit'),
  };
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  isRepresentative.value = parseHomeWageRepresentativeFlag(director.isRepresentative);
  applyRetirement.value = parseHomeWageApplyRetirementFlag(director.applyRetirement);
  if (applyRetirement.value) {
    loadRetirementSourceFromRow(director);
  } else {
    resetRetirementSource();
  }
  const derivedAutoLunch = hasHomeWageStoredAdParts(director)
    ? (director.lunchAuto != null
      ? Number(director.lunchAuto) || 0
      : deriveHomeWageAdminAutoLunchFromCtAd(director))
    : store.lunchAuto;
  autoBaseLunchRef.value = derivedAutoLunch;
  dbSavedAutoLunchRef.value = derivedAutoLunch;
  nontaxSettings.value = {
    productionNontax: true,
    lunchExpense: store.lunchExpense,
  };
  directorPremiumPayOverride.value = {
    payOvertime: store.payOvertime,
    payHoliday: store.payHoliday,
    payNight: store.payNight,
  };
  applyLoadedPayCalcHourlyFromRecord(
    {
      ...director,
      payCalcHourly: store.payCalcHourly,
    },
    emp
  );
  if (!isCaregiverEmployee(emp)) {
    syncStaffFormFromLoadedData(
      {
        ...director,
        basic: store.basic,
        hour: store.hour,
        payCalcHourly: store.payCalcHourly,
        annual: store.annual,
      },
      store.payCalcHourly
    );
  }
};

/** 저장 시 관리자 자동중식 — 점심일·연차잉여·20만 채우기. 센터장 합계는 그대로 */
const resolveDirectorAdminAutoLunchForSave = (
  emp: any,
  store: {
    job: string;
    svc: string | null;
    lunchExpense: boolean;
    annual: number;
    basic: number;
    weekend: number;
    lunch: number;
    payCalcHourly: number;
    hour: number;
    days: number;
  }
) => {
  const empKey = getEmpKey(emp);
  const isSelected = !!(selectedEmployee.value && getEmpKey(selectedEmployee.value) === empKey);
  const claimRows = isCaregiverEmployee(emp)
    ? (isSelected
      ? caregiverClaimRows.value
      : (allEmpClaimData.value.get(empKey) || []))
    : [];
  return calcHomeWageDirectorAdminAutoLunch({
    job: store.job,
    svc: store.svc,
    lunchExpense: store.lunchExpense,
    ctAnnual: store.annual,
    ctBasic: store.basic,
    ctWeekend: store.weekend,
    ctLunch: store.lunch,
    payCalcHourly: store.payCalcHourly,
    hour: store.hour,
    days: store.days,
    yearMonth: yearMonth.value,
    joinDate: getEmpJoinDate(emp),
    claimRows,
    lunchWorkDayCount: isSelected
      ? workCalendarSummary.value.lunchWorkDayCount
      : undefined,
  });
};

const buildDirectorWageSavePayloadFromCurrentForm = (emp: any) => {
  const form = payrollForm.value;
  const job = form.job || getEmpJob(emp) || '';
  const svc = isCaregiverEmployee(emp)
    ? (resolveSvcPayloadForEmp(emp) || form.svc || null)
    : (form.svc || null);
  const sourceRow: Record<string, unknown> = {
    ...form,
    job,
    svc,
    hour: isCaregiverEmployee(emp) ? totalSugaHours.value : staffForm.value.workHours,
    payCalcHourly: isCaregiverEmployee(emp)
      ? caregiverHourlyWage.value
      : staffForm.value.hourlyWage,
    lunchExpense: nontaxSettings.value.lunchExpense ? 'Y' : 'N',
    productionNontax: 'Y',
    payOvertime: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payOvertime
    ),
    payHoliday: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payHoliday
    ),
    payNight: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payNight
    ),
    yearMonth: yearMonth.value,
  };
  const store = directorCanonicalActive.value
    ? mapHomeWageRowToDirectorStore({
        ...sourceRow,
        isDirectorCanonical: true,
        lunchAuto: Number(form.lunch) || Number(dbSavedAutoLunchRef.value) || 0,
        lunchDirector: Number(form.lunchDirector) || 0,
        lunch: Number(form.lunchDirector) || 0,
      }, yearMonth.value)
    : buildHomeWageDirectorStoreFromLegacyRow(sourceRow, yearMonth.value);
  store.lunchAuto = resolveDirectorAdminAutoLunchForSave(emp, store);
  const gross = calcHomeWageClientReportGrossPay(
    {
      ...clientReportPayRow.value,
      basic: store.basic,
      weekend: store.weekend,
      annual: store.annual,
      lunch: 0,
      lunchDirector: store.lunch,
      isDirectorCanonical: true,
    },
    yearMonth.value
  );
  const admin = getHomeWageAdminPartsFromDirectorStore(store, yearMonth.value);
  const net = Math.max(0, gross - (Number(form.l_total) || 0));
  return {
    m_code: memberCode.value,
    mCode: memberCode.value,
    bizGubun: selectedBizGubun.value || '',
    yearMonth: yearMonth.value,
    srcIdx: currentWageDataIdx.value,
    payDate: paymentDate.value || null,
    name: getEmpName(emp),
    birth: getEmpBirth(emp),
    ...store,
    lunch: store.lunch,
    lunchAuto: store.lunchAuto,
    ...packHomeWageDirectorCtAdPayload(store, admin, {
      wageTotal: Number(form.wageTotal) || 0,
      nonTax: Number(form.nonTax) || 0,
      ctGross: gross,
      adGross:
        admin.basic
        + admin.weekend
        + admin.annual
        + admin.laborDay
        + admin.overtime
        + admin.overtimeNontax
        + admin.night
        + admin.nightNontax
        + admin.holiday
        + admin.holidayNontax
        + admin.senior
        + admin.sunim
        + admin.bosu
        + admin.severe
        + admin.bonus
        + admin.lunchAuto
        + admin.lunchDirector
        + admin.car
        + admin.child
        + admin.welfare
        + admin.other,
      net,
    }),
    wageTotal: Number(form.wageTotal) || 0,
    nonTax: Number(form.nonTax) || 0,
    grossPay: gross,
    net,
    l_yeon: form.l_yeon || 0,
    l_gun: form.l_gun || 0,
    l_jang: form.l_jang || 0,
    l_go: form.l_go || 0,
    l_so: form.l_so || 0,
    l_ji: form.l_ji || 0,
    l_jiwon: form.l_jiwon || 0,
    l_bonin: form.l_bonin || 0,
    l_etc: form.l_etc || 0,
    l_total: form.l_total || 0,
    c_yeon: form.c_yeon || 0,
    c_gun: form.c_gun || 0,
    c_jang: form.c_jang || 0,
    c_go: form.c_go || 0,
    c_sil: form.c_sil || 0,
    c_san: form.c_san || 0,
    c_out: form.c_out || 0,
    c_total: form.c_total || 0,
    svc,
    cnt: form.cnt || '',
    hour: store.hour > 0 ? String(store.hour) : form.hour || '',
    days: store.days > 0 ? store.days : form.days,
    memo: form.memo || null,
    lunchExpense: store.lunchExpense ? 'Y' : 'N',
    productionNontax: 'Y',
    payOvertime: formatHomeWageDirectorPremiumPayFlag(store.payOvertime),
    payHoliday: formatHomeWageDirectorPremiumPayFlag(store.payHoliday),
    payNight: formatHomeWageDirectorPremiumPayFlag(store.payNight),
    premiumManual: premiumManualAdjustEnabled.value ? 'Y' : 'N',
    isRepresentative: formatHomeWageRepresentativeFlag(isRepresentative.value),
    applyRetirement: formatHomeWageApplyRetirementFlag(applyRetirement.value),
    ...getRetirementSourceSavePayload(),
    ...mapDeductionFixedFieldsFromRow({
      l_yeon_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_yeon),
      l_gun_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_gun),
      l_jang_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_jang),
      l_go_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_go),
      l_so_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_so),
      l_ji_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_ji),
      l_jiwon_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.l_jiwon),
      c_yeon_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_yeon),
      c_gun_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_gun),
      c_jang_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_jang),
      c_go_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_go),
      c_sil_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_sil),
      c_san_fixed: formatHomeWageDeductionFixedFlag(deductionFixed.value.c_san),
    }),
    ...mapSupportDeductionFieldsFromFlags(),
    l_save: form.l_save || 0,
    c_save: form.c_save || 0,
    profit: form.profit || 0,
  };
};

const buildDirectorWageSavePayloadFromLegacyLike = (item: Record<string, unknown>) => {
  const store = buildHomeWageDirectorStoreFromLegacyRow(item, String(item.yearMonth || yearMonth.value));
  const gross = calcHomeWageClientReportGrossPay(
    {
      ...store,
      lunchDirector: store.lunch,
      lunch: 0,
      isDirectorCanonical: true,
      laborDay: store.laborDay,
    } as HomeWageClientReportRow,
    String(item.yearMonth || yearMonth.value)
  );
  const admin = getHomeWageAdminPartsFromDirectorStore(
    store,
    String(item.yearMonth || yearMonth.value)
  );
  const net = Math.max(0, gross - (Number(item.l_total) || 0));
  return {
    ...item,
    ...store,
    lunch: store.lunch,
    lunchAuto: store.lunchAuto,
    ...packHomeWageDirectorCtAdPayload(store, admin, {
      wageTotal: Number(item.wageTotal) || 0,
      nonTax: Number(item.nonTax) || 0,
      ctGross: gross,
      adGross:
        admin.basic
        + admin.weekend
        + admin.annual
        + admin.laborDay
        + admin.overtime
        + admin.overtimeNontax
        + admin.night
        + admin.nightNontax
        + admin.holiday
        + admin.holidayNontax
        + admin.senior
        + admin.sunim
        + admin.bosu
        + admin.severe
        + admin.bonus
        + admin.lunchAuto
        + admin.lunchDirector
        + admin.car
        + admin.child
        + admin.welfare
        + admin.other,
      net,
    }),
    srcIdx: item.idx ?? item.cwhd_idx ?? item.srcIdx ?? null,
    grossPay: gross,
    lunchExpense: store.lunchExpense ? 'Y' : 'N',
    productionNontax: 'Y',
    payOvertime: formatHomeWageDirectorPremiumPayFlag(store.payOvertime),
    payHoliday: formatHomeWageDirectorPremiumPayFlag(store.payHoliday),
    payNight: formatHomeWageDirectorPremiumPayFlag(store.payNight),
  };
};

const persistDirectorWageFromCurrentForm = async (emp: any) => {
  if (!isHomeWageDirectorStorePage.value) return;
  const payload = buildDirectorWageSavePayloadFromCurrentForm(emp);
  const res = await homeScheduleApi.saveDirectorWageData(payload);
  if (!res.data?.success) {
    throw new Error(res.data?.message || '센터장 정본 저장 실패');
  }
};

/** 저장 API — 조정된 lunch + lunchDirector */
const resolveLunchFieldsForSave = () => {
  const lunchDirector = clampHomeWageLunchDirectorInput(payrollForm.value.lunchDirector);
  if (isCaregiverEmployee(selectedEmployee.value)) {
    syncAccountingPayrollShadow();
    const shadow = accountingPayrollShadow.value;
    if (shadow) {
      return { lunch: Number(shadow.lunch) || 0, lunchDirector };
    }
  }
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    const hourly = Number(staffForm.value.hourlyWage) || 0;
    const hours = Number(staffForm.value.workHours) || 0;
    const pool = calcHomeWageStaffBasePay(hourly, hours, yearMonth.value);
    return {
      lunch: nontaxSettings.value.lunchExpense
        ? resolveHomeWageStaffAutoLunchFill(lunchDirector, pool)
        : 0,
      lunchDirector,
    };
  }
  const autoBase = autoBaseLunchRef.value || Number(payrollForm.value.lunch) || 0;
  return {
    lunch: resolveHomeWageAdjustedAutoLunch(autoBase, lunchDirector),
    lunchDirector,
  };
};

/**
 * 관리자 시점 급여 산출 (비과세 정책 전체 적용) — 4대보험·세금 산정 전용
 * payrollForm(센터장 표시·DB 저장)과 분리된 결과물.
 */
const buildAdminPayrollShadowForm = (): HomeWagePayrollFormData | null => {
  if (!isCaregiverEmployee(selectedEmployee.value)) return null;

  const hourly = caregiverHourlyWage.value;
  if (hourly <= 0 || totalSugaHours.value <= 0) return null;

  const nonSevereRows = claimSummaryRows.value.filter((r) => !r.isSevereRecipientSurcharge);
  if (nonSevereRows.length === 0 || caregiverClaimRows.value.length === 0) return null;

  // 비과세·부가수당 등만 payrollForm에서 복사 — 급여 핵심 항목은 아래에서 관리자 정책으로만 산출
  const shadow = clonePreview(payrollForm.value) as HomeWagePayrollFormData;
  const adminHours = calcPremiumHoursFromClaims(
    caregiverClaimRows.value,
    yearMonth.value,
    'admin'
  );
  shadow.overtimeHours = adminHours.overtimeHours;
  shadow.nightHours = adminHours.nightHours;
  shadow.holidayHours = adminHours.holidayHours;

  const hoursForAlloc = resolvePremiumHoursForPayAllocation(
    shadow.overtimeHours,
    shadow.nightHours,
    shadow.holidayHours,
    'admin'
  );

  const allocated = allocateHomeWagePremiumPays({
    mode: 'additive',
    hourlyWage: hourly,
    overtimeHours: hoursForAlloc.overtimeHours,
    nightHours: hoursForAlloc.nightHours,
    holidayHours: hoursForAlloc.holidayHours,
    yearMonth: yearMonth.value,
  });
  applyPremiumSplitToFormOn(
    shadow,
    allocated.overtime,
    allocated.night,
    allocated.holiday,
    productionNontaxActive.value
  );

  const severeSum = severeSumFromClaim.value;
  shadow.severe = severeSum;
  const nonSevereTarget = claimGrandTotal.value - severeSum;
  let autoBaseLunch = 0;

  if (isAllFamilyCareInClaim.value) {
    let finalLunch = 0;
    if (nontaxSettings.value.lunchExpense) {
      finalLunch = calcHomeWageAutoLunch({
        lunchExpense: true,
        isFamilyCareOnly: true,
        nonSevereTarget,
        lunchWorkDayCount: workCalendarSummary.value.lunchWorkDayCount,
      }).autoLunch;
    }
    autoBaseLunch = finalLunch;
    shadow.weekend = 0;
    shadow.annual = 0;
    const { premiumTotal } = applyCaregiverPremiumAmounts(
      {
        hourly,
        nonSevereTarget,
        linkedBasic: 0,
        linkedWeekend: 0,
        linkedAnnual: 0,
        finalLunch,
      },
      { form: shadow, viewMode: 'admin' }
    );
    shadow.basic = Math.max(0, nonSevereTarget - finalLunch - premiumTotal);
    const lunchAdj = applyHomeWageLunchDirectorAdjustment(
      autoBaseLunch,
      shadow.lunchDirector,
      shadow.basic
    );
    shadow.lunch = lunchAdj.lunch;
    shadow.basic = lunchAdj.annualOrBasic;
  } else {
    const workDays = workCalendarSummary.value.workDayCount;
    const annualDays = annualLeaveDays.value;
    const linkedMin = calcHomeWageHourlyLinkedPay(getHourlyLinkedPayInput());
    const breakdown = calcHomeWagePayBreakdown({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: hourly,
      minHourlyWage: minHourlyWage.value,
      totalSugaHours: totalSugaHours.value,
      workDays,
      annualLeaveDays: annualDays,
    });

    let finalLunch = 0;
    let finalAnnual = breakdown.annualLeave;

    const lunchResult = calcHomeWageAutoLunch({
      lunchExpense: nontaxSettings.value.lunchExpense,
      isFamilyCareOnly: false,
      nonSevereTarget,
      lunchWorkDayCount: workCalendarSummary.value.lunchWorkDayCount,
      annualLeaveBeforeLunch: finalAnnual,
      linkedMinAnnual: linkedMin.annual,
    });
    finalLunch = lunchResult.autoLunch;
    finalAnnual = lunchResult.annualAfterLunch ?? finalAnnual;

    if (!nontaxSettings.value.lunchExpense) {
      shadow.lunch = 0;
    } else {
      shadow.lunch = finalLunch;
    }
    autoBaseLunch = finalLunch;

    applyCaregiverPremiumAmounts(
      {
        hourly,
        nonSevereTarget,
        linkedBasic: linkedMin.basic,
        linkedWeekend: linkedMin.weekend,
        linkedAnnual: linkedMin.annual,
        finalLunch,
      },
      { form: shadow, viewMode: 'admin' }
    );

    shadow.basic = breakdown.base;
    shadow.weekend = breakdown.weeklyHoliday;
    shadow.annual = Math.max(0, finalAnnual);

    const lunchAdj = applyHomeWageLunchDirectorAdjustment(
      autoBaseLunch,
      shadow.lunchDirector,
      shadow.annual
    );
    shadow.lunch = lunchAdj.lunch;
    shadow.annual = lunchAdj.annualOrBasic;
  }

  if (autoBaseLunch > 0) {
    autoBaseLunchRef.value = autoBaseLunch;
  }

  shadow.laborDay = payrollForm.value.laborDay;
  recalcWageTotalOn(shadow, productionNontaxActive.value);
  syncNonTaxTotalOn(shadow, productionNontaxActive.value, 'admin');
  return shadow;
};

/** 저장된 관리자 칸으로 shadow 구성 — 청구 미준비 시 4대보험 과세 기준 */
const buildAdminShadowFromStoredAdParts = (): HomeWagePayrollFormData | null => {
  const row = loadedDirectorRow.value;
  if (!row) return null;
  if (!hasHomeWageStoredAdParts(row) && !isHomeWageDirectorCanonicalRow(row)) {
    return null;
  }
  const admin = getHomeWageAdminSettingDisplayParts(row, yearMonth.value);
  const shadow = applyAdminSettingPartsToFormData(payrollForm.value, admin);
  recalcWageTotalOn(shadow, productionNontaxActive.value);
  syncNonTaxTotalOn(shadow, productionNontaxActive.value, 'admin');
  return shadow;
};

/** 요양보호사 — 관리자 shadow 캐시 갱신 (페이지 진입·입력 변경·저장 점검 시) */
const syncAccountingPayrollShadow = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    accountingPayrollShadow.value = null;
    return;
  }
  const stored = buildAdminShadowFromStoredAdParts();
  const live = buildAdminPayrollShadowForm();
  if (payrollDbLock.value) {
    accountingPayrollShadow.value = stored || live;
    return;
  }
  const reliableLive = live && isLiveAdminInsuranceShadowReliable(live) ? live : null;
  accountingPayrollShadow.value = reliableLive || stored || live;
};

/** DB row·초안 저장용 노동절수당 산출 (5월·요양보호사) */
const resolveLaborDayAmountForSave = (input: {
  job?: string;
  payCalcHourly?: number;
  totalSugaHours?: number;
  workDays?: number;
  storedLaborDay?: number;
  claimRows?: HomeWageClaimRow[];
}): number => {
  const job = String(input.job || '');
  if (!job.includes('요양보호사')) return 0;
  if (!isHomeWageLaborDayMonth(yearMonth.value)) return 0;
  const workedOnMayFirst = input.claimRows?.length
    ? hasWorkedOnMayFirstFromClaims(input.claimRows, yearMonth.value)
    : false;
  const computed = calcHomeWageLaborDayPay({
    yearMonth: yearMonth.value,
    payCalcHourlyWage: Number(input.payCalcHourly) || 0,
    totalSugaHours: Number(input.totalSugaHours) || 0,
    workDays: Number(input.workDays) || 0,
    isCaregiver: true,
    workedOnMayFirst,
  });
  if (computed > 0) return computed;
  const year = parseInt(String(yearMonth.value).replace(/\D/g, '').slice(0, 4), 10);
  if (isMayFirstSunday(year)) {
    const worked = input.claimRows?.length
      ? hasWorkedOnMayFirstFromClaims(input.claimRows, yearMonth.value)
      : false;
    if (!worked) return 0;
  }
  return Number(input.storedLaborDay) || 0;
};

/** 노동절수당(비과세) 자동 산출 — wageTotal 제외, nonTax 반영 */
const syncLaborDayPay = () => {
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    payrollForm.value.laborDay = 0;
    syncNonTaxTotal();
    recalcWageTotal();
    return;
  }
  if (!isHomeWageLaborDayMonth(yearMonth.value)) {
    payrollForm.value.laborDay = 0;
    syncNonTaxTotal();
    recalcWageTotal();
    return;
  }
  const workDays =
    workCalendarSummary.value.workDayCount ||
    parseInt(String(payrollForm.value.days || ''), 10) ||
    0;
  payrollForm.value.laborDay = calcHomeWageLaborDayPay({
    yearMonth: yearMonth.value,
    payCalcHourlyWage: caregiverHourlyWage.value || 0,
    totalSugaHours: totalSugaHours.value,
    workDays,
    isCaregiver: true,
    workedOnMayFirst: hasWorkedOnMayFirstFromClaims(
      caregiverClaimRows.value,
      yearMonth.value
    ),
  });
  syncNonTaxTotal();
  recalcWageTotal();
};

const recalcWageTotal = () => {
  const keys = isCaregiverEmployee(selectedEmployee.value)
    ? HOME_WAGE_TAXABLE_COMPONENT_KEYS
    : HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS;
  const form = payrollForm.value;
  let total = keys.reduce((sum, key) => sum + (Number(form[key]) || 0), 0);
  if (isCaregiverEmployee(selectedEmployee.value)) {
    total += taxablePremiumTotal.value;
  }
  payrollForm.value.wageTotal = total;
};

/** DB svc 스냅샷 기준 중증수급자가산 합계 (live 집계 0일 때 fallback) */
const severeSumFromDbSnapshot = () =>
  dbSavedSummaryRows.value
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + (r.totalAmount || 0), 0);

/** DB svc 스냅샷 + 청구 집계 — 중증수급자가산 합계 (비과세 배분용) */
const resolveEffectiveSeverePaySum = (): number => {
  const fromClaim = severeSumFromClaim.value;
  if (fromClaim > 0) return fromClaim;
  return severeSumFromDbSnapshot();
};

/** 중증수급자가산 — 수급자별 표 합계 → payrollForm.severe (기본급 카드·합계) */
const syncSeverePayFromClaim = (options?: { force?: boolean }) => {
  if (payrollHydrating.value || !autoCalcMode.value) return;
  // DB lock + 저장된 severe>0 이면 유지 (0이면 청구·svc에서 표시용 복원)
  if (
    !options?.force
    && payrollDbLock.value
    && (Number(payrollForm.value.severe) || 0) > 0
  ) {
    return;
  }
  const fromClaim = severeSumFromClaim.value;
  if (fromClaim > 0) {
    payrollForm.value.severe = fromClaim;
    return;
  }
  const fromDb = severeSumFromDbSnapshot();
  if (fromDb > 0) {
    payrollForm.value.severe = fromDb;
    return;
  }
  payrollForm.value.severe = 0;
};

/** 중증수급자가산 자동 동기화 — 수급자별 설정 변경 시 과세수당에 반영 */
watch(severeSumFromClaim, (newVal) => {
  if (payrollHydrating.value || !autoCalcMode.value) return;
  if (payrollDbLock.value && (Number(payrollForm.value.severe) || 0) > 0) return;
  if (newVal > 0) {
    payrollForm.value.severe = newVal;
  } else {
    const fromDb = severeSumFromDbSnapshot();
    payrollForm.value.severe = fromDb > 0 ? fromDb : newVal;
  }
  recalcWageTotal();
});

/**
 * ────────────────────────────────────────────────────────────────────────────
 * 지원공제(l_jiwon) 통합 계산 시스템
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 지원공제 = (선택된 5종 base) × 해당 사원에게 적용되는 보험요율 + base÷12
 * 
 * 계산 조건:
 * - base = senior + bosu + welfare + severe + sunim (항목 선택·사원 유형 반영)
 * - 미체크: 값 있는 항목 전부 / 체크: jiwon_* 플래그
 * - 국민연금: l_yeon > 0일 때만 포함 (65세 이상 등 면제 시 제외)
 * - 고용보험: l_go > 0일 때만 포함 (65세 이상 등 면제 시 제외)
 * - 퇴직금분: applyRetirement가 true일 때만 포함 (60h 미만/대표자 시 제외)
 * 
 * ★ 핵심 원칙: 툴팁과 실제 값은 반드시 동일한 계산 결과를 사용한다
 */

/** 지원공제 계산 결과 — 툴팁과 실제 값에서 공통 사용 */
const getSupportDeductionResult = (): {
  amounts: HomeWageSupportDeductionAmounts;
  parts: HomeWageSupportDeductionAmounts;
  base: number;
  yeonDeduct: boolean;
  goDeduct: boolean;
  includeRetirement: boolean;
  valid: boolean;
  result: HomeWageSupportDeductionResult;
} => {
  const emp = selectedEmployee.value;
  const isCg = emp ? isCaregiverEmployee(emp) : true;
  const amounts = buildSupportDeductionAmounts(payrollForm.value);
  const selectiveMode = deductionFixed.value.l_jiwon;

  const emptyResult = {
    base: 0,
    yeon: 0,
    gun: 0,
    jang: 0,
    go: 0,
    sil: 0,
    san: 0,
    retirement: 0,
    total: 0,
  };

  const empty = {
    amounts,
    parts: { senior: 0, bosu: 0, welfare: 0, severe: 0, sunim: 0 },
    base: 0,
    yeonDeduct: false,
    goDeduct: false,
    includeRetirement: false,
    result: emptyResult,
    valid: false,
  };

  if (!taxRateInfo.value) return empty;

  const yeonDeduct = (Number(payrollForm.value.l_yeon) || 0) > 0;
  const goDeduct = (Number(payrollForm.value.l_go) || 0) > 0;
  const rates = buildDeductionRates(yeonDeduct, goDeduct);
  if ((Number(payrollForm.value.l_gun) || 0) <= 0) {
    rates.rateGun = 0;
    rates.rateJang = 0;
  }
  const includeRetirement = (Number(payrollForm.value.c_out) || 0) > 0;

  const calc = calcHomeWageSupportDeductionWithItems({
    amounts,
    flags: supportDeductionItemFlags.value,
    selectiveMode,
    isCaregiver: isCg,
    rates,
    yeonDeduct,
    goDeduct,
    includeRetirement,
  });

  return {
    amounts,
    parts: calc.parts,
    base: calc.base,
    yeonDeduct,
    goDeduct,
    includeRetirement,
    valid: calc.base > 0,
    result: {
      base: calc.base,
      yeon: calc.yeon,
      gun: calc.gun,
      jang: calc.jang,
      go: calc.go,
      sil: calc.sil,
      san: calc.san,
      retirement: calc.retirement,
      total: calc.total,
    },
  };
};

/** 지원공제 값 동기화 — payrollForm.l_jiwon 업데이트 (항목 선택 반영, 항상 자동 산출) */
const syncSupportDeductionValue = () => {
  const { result } = getSupportDeductionResult();
  payrollForm.value.l_jiwon = result.total;
};

/**
 * 지원공제 자동 재계산 watch
 * - base 필드(senior/severe/welfare) 변경 시
 * - 공제 조건(l_yeon/l_go/c_out) 변경 시
 * 
 * ★ 실제 공제 금액 기준:
 *   - l_yeon > 0 → 국민연금 포함
 *   - l_go > 0 → 고용보험 포함
 *   - c_out > 0 → 퇴직금분(base÷12) 포함
 */
watch(
  () => [
    payrollForm.value.senior,
    payrollForm.value.bosu,
    payrollForm.value.severe,
    payrollForm.value.welfare,
    payrollForm.value.sunim,
    payrollForm.value.l_yeon,
    payrollForm.value.l_gun,
    payrollForm.value.l_jang,
    payrollForm.value.l_go,
    payrollForm.value.c_out,
    supportDeductionItemFlags.value,
    deductionFixed.value.l_jiwon,
  ],
  (newVal, oldVal) => {
    // 초기 로드 시(oldVal이 undefined)는 스킵 — DB에서 로드한 l_jiwon 값 유지
    if (!oldVal) return;
    syncSupportDeductionValue();
    syncLaborDeductTotal();
  }
);

/**
 * 식대 실비변상 체크박스 변경 시 퇴직금·지원공제 즉시 재계산
 * - payrollDbLock과 무관하게 항상 실행
 * - 퇴직금(c_out) 변경 → 지원공제(l_jiwon) 자동 재계산 (위 watch에서 처리)
 */
watch(
  () => nontaxSettings.value.lunchExpense,
  (newVal, oldVal) => {
    // 초기 로드 시 스킵
    if (oldVal === undefined) return;

    // 퇴직금 재계산 (applyRetirement 체크 여부와 무관하게)
    const newRetirement = calcRetirementPayAuto();
    payrollForm.value.c_out = newRetirement;

    // 기관공제합계 재계산
    syncCorpDeductTotal();
  }
);

/** 지급계산시급 연동: 기본급·주휴·연차(과세)
 *  - 센터장: 수급자별 합계(중증 제외) = 기본+주휴+연차. 자동중식·수당 손대지 않음
 *  - 관리자: 과세 배분 + 식대(비과세) 분리, 연·야·휴 금액 연동
 */
const syncHourlyLinkedPayroll = () => {
  const viewMode = resolveCaregiverPayrollViewMode();
  const isDirector = viewMode === 'director';

  // 관리자만 연·야·휴 시간·금액 연동. 센터장 수당은 직접 입력.
  if (!isDirector) {
    if (isCaregiverEmployee(selectedEmployee.value) && caregiverClaimRows.value.length > 0) {
      syncPremiumHoursFromClaims(false, viewMode);
    }
    if (!premiumManualAdjustEnabled.value && !payrollDbLock.value) {
      syncPremiumNontaxAmounts(viewMode);
    }
  }

  const hourly = caregiverHourlyWage.value;
  if (hourly <= 0 || totalSugaHours.value <= 0) {
    if (isDirector) {
      syncAccountingPayrollShadow();
    } else {
      accountingPayrollShadow.value = null;
    }
    return;
  }

  const severeSum = resolveEffectiveSeverePaySum();
  syncSeverePayFromClaim();

  const nonSevereRows = claimSummaryRows.value.filter((r) => !r.isSevereRecipientSurcharge);
  const allHourlyFilled = nonSevereRows.every((r) => r.hourlyWage > 0);
  if (!allHourlyFilled) {
    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    if (isDirector) {
      syncDirectorInsuranceFromAdminShadow(payrollDbLock.value);
    }
    return;
  }

  const nonSevereTarget = isDirector
    ? displayClaimGrandTotalForTable.value
    : claimGrandTotal.value - severeSum;

  /** 저장 급여대장 — 관리자 확정값 유지, 센터장은 lunch=0·autoBase만 정리 */
  if (payrollDbLock.value) {
    if (isDirector) {
      if (autoBaseLunchRef.value <= 0) {
        const shadowLunch = Number(accountingPayrollShadow.value?.lunch) || 0;
        autoBaseLunchRef.value = shadowLunch > 0
          ? shadowLunch
          : Number(payrollForm.value.lunch) || 0;
      }
      payrollForm.value.lunch = 0;
      syncAccountingPayrollShadow();
      syncDirectorInsuranceFromAdminShadow(true);
    } else {
      applyAdminAnnualLunchExtraction();
      syncAccountingPayrollShadow();
    }
    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    return;
  }

  if (isAllFamilyCareInClaim.value) {
    if (isDirector) {
      payrollForm.value.lunch = 0;
      payrollForm.value.weekend = 0;
      payrollForm.value.annual = 0;
      payrollForm.value.basic = Math.max(0, nonSevereTarget);
      recalcWageTotal();
      syncNonTaxTotal();
      syncReferenceValues();
      syncDirectorInsuranceFromAdminShadow(payrollDbLock.value);
      return;
    }

    let finalLunch = 0;
    if (nontaxSettings.value.lunchExpense) {
      finalLunch = resolveAutoLunchFromClaims({
        isFamily: true,
        nonSevereTarget,
      }).autoLunch;
    }

    payrollForm.value.weekend = 0;
    payrollForm.value.annual = 0;

    if (premiumManualAdjustEnabled.value) {
      const premiumTotal = getCurrentPremiumFullTotalFromForm();
      payrollForm.value.basic = Math.max(0, nonSevereTarget - finalLunch - premiumTotal);
    } else {
      const { premiumTotal } = applyCaregiverPremiumAmounts(
        {
          hourly,
          nonSevereTarget,
          linkedBasic: 0,
          linkedWeekend: 0,
          linkedAnnual: 0,
          finalLunch,
        },
        { viewMode }
      );
      payrollForm.value.basic = Math.max(0, nonSevereTarget - finalLunch - premiumTotal);
    }

    if (finalLunch > 0) {
      applyAutoLunchDirectorAdjustment({
        autoBase: finalLunch,
        adjustPayField: 'basic',
      });
    }

    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    accountingPayrollShadow.value = null;
    return;
  }

  const workDays = workCalendarSummary.value.workDayCount;
  const annualDays = annualLeaveDays.value;

  let breakdown;
  let finalLunch = 0;
  let finalAnnual = 0;

  if (isDirector) {
    const directorLinked = calcDirectorCaregiverLinkedPay({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: hourly,
      minHourlyWage: minHourlyWage.value,
      totalSugaHours: totalSugaHours.value,
      workDays,
      annualLeaveDays: annualDays,
    });
    breakdown = directorLinked.breakdown;
    finalLunch = 0;
    payrollForm.value.lunch = 0;
    finalAnnual = resolveHomeWageDirectorAnnualFromRecipientCap({
      recipientTotal: nonSevereTarget,
      basic: breakdown.base,
      weekend: breakdown.weeklyHoliday,
      proposedAnnual: breakdown.annualLeave,
    });
  } else {
    const linkedMin = calcHomeWageHourlyLinkedPay(getHourlyLinkedPayInput());
    breakdown = calcHomeWagePayBreakdown({
      targetMonthly: nonSevereTarget,
      payCalcHourlyWage: hourly,
      minHourlyWage: minHourlyWage.value,
      totalSugaHours: totalSugaHours.value,
      workDays,
      annualLeaveDays: annualDays,
    });
    finalAnnual = breakdown.annualLeave;

    const lunchResult = resolveAutoLunchFromClaims({
      isFamily: false,
      nonSevereTarget,
      annualLeaveBeforeLunch: finalAnnual,
      linkedMinAnnual: linkedMin.annual,
    });
    finalLunch = lunchResult.autoLunch;
    finalAnnual = lunchResult.annualAfterLunch ?? finalAnnual;

    if (!nontaxSettings.value.lunchExpense) {
      payrollForm.value.lunch = 0;
    } else {
      payrollForm.value.lunch = finalLunch;
    }
    if (!premiumManualAdjustEnabled.value) {
      applyCaregiverPremiumAmounts(
        {
          hourly,
          nonSevereTarget,
          linkedBasic: linkedMin.basic,
          linkedWeekend: linkedMin.weekend,
          linkedAnnual: linkedMin.annual,
          finalLunch,
        },
        { viewMode: 'admin' }
      );
    }
    payrollForm.value.basic = breakdown.base;
    payrollForm.value.weekend = breakdown.weeklyHoliday;
    payrollForm.value.annual = Math.max(0, finalAnnual);
    if (finalLunch > 0 || Number(payrollForm.value.lunchDirector) > 0) {
      applyAutoLunchDirectorAdjustment({
        autoBase: finalLunch,
        adjustPayField: 'annual',
      });
    } else {
      autoBaseLunchRef.value = finalLunch;
    }
  }

  payrollForm.value.basic = breakdown.base;
  payrollForm.value.weekend = breakdown.weeklyHoliday;
  payrollForm.value.annual = Math.max(0, finalAnnual);

  recalcWageTotal();
  syncNonTaxTotal();
  syncReferenceValues();

  if (isDirector) {
    syncDirectorInsuranceFromAdminShadow(payrollDbLock.value);
  } else {
    accountingPayrollShadow.value = null;
  }
};

let reconcilingHourlyLinkedPay = false;

/**
 * 청구 집계 기준 — 기본급·주휴·연차 일괄 재동기화
 * ★ 센터장 시점·DB lock 모두 동일 — 연차 변경 시 basic/weekend 함께 갱신
 * ★ 4대보험·공제는 payrollDbLock 시 syncLoadedPayrollDeductions로 저장값 유지
 */
const reconcileCaregiverHourlyLinkedPayFromClaims = (options?: {
  restoreLockedDeductions?: boolean;
  /** 센터장 [계산] 버튼 — 수동조정 ON이어도 청구 기준 재산출 */
  force?: boolean;
}) => {
  if (!options?.force && shouldSkipAdminLiveCalc()) return;
  if (reconcilingHourlyLinkedPay) return;
  if (!isCaregiverEmployee(selectedEmployee.value)) return;
  if (!autoCalcMode.value || isWageReadOnly.value) return;
  // 센터장+수동조정 ON — 화면 수동값 유지 / 관리자 미리보기·[계산] force는 재계산
  if (
    premiumManualAdjustEnabled.value
    && isDirectorPayrollView.value
    && !options?.force
  ) {
    return;
  }
  if (payrollHydrating.value || loading.value) return;
  if (caregiverHourlyWage.value <= 0 || totalSugaHours.value <= 0) return;

  reconcilingHourlyLinkedPay = true;
  try {
    if (payrollDbLock.value && dbSavedSummaryRows.value.length > 0) {
      syncLiveRecipientMapsFromDbSnapshot();
      syncRecipientSettingsDisplay();
    }
    syncHourlyLinkedPayroll();
    if (options?.restoreLockedDeductions !== false && payrollDbLock.value) {
      syncLoadedPayrollDeductions();
    }
  } finally {
    reconcilingHourlyLinkedPay = false;
  }
};

/**
 * ★ 관리자 시점(미리보기) 전환 대비 — 센터장 캐논 상태 스냅샷/복원
 * 관리자 시점에서 계산·수정한 값은 저장되지 않고, 센터장 시점 복귀 시 폐기·원복한다.
 */
interface PayrollPreviewSnapshot {
  payrollForm: HomeWagePayrollFormData;
  premiumHoursDisplay: Record<HomeWagePremiumHoursKey, string>;
  nontaxSettings: HomeWageNontaxSettings;
  deductionFixed: HomeWageDeductionFixedFlags;
  supportDeductionItemFlags: HomeWageSupportDeductionItemFlags;
  premiumManualAdjustEnabled: boolean;
  premiumHoursUserEdited: boolean;
  directorPremiumPayOverride: {
    payOvertime: boolean | null;
    payHoliday: boolean | null;
    payNight: boolean | null;
  };
  isRepresentative: boolean;
  applyRetirement: boolean;
  retirementSource: typeof retirementSource.value;
  caregiverHourlyWage: number;
  caregiverHourlyWageDisplay: string;
  recipientMaps: ReturnType<typeof getMapsForSave>;
  hourlyWageDisplay: Record<string, string>;
  severeFeeDisplay: Record<string, string>;
  sugaHoursDisplay: Record<string, string>;
}

const payrollPreviewSnapshot = ref<PayrollPreviewSnapshot | null>(null);

function clonePreview<T>(v: T): T {
  return JSON.parse(JSON.stringify(v ?? null));
}

/** 센터장 → 관리자 미리보기 진입 시 — 현재(센터장 캐논) 상태 보관 */
const capturePayrollPreviewSnapshot = () => {
  payrollPreviewSnapshot.value = {
    payrollForm: clonePreview(payrollForm.value),
    premiumHoursDisplay: clonePreview(payrollPremiumHoursDisplay.value),
    nontaxSettings: clonePreview(nontaxSettings.value),
    deductionFixed: clonePreview(deductionFixed.value),
    supportDeductionItemFlags: clonePreview(supportDeductionItemFlags.value),
    premiumManualAdjustEnabled: premiumManualAdjustEnabled.value,
    premiumHoursUserEdited: premiumHoursUserEdited.value,
    directorPremiumPayOverride: clonePreview(directorPremiumPayOverride.value),
    isRepresentative: isRepresentative.value,
    applyRetirement: applyRetirement.value,
    retirementSource: clonePreview(retirementSource.value),
    caregiverHourlyWage: caregiverHourlyWage.value,
    caregiverHourlyWageDisplay: caregiverHourlyWageDisplay.value,
    recipientMaps: clonePreview(getMapsForSave()),
    hourlyWageDisplay: clonePreview(hourlyWageDisplay.value),
    severeFeeDisplay: clonePreview(severeFeeDisplay.value),
    sugaHoursDisplay: clonePreview(sugaHoursDisplay.value),
  };
};

/** 관리자 미리보기 → 센터장 복귀 시 — 미리보기 변경 폐기, 캐논 복원 */
const restorePayrollPreviewSnapshot = (): boolean => {
  const snap = payrollPreviewSnapshot.value;
  if (!snap) return false;
  payrollForm.value = clonePreview(snap.payrollForm);
  payrollPremiumHoursDisplay.value = clonePreview(snap.premiumHoursDisplay);
  nontaxSettings.value = clonePreview(snap.nontaxSettings);
  deductionFixed.value = clonePreview(snap.deductionFixed);
  supportDeductionItemFlags.value = clonePreview(snap.supportDeductionItemFlags);
  premiumManualAdjustEnabled.value = snap.premiumManualAdjustEnabled;
  premiumHoursUserEdited.value = snap.premiumHoursUserEdited;
  directorPremiumPayOverride.value = clonePreview(
    snap.directorPremiumPayOverride ?? { payOvertime: null, payHoliday: null, payNight: null }
  );
  isRepresentative.value = snap.isRepresentative;
  applyRetirement.value = snap.applyRetirement;
  retirementSource.value = clonePreview(snap.retirementSource);
  setCaregiverHourlyWage(snap.caregiverHourlyWage);
  caregiverHourlyWageDisplay.value = snap.caregiverHourlyWageDisplay;
  loadMapsFromDb({
    hourlyByGroup: snap.recipientMaps.hourlyByGroup ?? {},
    severeFeeByGroup: snap.recipientMaps.severeFeeByGroup ?? {},
    sugaHoursByGroup: snap.recipientMaps.sugaHoursByGroup ?? {},
  });
  hourlyWageDisplay.value = clonePreview(snap.hourlyWageDisplay);
  severeFeeDisplay.value = clonePreview(snap.severeFeeDisplay);
  sugaHoursDisplay.value = clonePreview(snap.sugaHoursDisplay);
  payrollPreviewSnapshot.value = null;
  return true;
};

/** 관리자 시점 — 비과세 정책 기준 급여 항목 표시 */
const applyStoredAdminPartsToForm = () => {
  const row = loadedDirectorRow.value;
  if (!row || !hasHomeWageStoredAdParts(row)) return;
  const admin = readHomeWageStoredAdParts(row, yearMonth.value);
  payrollForm.value = applyAdminSettingPartsToFormData(payrollForm.value, admin);
};

const applyAdminPayrollPreviewToForm = () => {
  if (isHomeWageDirectorStorePage.value && hasHomeWageStoredAdParts(loadedDirectorRow.value)) {
    applyStoredAdminPartsToForm();
    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    return;
  }
  if (directorCanonicalActive.value) {
    syncAccountingPayrollShadow();
    if (isCaregiverEmployee(selectedEmployee.value)) {
      reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
      ensureCaregiverPremiumAmountsSynced();
    }
    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    return;
  }
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    // 사복·시설장·운전 — 풀→기본급+자동중식 분리 (센터장 표시와 무관, form만)
    applyStaffFundedAutoLunchCarve();
  } else {
    // 센터장 시점에서 lunch=0 으로 숨긴 자동중식 복원 — 흡수 고스트는 복원하지 않음 (고영이)
    const savedAuto = Math.max(0, Number(dbSavedAutoLunchRef.value) || 0);
    if (savedAuto > 0 && (Number(payrollForm.value.lunch) || 0) <= 0) {
      const funded = resolveCaregiverFundedAutoLunchForPay(savedAuto);
      if (funded > 0) {
        payrollForm.value.lunch = funded;
        if ((Number(autoBaseLunchRef.value) || 0) <= 0) {
          autoBaseLunchRef.value = funded;
        }
      } else {
        payrollForm.value.lunch = 0;
        autoBaseLunchRef.value = 0;
      }
    }
    applyAdminAnnualLunchExtraction();
  }
  syncAccountingPayrollShadow();
  reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
  ensureCaregiverPremiumAmountsSynced();
  recalcWageTotal();
  syncNonTaxTotal();
  syncReferenceValues();
};

/** 센터장 시점 복귀 — 스냅샷 복원 후 표시·shadow 동기화 */
const applyDirectorPayrollViewRestore = () => {
  if (restorePayrollPreviewSnapshot()) {
    applyDirectorAnnualLunchDisplayPrep();
    syncRecipientSettingsDisplay();
    if (premiumManualAdjustEnabled.value) {
      premiumAutoSnapshot.value = buildPremiumAutoSnapshot();
    }
    recalcWageTotal();
    syncNonTaxTotal();
    syncReferenceValues();
    if (isCaregiverEmployee(selectedEmployee.value)) {
      syncAccountingPayrollShadow();
    }
    return;
  }
  if (isCaregiverEmployee(selectedEmployee.value)) {
    applyDirectorAnnualLunchDisplayPrep();
    reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
    ensureCaregiverPremiumAmountsSynced();
    syncAccountingPayrollShadow();
  }
  recalcWageTotal();
  syncNonTaxTotal();
  syncReferenceValues();
};

/** 청구 변경 시 연·야·휴 시간 즉시 반영 (모달 없이도 정확) */
watch(
  [
    () => caregiverClaimRows.value,
    () => selectedEmployee.value,
    selectedYear,
  ],
  () => {
    if (!isCaregiverEmployee(selectedEmployee.value)) return;
    if (!caregiverClaimRows.value.length) return;
    if (payrollHydrating.value || loading.value) return;
    void refreshPremiumHoursFromClaims().then(() => {
      if (isCaregiverEmployee(selectedEmployee.value)) {
        syncLaborDayPay();
        recalcWageTotal();
      }
      if (payrollHydrating.value || loading.value || !autoCalcMode.value) return;
      if (payrollDbLock.value) {
        reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
        return;
      }
      schedulePayrollAutoCalc('full', true);
    });
  },
  { deep: true }
);

/** 지급계산시급 변경 시 비과세 연·야·휴 금액 즉시 반영 (수동입력 중에는 스킵) */
watch(
  () => caregiverHourlyWage.value,
  () => {
    if (!isCaregiverEmployee(selectedEmployee.value)) return;
    if (premiumManualAdjustEnabled.value) return;
    if (payrollHydrating.value || loading.value || payrollViewSwitching.value) return;
    syncPremiumNontaxAmounts();
    reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
  }
);

/**
 * 시점 전환 처리
 * - 센터장 → 관리자: 센터장 캐논 스냅샷 후 관리자 표시 적용
 * - 관리자 → 센터장: 미저장 변경 폐기·스냅샷 복원
 */
watch(isDirectorPayrollView, (isDirector, wasDirector) => {
  if (isDirector === wasDirector) return;
  if (!selectedEmployee.value || payrollHydrating.value || loading.value) return;
  if (payrollViewSwitching.value) return;

  payrollViewSwitching.value = true;
  try {
    if (!isDirector) {
      capturePayrollPreviewSnapshot();
      applyAdminPayrollPreviewToForm();
    } else {
      applyDirectorPayrollViewRestore();
    }
  } finally {
    void nextTick(() => {
      payrollViewSwitching.value = false;
    });
  }
});

/** 수동조정 ON — 관리자 표시용 자동 스냅샷 갱신 */
watch(
  [
    () => caregiverHourlyWage.value,
    totalSugaHours,
    () => payrollForm.value.overtimeHours,
    () => payrollForm.value.nightHours,
    () => payrollForm.value.holidayHours,
    () => nontaxSettings.value.productionNontax,
    claimGrandTotal,
    () => payrollForm.value.lunch,
  ],
  () => {
    if (premiumManualAdjustEnabled.value) {
      premiumAutoSnapshot.value = buildPremiumAutoSnapshot();
    }
  }
);

watch(
  [
    () => caregiverHourlyWage.value,
    totalSugaHours,
    () => workCalendarSummary.value.workDayCount,
    annualLeaveDays,
    () => selectedEmployee.value,
    claimGrandTotal,
    yearMonth,
  ],
  () => {
    if (!isCaregiverEmployee(selectedEmployee.value)) return;
    syncLaborDayPay();
    recalcWageTotal();
    if (payrollHydrating.value || loading.value || payrollViewSwitching.value) return;
    if (payrollDbLock.value) {
      reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
      return;
    }
    schedulePayrollAutoCalc('full');
  }
);

watch(
  [totalSugaHours, () => staffForm.value.workHours, isRepresentative],
  () => {
    if (!selectedEmployee.value) return;
    const { applicable } = resolveHomeWageRetirementApplicable(getRetirementApplicableInput());
    if (!applicable) {
      applyRetirement.value = false;
      if (!payrollDbLock.value) {
        payrollForm.value.c_out = 0;
        syncSupportDeduction(true);
        syncCorpDeductTotal();
      }
    }
  }
);

const formatPremiumHoursField = (key: HomeWagePremiumHoursKey) => {
  if (directorMaskPremiumDisplay.value) return '';
  const stored = payrollPremiumHoursDisplay.value[key];
  if (stored !== undefined && stored !== '') {
    if (premiumHoursUserEdited.value) return stored;
    return formatHomeWagePremiumHoursDisplay(stored);
  }
  return formatHomeWagePremiumHoursDisplay(payrollForm.value[key]);
};

const onPremiumHoursInput = (hoursKey: HomeWagePremiumHoursKey, event: Event) => {
  if (alertDirectorPremiumBlocked()) {
    (event.target as HTMLInputElement).blur();
    return;
  }
  const target = event.target as HTMLInputElement;
  const raw = sanitizeHoursInput(target.value);
  if (raw !== target.value) {
    target.value = raw;
  }
  premiumHoursUserEdited.value = true;
  payrollPremiumHoursDisplay.value = {
    ...payrollPremiumHoursDisplay.value,
    [hoursKey]: raw,
  };
  payrollForm.value[hoursKey] = parseHours(raw.replace(/시간/g, ''));
  if (!premiumManualAdjustEnabled.value && isCaregiverEmployee(selectedEmployee.value) && caregiverHourlyWage.value > 0) {
    syncPremiumNontaxAmounts();
  } else if (!isCaregiverEmployee(selectedEmployee.value)) {
    syncStaffFormFromPayroll();
    syncStaffLinkedPayroll();
  }
};

const onPremiumHoursBlur = (
  hoursKey: HomeWagePremiumHoursKey,
  _amountKey: HomeWagePremiumAmountKey
) => {
  if (alertDirectorPremiumBlocked()) return;
  const raw = payrollPremiumHoursDisplay.value[hoursKey] ?? '';
  const parsed = parseHours(raw.replace(/시간/g, ''));
  premiumHoursUserEdited.value = true;
  payrollForm.value[hoursKey] = parsed;
  payrollPremiumHoursDisplay.value = {
    ...payrollPremiumHoursDisplay.value,
    [hoursKey]: formatHomeWagePremiumHoursDisplay(parsed),
  };
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    syncStaffFormFromPayroll();
    syncStaffLinkedPayroll();
  } else if (!premiumManualAdjustEnabled.value) {
    syncPremiumNontaxAmounts();
  }
  schedulePayrollAutoCalc('downstream', true, true);
};

const formatSugaHours = (hours: number) => formatDurationHoursMinutes(hours);

const requireMinHourlyWage = (silent = false): boolean => {
  if (minHourlyWage.value <= 0) {
    if (!silent) {
      alert('당해 연도 최저시급을 불러오지 못했습니다. 연도를 확인하거나 잠시 후 다시 시도해 주세요.');
    }
    return false;
  }
  return true;
};

const requireTaxRate = (silent = false): boolean => {
  if (!taxRateInfo.value) {
    if (!silent) {
      alert('당해 연도 세율을 불러오지 못했습니다. 연도를 확인하거나 잠시 후 다시 시도해 주세요.');
    }
    return false;
  }
  return true;
};

const buildDeductionRates = (
  applyPension: boolean,
  applyEmployment: boolean
): DeductionRates => {
  const t = taxRateInfo.value!;
  const san =
    sanRate.value > 0
      ? sanRate.value
      : parseHomeWageTaxRateValue(t.rateSan, 0.007);
  const applyCorpEmployment = applyEmployment && !isRepresentative.value;
  const fullGunRate = parseHomeWageTaxRateValue(t.rateGun, 0.03545);
  const fullJangRate = parseHomeWageTaxRateValue(t.rateJang, 0.1295);
  const familyHealthExempt = isFamilyUnder60HealthExempt.value;
  return {
    rateYeon: applyPension ? parseHomeWageTaxRateValue(t.rateYeon, 0.045) : 0,
    rateGun: familyHealthExempt ? 0 : fullGunRate,
    rateJang: familyHealthExempt ? 0 : fullJangRate,
    rateGo: applyCorpEmployment ? parseHomeWageTaxRateValue(t.rateGo, 0.009) : 0,
    /** 실업급여 — 과세임금×요율 (대표자는 applyRepresentativeCorpRules에서 0 처리) */
    rateSil: parseHomeWageTaxRateValue(t.rateSil, 0.0065),
    rateSan: isRepresentative.value ? 0 : san,
  };
};

/**
 * 4대보험·기관부담 산정용 과세임금·요율
 * ★ 순수 조회만 — accountingPayrollShadow write 금지
 *   (모달 미리보기 computed → 이 함수 경로에서 write 시 Vue 무한 갱신으로 화면 정지)
 *   shadow 갱신은 runInsuranceDeductionAutoCalc / openDeductionFixModal 등 이벤트 핸들러에서만
 */
const resolveInsuranceCalcContext = (
  silent = false
): {
  taxable: number;
  rates: DeductionRates;
  ageMessage: string;
} | null => {
  if (!requireTaxRate(silent)) return null;

  const taxable = getInsuranceTaxableBase();
  if (taxable <= 0) {
    if (!silent) {
      alert('과세 임금이 없습니다. 기본급·수당을 먼저 입력해 주세요.');
    }
    return null;
  }

  const birth = getSelectedInsuranceBirth();
  const ageExempt = resolveSelectedHomeWageAgeInsurance(birth);
  const rates = buildDeductionRates(
    !ageExempt.pensionExempt,
    !ageExempt.employmentExempt
  );

  const ageParts: string[] = [];
  if (ageExempt.pensionMessage) ageParts.push(ageExempt.pensionMessage);
  if (ageExempt.employmentMessage) ageParts.push(ageExempt.employmentMessage);

  return {
    taxable,
    rates,
    ageMessage: ageParts.join('\n'),
  };
};

const syncPayrollEmployeeMeta = () => {
  const emp = selectedEmployee.value;
  if (!emp) return;
  payrollForm.value.name = getEmpName(emp);
  payrollForm.value.job = getEmpJob(emp);
  payrollForm.value.birth =
    getEmpBirth(emp)
    || parseEmpBirthDate(loadedDirectorRow.value?.birth)
    || payrollForm.value.birth
    || '';

  // ★ 저장된 급여대장이 있을 때만 근무시간·근무일수 자동 반영 (미저장 월은 빈 값 유지)
  if (isCaregiverEmployee(emp) && payrollDbLock.value) {
    if (totalSugaHours.value > 0) {
      payrollForm.value.hour = String(Math.round(totalSugaHours.value * 10) / 10);
    }
    const workDays = workCalendarSummary.value.workDayCount;
    if (workDays > 0) {
      payrollForm.value.days = String(workDays);
    }
  }
};

/** 기본급 계산: 요양보호사=청구 기준 / 그 외=레거시 시급×근무시간 */
const runAutoFillPayCalc = (silent = false): boolean => {
  unlockPayrollLedgerForEdit();
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    return runAutoFillStaffPayCalc(silent);
  }
  if (!requireMinHourlyWage(silent)) return false;
  const currentWage = caregiverHourlyWage.value;
  if (!currentWage || currentWage <= 0) {
    setCaregiverHourlyWage(minHourlyWage.value);
    caregiverHourlyWageDisplay.value = formatWageNumber(minHourlyWage.value);
  }
  syncHourlyLinkedPayroll();
  return true;
};

/** 센터장 [계산] 전 — lock 해제·수급자 maps·표시값·연·야·휴 시간 초기화 */
const prepareDirectorPayRecalculate = () => {
  unlockPayrollLedgerForEdit();
  premiumHoursUserEdited.value = false;
  if (caregiverClaimRowsForAgg.value.length > 0) {
    syncLiveRecipientMapsFromDbSnapshot();
    syncUnjoinedSevereFeesFromClaims();
    rebuildSummaryRows();
  }
  syncRecipientSettingsDisplay();
};

/** 관리자(90+) — 센터장 시점 시급·기본급·주휴·연차 일괄 재계산 (DB lock·수급자 시급 미입력 보정 포함) */
const payrollRecalculating = ref(false);

const runDirectorBasicPayRecalculate = () => {
  if (!canRecalcDirectorBasicPay.value || !selectedEmployee.value || isWageReadOnly.value) return;
  if (isAdminPreviewView.value) {
    alert('센터장 시점에서만 사용할 수 있습니다. 시점을 센터장으로 전환해 주세요.');
    return;
  }
  if (isDetailPageLoading.value || payrollHydrating.value) return;

  payrollRecalculating.value = true;
  try {
    clearPendingPayrollAutoCalc();
    markDirty();

    if (isCaregiverEmployee(selectedEmployee.value)) {
      if (!caregiverClaimRowsForAgg.value.length) {
        alert('청구내역이 없습니다.');
        return;
      }
      if (!requireMinHourlyWage(false)) return;

      if (caregiverHourlyWage.value <= 0) {
        setCaregiverHourlyWage(minHourlyWage.value);
        caregiverHourlyWageDisplay.value = formatWageNumber(minHourlyWage.value);
      }

      prepareDirectorPayRecalculate();

      const nonSevereRows = claimSummaryRows.value.filter((row) => !row.isSevereRecipientSurcharge);
      if (nonSevereRows.length > 0) {
        const allHourlyFilled = nonSevereRows.every((row) => (row.hourlyWage ?? 0) > 0);
        if (!allHourlyFilled) {
          alert('수급자별 시급을 입력해 주세요. 최저시급은 자동으로 들어가지 않습니다.');
          return;
        }
        if (totalSugaHours.value <= 0) {
          alert('수가시간이 없습니다. 청구내역을 확인해 주세요.');
          return;
        }
      }

      reconcileCaregiverHourlyLinkedPayFromClaims({
        restoreLockedDeductions: false,
        force: true,
      });
      syncSeverePayFromClaim({ force: true });
      recalcWageTotal();
      syncNonTaxTotal();
      syncDirectorInsuranceFromAdminShadow(false);
      syncReferenceValues();
      return;
    }

    const ok = runAutoFillStaffPayCalc(false);
    if (ok) {
      runInsuranceDeductionAutoCalc(false, false);
      syncReferenceValues();
    }
  } catch (error: unknown) {
    const err = error as { message?: string };
    const message = err?.message || '알 수 없는 오류';
    alert(`급여 계산 실패: ${message}`);
    console.error('급여 계산 오류:', error);
  } finally {
    payrollRecalculating.value = false;
  }
};

/** 과세 임금: 지급계산시급·청구합계 기준 (수급자별 표 미변경)
 *  - 중증수급자가산은 별도 처리 (claimGrandTotal에서 먼저 제외 후 배분)
 *  - 기본급/주휴/연차는 최저시급 기준으로 계산
 *  - 남은금액(차액)은 연차수당에 추가 (식대 실비변상 시에는 식대 먼저)
 *  - 최종: (basic + weekend + annual + lunch) + severe = claimGrandTotal
 */
const runAutoFillTaxable = (silent = false): boolean => {
  unlockPayrollLedgerForEdit();
  if (!caregiverClaimRows.value.length) {
    if (!silent) alert('청구내역이 없습니다.');
    return false;
  }
  if (!requireMinHourlyWage(silent)) return false;
  if (claimGrandTotal.value <= 0) {
    if (!silent) {
      alert('청구 합계가 없습니다. 수급자별 시급·수가시간을 먼저 입력해 주세요.');
    }
    return false;
  }

  // 중증수급자가산 합계 (claimGrandTotal에 이미 포함되어 있음)
  const severeSum = claimSummaryRows.value
    .filter((r) => r.isSevereRecipientSurcharge)
    .reduce((sum, r) => sum + r.totalAmount, 0);

  // 중증수급자가산 제외한 금액으로 배분 계산
  const nonSevereTotal = claimGrandTotal.value - severeSum;
  const lunchDays = nontaxSettings.value.lunchExpense ? workCalendarSummary.value.lunchWorkDayCount : 0;

  // ───────────────────────────────────────────
  // ★★★ 가족요양 전용 처리 ★★★
  // - 기본급 = 월급여 전체 (주휴·연차 포함)
  // - 주휴수당 = 0원
  // - 연차수당 = 0원
  // - 식대 = 지급 가능
  // ───────────────────────────────────────────
  if (isAllFamilyCareInClaim.value) {
    const finalLunch = nontaxSettings.value.lunchExpense
      ? resolveAutoLunchFromClaims({
          isFamily: true,
          nonSevereTarget: nonSevereTotal,
        }).autoLunch
      : 0;
    if (finalLunch > 0) {
      payrollForm.value.lunch = finalLunch;
    }

    syncPremiumHoursFromClaims();
    payrollForm.value.weekend = 0;
    payrollForm.value.annual = 0;

    const hourly = caregiverHourlyWage.value || minHourlyWage.value;
    const { premiumTotal } = applyCaregiverPremiumAmounts({
      hourly,
      nonSevereTarget: nonSevereTotal,
      linkedBasic: 0,
      linkedWeekend: 0,
      linkedAnnual: 0,
      finalLunch,
    });

    payrollForm.value.basic = Math.max(0, nonSevereTotal - finalLunch - premiumTotal);
    payrollForm.value.severe = severeSum;
    recalcWageTotal();
    syncNonTaxTotal();
    syncPayrollEmployeeMeta();
    return true;
  }

  // ───────────────────────────────────────────
  // 일반 요양: 기본급·주휴·연차 분리 계산
  // ───────────────────────────────────────────
  const minWage = minHourlyWage.value;
  const sugaHours = totalSugaHours.value;
  const workDays = workCalendarSummary.value.workDayCount;
  const annualDays = annualLeaveDays.value;

  // 최저시급 기준 식대 배분 (실비변상)
  const dist = distributeHomeWagePay({
    monthlyWage: nonSevereTotal,
    minHourlyWage: minWage,
    totalSugaHours: sugaHours,
    workDays,
    annualLeaveDays: annualDays,
    lunchWorkDays: lunchDays,
  });

  if (nontaxSettings.value.lunchExpense) {
    payrollForm.value.lunch = dist.lunchPay;
  }

  syncPremiumHoursFromClaims();
  const linkedMin = calcHomeWageHourlyLinkedPay(getHourlyLinkedPayInput());
  const finalLunch = nontaxSettings.value.lunchExpense ? dist.lunchPay : 0;
  const hourly = caregiverHourlyWage.value || minWage;

  const breakdown = calcHomeWagePayBreakdown({
    targetMonthly: nonSevereTotal,
    payCalcHourlyWage: hourly,
    minHourlyWage: minWage,
    totalSugaHours: sugaHours,
    workDays,
    annualLeaveDays: annualDays,
  });

  applyCaregiverPremiumAmounts({
    hourly,
    nonSevereTarget: nonSevereTotal,
    linkedBasic: linkedMin.basic,
    linkedWeekend: linkedMin.weekend,
    linkedAnnual: linkedMin.annual,
    finalLunch,
  });

  payrollForm.value.basic = breakdown.base;
  payrollForm.value.weekend = breakdown.weeklyHoliday;
  payrollForm.value.annual = breakdown.annualLeave;

  payrollForm.value.severe = severeSum;
  recalcWageTotal();
  syncNonTaxTotal();
  syncPayrollEmployeeMeta();
  return true;
};

/** 고정 체크 시 저장값 유지(0원 포함). 조건 면제 0을 산출값으로 다시 채우지 않음 */
const resolveDeductionAmount = (
  key: HomeWageDeductionFixFieldKey,
  calculated: number
) => {
  if (!deductionFixed.value[key]) return calculated;
  return Number(payrollForm.value[key]) || 0;
};

const LOADED_SOCIAL_INSURANCE_KEYS = [
  ['l_yeon', ['l_yeon', 'cwhdir_l_yeon', 'lYeon']],
  ['c_yeon', ['c_yeon', 'cwhdir_c_yeon', 'cYeon']],
  ['l_gun', ['l_gun', 'cwhdir_l_gun', 'lGun']],
  ['c_gun', ['c_gun', 'cwhdir_c_gun', 'cGun']],
  ['l_jang', ['l_jang', 'cwhdir_l_jang', 'lJang']],
  ['c_jang', ['c_jang', 'cwhdir_c_jang', 'cJang']],
  ['l_go', ['l_go', 'cwhdir_l_go', 'lGo']],
  ['c_go', ['c_go', 'cwhdir_c_go', 'cGo']],
] as const;

const readLoadedSocialInsuranceAmount = (aliases: readonly string[]): number | null => {
  const row = loadedDirectorRow.value;
  if (!row) return null;
  for (const key of aliases) {
    if (row[key] == null || row[key] === '') continue;
    const n = Number(row[key]);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/** 대장에 0으로 저장된 국민·건강·장기·고용은 재산출로 다시 채우지 않음. 고정 입력값은 유지 */
const keepStoredSocialInsuranceZeros = () => {
  if (!payrollDbLock.value || !loadedDirectorRow.value) return;
  for (const [field, aliases] of LOADED_SOCIAL_INSURANCE_KEYS) {
    if (deductionFixed.value[field]) continue;
    if (readLoadedSocialInsuranceAmount(aliases) === 0) {
      payrollForm.value[field] = 0;
    }
  }
};

/** 나이·가족 60h 면제는 0. 금액 설정에서 고정한 값은 요율 산출액을 유지한다. */
const applyInsurancePolicyZeros = () => {
  const ageExempt = resolveSelectedHomeWageAgeInsurance();
  const familyHealthExempt = isFamilyUnder60HealthExempt.value;
  const fixed = deductionFixed.value;

  if (ageExempt.pensionExempt) {
    if (!fixed.l_yeon) payrollForm.value.l_yeon = 0;
    if (!fixed.c_yeon) payrollForm.value.c_yeon = 0;
  }
  if (familyHealthExempt) {
    if (!fixed.l_gun) payrollForm.value.l_gun = 0;
    if (!fixed.c_gun) payrollForm.value.c_gun = 0;
    if (!fixed.l_jang) payrollForm.value.l_jang = 0;
    if (!fixed.c_jang) payrollForm.value.c_jang = 0;
  }
  if (ageExempt.employmentExempt) {
    if (!fixed.l_go) payrollForm.value.l_go = 0;
    if (!fixed.c_go) payrollForm.value.c_go = 0;
  }
  keepStoredSocialInsuranceZeros();
};

/** 4대보험: caion_tax_rate × 과세임금 (요양시설 임금대장과 동일 산식) */
const runAutoFillSocialInsurance = (
  silent = false,
  preservePayrollLock = false
): string | null => {
  if (!preservePayrollLock) {
    unlockPayrollLedgerForEdit();
  }
  const ctx = resolveInsuranceCalcContext(silent);
  if (!ctx) return null;

  const emp = calcEmployeeDeductions(ctx.taxable, ctx.rates, 0);
  const corp = calcEmployerDeductions(ctx.taxable, ctx.rates);

  const resolveFixed = (key: HomeWageDeductionFixFieldKey, calculated: number) =>
    resolveDeductionAmount(key, calculated);

  payrollForm.value.l_yeon = resolveFixed('l_yeon', emp.yeon);
  payrollForm.value.l_gun = resolveFixed('l_gun', emp.gun);
  payrollForm.value.l_jang = resolveFixed('l_jang', emp.jang);
  payrollForm.value.l_go = resolveFixed('l_go', emp.go);

  payrollForm.value.c_yeon = resolveFixed('c_yeon', corp.yeon);
  payrollForm.value.c_gun = resolveFixed('c_gun', corp.gun);
  payrollForm.value.c_jang = resolveFixed('c_jang', corp.jang);
  payrollForm.value.c_go = resolveFixed('c_go', corp.go);

  applyInsurancePolicyZeros();
  applyRepresentativeCorpRules();

  syncLaborDeductTotal();
  syncCorpDeductTotal();
  return ctx.ageMessage || null;
};

const getGrossPay = () => getHomeWageLedgerGrossPay(payrollForm.value);

/** 대표자 기관공제 0 처리 (고용·산재·근로자 고용) — 실업·퇴직금은 별도, 고정 체크 시 해당 금액 유지 */
const applyRepresentativeCorpRules = () => {
  if (!isRepresentative.value) return;
  const fixed = deductionFixed.value;
  const applied = applyHomeWageRepresentativeCorpDeduction(
    {
      c_go: Number(payrollForm.value.c_go) || 0,
      c_sil: Number(payrollForm.value.c_sil) || 0,
      c_san: Number(payrollForm.value.c_san) || 0,
      c_out: Number(payrollForm.value.c_out) || 0,
      l_go: Number(payrollForm.value.l_go) || 0,
    },
    true
  );
  if (!fixed.c_go) payrollForm.value.c_go = applied.c_go;
  if (!fixed.c_sil) payrollForm.value.c_sil = applied.c_sil;
  if (!fixed.c_san) payrollForm.value.c_san = applied.c_san;
  if (!fixed.l_go && applied.l_go != null) payrollForm.value.l_go = applied.l_go;
};

const toggleApplyRetirement = (checked: boolean) => {
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  applyRetirement.value = checked;
  if (!checked) {
    payrollForm.value.c_out = 0;
    resetRetirementSource();
    syncSupportDeduction(true);
    syncCorpDeductTotal();
  } else if (!isWageReadOnly.value) {
    nextTick(() => openRetirementSourceModal());
  }
  schedulePayrollAutoCalc('downstream', true);
};

const toggleRepresentative = (checked: boolean) => {
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  isRepresentative.value = checked;
  if (checked) {
    applyRetirement.value = false;
    payrollForm.value.c_out = 0;
    resetRetirementSource();
    applyRepresentativeCorpRules();
    syncLaborDeductTotal();
    syncSupportDeduction(true);
    syncCorpDeductTotal();
  } else {
    applyRetirement.value = resolveHomeWageRetirementApplicable(getRetirementApplicableInput()).applicable;
  }
  schedulePayrollAutoCalc('downstream', true);
};

/** 퇴직금 미체크 시 c_out 0 동기화 */
const syncRetirementApplicability = () => {
  if (!applyRetirement.value && !payrollDbLock.value) {
    payrollForm.value.c_out = 0;
    syncSupportDeduction(true);
    syncCorpDeductTotal();
  }
};

/** 퇴직금(c_out) 자동 산출 — applyRetirement 미체크(고정 해제) 시에만 사용 */
const calcRetirementPayAuto = (): number => {
  const lunchParts = resolveRetirementLunchPartsForCalc();
  return calcHomeWageInstitutionRetirement(
    {
      ...payrollForm.value,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
    },
    {
      workHours: getRetirementWorkHours(),
      isRepresentative: isRepresentative.value,
      lunchExpense: nontaxSettings.value.lunchExpense,
      lunch: lunchParts.lunch,
      lunchDirector: lunchParts.lunchDirector,
    }
  );
};

/** 기관공제 계산 시 퇴직금 반영 — 체크(고정) 시 선택·입력값, 미체크 시 자동 산출 */
const applyRetirementOnCorpCalc = () => {
  if (applyRetirement.value) {
    syncRetirementFromSourceSelection();
    return;
  }
  payrollForm.value.c_out = calcRetirementPayAuto();
};

/**
 * 지원공제(l_jiwon) 동기화 — 통합 계산 함수 사용
 * ★ getSupportDeductionResult()를 사용하여 툴팁과 동일한 계산 보장
 */
const syncSupportDeduction = (silent = false): boolean => {
  // 세율 정보 필요
  if (!taxRateInfo.value) {
    if (!silent) {
      alert('당해 연도 세율을 불러오지 못했습니다.');
    }
    return false;
  }

  // 통합 계산 함수 사용
  syncSupportDeductionValue();
  return true;
};

/** 인건비절감액 — row/폼 공용 입력 (모두저장·툴팁) */
const buildCenterTaxSavingsInput = (
  nontaxBase: number,
  opts: { birth?: string; job?: string; workHours?: number; joinDate?: string | null } = {}
): HomeWageNontaxSavingsInput | null => {
  if (nontaxBase <= 0 || !taxRateInfo.value) return null;

  const birth =
    opts.birth ||
    getSelectedInsuranceBirth();
  const joinDate =
    opts.joinDate !== undefined
      ? opts.joinDate
      : getEmpJoinDate(selectedEmployee.value);
  const ageExempt = resolveHomeWageAgeInsurance(
    birth,
    selectedYear.value,
    selectedMonth.value,
    joinDate
  );
  const rates = buildDeductionRates(
    !ageExempt.pensionExempt,
    !ageExempt.employmentExempt
  );

  const isCg = opts.job
    ? opts.job.includes('요양보호사')
    : isCaregiverEmployee(selectedEmployee.value);
  const workHours = opts.workHours ?? getRetirementWorkHours();

  return {
    nonTaxableAmount: nontaxBase,
    taxSavings: 0,
    rateYeon: rates.rateYeon,
    rateGun: rates.rateGun,
    rateJang: rates.rateJang,
    rateGo: rates.rateGo,
    rateSil: rates.rateSil,
    rateSan: rates.rateSan,
    applyPension: rates.rateYeon > 0,
    applyHealth: rates.rateGun > 0,
    applyEmployment: rates.rateGo > 0,
    includeRetirementSavings: !isCg && workHours >= HOME_WAGE_INSURANCE_MIN_HOURS,
  };
};

/** 인건비절감액 — row/폼 공용 (모두저장 등) */
const calcCenterTaxSavingsFromParts = (
  nontaxBase: number,
  opts: { birth?: string; job?: string; workHours?: number; joinDate?: string | null } = {}
) => {
  const input = buildCenterTaxSavingsInput(nontaxBase, opts);
  if (!input) return 0;
  return calcHomeWageCenterTaxSavings(input);
};

/** 인건비절감액 — 비과세합계(차량·자녀 제외) 기준 기관 부담 절감 */
const calcReferenceNontaxSavings = () => {
  const nontaxBase = calcHomeWageCenterSavingsNontaxBase(
    payrollForm.value,
    productionNontaxActive.value,
    yearMonth.value,
    resolveNontaxViewMode()
  );
  return calcCenterTaxSavingsFromParts(nontaxBase);
};

/** 참고값(지급금액, 실수령액, 기관수익 등) 재계산 — lock 시에도 지급금액은 과세+비과세 정합 */
const syncReferenceValues = () => {
  // c_save — 비과세합계(차량·자녀 제외) 기준, lock과 무관 재계산
  payrollForm.value.c_save = calcReferenceNontaxSavings();

  const wageTotal = Number(payrollForm.value.wageTotal) || 0;
  const lTotal = Number(payrollForm.value.l_total) || 0;
  const cTotal = Number(payrollForm.value.c_total) || 0;
  const sugaMoneyTotal = isCaregiverEmployee(selectedEmployee.value)
    ? claimSugaMoneyTotal.value
    : 0;

  let nonTax = Number(payrollForm.value.nonTax) || 0;
  if (!isDirectorPayrollView.value) {
    const nontaxForm = { ...payrollForm.value };
    if (isCaregiverEmployee(selectedEmployee.value)) {
      nontaxForm.lunch = resolveAdminCaregiverAutoLunchForNontax();
    }
    nonTax = calcHomeWageTotalNontax(
      nontaxForm,
      resolveProductionNontaxForDisplay(),
      yearMonth.value,
      'admin'
    );
    payrollForm.value.nonTax = nonTax;
  }

  // 요보·사복 모두 화면 지급금액과 동일 (사복: wageTotal+stale nonTax → 목록 738240 등 방지)
  const grossPay = displayGrossPay.value;
  payrollForm.value.grossPay = grossPay;
  payrollForm.value.net = Math.max(0, grossPay - lTotal);
  payrollForm.value.profit = isCaregiverEmployee(selectedEmployee.value)
    ? calcHomeWageCenterProfit(sugaMoneyTotal, grossPay, cTotal)
    : 0;
};

const syncLaborDeductTotal = () => {
  payrollForm.value.l_total =
    (Number(payrollForm.value.l_yeon) || 0) +
    (Number(payrollForm.value.l_gun) || 0) +
    (Number(payrollForm.value.l_jang) || 0) +
    (Number(payrollForm.value.l_go) || 0) +
    (Number(payrollForm.value.l_so) || 0) +
    (Number(payrollForm.value.l_ji) || 0) +
    (Number(payrollForm.value.l_jiwon) || 0) +
    (Number(payrollForm.value.l_bonin) || 0) +
    (Number(payrollForm.value.l_etc) || 0);
  syncReferenceValues();
};

const syncCorpDeductTotal = () => {
  payrollForm.value.c_total =
    (Number(payrollForm.value.c_yeon) || 0) +
    (Number(payrollForm.value.c_gun) || 0) +
    (Number(payrollForm.value.c_jang) || 0) +
    (Number(payrollForm.value.c_go) || 0) +
    (Number(payrollForm.value.c_sil) || 0) +
    (Number(payrollForm.value.c_san) || 0) +
    (Number(payrollForm.value.c_out) || 0);
  syncReferenceValues();
};

/** wageTotal, nonTax, 수당·청구 수가금액·산재요율 변경 시 참고값 재계산 */
watch(
  () => [
    payrollForm.value.wageTotal,
    payrollForm.value.nonTax,
    payrollForm.value.lunch,
    payrollForm.value.lunchDirector,
    payrollForm.value.laborDay,
    payrollForm.value.overtime,
    payrollForm.value.night,
    payrollForm.value.holiday,
    payrollForm.value.senior,
    payrollForm.value.sunim,
    payrollForm.value.bosu,
    payrollForm.value.bonus,
    payrollForm.value.welfare,
    payrollForm.value.other,
    payrollForm.value.car,
    payrollForm.value.child,
    productionNontaxActive.value,
    claimSugaMoneyTotal.value,
    sanRate.value,
    isRepresentative.value,
  ],
  () => {
    syncReferenceValues();
  }
);

/** 근로자공제: 소득세·지방세 계산 (4대보험은 별도 카드에서 계산) */
const runAutoFillLaborDeduct = (silent = false, preservePayrollLock = false): boolean => {
  if (!preservePayrollLock) {
    unlockPayrollLedgerForEdit();
  }
  const ctx = resolveInsuranceCalcContext(silent);
  if (!ctx) return false;

  const incomeTax = calculateIncomeTax(ctx.taxable, incomeTaxTable.value);
  const emp = calcEmployeeDeductions(ctx.taxable, ctx.rates, incomeTax);
  const resolveLaborFixed = (key: HomeWageDeductionFixFieldKey, calculated: number) =>
    resolveDeductionAmount(key, calculated);

  payrollForm.value.l_so = resolveLaborFixed('l_so', emp.so);
  payrollForm.value.l_ji = resolveLaborFixed('l_ji', emp.ji);
  syncSupportDeduction(true);
  syncLaborDeductTotal();
  return true;
};

/** 기관공제(4대보험 외): 실업급여·산재(리모컨 산재요율)·퇴직금 */
const runAutoFillCorpDeduct = (silent = false, preservePayrollLock = false): boolean => {
  if (!preservePayrollLock) {
    unlockPayrollLedgerForEdit();
  }
  const ctx = resolveInsuranceCalcContext(silent);
  if (!ctx) return false;

  const corp = calcEmployerDeductions(ctx.taxable, ctx.rates);
  const resolveCorpFixed = (key: HomeWageDeductionFixFieldKey, calculated: number) =>
    resolveDeductionAmount(key, calculated);

  payrollForm.value.c_sil = resolveCorpFixed('c_sil', corp.sil);

  if (isRepresentative.value) {
    payrollForm.value.c_san = resolveCorpFixed('c_san', 0);
    applyRetirementOnCorpCalc();
    applyRepresentativeCorpRules();
    syncCorpDeductTotal();
    return true;
  }

  payrollForm.value.c_san = resolveCorpFixed('c_san', corp.san);
  applyRetirementOnCorpCalc();
  applyRepresentativeCorpRules();
  syncCorpDeductTotal();
  return true;
};

/** 비과세 수당 합계 동기화 */
const runAutoFillNonTax = (silent = false): boolean => {
  unlockPayrollLedgerForEdit();
  syncNonTaxTotal();
  syncReferenceValues();
  return true;
};

/** 비과세 설정 변경 시 — 식대·생산직비과세·실비변상 반영 */
const onNontaxSettingChange = () => {
  markDirty();
  if (isCaregiverEmployee(selectedEmployee.value)) {
    resyncPremiumSplitForStorage();
  }
  syncAfterPremiumSplitChange();
  if (isCaregiverEmployee(selectedEmployee.value)) {
    schedulePayrollAutoCalc('full', true, true);
  } else {
    schedulePayrollAutoCalc('downstream', true, true);
  }
};

const onProductionNontaxChange = (event: Event) => {
  const el = event.target as HTMLInputElement;
  if (el.checked && !productionNontaxValidation.value.canApply) {
    const msg =
      productionNontaxValidation.value.positionMessage ||
      productionNontaxValidation.value.usualMessage ||
      '생산직 비과세 적용 조건(직종·통상임금)을 충족하지 않습니다.';
    alert(msg);
    nontaxSettings.value.productionNontax = false;
    el.checked = false;
    return;
  }
  onNontaxSettingChange();
};

/** 4대보험·근로자공제·기관공제 연쇄 자동 계산 */
const runDownstreamPayrollAutoCalc = (silent = true) => {
  if (shouldSkipAdminLiveCalc()) return;
  if (isWageReadOnly.value || !selectedEmployee.value || payrollDbLock.value || payrollHydrating.value) return;
  runInsuranceDeductionAutoCalc(silent, false);
};

/** 기본급(시급 연동) + 4대보험·공제 전체 자동 계산 */
const runFullPayrollAutoCalc = (
  silent = true,
  forcedRun = false,
  allowDuringHydrate = false
) => {
  if (shouldSkipAdminLiveCalc()) return;
  if (isWageReadOnly.value || !selectedEmployee.value) return;
  if (!allowDuringHydrate && payrollHydrating.value) return;
  // ★ forcedRun이면 lock 상태와 무관하게 실행 (수급자별 시급 입력 시)
  if (!forcedRun && payrollDbLock.value) return;
  unlockPayrollLedgerForEdit();
  runAutoFillPayCalc(silent);
  if (forcedRun) {
    runInsuranceDeductionAutoCalc(silent, false);
  } else {
    runDownstreamPayrollAutoCalc(silent);
  }
};

let payrollAutoCalcTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPayrollAutoCalcMode: 'full' | 'downstream' = 'downstream';

/** 입력 중 debounce · blur 시 immediate — 계산 버튼 없이 연쇄 반영 */
const schedulePayrollAutoCalc = (
  mode: 'full' | 'downstream' = 'downstream',
  immediate = false,
  force = false
) => {
  if (isWageReadOnly.value || !selectedEmployee.value || !autoCalcMode.value || payrollHydrating.value) return;
  if (shouldSkipAdminLiveCalc()) return;
  if (!force && payrollDbLock.value) return;

  if (mode === 'full') pendingPayrollAutoCalcMode = 'full';

  if (payrollAutoCalcTimer) {
    clearTimeout(payrollAutoCalcTimer);
    payrollAutoCalcTimer = null;
  }

  const execute = () => {
    payrollAutoCalcTimer = null;
    const runMode = pendingPayrollAutoCalcMode;
    pendingPayrollAutoCalcMode = 'downstream';
    if (runMode === 'full') runFullPayrollAutoCalc(true, force);
    else if (force && isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value)) {
      syncDirectorInsuranceFromAdminShadow(false);
    } else if (force) runInsuranceDeductionAutoCalc(true, false);
    else if (isDirectorPayrollView.value && isCaregiverEmployee(selectedEmployee.value)) {
      syncDirectorInsuranceFromAdminShadow(false);
    } else runDownstreamPayrollAutoCalc(true);
  };

  if (immediate) {
    execute();
  } else {
    payrollAutoCalcTimer = setTimeout(execute, 300);
  }
};

const clearPendingPayrollAutoCalc = () => {
  if (payrollAutoCalcTimer) {
    clearTimeout(payrollAutoCalcTimer);
    payrollAutoCalcTimer = null;
  }
  pendingPayrollAutoCalcMode = 'downstream';
};

let payCalcStepTimer: ReturnType<typeof setInterval> | null = null;
let payCalcStepDelayTimer: ReturnType<typeof setTimeout> | null = null;

const clearPayCalcStepTimers = () => {
  if (payCalcStepTimer) {
    clearInterval(payCalcStepTimer);
    payCalcStepTimer = null;
  }
  if (payCalcStepDelayTimer) {
    clearTimeout(payCalcStepDelayTimer);
    payCalcStepDelayTimer = null;
  }
};

const stepPayCalcHourly = (delta: number) => {
  const next = caregiverHourlyWage.value + delta;
  setCaregiverHourlyWage(next);
  caregiverHourlyWageDisplay.value =
    caregiverHourlyWage.value > 0 ? formatWageNumber(caregiverHourlyWage.value) : '';
  schedulePayrollAutoCalc('full');
};

const onPayCalcStepperDown = (delta: number) => {
  stepPayCalcHourly(delta);
  clearPayCalcStepTimers();
  payCalcStepDelayTimer = setTimeout(() => {
    payCalcStepTimer = setInterval(() => stepPayCalcHourly(delta), 60);
  }, 350);
};

const onPayCalcStepperUp = () => {
  clearPayCalcStepTimers();
};

const onPayCalcHourlyKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    stepPayCalcHourly(1);
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    stepPayCalcHourly(-1);
  }
};

const applyOptimalHourlyWage = () => {
  const opt = optimalHourlyWage.value;
  if (opt == null || opt <= 0) return;
  setCaregiverHourlyWage(opt);
  caregiverHourlyWageDisplay.value = formatWageNumber(opt);
  schedulePayrollAutoCalc('full', true);
};

const onStaffHourlyWageInput = (event: Event) => {
  staffHourlyWageDisplay.value = (event.target as HTMLInputElement).value;
  schedulePayrollAutoCalc('full');
};

const onStaffHourlyWageBlur = () => {
  const parsed = parseWageNumber(staffHourlyWageDisplay.value);
  staffForm.value.hourlyWage = parsed;
  staffHourlyWageDisplay.value = parsed > 0 ? formatWageNumber(parsed) : '';
  schedulePayrollAutoCalc('full', true);
};

const onStaffWorkHoursInput = (event: Event) => {
  staffWorkHoursDisplay.value = sanitizeHoursInput((event.target as HTMLInputElement).value);
  schedulePayrollAutoCalc('full');
};

const onStaffWorkHoursBlur = () => {
  const parsed = parseHours(staffWorkHoursDisplay.value.replace(/시간/g, ''));
  staffForm.value.workHours = parsed > 0 ? parsed : 0;
  staffWorkHoursDisplay.value =
    staffForm.value.workHours > 0 ? formatHoursDisplay(staffForm.value.workHours) : '';
  if (staffForm.value.workHours > 0) {
    schedulePayrollAutoCalc('full', true);
  }
};

onUnmounted(() => {
  clearPayCalcStepTimers();
  if (payrollAutoCalcTimer) {
    clearTimeout(payrollAutoCalcTimer);
    payrollAutoCalcTimer = null;
  }
});

watch(
  caregiverHourlyWage,
  (wage) => {
    caregiverHourlyWageDisplay.value = wage > 0 ? formatWageNumber(wage) : '';
  },
  { immediate: true }
);

const onCaregiverHourlyWageInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  caregiverHourlyWageDisplay.value = val;
  schedulePayrollAutoCalc('full');
};

const onCaregiverHourlyWageBlur = () => {
  const parsed = parseWageNumber(caregiverHourlyWageDisplay.value);
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  setCaregiverHourlyWage(parsed);
  caregiverHourlyWageDisplay.value =
    caregiverHourlyWage.value > 0 ? formatWageNumber(caregiverHourlyWage.value) : '';
  // ★ 기본급 카드 시급 변경 시 강제 재계산 (lock 상태와 무관)
  runFullPayrollAutoCalc(true, true);
};

const onHourlyWageInput = (groupKey: string, event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  hourlyWageDisplay.value = { ...hourlyWageDisplay.value, [groupKey]: val };
  schedulePayrollAutoCalc('full');
};

const onHourlyWageBlur = (groupKey: string) => {
  const raw = hourlyWageDisplay.value[groupKey] ?? '';
  const parsed = parseWageNumber(raw);
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  updateRowHourly(groupKey, parsed);
  hourlyWageDisplay.value = {
    ...hourlyWageDisplay.value,
    [groupKey]: parsed > 0 ? formatWageNumber(parsed) : '',
  };
  // ★ 수급자별 시급 변경 시 강제 재계산 (lock 상태와 무관)
  runFullPayrollAutoCalc(true, true);
};

/** 일괄시급 모달 */
const onBulkHourlyInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  bulkHourlyDisplay.value = val;
  bulkHourlyValue.value = parseWageNumber(val);
};

const closeBulkHourlyModal = () => {
  showBulkHourlyModal.value = false;
  bulkHourlyDisplay.value = '';
  bulkHourlyValue.value = 0;
};

const applyBulkHourlyWage = () => {
  if (bulkHourlyValue.value <= 0) return;

  const wage = bulkHourlyValue.value;
  const formattedWage = formatWageNumber(wage);
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적

  // 중증수급자가산 제외한 모든 수급자에게 적용
  claimSummaryRows.value.forEach((row) => {
    if (row.isSevereRecipientSurcharge) return;
    updateRowHourly(row.groupKey, wage);
    hourlyWageDisplay.value = {
      ...hourlyWageDisplay.value,
      [row.groupKey]: formattedWage,
    };
  });

  closeBulkHourlyModal();
  // ★ 일괄 시급 적용 시 강제 재계산
  runFullPayrollAutoCalc(true, true);
};

/** 펼침 뷰 개인별 일괄시급 모달 */
const openEmpBulkHourlyModal = (empKey: string, empName: string) => {
  empBulkHourlyKey.value = empKey;
  empBulkHourlyName.value = empName;
  empBulkHourlyDisplay.value = '';
  empBulkHourlyValue.value = 0;
  showEmpBulkHourlyModal.value = true;
};

const onEmpBulkHourlyInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  empBulkHourlyDisplay.value = val;
  empBulkHourlyValue.value = parseWageNumber(val);
};

const closeEmpBulkHourlyModal = () => {
  showEmpBulkHourlyModal.value = false;
  empBulkHourlyKey.value = '';
  empBulkHourlyName.value = '';
  empBulkHourlyDisplay.value = '';
  empBulkHourlyValue.value = 0;
};

const applyEmpBulkHourlyWage = () => {
  if (empBulkHourlyValue.value <= 0) return;

  const empKey = empBulkHourlyKey.value;
  const wage = empBulkHourlyValue.value;
  const formattedWage = formatWageNumber(wage);

  // 해당 사원의 모든 수급자(중증수급자가산 제외)에게 적용
  const summaryRows = allEmpSummaryRows.value.get(empKey);
  if (!summaryRows?.length) {
    closeEmpBulkHourlyModal();
    return;
  }

  if (!expandedHourlyWage.value.has(empKey)) {
    expandedHourlyWage.value.set(empKey, new Map());
    expandedHourlyDisplay.value.set(empKey, new Map());
  }

  const hourlyMap = expandedHourlyWage.value.get(empKey)!;
  const displayMap = expandedHourlyDisplay.value.get(empKey)!;

  for (const row of summaryRows) {
    if (row.isSevereRecipientSurcharge) continue;
    hourlyMap.set(row.groupKey, wage);
    displayMap.set(row.groupKey, formattedWage);
  }

  // 집계 재계산
  recalcExpandedSummary(empKey);

  closeEmpBulkHourlyModal();
};

/** 전체 일괄시급 모달: 일반요양/가족요양 건수 및 최적시급 계산 */
const globalBulkGeneralCount = computed(() => {
  let count = 0;
  allEmpSummaryRows.value.forEach((rows) => {
    for (const row of rows) {
      if (row.isSevereRecipientSurcharge) continue;
      if (!isFamilyCare(row.feeName || '')) count++;
    }
  });
  return count;
});

const globalBulkFamilyCount = computed(() => {
  let count = 0;
  allEmpSummaryRows.value.forEach((rows) => {
    for (const row of rows) {
      if (row.isSevereRecipientSurcharge) continue;
      if (isFamilyCare(row.feeName || '')) count++;
    }
  });
  return count;
});

const onGlobalBulkGeneralInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  globalBulkGeneralDisplay.value = val;
  globalBulkGeneralValue.value = parseWageNumber(val);
};

const onGlobalBulkFamilyInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  globalBulkFamilyDisplay.value = val;
  globalBulkFamilyValue.value = parseWageNumber(val);
};

const closeGlobalBulkHourlyModal = () => {
  showGlobalBulkHourlyModal.value = false;
  globalBulkGeneralDisplay.value = '';
  globalBulkGeneralValue.value = 0;
  globalBulkFamilyDisplay.value = '';
  globalBulkFamilyValue.value = 0;
};

const applyGlobalBulkHourlyWage = () => {
  const generalWage = globalBulkGeneralValue.value;
  const familyWage = globalBulkFamilyValue.value;

  if (generalWage <= 0 && familyWage <= 0) return;

  const generalFormatted = generalWage > 0 ? formatWageNumber(generalWage) : '';
  const familyFormatted = familyWage > 0 ? formatWageNumber(familyWage) : '';

  // 모든 사원의 수급자에게 적용
  allEmpSummaryRows.value.forEach((rows, empKey) => {
    if (!expandedHourlyWage.value.has(empKey)) {
      expandedHourlyWage.value.set(empKey, new Map());
      expandedHourlyDisplay.value.set(empKey, new Map());
    }

    const hourlyMap = expandedHourlyWage.value.get(empKey)!;
    const displayMap = expandedHourlyDisplay.value.get(empKey)!;

    for (const row of rows) {
      if (row.isSevereRecipientSurcharge) continue;

      const isFamily = isFamilyCare(row.feeName || '');

      if (isFamily && familyWage > 0) {
        hourlyMap.set(row.groupKey, familyWage);
        displayMap.set(row.groupKey, familyFormatted);
      } else if (!isFamily && generalWage > 0) {
        hourlyMap.set(row.groupKey, generalWage);
        displayMap.set(row.groupKey, generalFormatted);
      }
    }

    // 집계 재계산
    recalcExpandedSummary(empKey);
  });

  closeGlobalBulkHourlyModal();
};

/** 수급자별 시급/수가시간 — 상단 저장 버튼 사용 */
const saveRecipientSettingsData = async () => {
  await saveCurrentEmployeeData();
};

const onSevereFeeInput = (groupKey: string, event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  severeFeeDisplay.value = { ...severeFeeDisplay.value, [groupKey]: val };
};

const onSevereFeeBlur = (groupKey: string, defaultFee: number) => {
  const raw = severeFeeDisplay.value[groupKey] ?? '';
  let parsed = parseWageNumber(raw);
  if (parsed <= 0 && defaultFee > 0) parsed = defaultFee;
  unlockPayrollLedgerForEdit();
  markDirty(); // ★ 변경사항 추적
  updateRowSevereFee(groupKey, parsed);
  severeFeeDisplay.value = {
    ...severeFeeDisplay.value,
    [groupKey]: parsed > 0 ? formatWageNumber(parsed) : '',
  };
  // ★ 중증수급자가산 변경 시 강제 재계산 (lock 상태와 무관)
  runFullPayrollAutoCalc(true, true);
};

const onSugaHoursInput = (groupKey: string, event: Event) => {
  const val = sanitizeHoursInput((event.target as HTMLInputElement).value);
  sugaHoursDisplay.value = { ...sugaHoursDisplay.value, [groupKey]: val };
};

const onSugaHoursBlur = (groupKey: string, defaultHours: number) => {
  const raw = sugaHoursDisplay.value[groupKey] ?? '';
  let parsed = parseHours(raw.replace(/시간/g, ''));
  if (parsed <= 0 && defaultHours > 0) parsed = parseHours(defaultHours);
  updateRowSugaHours(groupKey, parsed);
  sugaHoursDisplay.value = {
    ...sugaHoursDisplay.value,
    [groupKey]: parsed > 0 ? formatSugaHours(parsed) : '',
  };
};

const searchName = ref('');
const filterJobType = ref('');
const searchFiltersExpanded = ref(false);
/** 방문요양 / 방문목욕 — 시설에 B1·B2 청구가 모두 있을 때만 표시 */
const filterClaimBiz = ref('');
type EmployeeListSortKey = 'name' | 'job' | 'monthlyPay' | 'corpInsurance' | 'retirement' | 'default';
const sortKey = ref<EmployeeListSortKey>('default');
const sortOrder = ref<'asc' | 'desc'>('asc');

interface ListWageSummary {
  monthlyPay: number;
  corpInsurance: number;
  retirement: number;
  isRepresentative: boolean;
  /** 관리자 시점 ↔ 센터장 시점 연·야·휴 표시 불일치 (최고관리자 감사용) */
  viewMismatch: boolean;
}

const listWageByEmpKey = ref<Map<string, ListWageSummary>>(new Map());
/** 목록 시점 불일치 재판정용 — API 원본 급여대장 행 */
const listWageRowByEmpKey = ref<Map<string, Record<string, unknown>>>(new Map());

const buildListWageMap = (rows: any[]) => {
  const map = new Map<string, ListWageSummary>();
  const rowMap = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const key = rowEmpKey(row);
    if (!key.split('|')[0]) continue;
    rowMap.set(key, row);
    const job = String(row.job || row.cwhd_job || '');
    map.set(key, {
      // 시설장·사복·운전원: 동일 스태프 정책 지급금액 (풀+센터장중식+차량…)
      monthlyPay: hasHomeWageStoredCtParts(row)
        ? sumHomeWageStoredCtPayParts(
            readHomeWageStoredCtParts(
              row,
              String(row.yearMonth ?? row.cwhdir_ym ?? row.cwhd_ym ?? '')
            )
          )
        : isHomeWageCaregiverJob(job)
          ? getHomeWageLedgerGrossPay(row)
          : getHomeWageStaffLedgerGrossPay(row),
      corpInsurance: getHomeWageLedgerEmployerInsurance(row),
      retirement: getHomeWageLedgerRetirement(row),
      isRepresentative: row.isRepresentative === 'Y' || row.cwhd_is_representative === 'Y',
      viewMismatch: false,
    });
  }
  listWageRowByEmpKey.value = rowMap;
  return map;
};

/** 청구·수급자 집계 로드 후 — 센터장 forward 재계산 기준으로 viewMismatch 갱신 */
const refreshListWageViewMismatchFlags = () => {
  if (!isSuperAdmin.value) return;
  if (listWageByEmpKey.value.size === 0) return;

  const next = new Map(listWageByEmpKey.value);

  for (const emp of sortedEmployees.value) {
    if (!isCaregiverEmployee(emp)) continue;

    const key = getEmpKey(emp);
    const wageRow = findListWageRowForEmp(emp);
    if (!wageRow) continue;

    const mapKey = rowEmpKey(wageRow as Parameters<typeof rowEmpKey>[0]);
    const summary = next.get(mapKey) ?? next.get(key);
    if (!summary) continue;

    const context = buildViewMismatchContextForEmp(emp, key);
    const updated = {
      ...summary,
      viewMismatch: hasHomeWagePayAmountTripleMismatch(wageRow, context),
    };

    next.set(mapKey, updated);
    if (key !== mapKey) {
      next.set(key, updated);
    }
  }

  listWageByEmpKey.value = next;
};

const loadListWageSummaries = async () => {
  if (!memberCode.value) {
    listWageByEmpKey.value = new Map();
    listWageRowByEmpKey.value = new Map();
    return;
  }

  /** 사업구분 미선택 시 member m_biz_gubun·방문계열 fallback (빈 문자열 API 조회 방지) */
  const resolveListWageBizGubuns = (): string[] => {
    const selected = selectedBizGubun.value?.trim();
    if (selected) return [selected];

    const raw = memberData?.value?.m_biz_gubun;
    if (raw) {
      const list = String(raw).split(',').map((s: string) => s.trim()).filter(Boolean);
      const homeCare = list.filter((b: string) => b.startsWith('방문'));
      if (homeCare.length > 0) return homeCare;
      if (list.length > 0) return [list[0]];
    }
    return ['방문요양'];
  };

  try {
    const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    const bizList = resolveListWageBizGubuns();
    const directorResponses = await Promise.all(
      bizList.map((biz) => fetchDirectorWageRows(memberCode.value, ym, biz))
    );
    const allRows: any[] = directorResponses.flat();

    if (allRows.length > 0) {
      listWageByEmpKey.value = buildListWageMap(allRows);
      refreshListWageViewMismatchFlags();
    } else {
      listWageByEmpKey.value = new Map();
      listWageRowByEmpKey.value = new Map();
    }
  } catch (error) {
    console.error('목록 급여 요약 조회 오류:', error);
    listWageByEmpKey.value = new Map();
    listWageRowByEmpKey.value = new Map();
  }
};

/** 사원 ↔ 급여대장 행 매칭 (생년월일·키 형식 차이 보정) */
const findListWageRowForEmp = (emp: any): Record<string, unknown> | undefined => {
  const key = getEmpKey(emp);
  const direct = listWageRowByEmpKey.value.get(key);
  if (direct) return direct;

  const name = normalizeEmpName(getEmpName(emp));
  const birth = getEmpBirth(emp);
  if (name) {
    const byNameBirth = buildEmpMatchKey(name, birth);
    const byBirth = listWageRowByEmpKey.value.get(byNameBirth);
    if (byBirth) return byBirth;

    const nameOnlyKey = buildEmpMatchKey(name, null);
    const byNameOnly = listWageRowByEmpKey.value.get(nameOnlyKey);
    if (byNameOnly) return byNameOnly;

    for (const row of listWageRowByEmpKey.value.values()) {
      const rowName = normalizeEmpName(String(row.name ?? row.cwhd_name ?? ''));
      if (rowName !== name) continue;
      const rowBirth = normalizeEmpBirth(String(row.birth ?? row.cwhd_birth ?? ''));
      if (!birth || !rowBirth || birth === rowBirth) return row;
    }
  }
  return undefined;
};

const buildViewMismatchContextForEmp = (emp: any, empKey: string) => {
  const minWage = minHourlyWage.value;
  const claimRows = allEmpClaimData.value.get(empKey) || [];
  const summaryRows =
    allEmpSummaryRows.value.get(empKey)?.length
      ? allEmpSummaryRows.value.get(empKey)!
      : resolveRecipientSummaryRowsForList(emp);
  if (minWage <= 0 || !summaryRows.length || !claimRows.length) return undefined;
  return {
    minHourlyWage: minWage,
    summaryRows,
    claimRows,
    yearMonth: yearMonth.value,
    joinDate: getEmpJoinDate(emp),
    workDayCount: calcWorkDaysFromClaimRows(claimRows),
    lunchWorkDayCount: calcHomeWageLunchWorkDayCountFromClaims(claimRows),
  };
};

/** 목록 월급여 — 캐시 Map 숫자 재사용 금지(HMR·이중차감 복원). 상세와 동일 함수로 즉시 계산 */
const computeListWageSummaryFromRow = (
  row: Record<string, unknown>,
  cached?: ListWageSummary
): ListWageSummary => {
  const job =
    String(row.job || row.cwhd_job || '')
    || String(cached && (cached as any).job || '');
  const ym = String(row.yearMonth ?? row.cwhd_ym ?? yearMonth.value ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);
  const rowWithYm = { ...row, yearMonth: ym || row.yearMonth, job: job || row.job };
  const monthlyPay = hasHomeWageStoredCtParts(rowWithYm)
    ? sumHomeWageStoredCtPayParts(readHomeWageStoredCtParts(rowWithYm, ym))
    : isHomeWageCaregiverJob(job)
      ? getHomeWageLedgerGrossPay(rowWithYm)
      : Math.max(
          getHomeWageStaffLedgerGrossPay(rowWithYm),
          calcHomeWageAdminDisplayGrossPay(rowWithYm, false, ym),
          calcHomeWageClientReportGrossPay(
            buildHomeWageClientReportRowFromDb(rowWithYm),
            ym
          )
        );
  return {
    monthlyPay,
    corpInsurance: getHomeWageLedgerEmployerInsurance(rowWithYm),
    retirement: getHomeWageLedgerRetirement(rowWithYm),
    isRepresentative:
      row.isRepresentative === 'Y'
      || row.isRepresentative === true
      || row.cwhd_is_representative === 'Y'
      || cached?.isRepresentative === true,
    viewMismatch: cached?.viewMismatch === true,
  };
};

const getListWageSummary = (emp: any): ListWageSummary | undefined => {
  const map = listWageByEmpKey.value;
  const wageRow = findListWageRowForEmp(emp);
  const draft = employeeWageDrafts.value.get(getEmpKey(emp));

  const cached = (() => {
    if (wageRow) {
      const mapKey = rowEmpKey(wageRow as Parameters<typeof rowEmpKey>[0]);
      const byWageRow = map.get(mapKey);
      if (byWageRow) return byWageRow;
    }
    const primaryKey = getEmpKey(emp);
    const found = map.get(primaryKey);
    if (found) return found;
    const name = normalizeEmpName(emp.clci_name || emp.ce_name);
    if (!name) return undefined;
    return map.get(buildEmpMatchKey(name, null));
  })();

  if (draft?.wagePayload || wageRow) {
    const mergedPayload: Record<string, unknown> = {
      ...(wageRow || {}),
      ...(draft?.wagePayload || {}),
      hour:
        draft?.wagePayload?.hour
        || draft?.wagePayload?.cwhd_hour
        || (wageRow as any)?.hour
        || (wageRow as any)?.cwhd_hour
        || '',
      payCalcHourly:
        Number(draft?.wagePayload?.payCalcHourly)
        || Number(draft?.wagePayload?.cwhd_pay_calc_hourly)
        || Number((wageRow as any)?.payCalcHourly)
        || Number((wageRow as any)?.cwhd_pay_calc_hourly)
        || 0,
      job:
        String(draft?.wagePayload?.job || draft?.wagePayload?.cwhd_job || '')
        || String((wageRow as any)?.job || (wageRow as any)?.cwhd_job || '')
        || getEmpJob(emp)
        || '',
      yearMonth:
        String(
          draft?.wagePayload?.yearMonth
          || (wageRow as any)?.yearMonth
          || yearMonth.value
          || ''
        ),
    };

    const live = computeListWageSummaryFromRow(mergedPayload, cached);
    // draft가 깨져도 API 행 기준이 더 크면(791000) 그쪽 사용
    if (wageRow && draft?.wagePayload) {
      const fromDb = computeListWageSummaryFromRow(
        wageRow as Record<string, unknown>,
        cached
      );
      return {
        ...live,
        monthlyPay: Math.max(live.monthlyPay, fromDb.monthlyPay),
        corpInsurance: Math.max(live.corpInsurance, fromDb.corpInsurance),
        retirement: Math.max(live.retirement, fromDb.retirement),
      };
    }
    return live;
  }

  return cached;
};

const getListWageSortValue = (emp: any, field: 'monthlyPay' | 'corpInsurance' | 'retirement') =>
  getListWageSummary(emp)?.[field] ?? -1;

/** 대표자 여부 확인 */
const isRepresentativeEmployee = (emp: any): boolean => {
  return getListWageSummary(emp)?.isRepresentative === true;
};

const formatListWageAmount = (
  emp: any,
  field: 'monthlyPay' | 'corpInsurance' | 'retirement'
) => {
  const summary = getListWageSummary(emp);
  if (!summary) return '-';
  const val = summary[field];
  if (val <= 0) return '-';
  return `${formatWageNumber(val)}원`;
};

const getClaimBizChipClass = (label: string) => ({
  'claim-biz-chip--regular': label === '일반',
  'claim-biz-chip--family': label === '가족',
  'claim-biz-chip--bath': label === '목욕',
});

/** 수급자별 시급 미입력 여부 (중증가산 제외). empKey 있으면 펼침 뷰 맵도 확인 */
const isRecipientHourlyEmpty = (
  row: { groupKey: string; isSevereRecipientSurcharge?: boolean; hourlyWage?: number },
  empKey?: string
): boolean => {
  if (row.isSevereRecipientSurcharge) return false;

  let stored = Number(row.hourlyWage) || 0;
  let display = hourlyWageDisplay.value[row.groupKey] ?? '';

  if (empKey) {
    const mapVal = expandedHourlyWage.value.get(empKey)?.get(row.groupKey);
    if (mapVal != null && mapVal > 0) stored = Math.max(stored, mapVal);
    const expDisplay = expandedHourlyDisplay.value.get(empKey)?.get(row.groupKey);
    if (expDisplay != null) display = expDisplay;
  }

  return Math.max(stored, parseWageNumber(display)) <= 0;
};

type RecipientHourlyStatus = 'complete' | 'incomplete' | null;

/** 목록 수가시급 컬럼 — 수급자별 집계 행 조회 */
const resolveRecipientSummaryRowsForList = (emp: any) => {
  const empKey = getEmpKey(emp);

  const cached = allEmpSummaryRows.value.get(empKey);
  if (cached?.length) return cached;

  if (
    selectedEmployee.value
    && getEmpKey(selectedEmployee.value) === empKey
    && claimSummaryRows.value.length > 0
  ) {
    return claimSummaryRows.value;
  }

  const claims = allEmpClaimData.value.get(empKey);
  if (!claims?.length) return [];

  const hourlyObj: Record<string, number> = {};
  const severeObj: Record<string, number> = {};
  expandedHourlyWage.value.get(empKey)?.forEach((v, k) => {
    hourlyObj[k] = v;
  });
  expandedSevereFee.value.get(empKey)?.forEach((v, k) => {
    severeObj[k] = v;
  });

  const draft = employeeWageDrafts.value.get(empKey);
  if (draft?.recipient?.hourlyByGroup) {
    Object.assign(hourlyObj, draft.recipient.hourlyByGroup);
  }
  if (draft?.recipient?.severeFeeByGroup) {
    Object.assign(severeObj, draft.recipient.severeFeeByGroup);
  }

  return aggregateRecipientFeeSummary(claims, hourlyObj, severeObj);
};

/** 사원 목록 빨간 이름 — 센터장·관리자 시점 기본급·수당·지급금액 불일치 */
const viewMismatchEmpKeySet = computed(() => {
  const set = new Set<string>();

  for (const emp of sortedEmployees.value) {
    if (!isCaregiverEmployee(emp)) continue;

    const empKey = getEmpKey(emp);
    const context = buildViewMismatchContextForEmp(emp, empKey);
    if (!context) continue;

    const draft = employeeWageDrafts.value.get(empKey);
    const wageRow = findListWageRowForEmp(emp);
    const rowForCheck = (draft?.wagePayload ?? wageRow) as Record<string, unknown> | undefined;
    if (!rowForCheck) continue;

    if (hasHomeWagePayAmountTripleMismatch(rowForCheck, context)) {
      set.add(empKey);
    }
  }
  return set;
});

const isViewMismatch = (emp: any): boolean =>
  isSuperAdmin.value && viewMismatchEmpKeySet.value.has(getEmpKey(emp));

/** 슈퍼바이저(최고관리자) 전용 — 선택 사원 3-way 금액 불일치 리포트 */
const selectedEmployeePayMismatchReport = computed((): HomeWagePayAmountMismatchReport | null => {
  if (!isSuperAdmin.value) return null;
  if (!selectedEmployee.value || !isCaregiverEmployee(selectedEmployee.value)) return null;

  const empKey = getEmpKey(selectedEmployee.value);
  const context = buildViewMismatchContextForEmp(selectedEmployee.value, empKey);
  if (!context) return null;

  const draft = employeeWageDrafts.value.get(empKey);
  const wageRow = findListWageRowForEmp(selectedEmployee.value);
  const rowForCheck = (draft?.wagePayload ?? wageRow) as Record<string, unknown> | undefined;
  if (!rowForCheck) return null;

  return buildHomeWagePayAmountTripleMismatchReport(rowForCheck, context);
});

const resavingPayMismatch = ref(false);

/**
 * 슈퍼바이저 — 불일치 패널에서 관리자(청구 forward) 금액으로 DB 전체 재저장
 * ★ 관리자 시점 기본 저장은 부분 저장이라 기본급이 안 바뀜 → forceFullSave
 */
const resavePayMismatchFromAdmin = async () => {
  if (!isSuperAdmin.value || isWageReadOnly.value) return;
  const emp = selectedEmployee.value;
  const report = selectedEmployeePayMismatchReport.value;
  if (!emp || !isCaregiverEmployee(emp) || !report?.mismatch) return;

  const empName = getEmpName(emp);
  const empKey = getEmpKey(emp);
  const context = buildViewMismatchContextForEmp(emp, empKey);
  const wageRow = (employeeWageDrafts.value.get(empKey)?.wagePayload
    ?? findListWageRowForEmp(emp)) as Record<string, unknown> | undefined;
  if (!context || !wageRow) {
    alert('재저장에 필요한 청구·급여 데이터를 찾지 못했습니다.');
    return;
  }

  const directorGross = report.director.grossPay;
  const forwardTotals = calcHomeWageAdminViewPayTotalsFromContext(wageRow, context);
  const forwardGross = forwardTotals?.grossPay ?? 0;
  if (
    !confirm(
      `${empName}님 — 청구 기준 재저장\n\n`
      + `현재 DB 지급금액: ${formatWageNumber(directorGross)}원\n`
      + `청구 재산출 지급금액: ${formatWageNumber(forwardGross)}원\n\n`
      + `청구·수급자 집계로 재산출한 금액으로 DB를 덮어씁니다.\n계속하시겠습니까?`
    )
  ) {
    return;
  }

  const adminForm = buildHomeWageAdminForwardPayrollForm(wageRow, context);
  if (!adminForm) {
    alert('청구 재산출 금액을 만들 수 없습니다. 시급·청구내역을 확인해 주세요.');
    return;
  }

  resavingPayMismatch.value = true;
  try {
    unlockPayrollLedgerForEdit();

    const payKeys: (keyof typeof adminForm)[] = [
      'basic', 'weekend', 'annual', 'laborDay',
      'overtime', 'night', 'holiday',
      'overtimeNontax', 'nightNontax', 'holidayNontax',
      'overtimeHours', 'nightHours', 'holidayHours',
      'senior', 'sunim', 'bosu', 'severe', 'bonus',
      'lunch', 'lunchDirector', 'car', 'child', 'welfare', 'other',
    ];
    for (const key of payKeys) {
      (payrollForm.value as any)[key] = (adminForm as any)[key];
    }
    autoBaseLunchRef.value = Number(adminForm.lunch) || 0;
    payrollPremiumHoursDisplay.value = {
      overtimeHours: formatHomeWagePremiumHoursDisplay(adminForm.overtimeHours),
      nightHours: formatHomeWagePremiumHoursDisplay(adminForm.nightHours),
      holidayHours: formatHomeWagePremiumHoursDisplay(adminForm.holidayHours),
    };

    // 관리자 시점 미리보기 스냅샷이 있으면 기본급도 갱신 (이후 부분저장과 충돌 방지)
    if (payrollPreviewSnapshot.value) {
      const snapForm = payrollPreviewSnapshot.value.payrollForm;
      for (const key of payKeys) {
        (snapForm as any)[key] = (adminForm as any)[key];
      }
    }

    recalcWageTotal();
    syncNonTaxTotal();
    syncAccountingPayrollShadow();
    if (isDirectorPayrollView.value) {
      syncDirectorInsuranceFromAdminShadow(false);
    } else {
      verifyAdminBasedInsuranceBeforeSave();
    }
    syncReferenceValues();
    markDirty();

    const ok = await saveCurrentEmployeeData({
      skipConfirm: true,
      forceFullSave: true,
      skipSuccessAlert: true,
    });
    if (ok) {
      alert(
        `${empName}님 — 관리자 재계산 금액으로 재저장되었습니다.\n`
        + `지급금액 ${formatWageNumber(adminGross)}원`
      );
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`재저장 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('관리자 재저장 오류:', error);
  } finally {
    resavingPayMismatch.value = false;
  }
};

/** 요양보호사 — 수급자별 시급 입력 완료 여부 (중증가산 제외) */
const getRecipientHourlyCompleteStatus = (emp: any): RecipientHourlyStatus => {
  if (!isCaregiverEmployee(emp)) return null;

  const empKey = getEmpKey(emp);
  const rows = resolveRecipientSummaryRowsForList(emp);
  if (!rows.length) return null;

  const nonSevereRows = rows.filter((row) => !row.isSevereRecipientSurcharge);
  if (!nonSevereRows.length) return null;

  return nonSevereRows.every((row) => !isRecipientHourlyEmpty(row, empKey))
    ? 'complete'
    : 'incomplete';
};

const formatRecipientHourlyCompleteLabel = (emp: any): string => {
  if (loadingClaimSummaries.value && isCaregiverEmployee(emp)) return '확인 중';
  const status = getRecipientHourlyCompleteStatus(emp);
  if (status === 'complete') return '입력완료';
  if (status === 'incomplete') return '미완료';
  return '-';
};

const getRecipientHourlyCompleteStatusClass = (emp: any) => {
  const status = getRecipientHourlyCompleteStatus(emp);
  return {
    'recipient-hourly-status': true,
    'recipient-hourly-status--complete': status === 'complete',
    'recipient-hourly-status--incomplete': status === 'incomplete',
    'recipient-hourly-status--na': status === null,
  };
};

const getEmpClaimBizChips = (emp: any): string[] => {
  if (!isCaregiverEmployee(emp)) return [];
  if (Array.isArray(emp.claim_biz_chips) && emp.claim_biz_chips.length > 0) {
    return emp.claim_biz_chips;
  }
  const label = String(emp.claim_biz_label || '');
  if (!label || label === '-') return [];
  return label.split('·').filter(Boolean);
};

const empHasVisitCareChip = (emp: any) =>
  getEmpClaimBizChips(emp).some((chip) => chip === '일반' || chip === '가족');

const empHasBathChip = (emp: any) => getEmpClaimBizChips(emp).includes('목욕');

/** 당월 청구에 B1(방문요양)·B2(방문목욕) 수가코드가 모두 있으면 사업구분 필터 표시 */
const showClaimBizFilter = computed(() => {
  let hasVisitCare = false;
  let hasVisitBath = false;
  for (const emp of employees.value) {
    if (!isCaregiverEmployee(emp)) continue;
    if (empHasVisitCareChip(emp)) hasVisitCare = true;
    if (empHasBathChip(emp)) hasVisitBath = true;
  }
  return hasVisitCare && hasVisitBath;
});

const matchesClaimBizFilter = (emp: any) => {
  if (!filterClaimBiz.value) return true;
  if (!isCaregiverEmployee(emp)) return true;
  if (filterClaimBiz.value === '방문요양') {
    return empHasVisitCareChip(emp);
  }
  if (filterClaimBiz.value === '방문목욕') {
    return empHasBathChip(emp);
  }
  return true;
};

watch(showClaimBizFilter, (show) => {
  if (!show) filterClaimBiz.value = '';
});

const formatClaimDate = (val: string | null | undefined) => {
  if (!val) return '-';
  const s = String(val).substring(0, 10);
  const [y, m, d] = s.split('-');
  if (!d) return s;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
};

const formatRecipientWithCert = (name: string | null | undefined, cert: string | null | undefined) => {
  const n = String(name || '').trim();
  const c = String(cert || '').trim();
  if (n && c) return `${n}(${c})`;
  if (n) return n;
  if (c) return `(${c})`;
  return '-';
};

const formatFeeNameOnly = (feeName: string | null | undefined) => {
  const name = String(feeName || '').trim();
  return name || '-';
};

/** 계획 시작~종료 (예: 09:00~10:00) */
const formatPlanTimeRange = (start: string | null | undefined, end: string | null | undefined) => {
  if (!start && !end) return '-';
  const fmt = (t: string) => String(t).substring(0, 5);
  if (start && end) return `${fmt(start)}~${fmt(end)}`;
  return start ? fmt(start) : fmt(end!);
};

/** 계획시간 기준 서비스 소요 (예: 총1:00) */
const formatServiceDuration = (start: string | null | undefined, end: string | null | undefined) => {
  const duration = formatPlanDuration(start, end);
  return duration || '-';
};

const formatPlanDuration = (start: string | null | undefined, end: string | null | undefined) => {
  if (!start || !end) return '';
  const toMinutes = (t: string) => {
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  };
  let diff = toMinutes(end) - toMinutes(start);
  if (diff < 0) diff += 24 * 60;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `총${h}:${String(m).padStart(2, '0')}`;
};

const formatFee = (val: number | string | null | undefined) => {
  if (val == null || val === '') return '-';
  const n = Number(val);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('ko-KR');
};

/** 점심시간(12:00~13:00) 포함 근무 여부 판별 (12시 이상이면 식대 지급) — 중증수급자가산 제외 */
const isLunchIncludedRow = (row: HomeWageClaimRow): boolean => {
  if (isSevereRecipientSurcharge(row.fee_name, row.fee_code)) return false;
  const start = row.plan_start_time;
  const end = row.plan_end_time;
  if (!start || !end) return false;
  const startMatch = String(start).match(/^(\d{1,2}):(\d{2})/);
  const endMatch = String(end).match(/^(\d{1,2}):(\d{2})/);
  if (!startMatch || !endMatch) return false;
  const startMin = parseInt(startMatch[1], 10) * 60 + parseInt(startMatch[2], 10);
  const endMin = parseInt(endMatch[1], 10) * 60 + parseInt(endMatch[2], 10);
  const lunchStart = 12 * 60;
  const lunchEnd = 13 * 60;
  // 종료시간이 12:00 이상이면 식대 지급 (12시 초과가 아닌 12시 이상)
  return startMin < lunchEnd && endMin >= lunchStart;
};

const isSelectedEmployee = (emp: any) => {
  if (bulkEmployeeProcessing.value) return false;
  if (!selectedEmployee.value) return false;
  return getEmpKey(emp) === getEmpKey(selectedEmployee.value);
};

const showAllList = () => {
  // ★ 저장하지 않은 변경사항 확인
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  
  captureCurrentEmployeeDraftIfAny();
  resetEmployeeDetailFormState(null);
  clearCaregiverClaimData();
  mainView.value = 'list';
  selectedEmployee.value = null;
};

const selectedEmployeeIndex = computed(() => {
  if (!selectedEmployee.value) return -1;
  const key = getEmpKey(selectedEmployee.value);
  return sortedEmployees.value.findIndex((emp) => getEmpKey(emp) === key);
});

const canNavigatePrevEmployee = computed(() => selectedEmployeeIndex.value > 0);

const canNavigateNextEmployee = computed(() => {
  const idx = selectedEmployeeIndex.value;
  return idx >= 0 && idx < sortedEmployees.value.length - 1;
});

const isEmployeeNavDisabled = computed(
  () =>
    isDetailPageLoading.value
    || savingAllRecipients.value
    || importingPrevMonth.value
    || bulkEmployeeProcessing.value
);

const navigatePrevEmployee = () => {
  const idx = selectedEmployeeIndex.value;
  if (idx <= 0 || isEmployeeNavDisabled.value) return;
  void selectEmployee(sortedEmployees.value[idx - 1]);
};

const navigateNextEmployee = () => {
  const idx = selectedEmployeeIndex.value;
  if (idx < 0 || idx >= sortedEmployees.value.length - 1 || isEmployeeNavDisabled.value) return;
  void selectEmployee(sortedEmployees.value[idx + 1]);
};

// ─────────────────────────────────────────────
// 펼침 뷰 관련 함수
// ─────────────────────────────────────────────

/** 요양보호사인지 + 청구내역이 있는지 확인 */
const hasCaregiverClaims = (emp: any): boolean => {
  if (!isCaregiverEmployee(emp)) return false;
  const empKey = getEmpKey(emp);
  const claims = allEmpClaimData.value.get(empKey);
  return !!claims && claims.length > 0;
};

/** 펼침 뷰 토글 */
const toggleExpandedView = async () => {
  if (isExpandedView.value) {
    // 접기
    isExpandedView.value = false;
    expandedEmpKeys.value.clear();
    return;
  }

  // 펼침: 모든 요양보호사의 청구내역 로드
  isExpandedView.value = true;
  await loadAllCaregiverClaims();
};

const caregiverClaimsCacheKey = ref('');
const loadingClaimSummaries = ref(false);

const getCaregiverClaimsCacheKey = () =>
  `${memberCode.value}|${yearMonth.value}|${selectedBizGubun.value || ''}`;

/** 월 일괄 청구 — 생년월일 없는 사원은 같은 이름 묶음을 합친다 */
const pickMonthClaimBundle = (
  byKey: Record<string, { rows?: HomeWageClaimRow[]; unjoinedSevereDetails?: HomeWageClaimRow[] }>,
  emp: any
) => {
  const exact = byKey[getEmpKey(emp)];
  if (exact) return exact;
  if (getEmpBirth(emp)) return null;

  const prefix = `${normalizeEmpName(getEmpName(emp))}|`;
  const rows: HomeWageClaimRow[] = [];
  const unjoinedSevereDetails: HomeWageClaimRow[] = [];
  for (const [key, bundle] of Object.entries(byKey)) {
    if (!key.startsWith(prefix)) continue;
    rows.push(...(bundle?.rows || []));
    unjoinedSevereDetails.push(...(bundle?.unjoinedSevereDetails || []));
  }
  if (!rows.length && !unjoinedSevereDetails.length) return null;
  return { rows, unjoinedSevereDetails };
};

/** 월 일괄 수급자 시급 → 펼침 뷰 맵 */
const applyMonthRecipientSettings = (data: any, caregivers: any[]) => {
  const byKey = data?.byKey || {};
  const uniqueNameKey: Record<string, string> = data?.uniqueNameKey || {};

  for (const emp of caregivers) {
    const empKey = getEmpKey(emp);
    const name = normalizeEmpName(getEmpName(emp));
    const exact = byKey[empKey];
    const fallbackKey = exact ? '' : uniqueNameKey[name];
    const maps = exact || (fallbackKey ? byKey[fallbackKey] : null) || {};
    const { hourlyByGroup = {}, severeFeeByGroup = {} } = maps;

    expandedHourlyWage.value.set(empKey, new Map());
    expandedHourlyDisplay.value.set(empKey, new Map());
    expandedSevereFee.value.set(empKey, new Map());
    expandedSevereDisplay.value.set(empKey, new Map());

    for (const [groupKey, hourlyWage] of Object.entries(hourlyByGroup)) {
      if (hourlyWage != null && Number(hourlyWage) > 0) {
        expandedHourlyWage.value.get(empKey)!.set(groupKey, Number(hourlyWage));
        expandedHourlyDisplay.value.get(empKey)!.set(groupKey, formatWageNumber(Number(hourlyWage)));
      }
    }
    for (const [groupKey, severeFee] of Object.entries(severeFeeByGroup)) {
      if (severeFee != null && Number(severeFee) > 0) {
        expandedSevereFee.value.get(empKey)!.set(groupKey, Number(severeFee));
        expandedSevereDisplay.value.get(empKey)!.set(groupKey, formatWageNumber(Number(severeFee)));
      }
    }
  }

  expandedHourlyWage.value = new Map(expandedHourlyWage.value);
  expandedHourlyDisplay.value = new Map(expandedHourlyDisplay.value);
  expandedSevereFee.value = new Map(expandedSevereFee.value);
  expandedSevereDisplay.value = new Map(expandedSevereDisplay.value);
};

/** 요양보호사 청구·수급자 설정 로드 + 집계 (목록 수가시급 상태·펼침 뷰 공용) */
const loadCaregiverClaimsAndSummaries = async (options?: {
  silent?: boolean;
  autoExpand?: boolean;
  force?: boolean;
  loadSeq?: number;
}) => {
  if (!memberCode.value) return;

  const loadSeq = options?.loadSeq ?? monthDataLoadSeq.value;
  if (isMonthDataLoadStale(loadSeq)) return;

  const cacheKey = getCaregiverClaimsCacheKey();
  if (!options?.force && caregiverClaimsCacheKey.value === cacheKey && allEmpClaimData.value.size > 0) {
    refreshListWageViewMismatchFlags();
    return;
  }

  const caregivers = employees.value.filter((emp) => isCaregiverEmployee(emp));
  if (caregivers.length === 0) {
    if (!options?.silent) {
      alert('요양보호사가 없습니다.');
      isExpandedView.value = false;
    }
    return;
  }

  if (!options?.silent) {
    loadingExpandedData.value = true;
  } else {
    loadingClaimSummaries.value = true;
  }

  try {
    const [claimsRes, settingsRes] = await Promise.all([
      homeWageApi.getCaregiverClaimsMonth({
        mCode: memberCode.value,
        yearMonth: yearMonth.value,
        bizGubun: HOME_CARE_BIZ_GUBUNS,
      }),
      homeScheduleApi.getRecipientSettingsMonth({
        mCode: String(memberCode.value),
        yearMonth: yearMonth.value,
        bizGubun: selectedBizGubun.value || '',
      }).catch((err) => {
        console.error('수급자 설정 일괄 조회 실패:', err);
        return null;
      }),
    ]);
    if (isMonthDataLoadStale(loadSeq)) return;
    if (!claimsRes.data?.success) {
      throw new Error(claimsRes.data?.message || '청구내역 조회 실패');
    }

    const byKey = claimsRes.data.data?.byKey || {};
    for (const emp of caregivers) {
      const empKey = getEmpKey(emp);
      const bundle = pickMonthClaimBundle(byKey, emp);
      const rawRows: HomeWageClaimRow[] = bundle?.rows || [];
      const unjoinedSevere: HomeWageClaimRow[] = bundle?.unjoinedSevereDetails || [];
      allEmpClaimData.value.set(
        empKey,
        preferPaidClaimDetailRows(deduplicateClaimRows([...rawRows, ...unjoinedSevere]))
      );
    }

    if (settingsRes?.data?.success) {
      applyMonthRecipientSettings(settingsRes.data.data, caregivers);
    }
    if (isMonthDataLoadStale(loadSeq)) return;
    applyAllDraftsToExpandedView();

    allEmpClaimData.value.forEach((claims, empKey) => {
      if (claims.length > 0) {
        if (options?.autoExpand) {
          expandedEmpKeys.value.add(empKey);
        }
        buildExpandedSummary(empKey, claims);
      }
    });

    caregiverClaimsCacheKey.value = cacheKey;
    allEmpClaimData.value = new Map(allEmpClaimData.value);
    allEmpSummaryRows.value = new Map(allEmpSummaryRows.value);
    refreshListWageViewMismatchFlags();
  } catch (error: any) {
    if (isMonthDataLoadStale(loadSeq)) return;
    console.error('전체 청구내역 로드 실패:', error);
    if (!options?.silent) {
      alert('청구내역 로드 실패');
    }
  } finally {
    if (!options?.silent) {
      loadingExpandedData.value = false;
    }
    loadingClaimSummaries.value = false;
  }
};

/** 모든 요양보호사의 청구내역 일괄 로드 (펼침 뷰) */
const loadAllCaregiverClaims = async () => {
  await loadCaregiverClaimsAndSummaries({ autoExpand: true });
};

/** 요양보호사 행 펼침/접기 토글 */
const toggleEmpExpand = (empKey: string) => {
  if (expandedEmpKeys.value.has(empKey)) {
    expandedEmpKeys.value.delete(empKey);
  } else {
    expandedEmpKeys.value.add(empKey);
  }
};

/** 펼침 뷰용 집계 행 생성 */
const buildExpandedSummary = (empKey: string, claims: HomeWageClaimRow[]) => {
  if (!claims.length) return;

  // 디스플레이 맵 초기화
  if (!expandedHourlyDisplay.value.has(empKey)) {
    expandedHourlyDisplay.value.set(empKey, new Map());
    expandedHourlyWage.value.set(empKey, new Map());
  }
  if (!expandedSevereDisplay.value.has(empKey)) {
    expandedSevereDisplay.value.set(empKey, new Map());
    expandedSevereFee.value.set(empKey, new Map());
  }

  const hourlyMap = expandedHourlyWage.value.get(empKey)!;
  const severeMap = expandedSevereFee.value.get(empKey)!;

  const hourlyObj: Record<string, number> = {};
  const severeObj: Record<string, number> = {};
  hourlyMap.forEach((v, k) => { hourlyObj[k] = v; });
  severeMap.forEach((v, k) => { severeObj[k] = v; });

  const summary = aggregateRecipientFeeSummary(claims, hourlyObj, severeObj);
  allEmpSummaryRows.value.set(empKey, summary);

  // ★ 중증수급자 가산: 저장값 없으면 디폴트 수가(feeAmountPerVisit) 자동 설정
  for (const row of summary) {
    if (row.isSevereRecipientSurcharge && row.feeAmountPerVisit > 0) {
      const gk = row.groupKey;
      // 저장된 값이 없으면 디폴트 수가로 설정
      if (!severeMap.has(gk) || severeMap.get(gk) === 0) {
        severeMap.set(gk, row.feeAmountPerVisit);
        expandedSevereDisplay.value.get(empKey)!.set(gk, formatWageNumber(row.feeAmountPerVisit));
      }
    }
  }
};

/** 펼침 뷰 시급 입력 */
const onExpandedHourlyInput = (empKey: string, groupKey: string, event: Event) => {
  const raw = (event.target as HTMLInputElement).value;
  const val = parseWageNumber(raw);
  if (!expandedHourlyWage.value.has(empKey)) {
    expandedHourlyWage.value.set(empKey, new Map());
    expandedHourlyDisplay.value.set(empKey, new Map());
  }
  const wageMap = expandedHourlyWage.value.get(empKey)!;
  const displayMap = expandedHourlyDisplay.value.get(empKey)!;
  wageMap.set(groupKey, val);
  displayMap.set(groupKey, raw);
  expandedHourlyWage.value = new Map(expandedHourlyWage.value);
  expandedHourlyDisplay.value = new Map(expandedHourlyDisplay.value);
};

const onExpandedHourlyBlur = (empKey: string, groupKey: string) => {
  const val = expandedHourlyWage.value.get(empKey)?.get(groupKey) || 0;
  if (!expandedHourlyDisplay.value.has(empKey)) {
    expandedHourlyDisplay.value.set(empKey, new Map());
  }
  expandedHourlyDisplay.value.get(empKey)!.set(groupKey, val > 0 ? formatWageNumber(val) : '');
  expandedHourlyDisplay.value = new Map(expandedHourlyDisplay.value);
  recalcExpandedSummary(empKey);
};

/** 펼침 뷰 중증수가 입력 */
const onExpandedSevereFeeInput = (empKey: string, groupKey: string, event: Event) => {
  const val = parseWageNumber((event.target as HTMLInputElement).value);
  if (!expandedSevereFee.value.has(empKey)) {
    expandedSevereFee.value.set(empKey, new Map());
    expandedSevereDisplay.value.set(empKey, new Map());
  }
  expandedSevereFee.value.get(empKey)!.set(groupKey, val);
};

const onExpandedSevereFeeBlur = (empKey: string, groupKey: string) => {
  const val = expandedSevereFee.value.get(empKey)?.get(groupKey) || 0;
  if (!expandedSevereDisplay.value.has(empKey)) {
    expandedSevereDisplay.value.set(empKey, new Map());
  }
  expandedSevereDisplay.value.get(empKey)!.set(groupKey, val > 0 ? formatWageNumber(val) : '');
  recalcExpandedSummary(empKey);
};

/** 펼침 뷰 집계 재계산 */
const recalcExpandedSummary = (empKey: string) => {
  const claims = allEmpClaimData.value.get(empKey);
  if (!claims?.length) return;
  buildExpandedSummary(empKey, claims);
};

/** 펼침 뷰 사원 합계 계산 — 중증수급자가산 제외 */
const getExpandedEmpTotal = (empKey: string): number => {
  const rows = getEmpSummaryRowsForTable(empKey);
  return rows.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
};

/** 펼침 뷰 시급 디스플레이값 */
const getExpandedHourlyDisplay = (empKey: string, groupKey: string): string => {
  return expandedHourlyDisplay.value.get(empKey)?.get(groupKey) || '';
};

/** 펼침 뷰 중증수가 디스플레이값 (저장값 없으면 디폴트 수가 표시) */
const getExpandedSevereDisplay = (empKey: string, groupKey: string): string => {
  const display = expandedSevereDisplay.value.get(empKey)?.get(groupKey);
  if (display) return display;

  // fallback: 집계 row에서 디폴트 수가 가져오기
  const rows = allEmpSummaryRows.value.get(empKey);
  if (rows) {
    const row = rows.find((r: any) => r.groupKey === groupKey);
    if (row?.isSevereRecipientSurcharge && row.feeAmountPerVisit > 0) {
      return formatWageNumber(row.feeAmountPerVisit);
    }
  }
  return '';
};

/** 목록 모두저장 전 — 펼침 데이터·수급자 설정 로드 */
const ensureExpandedDataForSave = async () => {
  if (!memberCode.value) return;
  const caregivers = sortedEmployees.value.filter((emp) => isCaregiverEmployee(emp));
  if (caregivers.length === 0) return;

  if (!isExpandedView.value) {
    isExpandedView.value = true;
  }
  if (allEmpClaimData.value.size === 0) {
    await loadAllCaregiverClaims();
  } else {
    // ★ 이미 불러온 청구는 다시 조회하지 않음 — 입력 중인 시급을 유지
    for (const [empKey, claims] of allEmpClaimData.value.entries()) {
      if (claims.length > 0) {
        buildExpandedSummary(empKey, claims);
      }
    }
  }
};

/** 사원별 수급자 설정 저장 payload */
const collectRecipientSettingsPayloadForEmp = (emp: any) => {
  const empKey = getEmpKey(emp);
  const hourlyMap = expandedHourlyWage.value.get(empKey);
  const severeMap = expandedSevereFee.value.get(empKey);
  const summaryRows = allEmpSummaryRows.value.get(empKey);

  if (summaryRows?.length) {
    const hourlyByGroup: Record<string, number> = {};
    const severeFeeByGroup: Record<string, number> = {};
    const sugaHoursByGroup: Record<string, number> = {};

    for (const row of summaryRows) {
      const gk = row.groupKey;
      const hourlyWage = hourlyMap?.get(gk) || 0;
      const severeFee = severeMap?.get(gk) || 0;

      if (hourlyWage > 0) hourlyByGroup[gk] = hourlyWage;
      if (severeFee > 0) severeFeeByGroup[gk] = severeFee;
      if (row.sugaHoursPerVisit > 0) sugaHoursByGroup[gk] = row.sugaHoursPerVisit;
    }

    if (
      Object.keys(hourlyByGroup).length > 0
      || Object.keys(severeFeeByGroup).length > 0
      || Object.keys(sugaHoursByGroup).length > 0
    ) {
      return { hourlyByGroup, severeFeeByGroup, sugaHoursByGroup };
    }
  }

  const draft = employeeWageDrafts.value.get(empKey);
  if (draft?.recipient) {
    const { hourlyByGroup, severeFeeByGroup, sugaHoursByGroup } = draft.recipient;
    if (
      Object.keys(hourlyByGroup || {}).length > 0
      || Object.keys(severeFeeByGroup || {}).length > 0
      || Object.keys(sugaHoursByGroup || {}).length > 0
    ) {
      return {
        hourlyByGroup: hourlyByGroup || {},
        severeFeeByGroup: severeFeeByGroup || {},
        sugaHoursByGroup: sugaHoursByGroup || {},
      };
    }
  }

  return null;
};

/** 목록 펼침 시급·중증수가 입력 여부 */
const hasExpandedRecipientInputsForEmp = (empKey: string): boolean => {
  const hourlyMap = expandedHourlyWage.value.get(empKey);
  if (hourlyMap?.size && [...hourlyMap.values()].some((v) => v > 0)) return true;
  const severeMap = expandedSevereFee.value.get(empKey);
  if (severeMap?.size && [...severeMap.values()].some((v) => v > 0)) return true;
  return false;
};

/** 모두저장 시 급여대장 선계산 대상 여부 */
const shouldRecalcWageDraftForBulkSave = (emp: any): boolean => {
  const empKey = getEmpKey(emp);
  if (!isCaregiverEmployee(emp)) {
    return employeeWageDrafts.value.has(empKey);
  }
  if (hasExpandedRecipientInputsForEmp(empKey)) return true;
  return employeeWageDrafts.value.has(empKey);
};

/** 펼침 시급·청구 기준 4대보험·지원공제 등 선계산 → employeeWageDrafts 반영 */
const computeAndCaptureWageDraftFromList = async (
  emp: any,
  recipient?: NonNullable<EmployeeWageDraft['recipient']>
) => {
  const empKey = getEmpKey(emp);
  const priorDraft = employeeWageDrafts.value.get(empKey);

  await withEmployeeDetailContext(emp, async () => {
    if (isCaregiverEmployee(emp)) {
      if (!caregiverClaimRows.value.length) {
        await loadCaregiverClaims();
      }
      if (recipient) {
        unlockPayrollLedgerForEdit();
        loadMapsFromDb({
          hourlyByGroup: recipient.hourlyByGroup,
          severeFeeByGroup: recipient.severeFeeByGroup,
          sugaHoursByGroup: recipient.sugaHoursByGroup,
        });
        syncRecipientSettingsDisplay();
        ensureCaregiverHourlyAtLeastMin();
        if (caregiverHourlyWage.value > 0) {
          caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
        }
      }
      syncPremiumHoursFromClaims();
      syncPremiumNontaxAmounts();
      syncLaborDayPay();
    } else if (priorDraft?.wagePayload) {
      // 사복/센터장: 전월가져오기 초안의 시급·근무시간을 선계산 전에 복원
      applyStaffWorkInfoFromDraftPayload(priorDraft.wagePayload);
    }

    unlockPayrollLedgerForEdit();
    runFullPayrollAutoCalc(true, true);
    if (priorDraft?.wagePayload) {
      restorePayrollOverridesFromDraftPayload(priorDraft.wagePayload);
      if (!isCaregiverEmployee(emp)) {
        restoreStaffAllowancesFromDraftPayload(priorDraft.wagePayload);
        runAutoFillSocialInsurance(true);
        runAutoFillLaborDeduct(true);
        runAutoFillCorpDeduct(true);
        syncSupportDeduction(true);
      }
    }
    syncReferenceValues();
    captureEmployeeWageDraft(emp);
  });
};

/** 모두저장 전 — 펼침 시급 반영 급여대장(4대보험·지원공제 등) 일괄 선계산 */
const prepareAllWageDraftsForBulkSave = async (): Promise<number> => {
  let failCount = 0;

  for (const emp of sortedEmployees.value) {
    if (!shouldRecalcWageDraftForBulkSave(emp)) continue;

    try {
      const recipientPayload = isCaregiverEmployee(emp)
        ? collectRecipientSettingsPayloadForEmp(emp)
        : null;
      await computeAndCaptureWageDraftFromList(
        emp,
        recipientPayload ?? undefined
      );
    } catch (error) {
      failCount++;
      console.error(`${getEmpName(emp)} 급여 선계산 오류:`, error);
    }
  }

  return failCount;
};

/** 요양보호사 — 화면 수급자별 집계 → cwhd_svc (저장 스냅샷) */
const resolveSvcPayloadForEmp = (emp: any): string => {
  if (!isCaregiverEmployee(emp)) return '';

  const empKey = getEmpKey(emp);
  const isSelected = selectedEmployee.value && getEmpKey(selectedEmployee.value) === empKey;

  if (isSelected) {
    if (payrollDbLock.value) {
      if (claimSummaryRows.value.length > 0 && dbSavedSummaryRows.value.length > 0) {
        return serializeHomeWageRecipientSummaryToSvc(
          mergeLiveClaimSummaryWithDbSnapshot(
            claimSummaryRows.value,
            dbSavedSummaryRows.value
          )
        );
      }
      if (payrollForm.value.svc) return String(payrollForm.value.svc);
      if (dbSavedSummaryRows.value.length > 0) {
        return serializeHomeWageRecipientSummaryToSvc(dbSavedSummaryRows.value);
      }
    } else if (claimSummaryRows.value.length > 0) {
      return serializeHomeWageRecipientSummaryToSvc(claimSummaryRows.value);
    }
  }

  const expandedRows = allEmpSummaryRows.value.get(empKey);
  if (expandedRows?.length) {
    return serializeHomeWageRecipientSummaryToSvc(expandedRows);
  }

  return '';
};

/** DB 급여대장 row → 저장 API payload */
const buildWageSavePayloadFromDbRow = (row: any, empKey?: string) => {
  const birthRaw = row.birth ? String(row.birth).split('T')[0] : null;
  const job = row.job || '';
  const storedLaborDay = Number(row.laborDay) || 0;
  const claimRows = empKey ? allEmpClaimData.value.get(empKey) : undefined;
  const resolvedHour = resolveLiveCaregiverSugaHours(empKey, row.hour);
  const laborDay = resolveLaborDayAmountForSave({
    job,
    payCalcHourly: Number(row.payCalcHourly) || 0,
    totalSugaHours: resolvedHour,
    workDays: parseInt(String(row.days ?? ''), 10) || 0,
    storedLaborDay,
    claimRows,
  });
  // 레거시: wageTotal에 laborDay가 과세로 포함된 경우 — 5월만 차감
  const wageTotal =
    isHomeWageLaborDayMonth(yearMonth.value) && storedLaborDay > 0
      ? Math.max(0, (Number(row.wageTotal) || 0) - storedLaborDay)
      : Number(row.wageTotal) || 0;
  const nonTax = calcHomeWageTotalNontax(
    {
      lunch: Number(row.lunch) || 0,
      lunchDirector: Number(row.lunchDirector) || 0,
      car: Number(row.car) || 0,
      child: Number(row.child) || 0,
      laborDay,
      overtime: Number(row.overtime) || 0,
      night: Number(row.night) || 0,
      holiday: Number(row.holiday) || 0,
    },
    true,
    yearMonth.value
  );
  const productionNontax = true;
  const cSaveBase = calcHomeWageCenterSavingsNontaxBase(
    {
      lunch: Number(row.lunch) || 0,
      lunchDirector: Number(row.lunchDirector) || 0,
      car: Number(row.car) || 0,
      child: Number(row.child) || 0,
      laborDay: laborDay,
      overtime: Number(row.overtime) || 0,
      night: Number(row.night) || 0,
      holiday: Number(row.holiday) || 0,
      overtimeNontax: Number(row.overtimeNontax) || 0,
      nightNontax: Number(row.nightNontax) || 0,
      holidayNontax: Number(row.holidayNontax) || 0,
    },
    productionNontax,
    yearMonth.value
  );
  const c_save = calcCenterTaxSavingsFromParts(cSaveBase, {
    birth: birthRaw || '',
    job,
    workHours: resolvedHour,
  });
  const basicAmt = Number(row.basic) || 0;
  const weekendAmt = Number(row.weekend) || 0;
  const dbAnnual = Number(row.annual) || 0;
  /** 요양보호사 — 연차 = 수급자별합계 − 기본 − 주휴 (기본급합계 일치) */
  const annualAmt = (() => {
    if (!job.includes('요양보호사')) return dbAnnual;
    const recipientTotal = calcHomeWageNonSevereRecipientTotalFromSummaryRows(
      parseHomeWageRecipientSummaryFromSvc(String(row.svc || ''))
    );
    if (recipientTotal <= 0) return dbAnnual;
    return resolveHomeWageDirectorAnnualFromRecipientCap({
      recipientTotal,
      basic: basicAmt,
      weekend: weekendAmt,
      proposedAnnual: dbAnnual,
    });
  })();
  const rowForGross = job.includes('요양보호사')
    ? { ...row, annual: annualAmt }
    : row;
  const grossPay = job.includes('요양보호사')
    ? calcHomeWageStoredUnifiedGrossPay(rowForGross, yearMonth.value)
    : wageTotal + nonTax;
  const wageTotalForSave = job.includes('요양보호사') && annualAmt !== dbAnnual
    ? Math.max(0, wageTotal - dbAnnual + annualAmt)
    : wageTotal;
  return {
    m_code: memberCode.value,
    bizGubun: selectedBizGubun.value || '',
    yearMonth: yearMonth.value,
    payDate: paymentDate.value || row.payDate || null,
    payCalcHourly: Number(row.payCalcHourly) || 0,
    name: row.name || '',
    birth: birthRaw,
    job,
    basic: basicAmt,
    weekend: weekendAmt,
    annual: annualAmt,
    laborDay,
    overtime: Number(row.overtime) || 0,
    overtimeNontax: Number(row.overtimeNontax) || 0,
    night: Number(row.night) || 0,
    nightNontax: Number(row.nightNontax) || 0,
    holiday: Number(row.holiday) || 0,
    holidayNontax: Number(row.holidayNontax) || 0,
    senior: Number(row.senior) || 0,
    sunim: Number(row.sunim) || 0,
    bosu: Number(row.bosu) || 0,
    severe: Number(row.severe) || 0,
    lunch: Number(row.lunch) || 0,
    lunchDirector: Number(row.lunchDirector) || 0,
    bonus: Number(row.bonus) || 0,
    car: Number(row.car) || 0,
    child: Number(row.child) || 0,
    welfare: Number(row.welfare) || 0,
    other: Number(row.other) || 0,
    overtimeHours: Number(row.overtimeHours) || 0,
    nightHours: Number(row.nightHours) || 0,
    holidayHours: Number(row.holidayHours) || 0,
    wageTotal: wageTotalForSave,
    l_yeon: Number(row.l_yeon) || 0,
    l_gun: Number(row.l_gun) || 0,
    l_jang: Number(row.l_jang) || 0,
    l_go: Number(row.l_go) || 0,
    l_so: Number(row.l_so) || 0,
    l_ji: Number(row.l_ji) || 0,
    l_jiwon: Number(row.l_jiwon) || 0,
    l_bonin: Number(row.l_bonin) || 0,
    l_etc: Number(row.l_etc) || 0,
    l_total: Number(row.l_total) || 0,
    c_yeon: Number(row.c_yeon) || 0,
    c_gun: Number(row.c_gun) || 0,
    c_jang: Number(row.c_jang) || 0,
    c_go: Number(row.c_go) || 0,
    c_sil: Number(row.c_sil) || 0,
    c_san: Number(row.c_san) || 0,
    c_out: Number(row.c_out) || 0,
    ...mapDeductionFixedFieldsFromRow(row),
    ...mapSupportDeductionItemFlagsToDb(loadSupportDeductionItemFlagsFromRow(row)),
    c_total: Number(row.c_total) || 0,
    grossPay,
    net: Math.max(0, grossPay - (Number(row.l_total) || 0)),
    nonTax,
    l_save: Number(row.l_save) || 0,
    c_save,
    profit: Number(row.profit) || 0,
    svc: row.svc || '',
    cnt: row.cnt || '',
    hour:
      resolvedHour > 0
        ? String(Math.round(resolvedHour * 10) / 10)
        : row.hour || '',
    days: row.days || '',
    memo: row.memo || '',
    productionNontax: 'Y',
    lunchExpense: row.lunchExpense === 'Y' ? 'Y' : 'N',
    premiumManual: row.premiumManual === 'Y' ? 'Y' : 'N',
    payOvertime:
      row.payOvertime === 'Y' || row.payOvertime === true
        ? 'Y'
        : row.payOvertime === 'N' || row.payOvertime === false
          ? 'N'
          : null,
    payHoliday:
      row.payHoliday === 'Y' || row.payHoliday === true
        ? 'Y'
        : row.payHoliday === 'N' || row.payHoliday === false
          ? 'N'
          : null,
    payNight:
      row.payNight === 'Y' || row.payNight === true
        ? 'Y'
        : row.payNight === 'N' || row.payNight === false
          ? 'N'
          : null,
    isRepresentative: row.isRepresentative === 'Y' ? 'Y' : 'N',
    applyRetirement: row.applyRetirement === 'Y' ? 'Y' : 'N',
    retirementVisitCarePay: Number(row.retirementVisitCarePay ?? row.retirementGeneralPay) || 0,
    retirementVisitBathPay: Number(row.retirementVisitBathPay) || 0,
    retirementFamilyPay: Number(row.retirementFamilyPay) || 0,
    retirementUseVisitCare:
      (row.retirementUseVisitCare ?? row.retirementUseGeneral) === 'Y' ? 'Y' : 'N',
    retirementUseVisitBath: row.retirementUseVisitBath === 'Y' ? 'Y' : 'N',
    retirementUseFamily: row.retirementUseFamily === 'Y' ? 'Y' : 'N',
  };
};

/** 관리자 시점 — 센터장 캐논 + 연·야·휴·비과세만 병합 (기본급·공제 등은 스냅샷 유지) */
const buildMergedPayrollFormForAdminSave = (): HomeWagePayrollFormData => {
  const snap = payrollPreviewSnapshot.value;
  if (!snap) {
    throw new Error(
      '센터장 기준 급여 데이터가 없습니다. 페이지를 새로고침하거나 센터장 시점으로 전환 후 다시 시도해 주세요.'
    );
  }

  const base = snap.payrollForm;
  const current = payrollForm.value;
  const oldProd = snap.nontaxSettings.productionNontax;
  /** 관리자 저장 — 연야휴 월 20만 비과세 항상 적용 */
  const newProd = true;
  const merged = clonePreview(base) as HomeWagePayrollFormData;

  merged.overtime = Number(current.overtime) || 0;
  merged.night = Number(current.night) || 0;
  merged.holiday = Number(current.holiday) || 0;
  merged.overtimeNontax = Number(current.overtimeNontax) || 0;
  merged.nightNontax = Number(current.nightNontax) || 0;
  merged.holidayNontax = Number(current.holidayNontax) || 0;
  merged.overtimeHours = Number(current.overtimeHours) || 0;
  merged.nightHours = Number(current.nightHours) || 0;
  merged.holidayHours = Number(current.holidayHours) || 0;
  merged.lunch = Number(current.lunch) || 0;
  merged.lunchDirector = Number(current.lunchDirector) || 0;
  merged.car = Number(current.car) || 0;
  merged.child = Number(current.child) || 0;
  merged.laborDay =
    isCaregiverEmployee(selectedEmployee.value) && isHomeWageLaborDayMonth(yearMonth.value)
      ? (Number(current.laborDay) || 0)
      : 0;

  applyLunchDirectorAdjustmentToForm(merged);

  const oldPremiumTax = calcHomeWagePremiumTaxableAmount(base, oldProd);
  const newPremiumTax = calcHomeWagePremiumTaxableAmount(merged, newProd);
  merged.wageTotal = Math.max(0, (Number(base.wageTotal) || 0) - oldPremiumTax + newPremiumTax);
  merged.nonTax = calcHomeWageTotalNontax(merged, newProd, yearMonth.value, 'admin');
  merged.grossPay = resolveSaveGrossPay(selectedEmployee.value, merged);
  merged.net = merged.grossPay - (Number(base.l_total) || 0);
  merged.c_save = calcCenterTaxSavingsFromParts(
    calcHomeWageCenterSavingsNontaxBase(merged, newProd, yearMonth.value, 'admin'),
    {
      birth: getEmpBirth(selectedEmployee.value) || '',
      job: merged.job || getEmpJob(selectedEmployee.value) || '',
      workHours: totalSugaHours.value,
    }
  );
  merged.profit = isCaregiverEmployee(selectedEmployee.value)
    ? calcHomeWageCenterProfit(
        claimSugaMoneyTotal.value,
        merged.grossPay,
        Number(base.c_total) || 0
      )
    : 0;

  const premiumOverride = snap.directorPremiumPayOverride ?? directorPremiumPayOverride.value;
  applyDirectorPremiumPayOverrideMaskToForm(merged, base, premiumOverride, newProd);

  return merged;
};

/** 센터장 연·야·휴 미지급 시 premium 금액 0 처리 (관리자 부분 저장·병합 공통) */
const applyDirectorPremiumPayOverrideMaskToForm = (
  form: HomeWagePayrollFormData,
  baseForm: HomeWagePayrollFormData,
  override: { payOvertime: boolean | null; payHoliday: boolean | null; payNight: boolean | null },
  productionNontax: boolean
) => {
  const isFamily = isAllFamilyCareInClaim.value;
  const sugaH = totalSugaHours.value;
  const payOt = resolveDirectorPremiumPayEnabled({
    type: 'overtime',
    payOverride: override.payOvertime,
    totalSugaHours: sugaH,
    isFamilyCareOnly: isFamily,
  });
  const payHol = resolveDirectorPremiumPayEnabled({
    type: 'holiday',
    payOverride: override.payHoliday,
    totalSugaHours: sugaH,
    isFamilyCareOnly: isFamily,
  });
  const payN = resolveDirectorPremiumPayEnabled({
    type: 'night',
    payOverride: override.payNight,
    totalSugaHours: sugaH,
    isFamilyCareOnly: isFamily,
  });

  const beforeTax = calcHomeWagePremiumTaxableAmount(form, productionNontax);
  if (!payOt) {
    form.overtime = 0;
    form.overtimeNontax = 0;
  }
  if (!payHol) {
    form.holiday = 0;
    form.holidayNontax = 0;
  }
  if (!payN) {
    form.night = 0;
    form.nightNontax = 0;
  }
  const afterTax = calcHomeWagePremiumTaxableAmount(form, productionNontax);
  form.wageTotal = Math.max(0, (Number(form.wageTotal) || 0) - beforeTax + afterTax);
  form.nonTax = calcHomeWageTotalNontax(form, productionNontax, yearMonth.value, 'admin');
  form.grossPay = resolveSaveGrossPay(selectedEmployee.value, form);
  form.net = form.grossPay - (Number(baseForm.l_total) || 0);
  form.c_save = calcCenterTaxSavingsFromParts(
    calcHomeWageCenterSavingsNontaxBase(form, productionNontax, yearMonth.value, 'admin'),
    {
      birth: getEmpBirth(selectedEmployee.value) || '',
      job: form.job || getEmpJob(selectedEmployee.value) || '',
      workHours: sugaH,
    }
  );
  if (isCaregiverEmployee(selectedEmployee.value)) {
    form.profit = calcHomeWageCenterProfit(
      claimSugaMoneyTotal.value,
      form.grossPay,
      Number(baseForm.c_total) || 0
    );
  }
};

/** 관리자 시점 — 연·야·휴·비과세만 반영한 저장 payload */
const buildWageSavePayloadFromAdminPartialSave = (emp: any) => {
  const snap = payrollPreviewSnapshot.value;
  if (!snap) {
    throw new Error(
      '센터장 기준 급여 데이터가 없습니다. 페이지를 새로고침하거나 센터장 시점으로 전환 후 다시 시도해 주세요.'
    );
  }

  const merged = buildMergedPayrollFormForAdminSave();
  const base = snap.payrollForm;
  const fixedFields = Object.fromEntries(
    HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS.map((key) => [
      `${key}_fixed`,
      formatHomeWageDeductionFixedFlag(snap.deductionFixed[key]),
    ])
  );

  return {
    m_code: memberCode.value,
    bizGubun: selectedBizGubun.value || '',
    yearMonth: yearMonth.value,
    payDate: paymentDate.value || null,
    payCalcHourly: isCaregiverEmployee(emp)
      ? (snap.caregiverHourlyWage || 0)
      : (staffForm.value.hourlyWage || 0),
    name: getEmpName(emp),
    birth: getEmpBirth(emp),
    job: merged.job || getEmpJob(emp) || '',
    basic: Number(base.basic) || 0,
    weekend: Number(base.weekend) || 0,
    annual: Number(base.annual) || 0,
    laborDay: merged.laborDay,
    overtime: merged.overtime,
    overtimeNontax: merged.overtimeNontax,
    night: merged.night,
    nightNontax: merged.nightNontax,
    holiday: merged.holiday,
    holidayNontax: merged.holidayNontax,
    senior: Number(base.senior) || 0,
    sunim: Number(base.sunim) || 0,
    bosu: Number(base.bosu) || 0,
    severe: Number(base.severe) || 0,
    lunch: merged.lunch,
    lunchDirector: merged.lunchDirector,
    bonus: Number(base.bonus) || 0,
    car: merged.car,
    child: merged.child,
    welfare: Number(base.welfare) || 0,
    other: Number(base.other) || 0,
    overtimeHours: merged.overtimeHours,
    nightHours: merged.nightHours,
    holidayHours: merged.holidayHours,
    wageTotal: merged.wageTotal,
    l_yeon: Number(base.l_yeon) || 0,
    l_gun: Number(base.l_gun) || 0,
    l_jang: Number(base.l_jang) || 0,
    l_go: Number(base.l_go) || 0,
    l_so: Number(base.l_so) || 0,
    l_ji: Number(base.l_ji) || 0,
    l_jiwon: Number(base.l_jiwon) || 0,
    l_bonin: Number(base.l_bonin) || 0,
    l_etc: Number(base.l_etc) || 0,
    l_total: Number(base.l_total) || 0,
    c_yeon: Number(base.c_yeon) || 0,
    c_gun: Number(base.c_gun) || 0,
    c_jang: Number(base.c_jang) || 0,
    c_go: Number(base.c_go) || 0,
    c_sil: Number(base.c_sil) || 0,
    c_san: Number(base.c_san) || 0,
    c_out: Number(base.c_out) || 0,
    ...fixedFields,
    ...mapSupportDeductionItemFlagsToDb(snap.supportDeductionItemFlags),
    c_total: Number(base.c_total) || 0,
    grossPay: merged.grossPay,
    net: merged.net,
    nonTax: merged.nonTax,
    l_save: Number(base.l_save) || 0,
    c_save: merged.c_save,
    profit: merged.profit,
    svc: isCaregiverEmployee(emp)
      ? (resolveSvcPayloadForEmp(emp) || base.svc || '')
      : (base.svc || ''),
    cnt: base.cnt || '',
    hour: base.hour || '',
    days: base.days || '',
    memo: (() => {
      const baseMemo = stripPremiumManualMemo(base.memo);
      const encoded = premiumManualAdjustEnabled.value
        ? encodePremiumManualMemo(baseMemo, getCurrentPremiumFullFromForm())
        : baseMemo;
      return encodeHomeWageDirectorResavedMemo(encoded, yearMonth.value);
    })(),
    productionNontax: 'Y',
    lunchExpense: nontaxSettings.value.lunchExpense ? 'Y' : 'N',
    premiumManual: premiumManualAdjustEnabled.value ? 'Y' : 'N',
    payOvertime: formatHomeWageDirectorPremiumPayFlag(
      snap.directorPremiumPayOverride?.payOvertime ?? directorPremiumPayOverride.value.payOvertime
    ),
    payHoliday: formatHomeWageDirectorPremiumPayFlag(
      snap.directorPremiumPayOverride?.payHoliday ?? directorPremiumPayOverride.value.payHoliday
    ),
    payNight: formatHomeWageDirectorPremiumPayFlag(
      snap.directorPremiumPayOverride?.payNight ?? directorPremiumPayOverride.value.payNight
    ),
    isRepresentative: formatHomeWageRepresentativeFlag(snap.isRepresentative),
    applyRetirement: formatHomeWageApplyRetirementFlag(snap.applyRetirement),
    ...getRetirementSourceSavePayload(),
  };
};

/** 현재 상세 폼 → 급여대장 저장 payload */
const buildWageSavePayloadFromCurrentForm = (
  emp: any,
  options?: { forceFullSave?: boolean }
) => {
  // 관리자 시점 기본: 연·야·휴·비과세만 부분 저장. 불일치 재저장 등은 전체 저장.
  if (isAdminPreviewView.value && !options?.forceFullSave) {
    return buildWageSavePayloadFromAdminPartialSave(emp);
  }
  // ★ 저장 직전 — 4대보험·세금이 관리자 shadow 기준인지 재점검 (급여 항목은 센터장 form 유지)
  verifyAdminBasedInsuranceBeforeSave();
  syncReferenceValues();
  applyRepresentativeCorpRules();
  syncLaborDeductTotal();
  syncCorpDeductTotal();
  if (isCaregiverEmployee(emp)) {
    syncPayrollEmployeeMeta();
    if (!isAllFamilyCareInClaim.value) {
      syncPremiumNontaxAmounts('director', true);
      recalcWageTotal();
    }
    syncNonTaxTotal();
    syncReferenceValues();
  }
  const form = payrollForm.value;
  const lunchFields = resolveLunchFieldsForSave();
  const saveGrossPay = resolveSaveGrossPay(emp, form);
  /** 요양보호사 — 연차 = 수급자별합계 − 기본 − 주휴 (기본급합계 일치) */
  const formAnnual = Number(form.annual) || 0;
  const saveAnnual = (() => {
    if (!isCaregiverEmployee(emp) || isAllFamilyCareInClaim.value) {
      return formAnnual;
    }
    const recipientTotal = claimGrandTotal.value - severeSumFromClaim.value;
    if (recipientTotal <= 0) return formAnnual;
    return resolveHomeWageDirectorAnnualFromRecipientCap({
      recipientTotal,
      basic: Number(form.basic) || 0,
      weekend: Number(form.weekend) || 0,
      proposedAnnual: formAnnual,
    });
  })();
  const saveWageTotal = (() => {
    const wt = Number(form.wageTotal) || 0;
    if (!isCaregiverEmployee(emp) || saveAnnual === formAnnual) return wt;
    return Math.max(0, wt - formAnnual + saveAnnual);
  })();
  const fixedFields = Object.fromEntries(
    HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS.map((key) => [
      `${key}_fixed`,
      formatHomeWageDeductionFixedFlag(deductionFixed.value[key]),
    ])
  );

  const payload: Record<string, unknown> = {
    m_code: memberCode.value,
    bizGubun: selectedBizGubun.value || '',
    yearMonth: yearMonth.value,
    payDate: paymentDate.value || null,
    payCalcHourly: isCaregiverEmployee(emp)
      ? (caregiverHourlyWage.value || 0)
      : (staffForm.value.hourlyWage || 0),
    name: getEmpName(emp),
    birth: getEmpBirth(emp),
    job: form.job || getEmpJob(emp) || '',
    basic: Number(form.basic) || 0,
    weekend: form.weekend || 0,
    annual: saveAnnual,
    laborDay:
      isCaregiverEmployee(emp) && isHomeWageLaborDayMonth(yearMonth.value)
        ? (form.laborDay || 0)
        : 0,
    overtime: form.overtime || 0,
    overtimeNontax: form.overtimeNontax || 0,
    night: form.night || 0,
    nightNontax: form.nightNontax || 0,
    holiday: form.holiday || 0,
    holidayNontax: form.holidayNontax || 0,
    senior: form.senior || 0,
    sunim: form.sunim || 0,
    bosu: form.bosu || 0,
    severe: form.severe || 0,
    lunch: lunchFields.lunch,
    lunchDirector: lunchFields.lunchDirector,
    bonus: form.bonus || 0,
    car: form.car || 0,
    child: form.child || 0,
    welfare: form.welfare || 0,
    other: form.other || 0,
    overtimeHours: form.overtimeHours || 0,
    nightHours: form.nightHours || 0,
    holidayHours: form.holidayHours || 0,
    wageTotal: saveWageTotal,
    l_yeon: form.l_yeon || 0,
    l_gun: form.l_gun || 0,
    l_jang: form.l_jang || 0,
    l_go: form.l_go || 0,
    l_so: form.l_so || 0,
    l_ji: form.l_ji || 0,
    l_jiwon: form.l_jiwon || 0,
    l_bonin: form.l_bonin || 0,
    l_etc: form.l_etc || 0,
    l_total: form.l_total || 0,
    c_yeon: form.c_yeon || 0,
    c_gun: form.c_gun || 0,
    c_jang: form.c_jang || 0,
    c_go: form.c_go || 0,
    c_sil: form.c_sil || 0,
    c_san: form.c_san || 0,
    c_out: form.c_out || 0,
    ...fixedFields,
    ...mapSupportDeductionFieldsFromFlags(),
    c_total: form.c_total || 0,
    grossPay: saveGrossPay,
    net: Math.max(0, saveGrossPay - (Number(form.l_total) || 0)),
    nonTax: form.nonTax || 0,
    l_save: form.l_save || 0,
    c_save: form.c_save || 0,
    profit: form.profit || 0,
    svc: isCaregiverEmployee(emp)
      ? (resolveSvcPayloadForEmp(emp) || form.svc || '')
      : (form.svc || ''),
    cnt: form.cnt || '',
    hour: isCaregiverEmployee(emp)
      ? (() => {
          if (totalSugaHours.value > 0) {
            return String(Math.round(totalSugaHours.value * 10) / 10);
          }
          const fromForm = parseHours(form.hour);
          if (fromForm > 0) return String(fromForm);
          const premiumSum =
            (Number(form.overtimeHours) || 0) +
            (Number(form.nightHours) || 0) +
            (Number(form.holidayHours) || 0);
          return premiumSum > 0 ? String(Math.round(premiumSum * 10) / 10) : '0';
        })()
      : String(staffForm.value.workHours > 0 ? staffForm.value.workHours : ''),
    days: isCaregiverEmployee(emp)
      ? String(workCalendarSummary.value.workDayCount || form.days || '')
      : form.days || '',
    memo: (() => {
      const base = premiumManualAdjustEnabled.value
        ? encodePremiumManualMemo(stripPremiumManualMemo(form.memo), getCurrentPremiumFullFromForm())
        : stripPremiumManualMemo(form.memo);
      return encodeHomeWageDirectorResavedMemo(base, yearMonth.value);
    })(),
    productionNontax: 'Y',
    lunchExpense: nontaxSettings.value.lunchExpense ? 'Y' : 'N',
    premiumManual: premiumManualAdjustEnabled.value ? 'Y' : 'N',
    payOvertime: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payOvertime
    ),
    payHoliday: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payHoliday
    ),
    payNight: formatHomeWageDirectorPremiumPayFlag(
      directorPremiumPayOverride.value.payNight
    ),
    isRepresentative: formatHomeWageRepresentativeFlag(isRepresentative.value),
    applyRetirement: formatHomeWageApplyRetirementFlag(applyRetirement.value),
    ...getRetirementSourceSavePayload(),
  };
  if (directorCanonicalActive.value) {
    const store = mapHomeWageRowToDirectorStore({
      ...form,
      isDirectorCanonical: true,
      job: payload.job,
      svc: payload.svc,
      hour: payload.hour,
      payCalcHourly: payload.payCalcHourly,
      lunchAuto: Number(form.lunch) || Number(dbSavedAutoLunchRef.value) || 0,
      lunchDirector: Number(form.lunchDirector) || 0,
      lunch: Number(form.lunchDirector) || 0,
      yearMonth: yearMonth.value,
    }, yearMonth.value);
    store.lunchAuto = resolveDirectorAdminAutoLunchForSave(emp, store);
    const admin = getHomeWageAdminPartsFromDirectorStore(store, yearMonth.value);
    payload.basic = admin.basic;
    payload.weekend = admin.weekend;
    payload.annual = admin.annual;
    payload.laborDay = admin.laborDay;
    payload.overtime = admin.overtime;
    payload.overtimeNontax = admin.overtimeNontax;
    payload.night = admin.night;
    payload.nightNontax = admin.nightNontax;
    payload.holiday = admin.holiday;
    payload.holidayNontax = admin.holidayNontax;
    payload.senior = admin.senior;
    payload.sunim = admin.sunim;
    payload.bosu = admin.bosu;
    payload.severe = admin.severe;
    payload.bonus = admin.bonus;
    payload.lunch = admin.lunchAuto;
    payload.lunchDirector = admin.lunchDirector;
    payload.car = admin.car;
    payload.child = admin.child;
    payload.welfare = admin.welfare;
    payload.other = admin.other;
  }
  return payload;
};

/** 모두저장 선계산 후 — 초안의 고정 공제·수동 공제(본인부담금·기타) 값 복원 */
const restorePayrollOverridesFromDraftPayload = (payload: Record<string, unknown>) => {
  loadDeductionFixedFromRow(payload);

  for (const key of HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS) {
    if (key === 'l_jiwon') continue;
    if (deductionFixed.value[key] && payload[key] != null) {
      setPayrollMoneyField(key, Number(payload[key]) || 0);
    }
  }

  loadSupportDeductionFromRow(payload);
  syncSupportDeductionValue();

  if (payload.l_bonin != null) {
    setPayrollMoneyField('l_bonin', Number(payload.l_bonin) || 0);
  }
  if (payload.l_etc != null) {
    setPayrollMoneyField('l_etc', Number(payload.l_etc) || 0);
  }

  syncLaborDeductTotal();
  syncCorpDeductTotal();
};

/**
 * 사복/센터장 초안 — 선계산 전 시급·근무시간·연차·연야휴 복원
 * (당월 DB가 비어 있어도 전월 가져오기 초안이 유지되도록)
 */
const applyStaffWorkInfoFromDraftPayload = (payload: Record<string, unknown>) => {
  const hourly = Number(payload.payCalcHourly) || 0;
  let workHours = parseHours(String(payload.hour ?? ''));
  if (workHours <= 0 && Number(payload.basic) > 0 && hourly > 0) {
    workHours = Math.round((Number(payload.basic) / hourly) * 10) / 10;
  }

  if (hourly > 0) {
    staffForm.value.hourlyWage = hourly;
    staffHourlyWageDisplay.value = formatWageNumber(hourly);
  }
  if (workHours > 0) {
    staffForm.value.workHours = workHours;
    staffWorkHoursDisplay.value = formatHoursDisplay(workHours);
    payrollForm.value.hour = String(workHours);
  }
  if (payload.annual != null) {
    staffForm.value.annualPay = Number(payload.annual) || 0;
  }
  staffForm.value.overtimeHours = parseHomeWagePremiumHours(payload.overtimeHours);
  staffForm.value.nightHours = parseHomeWagePremiumHours(payload.nightHours);
  staffForm.value.holidayHours = parseHomeWagePremiumHours(payload.holidayHours);
};

/** 사복/센터장 초안 — 선계산 후 비과세·과세수당·대표자 등 복원 */
const restoreStaffAllowancesFromDraftPayload = (payload: Record<string, unknown>) => {
  if (payload.lunch != null) {
    payrollForm.value.lunch = Number(payload.lunch) || 0;
    autoBaseLunchRef.value = Number(payload.lunch) || 0;
  }

  // ★ 고정 복사 수당 (장기근속·보수교육·상여·처우·기타·센터장식대·차량·양육비)
  for (const key of [
    ...HOME_WAGE_PREV_MONTH_FIXED_COPY_NONTAX_KEYS,
    ...HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS,
  ]) {
    if (payload[key] != null) {
      (payrollForm.value as Record<string, number | string>)[key] = Number(payload[key]) || 0;
    }
  }

  nontaxSettings.value = {
    productionNontax: true,
    lunchExpense: payload.lunchExpense === 'Y',
  };

  for (const key of HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS) {
    if ((HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS as string[]).includes(key)) continue;
    if (payload[key] != null) {
      (payrollForm.value as Record<string, number | string>)[key] = Number(payload[key]) || 0;
    }
  }
  if (payload.weekend != null) {
    payrollForm.value.weekend = Number(payload.weekend) || 0;
  }
  if (payload.basic != null && Number(payload.basic) > 0 && !(Number(payrollForm.value.basic) > 0)) {
    payrollForm.value.basic = Number(payload.basic) || 0;
  }

  if (payload.isRepresentative != null) {
    isRepresentative.value = parseHomeWageRepresentativeFlag(payload.isRepresentative);
  }
  if (payload.applyRetirement != null) {
    applyRetirement.value = parseHomeWageApplyRetirementFlag(payload.applyRetirement);
  }
  if (payload.c_out != null && applyRetirement.value) {
    payrollForm.value.c_out = Number(payload.c_out) || 0;
  }

  syncStaffFormFromPayroll();
  syncNonTaxTotal();
  recalcWageTotal();
};

/** 상세·가져오기 결과를 모두저장용 초안으로 보관 */
const captureEmployeeWageDraft = (emp: any) => {
  if (!emp || isWageReadOnly.value || !memberCode.value) return;

  const draft: EmployeeWageDraft = {
    wagePayload: buildWageSavePayloadFromCurrentForm(emp),
    directorPayload: buildDirectorWageSavePayloadFromCurrentForm(emp),
  };

  if (isCaregiverEmployee(emp)) {
    const mapsData = getMapsForSave();
    if (mapsData.hourlyByGroup || mapsData.severeFeeByGroup || mapsData.sugaHoursByGroup) {
      draft.recipient = {
        hourlyByGroup: mapsData.hourlyByGroup ?? undefined,
        severeFeeByGroup: mapsData.severeFeeByGroup ?? undefined,
        sugaHoursByGroup: mapsData.sugaHoursByGroup ?? undefined,
      };
    }
  }

  employeeWageDrafts.value.set(getEmpKey(emp), draft);
  employeeWageDrafts.value = new Map(employeeWageDrafts.value);
};

/** 수급자별 설정 초안 → 펼침 뷰 반영 */
const applyRecipientDraftToExpandedView = (
  empKey: string,
  recipient: NonNullable<EmployeeWageDraft['recipient']>
) => {
  if (!expandedHourlyWage.value.has(empKey)) {
    expandedHourlyWage.value.set(empKey, new Map());
    expandedHourlyDisplay.value.set(empKey, new Map());
    expandedSevereFee.value.set(empKey, new Map());
    expandedSevereDisplay.value.set(empKey, new Map());
  }

  const hourlyMap = expandedHourlyWage.value.get(empKey)!;
  const hourlyDisplay = expandedHourlyDisplay.value.get(empKey)!;
  const severeMap = expandedSevereFee.value.get(empKey)!;
  const severeDisplay = expandedSevereDisplay.value.get(empKey)!;

  for (const [groupKey, val] of Object.entries(recipient.hourlyByGroup || {})) {
    const wage = Number(val) || 0;
    if (wage <= 0) continue;
    hourlyMap.set(groupKey, wage);
    hourlyDisplay.set(groupKey, formatWageNumber(wage));
  }
  for (const [groupKey, val] of Object.entries(recipient.severeFeeByGroup || {})) {
    const fee = Number(val) || 0;
    if (fee <= 0) continue;
    severeMap.set(groupKey, fee);
    severeDisplay.set(groupKey, formatWageNumber(fee));
  }

  expandedHourlyWage.value = new Map(expandedHourlyWage.value);
  expandedHourlyDisplay.value = new Map(expandedHourlyDisplay.value);
  expandedSevereFee.value = new Map(expandedSevereFee.value);
  expandedSevereDisplay.value = new Map(expandedSevereDisplay.value);
  recalcExpandedSummary(empKey);
};

const applyAllDraftsToExpandedView = () => {
  for (const emp of sortedEmployees.value) {
    const draft = employeeWageDrafts.value.get(getEmpKey(emp));
    if (draft?.recipient && isCaregiverEmployee(emp)) {
      applyRecipientDraftToExpandedView(getEmpKey(emp), draft.recipient);
    }
  }
};

const captureCurrentEmployeeDraftIfAny = () => {
  if (mainView.value === 'detail' && selectedEmployee.value) {
    captureEmployeeWageDraft(selectedEmployee.value);
  }
};

/** 지급일·산재요율 저장 (모두저장 전용) */
const saveMonthSettingsInternal = async () => {
  const parsed = parseHomeWageRatePercentInput(sanRateDisplay.value);
  if (parsed === null) {
    throw new Error('산재보험 요율 형식이 올바르지 않습니다.');
  }
  sanRate.value = parsed;
  sanRateDisplay.value = formatSanRateDisplay(parsed);

  const response = await homeWageApi.savePaymentDate({
    mCode: memberCode.value,
    yearMonth: yearMonth.value,
    paymentDate: paymentDate.value || getDefaultPaymentDate(),
    sanRate: parsed,
    bizGubun: selectedBizGubun.value,
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || '지급일·산재요율 저장 실패');
  }
};

const saveRecipientSettingsForEmp = async (
  emp: any,
  recipient: NonNullable<EmployeeWageDraft['recipient']>
) => {
  const response = await homeScheduleApi.saveRecipientSettings({
    m_code: memberCode.value,
    yearMonth: yearMonth.value,
    bizGubun: selectedBizGubun.value || '',
    empName: getEmpName(emp),
    empBirth: getEmpBirth(emp),
    hourlyByGroup: recipient.hourlyByGroup,
    severeFeeByGroup: recipient.severeFeeByGroup,
    sugaHoursByGroup: recipient.sugaHoursByGroup,
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || '수급자별 설정 저장 실패');
  }
};

/** 사원 1명 급여 데이터 로드·처리 (화면은 목록 유지, 상세 페이지로 전환하지 않음) */
async function withEmployeeDetailContext<T>(
  emp: any,
  fn: () => Promise<T>
): Promise<T> {
  const savedEmployee = selectedEmployee.value;
  const wasDetailView = mainView.value === 'detail';

  bulkEmployeeProcessing.value = true;
  selectedEmployee.value = emp;
  resetEmployeeDetailFormState(emp);

  try {
    await loadCaregiverClaims();
    await ensureHolidaysLoaded();
    await nextTick();
    await loadPrevMonthInsurance();
    await loadWageData();
    if (isCaregiverEmployee(emp)) {
      syncRecipientSettingsDisplay();
      await nextTick();
    }
    return await fn();
  } finally {
    bulkEmployeeProcessing.value = false;
    selectedEmployee.value = savedEmployee;
    if (savedEmployee && wasDetailView) {
      resetEmployeeDetailFormState(savedEmployee);
      await loadEmployeeDetailData();
    } else {
      resetEmployeeDetailFormState(savedEmployee);
      if (!savedEmployee) {
        clearCaregiverClaimData();
      }
    }
  }
}

/** 지난달 가져오기 1명 처리 결과 */
type ImportPrevMonthEmpResult = {
  imported: boolean;
  skipped: boolean;
  skipReason?: 'no_prev_month' | 'no_claims';
};

/** 사원 1명 — 지난달 데이터 가져오기 (저장 없음, 초안만 반영) */
const importPrevMonthForEmployee = async (emp: any): Promise<ImportPrevMonthEmpResult> =>
  withEmployeeDetailContext(emp, async () => {
    if (isCaregiverEmployee(emp) && caregiverClaimRows.value.length === 0) {
      await loadCaregiverClaims();
    }

    if (isCaregiverEmployee(emp)) {
      if (caregiverClaimRows.value.length) {
        try {
          await applyPrevMonthRecipientSettings();
        } catch (error) {
          console.error(`${getEmpName(emp)} 전월 수급자별 시급 가져오기 오류:`, error);
        }
      }
    }

    const response = await homeScheduleApi.getPrevMonthInsurance({
      mCode: memberCode.value,
      yearMonth: yearMonth.value,
      bizGubun: selectedBizGubun.value || '',
      name: getEmpName(emp),
      birth: getEmpBirth(emp),
    });

    if (!response.data?.success || !response.data.data) {
      return { imported: false, skipped: true, skipReason: 'no_prev_month' };
    }

    runPrevMonthImportSteps(response.data.data as PrevMonthTransferData);
    captureEmployeeWageDraft(emp);
    const savedDraft = employeeWageDrafts.value.get(getEmpKey(emp));
    if (savedDraft?.recipient) {
      applyRecipientDraftToExpandedView(getEmpKey(emp), savedDraft.recipient);
    }
    return { imported: true, skipped: false };
  });

const formatPrevMonthMissingNamesMessage = (names: string[], prevYmLabel: string) => {
  if (names.length === 0) return '';
  const list = names.join(', ');
  return `\n전월(${prevYmLabel}) 데이터 없음 ${names.length}명:\n${list}`;
};

/** 목록 — 전체 사원 지난달 데이터 가져오기 (가져온 뒤 자동 모두저장) */
const importPrevMonthDataForAll = async () => {
  if (!canImportPrevMonthDataList.value || !memberCode.value) return;
  if (isAdminPreviewView.value) {
    alert('관리자 시점에서는 가져오기를 할 수 없습니다.');
    return;
  }

  const targetCount = sortedEmployees.value.length;
  if (targetCount === 0) {
    alert('가져올 사원이 없습니다.');
    return;
  }

  const prevYmLabel = formatYearMonthLabel(calcPrevYearMonthLabel(yearMonth.value));
  if (
    !confirm(
      `전체 ${targetCount}명에 대해 지난달(${prevYmLabel}) 데이터를 가져와\n`
      + `자동으로 저장(모두저장)합니다.\n`
      + `(수급자별 시급·급여대장)\n\n계속하시겠습니까?`
    )
  ) {
    return;
  }

  importingPrevMonth.value = true;
  let successCount = 0;
  let skipCount = 0;
  let failCount = 0;
  const noPrevMonthNames: string[] = [];

  try {
    for (const emp of sortedEmployees.value) {
      try {
        const result = await importPrevMonthForEmployee(emp);
        if (result.imported) {
          successCount++;
        } else if (result.skipped) {
          skipCount++;
          if (result.skipReason === 'no_prev_month') {
            noPrevMonthNames.push(getEmpName(emp));
          }
        }
      } catch (error) {
        failCount++;
        console.error(`${getEmpName(emp)} 지난달 데이터 가져오기 오류:`, error);
      }
    }

    if (isExpandedView.value) {
      await loadAllCaregiverClaims();
      applyAllDraftsToExpandedView();
    }

    let saveResult: {
      recipientSaved: number;
      wageSaved: number;
      recipientFailCount: number;
      wageFailCount: number;
    } | null = null;

    if (successCount > 0) {
      importingPrevMonth.value = false;
      saveResult = await saveAllData({ skipConfirm: true, skipSuccessAlert: true });
    }

    const missingNamesMsg = formatPrevMonthMissingNamesMessage(noPrevMonthNames, prevYmLabel);
    const lines = ['지난달 데이터 가져오기·저장 완료', ''];

    if (successCount > 0) lines.push(`✓ 가져오기: ${successCount}명`);
    if (skipCount > 0) lines.push(`- 건너뜀: ${skipCount}명`);
    if (failCount > 0) lines.push(`✗ 가져오기 실패: ${failCount}명`);

    if (saveResult) {
      lines.push('');
      lines.push(`✓ 저장 — 수급자별 설정: ${saveResult.recipientSaved}명`);
      lines.push(`✓ 저장 — 급여대장: ${saveResult.wageSaved}명`);
      if (saveResult.recipientFailCount > 0 || saveResult.wageFailCount > 0) {
        lines.push('');
        lines.push(`✗ 저장 실패 — 수급자 설정: ${saveResult.recipientFailCount}명`);
        lines.push(`✗ 저장 실패 — 급여대장: ${saveResult.wageFailCount}명`);
      }
    } else if (successCount > 0) {
      lines.push('');
      lines.push('✗ 자동 저장에 실패했습니다. 모두저장을 다시 시도해 주세요.');
    } else if (successCount === 0) {
      lines.push('가져온 데이터가 없습니다.');
    }

    if (noPrevMonthNames.length > 0) {
      lines.push(missingNamesMsg);
      lines.push('\n위 사원은 이번 연월 데이터를 직접 입력하세요.');
    }

    alert(lines.join('\n'));
  } finally {
    importingPrevMonth.value = false;
  }
};

/** 수급자별 설정 일괄 저장 (내부) */
const saveAllRecipientSettingsInternal = async () => {
  const empsToSave: Array<{
    emp: any;
    hourlyByGroup: Record<string, number>;
    severeFeeByGroup: Record<string, number>;
    sugaHoursByGroup: Record<string, number>;
  }> = [];

  for (const emp of sortedEmployees.value) {
    if (!isCaregiverEmployee(emp)) continue;
    const payload = collectRecipientSettingsPayloadForEmp(emp);
    if (!payload) continue;
    empsToSave.push({ emp, ...payload });
  }

  if (empsToSave.length === 0) {
    return { savedCount: 0, failCount: 0 };
  }

  let failCount = 0;
  await Promise.all(
    empsToSave.map(async ({ emp, hourlyByGroup, severeFeeByGroup, sugaHoursByGroup }) => {
      try {
        const response = await homeScheduleApi.saveRecipientSettings({
          m_code: memberCode.value,
          yearMonth: yearMonth.value,
          bizGubun: selectedBizGubun.value || '',
          empName: getEmpName(emp),
          empBirth: getEmpBirth(emp),
          hourlyByGroup,
          severeFeeByGroup,
          sugaHoursByGroup,
        });
        if (!response.data?.success) {
          failCount++;
          console.error(`${getEmpName(emp)} 수급자 설정 저장 실패:`, response.data?.message);
        }
      } catch (err) {
        failCount++;
        console.error(`${getEmpName(emp)} 수급자 설정 저장 오류:`, err);
      }
    })
  );

  return { savedCount: empsToSave.length - failCount, failCount };
};

/** 상세 — 선택 사원 1명만 저장 */
const saveCurrentEmployeeData = async (options?: {
  skipConfirm?: boolean;
  skipSuccessAlert?: boolean;
  /** true면 관리자 시점이어도 급여 전체(기본급 포함) 저장 */
  forceFullSave?: boolean;
}): Promise<boolean> => {
  if (isWageReadOnly.value) return false;
  if (!memberCode.value) {
    alert('시설을 선택해 주세요.');
    return false;
  }
  const emp = selectedEmployee.value;
  if (!emp) {
    alert('저장할 사원을 선택해 주세요.');
    return false;
  }

  const empName = getEmpName(emp);
  const adminPartialSave = isAdminPreviewView.value && !options?.forceFullSave;
  if (adminPartialSave) {
    alert('관리자 시점에서는 저장할 수 없습니다. 센터장 시점으로 전환 후 저장해 주세요.');
    return false;
  }
  if (
    !options?.skipConfirm
    && !confirm(
      `${empName}님의 임금 데이터를 저장합니다.\n`
      + `(수급자별 시급·수가시간, 급여대장)\n\n계속하시겠습니까?`
    )
  ) {
    return false;
  }

  savingAllRecipients.value = true;
  let recipientFail = false;

  try {
    captureEmployeeWageDraft(emp);

    if (isCaregiverEmployee(emp)) {
      const mapsData = getMapsForSave();
      const hasRecipient =
        (mapsData.hourlyByGroup && Object.keys(mapsData.hourlyByGroup).length > 0)
        || (mapsData.severeFeeByGroup && Object.keys(mapsData.severeFeeByGroup).length > 0);

      if (hasRecipient) {
        try {
          await saveRecipientSettingsForEmp(emp, {
            hourlyByGroup: mapsData.hourlyByGroup ?? undefined,
            severeFeeByGroup: mapsData.severeFeeByGroup ?? undefined,
            sugaHoursByGroup: mapsData.sugaHoursByGroup ?? undefined,
          });
          hasSavedRecipientSettings.value = true;
        } catch (error) {
          recipientFail = true;
          console.error(`${empName} 수급자 설정 저장 오류:`, error);
        }
      }
    }

    await persistDirectorWageFromCurrentForm(emp);

    const empKey = getEmpKey(emp);
    if (employeeWageDrafts.value.has(empKey)) {
      employeeWageDrafts.value.delete(empKey);
      employeeWageDrafts.value = new Map(employeeWageDrafts.value);
    }

    await loadListWageSummaries();
    await loadWageData();

    clearDirty();

    if (!options?.skipSuccessAlert) {
      if (recipientFail) {
        alert(`${empName}님 급여대장은 저장되었으나, 수급자별 설정 저장에 실패했습니다.`);
      } else {
        alert(`${empName}님의 임금 데이터가 저장되었습니다.`);
      }
    }
    return true;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`저장 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('저장 오류:', error);
    return false;
  } finally {
    savingAllRecipients.value = false;
  }
};

/** 목록 — 모두저장 (유일한 저장 경로) */
const saveAllData = async (options?: {
  skipConfirm?: boolean;
  skipSuccessAlert?: boolean;
}): Promise<{
  recipientSaved: number;
  wageSaved: number;
  recipientFailCount: number;
  wageFailCount: number;
} | null> => {
  if (isWageReadOnly.value) return null;
  if (isAdminPreviewView.value) {
    alert('관리자 시점에서는 저장할 수 없습니다.');
    return null;
  }
  if (!memberCode.value) {
    alert('시설을 선택해 주세요.');
    return null;
  }
  if (sortedEmployees.value.length === 0) {
    alert('저장할 사원이 없습니다.');
    return null;
  }

  captureCurrentEmployeeDraftIfAny();

  if (
    !options?.skipConfirm
    && !confirm(
      `전체 ${sortedEmployees.value.length}명의 임금 데이터를 저장합니다.\n`
      + `\n\n계속하시겠습니까?`
    )
  ) {
    return null;
  }

  savingAllRecipients.value = true;
  let recipientFailCount = 0;
  let wageFailCount = 0;
  let wagePrepFailCount = 0;

  try {
    await ensureExpandedDataForSave();

    wagePrepFailCount = await prepareAllWageDraftsForBulkSave();

    const recipientResult = await saveAllRecipientSettingsInternal();
    recipientFailCount = recipientResult.failCount;

    const wageItems: Record<string, unknown>[] = [];

    for (const emp of sortedEmployees.value) {
      const empKey = getEmpKey(emp);
      const draft = employeeWageDrafts.value.get(empKey);
      if (!draft?.directorPayload && !draft?.wagePayload) continue;

      const directorPayload = draft.directorPayload
        || buildDirectorWageSavePayloadFromLegacyLike(draft.wagePayload);
      wageItems.push({
        ...directorPayload,
        payDate: paymentDate.value || directorPayload.payDate || null,
        ...(isCaregiverEmployee(emp)
          ? { svc: resolveSvcPayloadForEmp(emp) || directorPayload.svc || '' }
          : {}),
      });

      if (draft.recipient && isCaregiverEmployee(emp)) {
        try {
          await saveRecipientSettingsForEmp(emp, draft.recipient);
        } catch (error) {
          recipientFailCount++;
          console.error(`${getEmpName(emp)} 수급자 설정(초안) 저장 오류:`, error);
        }
      }
    }

    if (wageItems.length > 0) {
      const directorBulk = await homeScheduleApi.saveDirectorWageDataBulk(wageItems);
      if (!directorBulk.data?.success) {
        throw new Error(directorBulk.data?.message || '급여대장 일괄 저장 실패');
      }
      const bulkResults = directorBulk.data.data || [];
      wageFailCount = bulkResults.filter((r: any) => r?.error).length;
    }

    clearEmployeeWageDrafts();
    await loadListWageSummaries();

    if (mainView.value === 'detail' && selectedEmployee.value) {
      await loadWageData();
    }

    const recipientSaved = recipientResult.savedCount;
    const wageSaved = wageItems.length - wageFailCount;

    clearDirty();

    const result = {
      recipientSaved,
      wageSaved,
      recipientFailCount,
      wageFailCount,
      wagePrepFailCount,
    };

    if (!options?.skipSuccessAlert) {
      if (recipientFailCount > 0 || wageFailCount > 0 || wagePrepFailCount > 0) {
        alert(
          `저장 완료 (일부 실패)\n\n`
          + `수급자별 설정: ${recipientSaved}명\n`
          + `급여대장: ${wageSaved}명\n\n`
          + `수급자 설정 실패: ${recipientFailCount}명\n`
          + `급여대장 실패: ${wageFailCount}명`
          + (wagePrepFailCount > 0 ? `\n급여 선계산 실패: ${wagePrepFailCount}명` : '')
        );
      } else if (recipientSaved === 0 && wageSaved === 0) {
        alert('저장할 데이터가 없습니다. 지난달 데이터 가져오기 또는 급여를 입력해 주세요.');
      } else {
        alert(
          `저장완료\n\n`
          + `수급자별 설정: ${recipientSaved}명\n`
          + `급여대장: ${wageSaved}명`
        );
      }
    }

    return result;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`모두저장 실패: ${message}`);
    console.error('모두저장 오류:', error);
    return null;
  } finally {
    savingAllRecipients.value = false;
  }
};

/** 모두저장: 모든 사원의 수급자별 설정 일괄 저장 (레거시 — saveAllData 사용) */
const saveAllRecipientSettings = async () => {
  await saveAllData();
};

/**
 * 청구내역 중복 제거 키
 * ★ clcd_idx 우선 (백엔드 dedupeJoinedClaimRows 와 동일) — 계획시간이 같아도 정상 2건이 지워지지 않게 함
 * 폴백: 실제서비스시간 → 계획시간 + 수가
 */
const buildClaimDedupKey = (row: HomeWageClaimRow): string => {
  if (row.clcd_idx != null && row.clcd_idx !== '') {
    return `clcd-${row.clcd_idx}`;
  }
  if (row.clc_idx != null && row.clc_idx !== '') {
    const start = String(row.service_start_time || row.plan_start_time || '').substring(0, 5);
    const end = String(row.service_end_time || row.plan_end_time || '').substring(0, 5);
    return `clc-${row.clc_idx}|${String(row.date || '').substring(0, 10)}|${start}|${end}`;
  }
  const date = String(row.date || '').substring(0, 10);
  const recipient = String(row.recipient_name || '').trim();
  const startTime = String(row.service_start_time || row.plan_start_time || '').substring(0, 5);
  const endTime = String(row.service_end_time || row.plan_end_time || '').substring(0, 5);
  const feeName = String(row.fee_name || '').trim();
  const feeCode = String(row.fee_code || '').trim();
  const feeAmount = String(row.fee_amount || 0);
  return `${date}|${recipient}|${startTime}|${endTime}|${feeName}|${feeCode}|${feeAmount}`;
};

/** 청구내역 중복 제거 */
const deduplicateClaimRows = (rows: HomeWageClaimRow[]): HomeWageClaimRow[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = buildClaimDedupKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const loadCaregiverClaims = async (loadSeq = monthDataLoadSeq.value) => {
  const emp = selectedEmployee.value;
  if (!memberCode.value || !emp || !isCaregiverEmployee(emp)) {
    clearCaregiverClaimData();
    return;
  }
  if (isMonthDataLoadStale(loadSeq)) return;

  claimLoading.value = true;
  try {
    const response = await homeWageApi.getCaregiverClaims({
      mCode: memberCode.value,
      yearMonth: yearMonth.value,
      caregiverName: getEmpName(emp),
      caregiverBirth: getEmpBirth(emp),
      bizGubun: HOME_CARE_BIZ_GUBUNS,
    });
    if (isMonthDataLoadStale(loadSeq)) return;
    if (!response.data?.success) {
      throw new Error(response.data?.message || '청구내역 조회 실패');
    }
    const data = response.data.data;
    const rawRows = data?.rows || [];
    // 중복 청구내역 제거 (같은 서비스일자+수급자+시간간격+수가명+수가코드+수가금액)
    caregiverClaimRows.value = preferPaidClaimDetailRows(deduplicateClaimRows(rawRows));
    caregiverUnjoinedClaims.value = deduplicateClaimRows(data?.unjoinedClaims || []);
    caregiverUnjoinedDetails.value = preferPaidClaimDetailRows(
      deduplicateClaimRows(data?.unjoinedDetails || [])
    );
    caregiverUnjoinedSevereRows.value = preferPaidClaimDetailRows(
      deduplicateClaimRows(data?.unjoinedSevereDetails || [])
    );
    rebuildStructureOnly();
    ensureCaregiverHourlyAtLeastMin();
    // 시급이 설정되면 display 동기화
    if (caregiverHourlyWage.value > 0) {
      caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`청구내역 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('청구내역 로드 오류:', error);
    clearCaregiverClaimData();
    resetStoredMaps();
    hourlyWageDisplay.value = {};
    severeFeeDisplay.value = {};
    sugaHoursDisplay.value = {};
  } finally {
    claimLoading.value = false;
  }
};

const resolveInitialClaimModalTab = (): ClaimModalTab => {
  if (caregiverClaimRows.value.length > 0) return 'list';
  if (caregiverClaimRowsSevere.value.length > 0) return 'severe';
  if (canViewUnjoinedClaimTab.value && claimUnjoinedTotalCount.value > 0) return 'unjoined';
  return 'list';
};

const openClaimDetailModal = () => {
  if (!hasAnyClaimModalData.value) return;
  claimModalSortKey.value = 'date';
  claimModalSortOrder.value = 'asc';
  claimModalTab.value = resolveInitialClaimModalTab();
  showClaimDetailModal.value = true;
};

/** 사원 상세 폼·수급자 설정 상태 초기화 (연월/사원 전환 시) */
const resetEmployeeDetailFormState = (emp: any | null) => {
  payrollDbLock.value = false;
  dbSavedSummaryRows.value = [];
  currentWageDataIdx.value = null;
  directorCanonicalActive.value = false;
  loadedDirectorRow.value = null;
  hasSavedRecipientSettings.value = false;
  isRepresentative.value = false;
  applyRetirement.value = false;
  resetRetirementSource();
  payrollForm.value = createEmptyPayrollForm();
  accountingPayrollShadow.value = null;
  resetPayrollPremiumState();
  resetInsuranceFixed();
  nontaxSettings.value = { ...DEFAULT_HOME_WAGE_NONTAX_SETTINGS };
  prevMonthInsurance.value = null;
  hourlyWageDisplay.value = {};
  severeFeeDisplay.value = {};
  sugaHoursDisplay.value = {};
  resetStoredMaps();
  if (emp && isCaregiverEmployee(emp)) {
    setCaregiverHourlyWage(0);
    caregiverHourlyWageDisplay.value = '';
  } else if (emp) {
    resetStaffForm();
  }
  if (emp) {
    syncPayrollEmployeeMeta();
  }
};

const selectEmployee = async (emp: any) => {
  // ★ 저장하지 않은 변경사항 확인
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  
  captureCurrentEmployeeDraftIfAny();
  selectedEmployee.value = emp;
  mainView.value = 'detail';
  showClaimDetailModal.value = false;
  claimModalTab.value = 'list';
  resetEmployeeDetailFormState(emp);
  await loadEmployeeDetailData();
};

/** 청구내역 → 급여대장 → 수급자별 시급까지 한 번에 로드 (로딩 UX 통합) */
const loadEmployeeDetailData = async (loadSeq = monthDataLoadSeq.value) => {
  if (!selectedEmployee.value || !memberCode.value) return;
  if (isMonthDataLoadStale(loadSeq)) return;

  payrollHydrating.value = true;
  detailLoading.value = true;
  try {
    // 순서 중요: 청구내역 → 공휴일(달력표시) → 급여대장/수급자별 설정 → 표시값 동기화
    await Promise.all([
      loadCaregiverClaims(loadSeq),
      ensureIncomeTaxTable(),
    ]);
    if (isMonthDataLoadStale(loadSeq)) return;
    await ensureHolidaysLoaded();
    if (isMonthDataLoadStale(loadSeq)) return;
    await nextTick();
    await loadPrevMonthInsurance();
    if (isMonthDataLoadStale(loadSeq)) return;
    await loadWageData(loadSeq);
    if (isMonthDataLoadStale(loadSeq)) return;
    if (isCaregiverEmployee(selectedEmployee.value)) {
      syncRecipientSettingsDisplay();
      await nextTick();
      ensureCaregiverPremiumAmountsSynced();
      if (isAdminPreviewView.value && canToggleDirectorPreview.value) {
        capturePayrollPreviewSnapshot();
        applyAdminPayrollPreviewToForm();
      } else {
        reconcileCaregiverHourlyLinkedPayFromClaims({ restoreLockedDeductions: false });
        syncAccountingPayrollShadow();
      }
    }
  } finally {
    if (!isMonthDataLoadStale(loadSeq)) {
      payrollHydrating.value = false;
      detailLoading.value = false;
    }
  }
  // ★ 로드·후처리 watcher가 정착한 뒤 기준 스냅샷 캡처 (blur·focus 오탐 방지)
  if (!isMonthDataLoadStale(loadSeq)) {
    await nextTick();
    // hydrating 중에는 공제 재산출이 스킵되므로, 끝난 뒤 고정 해제 항목만 맞춤
    syncLoadedPayrollDeductions();
    syncReferenceValues();
    clearDirty();
  }
};

const jobTypeOptions = computed(() => {
  const jobs = new Set<string>();
  employees.value.forEach((emp) => {
    const job = emp.clci_job_type || emp.ce_position;
    if (job) jobs.add(job);
  });
  return Array.from(jobs).sort((a, b) => compareJobThenName(a, '', b, ''));
});

const getJobRank = (job: string): number => getJobTypeSortRank(job);

const toggleSort = (key: Exclude<EmployeeListSortKey, 'default'>) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

const getSortIcon = (key: string) => {
  if (sortKey.value !== key) return 'mdi-unfold-more-horizontal';
  return sortOrder.value === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down';
};

const sortedEmployees = computed(() => {
  let list = [...employees.value];

  if (searchName.value) {
    const q = searchName.value.toLowerCase();
    list = list.filter((emp) => getEmpName(emp).toLowerCase().includes(q));
  }

  if (filterJobType.value) {
    list = list.filter((emp) => getEmpJob(emp) === filterJobType.value);
  }

  if (filterClaimBiz.value) {
    list = list.filter((emp) => matchesClaimBizFilter(emp));
  }

  list.sort((a, b) => {
    // 대표자는 항상 맨 위 (직종/정렬 조건과 관계없이)
    const repA = isRepresentativeEmployee(a);
    const repB = isRepresentativeEmployee(b);
    if (repA && !repB) return -1;
    if (!repA && repB) return 1;

    const nameA = getEmpName(a);
    const nameB = getEmpName(b);
    const jobA = getEmpJob(a);
    const jobB = getEmpJob(b);

    if (sortKey.value === 'name') {
      const cmp = nameA.localeCompare(nameB, 'ko');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'job') {
      const cmp = jobA.localeCompare(jobB, 'ko');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'monthlyPay') {
      const cmp = getListWageSortValue(a, 'monthlyPay') - getListWageSortValue(b, 'monthlyPay');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'corpInsurance') {
      const cmp = getListWageSortValue(a, 'corpInsurance') - getListWageSortValue(b, 'corpInsurance');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'retirement') {
      const cmp = getListWageSortValue(a, 'retirement') - getListWageSortValue(b, 'retirement');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    const rankA = getJobRank(jobA);
    const rankB = getJobRank(jobB);
    if (rankA !== rankB) return rankA - rankB;
    return compareJobThenName(jobA, nameA, jobB, nameB);
  });

  return list;
});

const getInitial = (name: string): string => (name && name !== '-' ? name.charAt(0) : '?');

const loadIncomeTaxTable = async () => {
  incomeTaxTable.value = [];
  try {
    const res = await commonWageApi.getIncomeTax(selectedYear.value);
    if (res.data?.success && Array.isArray(res.data.data)) {
      incomeTaxTable.value = res.data.data.map(
        (row: { income1: number; income2: number; depe_1: number }) => ({
          income1: Number(row.income1),
          income2: Number(row.income2),
          depe_1: Number(row.depe_1),
        })
      );
      incomeTaxLoadedYear.value = selectedYear.value;
    }
  } catch (error: unknown) {
    console.error('간이세액표 로드 오류:', error);
  }
};

const ensureIncomeTaxTable = () => {
  const year = selectedYear.value;
  if (incomeTaxTable.value.length > 0 && incomeTaxLoadedYear.value === year) {
    return Promise.resolve();
  }
  if (incomeTaxInflight?.year === year) return incomeTaxInflight.promise;
  const promise = loadIncomeTaxTable().finally(() => {
    if (incomeTaxInflight?.promise === promise) incomeTaxInflight = null;
  });
  incomeTaxInflight = { year, promise };
  return promise;
};

const loadTaxRateInfo = async (options?: { includeIncomeTax?: boolean }) => {
  try {
    const response = await commonWageApi.getTaxRate(selectedYear.value);
    if (response.data?.success && response.data.data) {
      taxRateInfo.value = response.data.data;
      taxRateFallback.value = !!response.data.fallback;
      minHourlyWage.value = Number(response.data.data.minHourlyWage) || 0;
    } else {
      taxRateInfo.value = null;
      taxRateFallback.value = false;
      minHourlyWage.value = 0;
    }
  } catch (error: unknown) {
    console.error('세율 조회 오류:', error);
    taxRateInfo.value = null;
    taxRateFallback.value = false;
    minHourlyWage.value = 0;
  }
  if (options?.includeIncomeTax !== false) {
    await ensureIncomeTaxTable();
  }
  if (mainView.value === 'detail' && selectedEmployee.value) {
    if (isCaregiverEmployee(selectedEmployee.value)) {
      ensureCaregiverHourlyAtLeastMin();
      if (caregiverHourlyWage.value > 0) {
        caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
      }
    } else {
      applyStaffDefaultHourlyWage();
    }
    syncNonTaxTotal();
    syncReferenceValues();
  }
};

const getDefaultPaymentDate = () => {
  // 귀속연월 익월 10일 (예: 4월 급여 → 5월 10일 지급)
  let payYear = selectedYear.value;
  let payMonth = selectedMonth.value + 1;
  if (payMonth > 12) {
    payMonth = 1;
    payYear += 1;
  }
  return `${payYear}-${String(payMonth).padStart(2, '0')}-10`;
};

const redirectIfFacility = () => {
  if (!selectedBizGubun.value || isHomeCareBiz(selectedBizGubun.value)) return;
  const params = isPopupMode.value ? { memberCode: memberCode.value } : undefined;
  router.replace({ name: facilityRouteName.value, params });
};

interface HomeWagePaymentData {
  cwp_date?: string | null;
  cwp_san_rate?: number | null;
  paymentDate?: string | null;
  sanRate?: number | null;
  /** @deprecated caion_wage_payment 전환 전 필드명 */
  hwpay_date?: string | null;
  hwpay_san_rate?: number | null;
}

const resolvePaymentDate = (data: HomeWagePaymentData | null | undefined) =>
  data?.cwp_date ?? data?.paymentDate ?? data?.hwpay_date ?? null;

const resolveSanRate = (data: HomeWagePaymentData | null | undefined) => {
  const raw = data?.cwp_san_rate ?? data?.sanRate ?? data?.hwpay_san_rate;
  return raw != null && Number(raw) > 0 ? Number(raw) : null;
};

const applyPaymentData = (data: HomeWagePaymentData | null) => {
  const payDate = resolvePaymentDate(data);
  if (payDate) {
    paymentDate.value = String(payDate).substring(0, 10);
  } else {
    paymentDate.value = getDefaultPaymentDate();
  }
  const rate = resolveSanRate(data);
  if (rate != null) {
    sanRate.value = rate;
    sanRateDisplay.value = formatSanRateDisplay(rate);
  }
};

const fetchPreviousSanRate = async () => {
  if (!memberCode.value) {
    sanRate.value = 0.0068;
    sanRateDisplay.value = '0.68';
    return;
  }
  for (let i = 1; i <= 12; i++) {
    let targetYear = selectedYear.value;
    let targetMonth = selectedMonth.value - i;
    if (targetMonth <= 0) {
      targetYear -= 1;
      targetMonth += 12;
    }
    const targetYearMonth = `${targetYear}${String(targetMonth).padStart(2, '0')}`;
    try {
      const response = await homeWageApi.getPaymentDate({
        mCode: memberCode.value,
        yearMonth: targetYearMonth,
        bizGubun: selectedBizGubun.value || '',
      });
      const rate = resolveSanRate(response.data?.data);
      if (response.data?.success && rate != null) {
        sanRate.value = rate;
        sanRateDisplay.value = formatSanRateDisplay(rate);
        return;
      }
    } catch {
      // continue
    }
  }
  sanRate.value = 0.0068;
  sanRateDisplay.value = '0.68';
};

const loadPaymentDate = async () => {
  if (!memberCode.value) return;
  try {
    const response = await homeWageApi.getPaymentDate({
      mCode: memberCode.value,
      yearMonth: yearMonth.value,
      bizGubun: selectedBizGubun.value || '',
    });
    if (response.data?.success && response.data.data) {
      applyPaymentData(response.data.data);
      if (resolveSanRate(response.data.data) == null) {
        await fetchPreviousSanRate();
      }
    } else {
      paymentDate.value = getDefaultPaymentDate();
      await fetchPreviousSanRate();
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`지급일 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    paymentDate.value = getDefaultPaymentDate();
    await fetchPreviousSanRate();
  }
};

/** @deprecated 모두저장(saveAllData)만 사용 */
const savePaymentDate = async () => {
  await handleSaveMonthSettings();
};

const handleSaveMonthSettings = async () => {
  // 전월 미마감(isWageReadOnly)과 무관 — 당월 마감 완료일 때만 차단
  if (isWageMagamClosed.value || !memberCode.value) return;
  savingMonthSettings.value = true;
  try {
    await saveMonthSettingsInternal();
    alert('지급일·산재요율이 저장되었습니다.');
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`지급일·산재요율 저장 실패: ${message}`);
  } finally {
    savingMonthSettings.value = false;
  }
};

/** @deprecated 모두저장(saveAllData)만 사용 */
const saveSanRate = async () => {
  await handleSaveMonthSettings();
};

/** @deprecated 상세는 saveCurrentEmployeeData, 목록은 saveAllData */
const saveWageData = async () => {
  if (mainView.value === 'detail' && selectedEmployee.value) {
    await saveCurrentEmployeeData();
  } else {
    await saveAllData();
  }
};

/** 관리자 — 해당 연월 임금 데이터 전체 삭제 (2차 확인, 마감 시 불가) */
const deleteMonthWageData = async () => {
  if (!canDeleteMonthWageData.value || !memberCode.value) return;

  const ym = yearMonth.value;
  const ymLabel = formatYearMonthLabel(ym);
  const biz = selectedBizGubun.value || '';

  if (
    !confirm(
      `[1차 확인] ${ymLabel} 해당 연월 임금 데이터를 전체 삭제하시겠습니까?\n`
      + `(시설 ${memberCode.value} · 급여대장 + 수급자별 설정)`
    )
  ) {
    return;
  }
  if (
    !confirm(
      `[2차 확인] 삭제 후 복구할 수 없습니다.\n`
      + `정말 ${ymLabel} · ${memberCode.value} 의 임금 데이터를 모두 삭제하시겠습니까?`
    )
  ) {
    return;
  }

  deletingWageData.value = true;
  try {
    const response = await homeScheduleApi.deleteMonthWageData({
      mCode: memberCode.value,
      yearMonth: ym,
      bizGubun: biz,
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || '삭제 실패');
    }

    const wageDeleted = response.data.data?.wageDeleted ?? 0;
    const recipientDeleted = response.data.data?.recipientDeleted ?? 0;

    alert(
      `해당 연월 임금 데이터가 삭제되었습니다.\n\n`
      + `급여대장: ${wageDeleted}건\n`
      + `수급자별 설정: ${recipientDeleted}건`
    );

    clearEmployeeWageDrafts();
    currentWageDataIdx.value = null;
    hasSavedRecipientSettings.value = false;
    payrollDbLock.value = false;

    if (mainView.value === 'detail' && selectedEmployee.value) {
      const emp = selectedEmployee.value;
      payrollForm.value = createEmptyPayrollForm();
      resetPayrollPremiumState();
      resetInsuranceFixed();
      nontaxSettings.value = { ...DEFAULT_HOME_WAGE_NONTAX_SETTINGS };
      isRepresentative.value = false;
      applyRetirement.value = false;
      resetStoredMaps();
      hourlyWageDisplay.value = {};
      severeFeeDisplay.value = {};
      sugaHoursDisplay.value = {};
      syncPayrollEmployeeMeta();
      if (isCaregiverEmployee(emp)) {
        setCaregiverHourlyWage(0);
        caregiverHourlyWageDisplay.value = '';
        rebuildStructureOnly();
        ensureCaregiverHourlyAtLeastMin();
        if (caregiverHourlyWage.value > 0) {
          caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
        }
      } else {
        resetStaffForm();
      }
    }

    if (isExpandedView.value) {
      allEmpClaimData.value = new Map();
      allEmpSummaryRows.value = new Map();
      expandedHourlyWage.value = new Map();
      expandedHourlyDisplay.value = new Map();
      expandedSevereFee.value = new Map();
      expandedSevereDisplay.value = new Map();
      await loadAllCaregiverClaims();
    }

    await loadListWageSummaries();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`임금 데이터 삭제 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('임금 데이터 삭제 오류:', error);
  } finally {
    deletingWageData.value = false;
  }
};

/** 이전 달 4대보험 고정값 로드 */
const loadPrevMonthInsurance = async () => {
  if (!selectedEmployee.value || !memberCode.value) {
    prevMonthInsurance.value = null;
    return;
  }

  try {
    const emp = selectedEmployee.value;
    const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    const name = getEmpName(emp);
    const birth = getEmpBirth(emp);

    const response = await homeScheduleApi.getPrevMonthInsurance({
      mCode: memberCode.value,
      yearMonth: ym,
      bizGubun: selectedBizGubun.value || '',
      name,
      birth,
    });

    if (response.data?.success && response.data.data) {
      prevMonthInsurance.value = response.data.data;
    } else {
      prevMonthInsurance.value = null;
    }
  } catch (error) {
    console.error('이전 달 4대보험 조회 오류:', error);
    prevMonthInsurance.value = null;
  }
};

const calcPrevYearMonthLabel = (yearMonth: string) => {
  const ym = String(yearMonth).replace(/-/g, '').substring(0, 6);
  const year = parseInt(ym.substring(0, 4), 10);
  const month = parseInt(ym.substring(4, 6), 10);
  let prevYear = year;
  let prevMonth = month - 1;
  if (prevMonth === 0) {
    prevMonth = 12;
    prevYear = year - 1;
  }
  return `${prevYear}${String(prevMonth).padStart(2, '0')}`;
};

/** 전월 시급 → 기본급 카드 입력 */
const applyPrevMonthHourlyWage = (data: PrevMonthTransferData) => {
  unlockPayrollLedgerForEdit();
  const hourly = Number(data.payCalcHourly) || 0;
  if (hourly <= 0) return;

  const emp = selectedEmployee.value;
  if (isCaregiverEmployee(emp)) {
    setCaregiverHourlyWage(hourly);
    caregiverHourlyWageDisplay.value = formatWageNumber(hourly);
  } else {
    staffForm.value.hourlyWage = hourly;
    staffHourlyWageDisplay.value = formatWageNumber(hourly);
  }
};

/**
 * 사복/센터장 — 전월 근무시간·연차·연야휴 시간을 staffForm에 반영
 * (기본급 계산에 근무시간 필수)
 */
const applyPrevMonthStaffWorkInfo = (data: PrevMonthTransferData) => {
  if (isCaregiverEmployee(selectedEmployee.value)) return;

  unlockPayrollLedgerForEdit();

  let workHours = parseHours(String(data.hour ?? ''));
  if (workHours <= 0 && Number(data.basic) > 0 && Number(data.payCalcHourly) > 0) {
    workHours = Math.round((Number(data.basic) / Number(data.payCalcHourly)) * 10) / 10;
  }

  if (workHours > 0) {
    staffForm.value.workHours = workHours;
    staffWorkHoursDisplay.value = formatHoursDisplay(workHours);
    payrollForm.value.hour = String(workHours);
  }

  if (data.annual != null) {
    staffForm.value.annualPay = Number(data.annual) || 0;
  }
  staffForm.value.overtimeHours = parseHomeWagePremiumHours(data.overtimeHours);
  staffForm.value.nightHours = parseHomeWagePremiumHours(data.nightHours);
  staffForm.value.holidayHours = parseHomeWagePremiumHours(data.holidayHours);
};

/** 전월 비과세 수당·설정 반영 — 컬럼 그대로 복사 (lunch ↔ lunchDirector 승격 없음) */
const applyPrevMonthNonTaxAllowances = (data: PrevMonthTransferData) => {
  // 요양보호사: 자동 중식대는 당월 청구 기준 재산출 — 전월 lunch(조정값) 복사 금지
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    payrollForm.value.lunch = Number(data.lunch) || 0;
    autoBaseLunchRef.value = Number(data.lunch) || 0;
  }
  // ★ 고정 복사: 센터장중식대·차량·양육비 (근무일·시간과 무관)
  for (const key of HOME_WAGE_PREV_MONTH_FIXED_COPY_NONTAX_KEYS) {
    const val = data[key as keyof PrevMonthTransferData];
    if (val != null) {
      (payrollForm.value as Record<string, number | string>)[key] = Number(val) || 0;
    }
  }
  nontaxSettings.value = {
    productionNontax: true,
    lunchExpense: data.lunchExpense === 'Y',
  };
  syncNonTaxTotal();
};

/** 전월 과세 수당 반영 */
const applyPrevMonthTaxableAllowances = (data: PrevMonthTransferData) => {
  payrollForm.value.overtimeHours = parseHomeWagePremiumHours(data.overtimeHours);
  payrollForm.value.nightHours = parseHomeWagePremiumHours(data.nightHours);
  payrollForm.value.holidayHours = parseHomeWagePremiumHours(data.holidayHours);
  payrollPremiumHoursDisplay.value = {
    overtimeHours: formatHomeWagePremiumHoursDisplay(data.overtimeHours),
    nightHours: formatHomeWagePremiumHoursDisplay(data.nightHours),
    holidayHours: formatHomeWagePremiumHoursDisplay(data.holidayHours),
  };

  const premiumSplit = normalizeHomeWagePremiumFromDb({
    overtime: data.overtime,
    night: data.night,
    holiday: data.holiday,
    overtimeNontax: data.overtimeNontax,
    nightNontax: data.nightNontax,
    holidayNontax: data.holidayNontax,
    productionNontax: data.productionNontax,
  });
  applyHomeWagePremiumSplitToForm(payrollForm.value, premiumSplit);

  // ★ 고정 복사: 장기근속·보수교육·상여·처우개선·기타 (근무일·시간과 무관)
  for (const key of HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS) {
    const val = data[key as keyof PrevMonthTransferData];
    if (val != null) {
      (payrollForm.value as Record<string, number | string>)[key] = Number(val) || 0;
    }
  }

  // 사복/센터장: 연차·선임·주휴는 전월 값이 있으면 추가 반영 (고정복사 목록 외)
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    for (const key of HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS) {
      if ((HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS as string[]).includes(key)) continue;
      const val = data[key as keyof PrevMonthTransferData];
      if (val != null) {
        (payrollForm.value as Record<string, number | string>)[key] = Number(val) || 0;
      }
    }
    if (data.weekend != null) {
      payrollForm.value.weekend = Number(data.weekend) || 0;
    }
    syncStaffFormFromPayroll();
  }

  recalcWageTotal();
  if (isCaregiverEmployee(selectedEmployee.value)) {
    syncLaborDayPay();
    recalcWageTotal();
  }
};

/** 전월 연·야·휴 지급/미지급 플래그 반영 후 금액 동기화 */
const applyPrevMonthPremiumPayOverrides = (data: PrevMonthTransferData) => {
  directorPremiumPayOverride.value = {
    payOvertime: parseHomeWageDirectorPremiumPayFlag(data.payOvertime),
    payHoliday: parseHomeWageDirectorPremiumPayFlag(data.payHoliday),
    payNight: parseHomeWageDirectorPremiumPayFlag(data.payNight),
  };
  if (isCaregiverEmployee(selectedEmployee.value)) {
    ensureCaregiverPremiumAmountsSynced();
    recalcWageTotal();
  }
};

/** 급여대장 저장값 → 지급계산시급(기본급 카드) 입력·표시 동기화 */
const applyLoadedPayCalcHourlyFromRecord = (found: any, emp: any): number => {
  const hourly = Math.floor(Number(found?.payCalcHourly) || 0);
  if (hourly <= 0) return 0;

  if (isCaregiverEmployee(emp)) {
    setCaregiverHourlyWage(hourly);
    caregiverHourlyWageDisplay.value = formatWageNumber(hourly);
  } else {
    staffForm.value.hourlyWage = hourly;
    staffHourlyWageDisplay.value = formatWageNumber(hourly);
  }
  return hourly;
};

/** 2단계: 기본급(전월 시급+계산) → 비과세 → 과세 */
const applyPrevMonthStep2PayAndAllowances = (data: PrevMonthTransferData) => {
  applyPrevMonthHourlyWage(data);
  applyPrevMonthStaffWorkInfo(data);
  applyPrevMonthNonTaxAllowances(data);

  const payCalcOk = runAutoFillPayCalc(true);

  // 사복/센터장: 계산 실패 시 전월 기본급·주휴·연차·근무시간 직접 복원
  if (!isCaregiverEmployee(selectedEmployee.value) && !payCalcOk) {
    if (Number(data.basic) > 0) {
      payrollForm.value.basic = Number(data.basic) || 0;
    }
    if (data.weekend != null) {
      payrollForm.value.weekend = Number(data.weekend) || 0;
    }
    if (data.annual != null) {
      payrollForm.value.annual = Number(data.annual) || 0;
      staffForm.value.annualPay = Number(data.annual) || 0;
    }
    const hourFallback = data.hour != null && data.hour !== ''
      ? data.hour
      : staffForm.value.workHours;
    if (hourFallback) payrollForm.value.hour = String(hourFallback);
    recalcWageTotal();
  }

  // 사복: 관리자 시점에서만 자동중식+센터장 합산 조정
  // ★ 센터장 시점 applyAutoLunchDirectorAdjustment 는 lunch=0 으로 지움 → 레거시 lunch-only 중식비 소실
  if (
    !isCaregiverEmployee(selectedEmployee.value)
    && !isDirectorPayrollView.value
    && (Number(payrollForm.value.lunchDirector) > 0 || autoBaseLunchRef.value > 0)
  ) {
    applyAutoLunchDirectorAdjustment({
      autoBase: autoBaseLunchRef.value || Number(payrollForm.value.lunch) || 0,
      adjustPayField: 'annual',
    });
  }
  if (isCaregiverEmployee(selectedEmployee.value)) {
    caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
  }
  applyPrevMonthTaxableAllowances(data);
  applyPrevMonthPremiumPayOverrides(data);

  // ★ 기본급·연차 재산출 후에도 고정 수당이 덮이지 않도록 비과세 고정복사 재적용
  if (!isCaregiverEmployee(selectedEmployee.value)) {
    applyPrevMonthNonTaxAllowances(data);
  }
};

/** 전월 수동 입력 공제(본인부담금·기타) — 항상 전월 금액 반영 */
const applyPrevMonthManualDeductionAmounts = (data: PrevMonthTransferData) => {
  if (data.l_bonin != null) {
    setPayrollMoneyField('l_bonin', Number(data.l_bonin) || 0);
  }
  if (data.l_etc != null) {
    setPayrollMoneyField('l_etc', Number(data.l_etc) || 0);
  }
};

/** 3단계: 공제 고정 플래그·체크된 금액 반영 후 계산 */
const applyPrevMonthStep3Insurance = (data: PrevMonthTransferData) => {
  unlockPayrollLedgerForEdit();
  loadDeductionFixedFromRow(data as unknown as Record<string, unknown>);
  applyPrevMonthFixedDeductionAmounts(data);
  applyPrevMonthManualDeductionAmounts(data);
  prevMonthInsurance.value = { ...data };
  runAutoFillSocialInsurance(true);
};

/** 5단계: 대표자·퇴직금(체크 시) 반영 후 기관공제 계산 */
const applyPrevMonthStep5CorpRetirement = (data: PrevMonthTransferData) => {
  unlockPayrollLedgerForEdit();

  isRepresentative.value = parseHomeWageRepresentativeFlag(data.isRepresentative);

  const prevApplyRetirement = parseHomeWageApplyRetirementFlag(data.applyRetirement);
  applyRetirement.value = prevApplyRetirement;
  if (prevApplyRetirement) {
    payrollForm.value.c_out = Number(data.c_out) || 0;
    loadRetirementSourceFromRow(data);
  } else {
    payrollForm.value.c_out = 0;
    resetRetirementSource();
  }

  runAutoFillCorpDeduct(true);
  syncSupportDeduction(true);
};

/** 전월 데이터 2~5단계 순차 실행 (순서 고정) */
const runPrevMonthImportSteps = (data: PrevMonthTransferData) => {
  applyPrevMonthStep2PayAndAllowances(data);
  applyPrevMonthStep3Insurance(data);
  runAutoFillLaborDeduct(true);
  applyPrevMonthStep5CorpRetirement(data);
  syncReferenceValues();
};

/** 전월 수급자별 시급·중증가산 가져오기 (caion_wage_home_recipient_setting) */
const applyPrevMonthRecipientSettings = async (): Promise<{
  totalCount: number;
  matchedCount: number;
} | null> => {
  if (!selectedEmployee.value || !memberCode.value) return null;
  if (!isCaregiverEmployee(selectedEmployee.value)) return null;

  const targetRows = claimSummaryRows.value.filter((row) => !row.isSevereRecipientSurcharge);
  if (targetRows.length === 0) return null;

  const emp = selectedEmployee.value;
  const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
  const prevYm = calcPrevYearMonthLabel(ym);

  const response = await homeScheduleApi.getRecipientSettings({
    mCode: memberCode.value,
    yearMonth: prevYm,
    bizGubun: selectedBizGubun.value || '',
    empName: getEmpName(emp),
    empBirth: getEmpBirth(emp),
  });

  if (!response.data?.success) {
    throw new Error(response.data?.message || '전월 수급자별 시급 조회 실패');
  }

  const maps = response.data.data || {};
  unlockPayrollLedgerForEdit();
  loadMapsFromDb({
    hourlyByGroup: maps.hourlyByGroup,
    severeFeeByGroup: maps.severeFeeByGroup,
  });
  syncRecipientSettingsDisplay();

  ensureCaregiverHourlyAtLeastMin();
  if (caregiverHourlyWage.value > 0) {
    caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
  }
  syncReferenceValues();

  const matchedCount = claimSummaryRows.value.filter(
    (row) => !row.isSevereRecipientSurcharge && (row.hourlyWage ?? 0) > 0
  ).length;

  return {
    totalCount: targetRows.length,
    matchedCount,
  };
};

/** 지난달 데이터 가져오기 — 1.전월 수급자별 시급 → 2~5.전월 급여 → 자동 저장 */
const importPrevMonthData = async () => {
  if (!canImportPrevMonthData.value || !selectedEmployee.value || !memberCode.value) return;
  if (isAdminPreviewView.value) {
    alert('관리자 시점에서는 가져오기를 할 수 없습니다.');
    return;
  }

  const emp = selectedEmployee.value;
  const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
  const prevYm = calcPrevYearMonthLabel(ym);
  const prevYmLabel = formatYearMonthLabel(prevYm);
  const empName = getEmpName(emp);

  if (
    !confirm(
      `${empName}님의 지난달(${prevYmLabel}) 데이터를 가져와\n`
      + `자동으로 저장합니다.\n`
      + `(수급자별 시급·급여대장)\n\n계속하시겠습니까?`
    )
  ) {
    return;
  }

  importingPrevMonth.value = true;

  try {
    // 1단계: 전월 수급자별 시급·중증가산 (청구내역 선행 로드)
    if (isCaregiverEmployee(selectedEmployee.value)) {
      if (caregiverClaimRows.value.length === 0) {
        await loadCaregiverClaims();
      }
      try {
        await applyPrevMonthRecipientSettings();
      } catch (error: any) {
        console.error('전월 수급자별 시급 가져오기 오류:', error);
      }
    }

    // 2~5단계: 전월 급여 데이터
    let prevData: PrevMonthTransferData | null = null;
    try {
      const emp = selectedEmployee.value;
      const response = await homeScheduleApi.getPrevMonthInsurance({
        mCode: memberCode.value,
        yearMonth: ym,
        bizGubun: selectedBizGubun.value || '',
        name: getEmpName(emp),
        birth: getEmpBirth(emp),
      });

      if (!response.data?.success || !response.data.data) {
        alert(
          `전월(${formatYearMonthLabel(prevYm)}) 급여 데이터가 없습니다.\n\n`
          + `${getEmpName(emp)}\n\n`
          + '해당 사원은 직접 입력하거나, 전월 급여 저장 후 다시 가져오세요.'
        );
      } else {
        prevData = response.data.data as PrevMonthTransferData;
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || '알 수 없는 오류';
      const status = error.response?.status || '';
      alert(`전월 데이터 가져오기 실패: ${message}${status ? ` (${status})` : ''}`);
      console.error('전월 데이터 가져오기 오류:', error);
    }

    if (prevData) {
      try {
        runPrevMonthImportSteps(prevData);
        captureCurrentEmployeeDraftIfAny();
        importingPrevMonth.value = false;
        const saved = await saveCurrentEmployeeData({
          skipConfirm: true,
          skipSuccessAlert: true,
        });
        if (saved) {
          alert(`${empName}님 지난달 데이터 가져오기·저장이 완료되었습니다.`);
        }
      } catch (error: any) {
        const message = error.message || '알 수 없는 오류';
        alert(`전월 데이터 반영 실패: ${message}`);
        console.error('전월 데이터 반영 오류:', error);
      }
    }
  } finally {
    importingPrevMonth.value = false;
  }
};

/** 기존 급여대장 불러오기 */
const loadWageData = async (loadSeq = monthDataLoadSeq.value) => {
  if (!selectedEmployee.value || !memberCode.value) return;
  if (isMonthDataLoadStale(loadSeq)) return;

  try {
    const emp = selectedEmployee.value;
    const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    const name = getEmpName(emp);
    const birth = getEmpBirth(emp);

    // 1. 급여대장 데이터 로드 (센터장 정본만)
    directorCanonicalActive.value = false;
    loadedDirectorRow.value = null;
    const directorRows = await fetchDirectorWageRows(
      memberCode.value,
      ym,
      selectedBizGubun.value || ''
    );
    if (isMonthDataLoadStale(loadSeq)) return;
    let payCalcHourly = 0;
    let loadedWageRecord: any = null;
    const directorFound = matchHomeWageRowByNameBirth(directorRows, name, birth);

    if (directorFound) {
      loadedWageRecord = directorFound;
      currentWageDataIdx.value = directorFound.idx != null ? Number(directorFound.idx) : null;
      payrollDbLock.value = true;
      if (directorFound.svc) {
        dbSavedSummaryRows.value = parseHomeWageRecipientSummaryFromSvc(
          String(directorFound.svc)
        );
      }
      loadDeductionFixedFromRow(directorFound);
      applyDirectorCanonicalRowToForm(directorFound, emp);
      payCalcHourly = Number(directorFound.payCalcHourly) || payCalcHourly;
    } else {
      currentWageDataIdx.value = null;
      payrollDbLock.value = false;
      dbSavedSummaryRows.value = [];
      isRepresentative.value = false;
      applyRetirement.value = false;
      resetRetirementSource();
      resetInsuranceFixed();
      nontaxSettings.value = { ...DEFAULT_HOME_WAGE_NONTAX_SETTINGS };
    }

    // 2. 수급자별 설정 로드 (요양보호사인 경우 — 급여대장 존재 여부와 무관하게 로드)
    if (isCaregiverEmployee(emp)) {
      try {
        const settingsRes = await homeScheduleApi.getRecipientSettings({
          mCode: memberCode.value,
          yearMonth: ym,
          bizGubun: selectedBizGubun.value || '',
          empName: name,
          empBirth: birth,
        });
        if (isMonthDataLoadStale(loadSeq)) return;
        if (settingsRes.data?.success && settingsRes.data.data) {
          const { hourlyByGroup, severeFeeByGroup, sugaHoursByGroup } = settingsRes.data.data;
          hasSavedRecipientSettings.value = [hourlyByGroup, severeFeeByGroup, sugaHoursByGroup].some(
            (map) => map && typeof map === 'object' && Object.keys(map).length > 0
          );

          loadMapsFromDb({
            hourlyByGroup,
            severeFeeByGroup,
            sugaHoursByGroup,
            payCalcHourly,
          });

          // 표시용 값 — 마이그레이션된 summaryRows 기준 (groupKey 4파트 호환)
          syncRecipientSettingsDisplay();
        }
      } catch (settingsError) {
        console.error('수급자별 설정 불러오기 오류:', settingsError);
      }
    }

    // ★ 시급이 0이면 최저시급으로 설정 (저장 데이터 없을 때 지급계산시급 기본값만)
    if (isCaregiverEmployee(emp)) {
      if (caregiverHourlyWage.value <= 0 && minHourlyWage.value > 0) {
        setCaregiverHourlyWage(minHourlyWage.value);
        caregiverHourlyWageDisplay.value = formatWageNumber(minHourlyWage.value);
      }
    } else if (!payrollDbLock.value) {
      applyStaffDefaultHourlyWage();
    }

    // ★ 연·야·휴 — plan 시간·금액 동기화
    if (isCaregiverEmployee(emp) && caregiverClaimRows.value.length > 0) {
      ensureCaregiverPremiumAmountsSynced();
      if (premiumOverrideAmountNeedsSync()) {
        syncPremiumNontaxAmounts('director', true);
        recalcWageTotal();
        syncNonTaxTotal();
      }
      if ((Number(payrollForm.value.severe) || 0) <= 0) {
        syncSeverePayFromClaim();
      }
    }

    // 노동절수당 — 5월만 자동 산출 (DB lock 시 저장값 유지)
    if (isCaregiverEmployee(emp)) {
      if (!isHomeWageLaborDayMonth(yearMonth.value)) {
        payrollForm.value.laborDay = 0;
      } else if (!payrollDbLock.value) {
        syncLaborDayPay();
      }
    }

    if (!payrollDbLock.value) {
      recalcWageTotal();
    } else if (isCaregiverEmployee(emp)) {
      // 저장 데이터 잠금 중에도 laborDay·과세합계만 반영
      recalcWageTotal();
      if (loadedWageRecord && caregiverHourlyWage.value <= 0) {
        applyLoadedPayCalcHourlyFromRecord(loadedWageRecord, emp);
      }
    }

    syncNonTaxTotal();
    syncReferenceValues();

    // ★ 저장 데이터 lock — 과세합계 반영 후 고정 해제 공제 최종 재산출
    syncLoadedPayrollDeductions();

    if (isCaregiverEmployee(emp)) {
      syncRecipientSettingsDisplay();
      // ★ 최종 display 동기화 — DB 로드 후 확실하게 표시값 설정
      if (caregiverHourlyWage.value > 0) {
        caregiverHourlyWageDisplay.value = formatWageNumber(caregiverHourlyWage.value);
      }
      // 자동 중식대 원값·관리자 shadow — 저장값 lunch 조정 역산·보험 산출용
      if (payrollDbLock.value && caregiverClaimRows.value.length > 0) {
        if (autoBaseLunchRef.value <= 0) {
          autoBaseLunchRef.value = Number(payrollForm.value.lunch) || 0;
        }
        if (directorCanonicalActive.value) {
          syncNonTaxTotal();
          syncReferenceValues();
        } else if (isDirectorPayrollView.value) {
          applyDirectorAnnualLunchDisplayPrep();
          syncNonTaxTotal();
          syncReferenceValues();
        } else if (!isAdminPreviewView.value) {
          // 관리자 미리보기 — loadEmployeeDetailData에서 applyAdminPayrollPreviewToForm 1회만
          applyAdminAnnualLunchExtraction();
          recalcWageTotal();
          syncNonTaxTotal();
          syncReferenceValues();
        }
        syncAccountingPayrollShadow();
      }
      // ★ 레거시 cwhd_hour(오조인 과다값) → 청구 집계 hour로 폼 동기화
      if (payrollDbLock.value && claimSummaryRows.value.length > 0) {
        syncPayrollEmployeeMeta();
      }
    }
  } catch (error) {
    console.error('기존 급여대장 불러오기 오류:', error);
  }
};

const loadEmployees = async (loadSeq = monthDataLoadSeq.value) => {
  if (!memberCode.value) return;
  if (isMonthDataLoadStale(loadSeq)) return;

  const keepDetailView = mainView.value === 'detail' && !!selectedEmployee.value;
  const detailEmpKey = keepDetailView ? getEmpKey(selectedEmployee.value) : null;
  const detailEmpSnapshot = keepDetailView ? { ...selectedEmployee.value } : null;

  try {
    const ym = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    const [response] = await Promise.all([
      homeWageApi.getWageSettingEmployees({
        mCode: memberCode.value,
        yearMonth: ym,
        bizGubun: HOME_CARE_BIZ_GUBUNS,
        limit: 2000,
      }),
      loadListWageSummaries(),
    ]);
    if (isMonthDataLoadStale(loadSeq)) return;
    if (!response.data?.success) {
      throw new Error(response.data?.message || '사원 목록 조회 실패');
    }
    const data = response.data.data;
    employees.value = data?.list || [];
    employeeListMeta.value = data?.meta
      ? {
          hasClaimDataForMonth: !!data.meta.hasClaimDataForMonth,
          hasClaimDetailForMonth: !!data.meta.hasClaimDetailForMonth,
          claimCaregiverCount: data.meta.claimCaregiverCount ?? 0,
          claimDetailCount: data.meta.claimDetailCount ?? 0,
        }
      : null;

    // 미등록 사원 모달 — 연월당 자동 1회 (등록 실패 직후 재오픈 루프 방지)
    const ymKey = `${memberCode.value}|${ym}`;
    if (unregisteredPromptYm.value !== ymKey) {
      unregisteredPromptYm.value = ymKey;
      skipUnregisteredAutoPrompt.value = false;
    }
    if (
      unregisteredEmployees.value.length > 0
      && !skipUnregisteredAutoPrompt.value
      && !showUnregisteredModal.value
    ) {
      showUnregisteredModal.value = true;
    }

    if (employeeListMeta.value?.hasClaimDataForMonth) {
      void loadCaregiverClaimsAndSummaries({ silent: true, force: true, loadSeq });
    }
    if (isMonthDataLoadStale(loadSeq)) return;

    if (keepDetailView && detailEmpKey) {
      const found = employees.value.find((e) => getEmpKey(e) === detailEmpKey);
      selectedEmployee.value = found || detailEmpSnapshot;
      mainView.value = 'detail';
      clearPendingPayrollAutoCalc();
      resetEmployeeDetailFormState(selectedEmployee.value);
      await loadEmployeeDetailData(loadSeq);
    } else if (selectedEmployee.value) {
      const key = getEmpKey(selectedEmployee.value);
      const found = employees.value.find((e) => getEmpKey(e) === key);
      if (found) selectedEmployee.value = found;
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`사원 목록 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('사원 목록 로드 오류:', error);
    employees.value = [];
    if (!keepDetailView) {
      selectedEmployee.value = null;
      mainView.value = 'list';
    }
    clearCaregiverClaimData();
  }
};

watch(selectedBizGubun, () => {
  redirectIfFacility();
  if (isHomeCareBiz(selectedBizGubun.value)) {
    reloadMonthData();
  }
});

const reloadLtcsWageData = async () => {
  if (mainView.value === 'detail' && selectedEmployee.value) {
    await loadEmployeeDetailData();
  } else {
    await loadEmployees();
  }
};

const onLtcsWageRequestReady = async () => {
  await reloadLtcsWageData();
};

/** 미등록 사원 일괄 등록 (청구내역 간편등록 — 성명·생년월일·직종) */
const registerUnregisteredEmployees = async () => {
  if (unregisteredEmployees.value.length === 0) {
    showUnregisteredModal.value = false;
    return;
  }

  registeringEmployees.value = true;
  let successCount = 0;
  let failCount = 0;
  const failNames: string[] = [];
  // 등록 중 computed 목록이 바뀌지 않도록 스냅샷
  const targets = [...unregisteredEmployees.value];

  try {
    for (const emp of targets) {
      const birth = getEmpBirth(emp);
      if (!birth) {
        failCount++;
        failNames.push(`${emp.clci_name || '?'}(생년월일 없음)`);
        continue;
      }
      try {
        await employeeApi.create(memberCode.value, {
          clci_name: String(emp.clci_name || '').trim(),
          clci_birth_date: birth,
          clci_job_type: emp.clci_job_type || '요양보호사',
          fromClaim: true,
        });
        successCount++;
      } catch (err: any) {
        const code = err.response?.data?.code;
        // 중복 사원 오류는 이미 등록된 것으로 처리
        if (code === 'DUPLICATE_EMPLOYEE') {
          successCount++;
        } else {
          failCount++;
          const msg = err.response?.data?.message || err.message || '오류';
          failNames.push(`${emp.clci_name || '?'}: ${msg}`);
          console.error(`${emp.clci_name} 등록 실패:`, err);
        }
      }
    }

    showUnregisteredModal.value = false;

    if (successCount === 0 && failCount > 0) {
      // ★ 전부 실패 시 loadEmployees로 모달을 다시 열면 무한루프처럼 보임
      skipUnregisteredAutoPrompt.value = true;
      alert(
        `종사자 등록에 실패했습니다.\n${failNames.slice(0, 5).join('\n')}` +
          (failNames.length > 5 ? `\n외 ${failNames.length - 5}명` : '')
      );
      return;
    }

    if (failCount > 0) {
      alert(
        `${successCount}명 등록 완료, ${failCount}명 실패\n${failNames.slice(0, 5).join('\n')}`
      );
    } else {
      alert(`${successCount}명 종사자가 등록되었습니다.`);
    }

    skipUnregisteredAutoPrompt.value = false;
    await loadEmployees();
  } catch (error: any) {
    skipUnregisteredAutoPrompt.value = true;
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    console.error('미등록 사원 등록 오류:', error);
    alert(`사원 등록 중 오류가 발생했습니다: ${message}`);
  } finally {
    registeringEmployees.value = false;
  }
};

/** 연월·시설 변경 시 사원 목록·지급일·상세(유지 시) 일괄 로드 */
const reloadMonthData = async () => {
  if (!memberCode.value || !isHomeCareBiz(selectedBizGubun.value)) return;

  const loadSeq = ++monthDataLoadSeq.value;
  clearPendingPayrollAutoCalc();
  clearEmployeeWageDrafts();
  clearExpandedRecipientMaps();
  caregiverClaimsCacheKey.value = '';
  allEmpClaimData.value = new Map();
  allEmpSummaryRows.value = new Map();
  loading.value = true;

  try {
    void ensureIncomeTaxTable();
    await Promise.all([
      loadTaxRateInfo({ includeIncomeTax: false }),
      loadPaymentDate(),
      loadEmployees(loadSeq),
    ]);
  } finally {
    if (!isMonthDataLoadStale(loadSeq)) {
      loading.value = false;
    }
  }
};

watch([selectedYear, selectedMonth], () => {
  if (!isHomeCareBiz(selectedBizGubun.value)) return;
  reloadMonthData();
});

/** 시설 변경 시에만 목록 화면으로 초기화 */
watch(memberCode, () => {
  if (!isHomeCareBiz(selectedBizGubun.value)) return;
  monthDataLoadSeq.value += 1;
  clearPendingPayrollAutoCalc();
  mainView.value = 'list';
  selectedEmployee.value = null;
  clearCaregiverClaimData();
  resetStoredMaps();
  hourlyWageDisplay.value = {};
  severeFeeDisplay.value = {};
  sugaHoursDisplay.value = {};
  clearExpandedRecipientMaps();
  reloadMonthData();
});

onMounted(async () => {
  redirectIfFacility();
  if (isHomeCareBiz(selectedBizGubun.value)) {
    await reloadMonthData();
  }
});

// ============== 임금대장/명세서 리포트 ==============

/** 인건비지출내역 모달 열기 */
const openLaborCostModal = async () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }

  loadingLaborCostModal.value = true;
  try {
    await laborCostModalRef.value?.open();
  } finally {
    loadingLaborCostModal.value = false;
  }
};

/** 인건비 절감 현황 모달 */
const openNontaxSavingsModal = () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }
  nontaxSavingsModalRef.value?.open();
};

/**
 * 임금대장 데이터 로드 — DB 저장 스냅샷만 사용
 * ★ 청구 live로 hour·recipientTotal 을 덮어쓰지 않음
 *   (금액은 DB인데 시간/수급자합만 live면 시급×시간≠기본급·연차/중식 extract 오탐 → 상세와 대장 불일치)
 */
const loadWageReportData = async (): Promise<boolean> => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return false;
  }

  loadingWageReportData.value = true;
  try {
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    const directorRows = await fetchDirectorWageRows(
      String(memberCode.value),
      yearMonth,
      selectedBizGubun.value || ''
    );
    wageReportData.value = directorRows;

    if (wageReportData.value.length === 0) {
      alert('해당 월의 저장된 급여 데이터가 없습니다.');
      return false;
    }

    return true;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`임금대장 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    console.error('임금대장 조회 오류:', error);
    return false;
  } finally {
    loadingWageReportData.value = false;
  }
};

const openAccountingWageReport = async () => {
  if (!canViewAccountingReport.value) {
    alert('회계처리용 임금대장은 매니저 이상 권한이 필요합니다.');
    return;
  }
  const ok = await loadWageReportData();
  if (ok) wageReportRef.value?.openPreview('accounting');
};

const openClientWageReport = async () => {
  const ok = await loadWageReportData();
  if (ok) wageReportRef.value?.openPreview('client');
};

/** 최고관리자 — 고객(급여÷12) vs 카이온 퇴직금 비교 */
const openRetirementCompareReport = async () => {
  if (!isSuperAdmin.value) {
    alert('최고관리자만 볼 수 있습니다.');
    return;
  }
  if (listWageRowByEmpKey.value.size === 0) {
    await loadListWageSummaries();
  }
  const ym = getSelectedYearMonth();
  const seen = new Set<string>();
  const wageRows: Record<string, unknown>[] = [];

  for (const emp of sortedEmployees.value) {
    const row = findListWageRowForEmp(emp);
    if (!row) continue;
    const key = getEmpKey(emp);
    if (seen.has(key)) continue;
    seen.add(key);
    wageRows.push({
      ...row,
      name: getEmpName(emp),
      job: getEmpJob(emp) || row.job || row.cwhdir_job,
      yearMonth: ym,
    });
  }

  if (wageRows.length === 0) {
    for (const row of listWageRowByEmpKey.value.values()) {
      wageRows.push({ ...row, yearMonth: ym });
    }
  }

  if (wageRows.length === 0) {
    alert('비교할 임금 데이터가 없습니다.');
    return;
  }

  retirementCompareModalRef.value?.open({
    yearMonth: ym,
    wageRows,
    memberName: memberData?.value?.m_name || '',
  });
};

const handleWageReportError = (message: string) => {
  alert(message);
};

const facilityName = computed(() => memberData?.value?.m_name || '시설');

const sanitizeFilePart = (value: string) => {
  const text = String(value || '').trim().replace(/[\\/:*?"<>|]/g, '_');
  return text || '미지정';
};

const getSelectedYearMonth = () =>
  `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;

const buildWageSlipZipFileName = () => {
  const facility = sanitizeFilePart(facilityName.value);
  const ym = `${selectedYear.value}년${String(selectedMonth.value).padStart(2, '0')}월`;
  return `${facility}_${ym}_임금명세서.zip`;
};

/** 임금명세서 PDF/ZIP — API URL 직접 호출 (blob URL은 저장 시 UUID 파일명이 됨) */
const buildWageSlipPdfUrl = (empKey?: string) => {
  const token = tokenManager.getToken();
  if (!token) {
    return null;
  }
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('mCode', String(memberCode.value));
  params.append('yearMonth', getSelectedYearMonth());
  if (selectedBizGubun.value) {
    params.append('bizGubun', selectedBizGubun.value);
  }
  if (facilityName.value) {
    params.append('memberName', facilityName.value);
  }
  if (empKey) {
    params.append('empKey', empKey);
  }
  params.append('disposition', 'inline');
  params.append('viewMode', 'client');
  return `/api/home-wage/wage-slip-pdf?${params.toString()}`;
};

const buildWageSlipZipUrl = () => {
  const token = tokenManager.getToken();
  if (!token) {
    return null;
  }
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('mCode', String(memberCode.value));
  params.append('yearMonth', getSelectedYearMonth());
  if (selectedBizGubun.value) {
    params.append('bizGubun', selectedBizGubun.value);
  }
  if (facilityName.value) {
    params.append('memberName', facilityName.value);
  }
  params.append('viewMode', 'client');
  return `/api/home-wage/wage-slip-pdf-zip?${params.toString()}`;
};

const openWageSlipPdfInNewTab = (empKey?: string) => {
  const url = buildWageSlipPdfUrl(empKey);
  if (!url) {
    alert('로그인이 필요합니다.');
    return false;
  }
  const previewWindow = window.open(url, '_blank');
  if (!previewWindow) {
    alert('팝업이 차단되었습니다. 브라우저에서 팝업 허용 후 다시 시도해 주세요.');
    return false;
  }
  return true;
};

/** 전체 명세서 PDF 새 창 미리보기 */
const previewAllWageSlipPdf = () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }
  if (!openWageSlipPdfInNewTab()) {
    return;
  }
  showWageSlipOptionsDialog.value = false;
};

/** 개별 사원 명세서 ZIP 다운로드 */
const downloadWageSlipZip = () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }
  const url = buildWageSlipZipUrl();
  if (!url) {
    alert('로그인이 필요합니다.');
    return;
  }
  const link = document.createElement('a');
  link.href = url;
  link.download = buildWageSlipZipFileName();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  showWageSlipOptionsDialog.value = false;
};

/** 개별 사원 임금명세서 PDF 새 창 미리보기 */
const previewIndividualWageSlipPdf = () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }
  if (!selectedEmployee.value) {
    alert('사원을 선택해주세요.');
    return;
  }
  openWageSlipPdfInNewTab(getEmpKey(selectedEmployee.value));
};
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/home-wage-page';
@import '@/assets/styles/pages/member-home-wage-monthly-setting';
</style>
